import { Fragment, useState, useRef, useEffect, useMemo, useCallback, useDeferredValue } from "react";
import {
  rectOf, polygonOf, polygonArea, polygonPerimeter, isPolygonRectangular, detectEdges, wallTakeoff, snapPosition, snapEdge, layoutBounds, pairKey, edgeOverrideKey, roomsAlongPolyline, snapToNearestWall, roomContainingPolygon, classifyConfidence, getRoomEdges, edgeLookup, matchingConstructionRules, snapDrawnPoint, orthoSnap, DEFAULT_TUNABLES,
} from "../../utils/takeoffGeometry";
import { isBackOut, ringOfGeometry as backOutRing } from "../../utils/takeoffExclusions";
import { drawingView, readDarkDrawingPreference, writeDarkDrawingPreference } from "../../utils/canvasTheme";
import { verifyScale, proposedCalibration, SCALE_VERDICTS, fmtPct } from "../../utils/scaleVerification";
import { detectScaleNotes, ENGINEERING_SCALES, sheetNeedsScale, SCALE_REQUIRED_MESSAGE } from "../../utils/scaleDetect";
import { polygonValidity, removePolygonVertex, boundsOfRooms, cleanTracePoints, wallSystemForPosition } from "../../utils/takeoffGeometry";
import { neighborWallReadout } from "../../utils/neighborWalls";
import { rankLearnedCandidates } from "../../utils/learning/wallLearning";
import { buildReviewQueue, applyReviewStates, reviewStatePayload, REVIEW_CATEGORIES } from "../../utils/reviewQueue";
import { summarizeUnreviewed, acceptPayload } from "../../utils/machineReview";
import { findOverlaps } from "../../utils/overlapCheck";
import { resolveElementQuantity as resolveElementQuantityForReview, placedCountFor as placedCountForReview } from "../../config/roomElements";
import { calcRoomSystems as calcRoomSystemsForReview } from "../../utils/roomSystemsEngine";
import { reviewRoomLayout } from "../../utils/layoutReview";
import { backOutEligibleElements } from "../../utils/backOutTargets";
import { backOutsForElement as backOutsForReview } from "../../utils/takeoffExclusions";
import { openingsForRoom as openingsForReview } from "../../config/openings";
import { computeConditionQuantities, WALL_SNAPPED_SHAPES, resolveSymbolShape } from "../../config/conditionCalcTypes";
import {
  isPdfDataUrl, isPdfSource, loadPdf, countPdfPages, renderPageRegion, pdfFileToDataUrl, ARCH_SCALES, nearestArchScale, extractPageText, rotateImageUrl,
  renderPageThumbnail, renderPageFull,
} from "../../utils/pdfRender";
import { orderedSheets, adjacentSheetId, pagePosition, stepPage, pageAt, keySheets, hasTakeoff, takeoffBadgeText, sheetTitle, sheetCalibration, sheetThumbKey, parseSheetMetaFromName, disciplineLabel, SHEET_DISCIPLINES } from "../../utils/sheetNavigation";
import {
  optionSetForItem, optionByKey, parseGeom, polylineLength,
} from "../../config/scopeOptions";
import RoomDetailFlyout from "./RoomDetailFlyout";
import PlanSearchPanel from "./PlanSearchPanel";
import CountByExamplePanel from "./CountByExamplePanel";
import CanvasStatusBar from "./CanvasStatusBar";
import CommandPalette from "./CommandPalette";
import ShortcutGuide from "./ShortcutGuide";
import AddConditionWindow from "./AddConditionWindow";
import DockPanelsMenu from "../../components/DockPanelsMenu";
import { dimText, snapToSixteenth } from "../../utils/feetInches";
import { readRibbonPinChoice, writeRibbonPinned, ribbonVisible, RIBBON_CLOSE_DELAY_MS } from "../../utils/ribbonPin";
import { useViewportTier, ribbonTabbed } from "../../utils/viewportTier";
import { RIBBON_TABS, readRibbonTab, writeRibbonTab, groupVisible } from "../../utils/ribbonTabs";
import TakeoffLegend from "./TakeoffLegend";
import { parseLegend, legendToJson, legendEntries, LEGEND_FONT_SIZES } from "../../utils/takeoffLegend";
import { useTakeoffUiTheme } from "../../utils/takeoffTheme";
import { readDockLayout, writeDockLayout, dockColumns, toggleDockPane, initialSectionsOpen, setHidden as setDockPanelHidden, hasExplicitDockColumns, effectiveDockLayout, DOCK_TWO_COL_W, DOCK_ONE_COL_W, readDockFloat, writeDockFloat, normalizeDockFloat, DOCK_FLOAT_TITLE_H } from "../../utils/dockLayout";
import { sortByCostCode, typeOptionLabel, styleLabel } from "../../utils/conditionPicker";
import ConditionStrip from "./ConditionStrip";
import { UnitsContext, useUnits, useUnitsPreference } from "../../utils/useUnits";
import { isMetric, fmtLength, fmtLengthDecimal, fmtArea, fmtQty, parseLengthToFeet, FT_TO_M } from "../../utils/units";
import { copyableRows, pasteItems, copyNotice } from "../../utils/conditionClipboard";
import LiveCounter from "./LiveCounter";
import { MAX_PINS, readPins, writePins, togglePin, resolvePins, typeForDigit, catalogKeyOfDrawn, toggleHidden, isolate, hiddenSummary, conditionTotals, readCounterOpen, writeCounterOpen } from "../../utils/conditionStrip";
import { buildPaletteItems } from "../../utils/commandPalette";
import { isBlobPointer } from "../../utils/blobStorage";
import { wallSystemTypes } from "../../utils/wallTypes";
import RoomPickDialog from "./RoomPickDialog";
import { cursorText, modeLabel, scaleChip, crosshairSpec, readCrosshairStyle, writeCrosshairStyle, readCrosshairTuning, writeCrosshairTuning, readCrosshairLayers, writeCrosshairLayers, CROSSHAIR_LAYERS, crosshairToolColor, nearbySnapTargets, snapKindLabel, segmentAngleDeg, angleArc, fmtFeetInches, runningQuantity, formatQuantity } from "../../utils/canvasStatus";
import SheetVersionsPanel from "./SheetVersionsPanel";
import { sha256Hex, sameFile } from "../../utils/fileHash";
import { hitToWorld } from "../../utils/planSearch";
import ConditionDetailFlyout from "./ConditionDetailFlyout";
import ChaseDetailFlyout from "./ChaseDetailFlyout";
import ContextMenu from "../../components/ContextMenu";
import { ROTATE_STEP_DEG, transformGeometryJson } from "../../utils/rotateConditions";
import { canDuplicateSheet } from "../../utils/duplicateSheet";
import { reshapeEligible, reshapeBounds, scaleFactorFor } from "../../utils/conditionReshape";
import { haloStroke } from "../../utils/labelHalo";

// Phase 2 layout canvas. Rooms are dragged into position over an optional
// calibrated drawing underlay; adjacency classifies joints as shared walls
// (deduped) or low-wall return cavities (both faces counted), each joint
// clickable to override. All positions are world FEET; zoom is view-only.
//
// Drawing underlays come in two flavours:
//   • PDF  — the original is stored and rasterized LIVE at the current zoom, so
//     it stays crisp. Placed in world feet via the drawing scale (ft/paper-inch),
//     so architectural-scale presets (1/4"=1'-0" …) calibrate in one click.
//   • Raster (PNG/JPEG) — legacy path, downscaled on upload, calibrated by the
//     two-point tool (ImageScaleFtPerPx).

const ZONE_COLORS = ["#3b82f6", "#8b5cf6", "#0d9488", "#d97706", "#dc2626", "#65a30d", "#0369a1", "#9333ea"];
const EDGE_STYLE = {
  shared: { stroke: "#16a34a", label: "shared" },
  cavity: { stroke: "#2563eb", label: "cavity" },
  ignore: { stroke: "#9ca3af", label: "ignored" },
  conflict: { stroke: "#dc2626", label: "conflict — rooms overlap" },
};

// What the Review Walls dock panel surfaces: the 3 low-confidence
// classifyConfidence() tiers, plus 'unassigned' — a wall the engine IS
// confident about (auto_apply) but that no Construction Standard rule
// filled (none matched, or two tied — see resolveConstructionRule), so it
// still needs a human to say what it's built of. manual_override and a
// filled auto_apply wall are deliberately excluded. Ranked so a conflict
// (a genuine drafting error) always sorts above a merely-ambiguous gap,
// and an ambiguous gap above a plain missing construction.
const REVIEW_TIERS = [
  { tier: "conflict",     label: "Conflict" },
  { tier: "needs_review", label: "Needs review" },
  { tier: "suggested",    label: "Suggested" },
  { tier: "unassigned",   label: "Unassigned" },
];

// Resolves which color a wall FACE should show once a real construction is
// assigned — distinct from EDGE_STYLE above, which colors the joint's
// GEOMETRY classification (shared one wall vs. cavity two faces vs.
// ignored) on the centerline between rooms. Feeds the Edge Halo fill
// (roomHaloFillSegments below) — originally built for a separate "Wall
// assembly colors (ring)" layer, folded into the halo 2026-09-09 per direct
// feedback that the halo itself should show what was actually interpreted,
// not a second overlapping ring.
//
// A wall can be flush (one physical shared partition) while still carrying
// two independently-priced finishes — e.g. 1/2" liner on the clean side,
// drywall or nothing on the other. A single 'shared' TakeoffConditionsDrawn row
// carries exactly that: TakeoffConditionLibraryID/TakeoffConditionTypeID (FACE A,
// tied to RoomID) plus TakeoffConditionLibraryIDFaceB/TakeoffConditionTypeIDFaceB
// (FACE B, tied to RoomBID — db/37_condition_library.sql), set from the
// Condition Detail Flyout's second picker. This is deliberately NOT
// resolved from a second manually-traced condition matched by geometry —
// roomsAlongPolyline() can't tell two rooms apart on a flush wall, where
// both room's own wall faces sit within inches of each other (a corridor
// bordering a long run of small rooms makes this the common case, not an
// edge case) — so each face's type is looked up directly off the one row's
// explicit RoomID/RoomBID, no geometry guessing involved.
const RING_UNASSIGNED_COLOR = "#94a3b8";

function wallColorInfo(cond, faceB) {
  if (!cond) return { color: RING_UNASSIGNED_COLOR, assigned: false };
  if (faceB) {
    // Widened to also treat a Kind-based Face B assignment (Phase D-2, see
    // project memory) as assigned — not just the legacy Condition Library
    // columns.
    const typedB = cond.TakeoffConditionLibraryIDFaceB || cond.TakeoffConditionTypeIDFaceB || cond.KindFaceB;
    // A shared wall with no Face B assigned is still symmetric (e.g. "Liner
    // Panel System, Both Sides") — fall through to Face A's own color/
    // assignment below rather than showing "unassigned" on a wall that's
    // actually fully typed, just not split into two faces.
    if (typedB) return { color: cond.FaceBColor || "#0d9488", assigned: true };
  }
  // Widened the same way for Face A — a Kind-based row (Phase B/C/D-1,
  // including real production W-1 walls) was rendering as "unassigned"
  // here even though it's fully typed everywhere else; bug found and fixed
  // alongside the Phase D-2 Face B work above.
  const typed = cond.TakeoffConditionLibraryID || cond.TakeoffConditionTypeID || cond.Kind;
  return { color: typed ? (cond.Color || "#0d9488") : RING_UNASSIGNED_COLOR, assigned: !!typed };
}

// Visual priority per the vision doc's Section 23 table: auto-resolved is
// calm/low-key, suggested is moderate, needs-review and conflict escalate
// toward high visibility, and manual-override is its own distinct color
// (identifiable, not alarming — the estimator already decided).
// Palette revised 2026-09-09 (per direct feedback that "needs review" and
// "conflict" were two near-identical shades of red, especially in a flat
// color-picker swatch where stroke width/dash — the OTHER way they used to
// be distinguished — don't show) into a clean 3-step warm escalation:
// amber (suggested) -> orange (needs_review) -> red (conflict). Colors are
// overridable per-revision — see HALO_TIER_FIELDS/haloStyle below and
// 61_takeoff_halo_settings.sql — a prior manual override survives this
// default-palette change untouched.
const HALO_STYLE = {
  auto_apply:      { stroke: "#94a3b8", opacity: 0.30 },
  suggested:       { stroke: "#f59e0b", opacity: 0.35 },
  needs_review:    { stroke: "#f97316", opacity: 0.45 },
  conflict:        { stroke: "#dc2626", opacity: 0.55 },
  manual_override: { stroke: "#2563eb", opacity: 0.4 },
  none:            { stroke: "#9ca3af", opacity: 0.3 }, // nothing detected yet — includes a room still being traced (draftRoom)
};
// A wall with a REAL assigned construction (an Item/Assembly/Condition
// Type's own catalog Color) always wins over the confidence tiers above —
// see roomHaloFillSegments' tier priority. Kept a calmer, fixed opacity
// (not overridable — it's not a confidence signal) so a resolved, already-
// typed wall reads as settled rather than competing for attention with an
// actual needs_review/conflict stretch elsewhere on the same room, per the
// vision doc's own "visual emphasis reserved for areas requiring attention"
// principle (Section 23).
const HALO_ASSIGNED_OPACITY = 0.4;

// Edge Halo — a translucent FILL band hugging each room's perimeter,
// extending outward by HALO_DISPLAY_DEPTH_FT (a visual depth, decoupled from
// CAVITY_MAX_GAP_FT's detection radius since 2026-09-10 — see
// DEFAULT_TUNABLES). Per stretch of each perimeter edge, resolves to exactly one of
// three states (2026-09-09 redesign — per direct feedback that the old
// implementation was a thin offset LINE, not a translucent fill, and that
// the halo should show what construction was actually interpreted, not
// just a confidence color; this also folds in the former separate "Wall
// assembly colors (ring)" layer, which already resolved exactly that
// signal on its own thinner ring — one overlapping visual layer removed):
//   1. no relationship detected at all (including a room still being
//      traced — draftRoom/haloRooms already fold that in live, unchanged)
//      -> 'none', a calm translucent gray fill (the "still just a search
//      zone" state)
//   2. a relationship IS detected but no real construction is assigned yet
//      -> classifyConfidence()'s tier (auto_apply/suggested/needs_review/
//      conflict/manual_override) — the vision doc's own 5-state confidence
//      model, unchanged
//   3. a REAL construction IS assigned (wallColorInfo() via condByAutoKey,
//      the exact resolution the old ring used) -> that assignment's own
//      catalog Color — a wall visibly turns whatever color the estimator
//      set on that Item/Assembly/Condition Type, with zero new code needed
//      per construction type (vision doc Section 25)
// Each relationship-derived segment (states 2/3, never state 1 — an 'ext'
// stretch has no other room to classify a relationship against) also
// carries `type: edge.type` ('shared' | 'cavity'), so the render below can
// layer a SECOND, independent visual channel — the wall's fill stays the
// confidence/assignment color from above, but its border reuses EDGE_STYLE's
// own shared=green/cavity=blue (already how the joint centerline itself is
// colored) so a common wall vs. a free-standing/chase wall reads at a
// glance without displacing the fill's own signal (2026-09-09, per direct
// feedback that the halo never visually distinguished the two).
// Built on getRoomEdges() (2026-09-10): the halo walks the room's REAL
// perimeter — an L-shaped room gets an L-shaped halo, not its bounding
// box's — and each Edge Run attaches to exactly the perimeter edge it was
// detected on (edgeAIndex/edgeBIndex), so no side-matching heuristic is
// needed. Each returned segment is a stretch [p1,p2] of one perimeter edge
// plus that edge's outward normal; the render extrudes it outward by
// HALO_DISPLAY_DEPTH_FT. `offset` (shared stretches only) is half the
// signed gap to the neighbor's face along the normal — the band is
// centered on the true joint so both rooms produce the identical rectangle
// and the render dedupes on `pairKey` (a shared wall is one wall, not two).
export function roomHaloFillSegments(room, edges, condByAutoKey, tunables = DEFAULT_TUNABLES) {
  const extAssignment = condByAutoKey.get(`ext:${room.RoomID}`);
  const extState = () => {
    const info = wallColorInfo(extAssignment);
    return info.assigned ? { tier: "assigned", color: info.color } : { tier: "none" };
  };

  const result = [];
  for (const pe of getRoomEdges(room)) {
    const t = pe.tangent, n = pe.outwardNormal;
    const at = s => ({ x: pe.p1.x + t.x * s, y: pe.p1.y + t.y * s });
    const emit = (from, to, state, offset = 0) => ({ edgeIndex: pe.edgeIndex, p1: at(from), p2: at(to), normal: n, offset, ...state });

    const intervals = [];
    for (const e of edges) {
      const isA = e.a.RoomID === room.RoomID, isB = e.b.RoomID === room.RoomID;
      if (!isA && !isB) continue;
      if ((isA ? e.edgeAIndex : e.edgeBIndex) !== pe.edgeIndex) continue; // a 'conflict' edge has no edge index and never attaches
      const other = isA ? e.b : e.a;
      const s1 = (e.seg.x1 - pe.p1.x) * t.x + (e.seg.y1 - pe.p1.y) * t.y;
      const s2 = (e.seg.x2 - pe.p1.x) * t.x + (e.seg.y2 - pe.p1.y) * t.y;
      const start = Math.max(0, Math.min(s1, s2)), end = Math.min(pe.length, Math.max(s1, s2));
      if (end <= start) continue;
      // Matches computeAutoWallSegments()'s own key format exactly (see its
      // runSuffix comment, src/utils/takeoffGeometry.js).
      const runSuffix = e.runKey ? `:${e.runKey}` : "";
      const sharedKey = `shared:${Math.min(e.a.RoomID, e.b.RoomID)}:${Math.max(e.a.RoomID, e.b.RoomID)}${runSuffix}`;
      const key = e.type === "shared" ? sharedKey : e.type === "cavity" ? `cavity:${room.RoomID}:${other.RoomID}${runSuffix}` : null;
      // On a 'shared' row, RoomID is always the LOWER numeric id (Face A) and
      // RoomBID the higher (Face B) — see sp_SyncAutoWallConditions'
      // SegKey convention. This room is the Face B side exactly when its own
      // id is the higher of the pair.
      const isFaceB = e.type === "shared" && room.RoomID > other.RoomID;
      intervals.push({ start, end, edge: e, sharedKey, assignment: key ? condByAutoKey.get(key) : null, isFaceB });
    }
    intervals.sort((a, b) => a.start - b.start);

    let cursor = 0;
    for (const iv of intervals) {
      if (iv.start > cursor + 0.05) result.push(emit(cursor, iv.start, extState()));
      const from = Math.max(cursor, iv.start);
      if (iv.end > from) {
        const info = wallColorInfo(iv.assignment, iv.isFaceB);
        const isShared = iv.edge.type === "shared";
        // signedGapFt is measured along A's normal but is the same number
        // from B's side (the normals are anti-parallel), so either room
        // lands its band on the same midline.
        result.push(emit(from, iv.end, {
          ...(info.assigned ? { tier: "assigned", color: info.color } : { tier: classifyConfidence(iv.edge, tunables) }),
          type: iv.edge.type,
          ...(isShared ? { pairKey: iv.sharedKey } : {}),
        }, isShared ? (iv.edge.signedGapFt ?? 0) / 2 : 0));
      }
      cursor = Math.max(cursor, iv.end);
    }
    if (cursor < pe.length - 0.05) result.push(emit(cursor, pe.length, extState()));
  }
  return result;
}

// Maps each confidence tier to its TakeoffHaloSettings column/payload key,
// for merging a per-revision color override on top of HALO_STYLE's own
// defaults (haloStyle below) and for building the save payload from the
// picker UI in the Layers dock section.
const HALO_TIER_FIELDS = [
  { tier: "auto_apply", label: "Auto-apply", settingsKey: "AutoApplyColor", payloadKey: "auto_apply_color" },
  { tier: "suggested", label: "Suggested", settingsKey: "SuggestedColor", payloadKey: "suggested_color" },
  { tier: "needs_review", label: "Needs review", settingsKey: "NeedsReviewColor", payloadKey: "needs_review_color" },
  { tier: "conflict", label: "Conflict", settingsKey: "ConflictColor", payloadKey: "conflict_color" },
  { tier: "manual_override", label: "Manual override", settingsKey: "ManualOverrideColor", payloadKey: "manual_override_color" },
];

const MIN_Z = 1, MAX_Z = 48;          // px per world-foot bounds
const ZOOM_STEP = 1.25;               // multiplicative step for buttons/wheel
const WALL_SUMMARY_H = 34;           // the one-line wall summary under the canvas — also taken out of the drawing's height
const TRACE_CLICK_TOL_PX = 6;          // two trace clicks closer than this (screen px) are one click — see cleanTracePoints
const DRAWER_W = 330;               // the dock's width when it slides over the drawing in a narrow window
const STATUS_BAR_H = 26;             // the status strip under the drawing (CanvasStatusBar) — taken out of the drawing's height
const SIDE_TILE_W = 172;               // Help and Options (right-hand end of the ribbon's two rows) share this width
const ROOM_DRAG_PX = 8;               // a room only starts to move once the pointer has travelled this far — a click or a slip selects, never moves
const CLICK_PX = 4;                   // pointerdown->up movement below this = a click, not a drag (Draw room tool)
const PDF_MAX_LEN = 9_000_000;        // data-URL length cap for stored PDFs (~6.5 MB)
// A quantity as text in the viewer's display units. Imperial keeps the canvas's existing whole-number look.
const qtyText = ({ q, u }, units) => (isMetric(units) ? fmtQty({ q, u }, units) : `${fmt(q)} ${u}`);
// A length in feet-and-inches (imperial) or metres / millimetres (metric).
const lenText = (ft, units) => (isMetric(units) ? fmtLength(ft, units) : fmtFtIn(ft));
// A short decimal length: `12′` or `3.81 m`.
const lenShort = (ft, units, d = 0) => (isMetric(units) ? fmtLengthDecimal(ft, units, 1) : `${fmt(ft, d)}′`);
const fmt = (n, d = 0) => (n === null || n === undefined || isNaN(n)) ? "—"
  : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// OST-style ortho/polar lock for every polyline draw tool (Draw room / Draw
// scope / Draw condition / Draw area, which all share the click-to-add-corner
// interaction): the live point defaults to the nearest DRAW_ANGLE_STEP_DEG
// increment from the last committed vertex (22.5° = 16 directions) — hold
// Shift to draw at a free angle instead, the same default-on/Shift-to-
// override convention OST itself uses. The angle is locked FIRST and any snap
// target may then only slide the point along that locked ray (orthoSnap(),
// takeoffGeometry.js) — snapping used to run after the lock and could turn a
// clearly-vertical wall into a ~5° one when a corner sat a foot to the side.
// The step, the vertex snap tolerance (VERTEX_SNAP_PX, screen pixels) and
// the door/window wall-snap reach (SYMBOL_WALL_SNAP_FT) are Takeoff Variables
// (Setup > Estimate Engine), read off `tunables` — see tracePoint/snapVertex.

// Whether the pending segment (mid -> next) is perpendicular to the last
// committed one (prev -> mid) — drives the small 90° corner glyph OST shows
// to confirm the ortho lock actually formed a right angle (a straight
// continuation isn't a corner, so it gets no glyph).
function rightAngleAt(prev, mid, next) {
  if (!prev || !mid || !next) return null;
  const d1 = { x: mid.x - prev.x, y: mid.y - prev.y };
  const d2 = { x: next.x - mid.x, y: next.y - mid.y };
  const len1 = Math.hypot(d1.x, d1.y), len2 = Math.hypot(d2.x, d2.y);
  if (len1 < 0.05 || len2 < 0.05) return null;
  const u1 = { x: d1.x / len1, y: d1.y / len1 }, u2 = { x: d2.x / len2, y: d2.y / len2 };
  if (Math.abs(u1.x * u2.x + u1.y * u2.y) > 0.02) return null; // not ~perpendicular
  return { dirIn: u1, dirOut: u2 };
}

// The small right-angle "elbow" mark itself, drawn at `mid` with a
// screen-constant leg length (matches the constant-size handles elsewhere
// in this file: world_units = screen_px / pxPerFt).
// Track A2 (2026-09-14) — live feedback while tracing: the length and angle
// of the segment being drawn, and a ring on the point the click will land
// on whenever snapping / the angle lock has moved it off the raw pointer
// (raw = crosshairPos). Screen-constant sizes; nothing here is stored.
function TraceReadout({ prev, cursor, raw, pxPerFt, view, extra }) {
  const units = useUnits();
  if (!prev || !cursor) return null;
  const dx = cursor.x - prev.x, dy = cursor.y - prev.y;
  const len = Math.hypot(dx, dy);
  const ang = ((Math.atan2(-dy, dx) * 180 / Math.PI) + 360) % 360;
  const snapped = raw && Math.hypot(raw.x - cursor.x, raw.y - cursor.y) > 0.02;
  const r = 6 / pxPerFt, fs = Math.min(1.5, 13 / pxPerFt);
  return (
    <g pointerEvents="none">
      {snapped && <circle cx={cursor.x} cy={cursor.y} r={r} fill="none" stroke="var(--cs-blue)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />}
      {snapped && <circle cx={cursor.x} cy={cursor.y} r={r * 0.25} fill="var(--cs-blue)" />}
      <text x={cursor.x + r * 1.6} y={cursor.y - r * 1.2} fontSize={fs} fontWeight={800} fill={view?.textFill || "var(--text)"}
        style={{ ...haloStroke(view, fs) }}>
        {lenText(len, units)} · {Math.round(ang * 10) / 10}°{snapped ? " · snap" : ""}
      </text>
      {extra && (
        <text x={cursor.x + r * 1.6} y={cursor.y - r * 1.2 + fs * 1.25} fontSize={fs} fontWeight={800} fill="var(--cs-blue)"
          style={{ ...haloStroke(view, fs) }}>
          {extra}
        </text>
      )}
    </g>
  );
}

function RightAngleGlyph({ chain, pxPerFt, color }) {
  if (chain.length < 3) return null;
  const [prev, mid, next] = chain.slice(-3);
  const ra = rightAngleAt(prev, mid, next);
  if (!ra) return null;
  const s = 5 / pxPerFt;
  const p1 = { x: mid.x - ra.dirIn.x * s, y: mid.y - ra.dirIn.y * s };
  const p2 = { x: p1.x + ra.dirOut.x * s, y: p1.y + ra.dirOut.y * s };
  const p3 = { x: mid.x + ra.dirOut.x * s, y: mid.y + ra.dirOut.y * s };
  return (
    <polyline points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`}
      fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
  );
}

// Geometry for a placed door/window symbol (Condition Library Style='count'),
// snapped to a wall via snapToNearestWall — { x, y, angleDeg, wallNormalDeg,
// widthFt, flip }. Returns the world-feet points a door needs (hinge, the
// leaf swung fully open, the opposite jamb, and an arc between them) or,
// for a window, just its two jambs — worked out directly from vectors
// rather than an SVG rotate transform, since a wall's "along" and "into
// room" directions aren't always a pure rotation of each other (a bottom
// wall's along-direction is the same as a top wall's, but its into-room
// direction is flipped — that needs reflection, not rotation).
function symbolGeometry(geom) {
  const { x, y, angleDeg, wallNormalDeg, widthFt, flip } = geom;
  const half = (widthFt || 3) / 2;
  // Unit vectors straight from the stored angles — reproduces the old
  // 0/90 and 0/90/180/−90 lookup tables exactly for axis-aligned walls, and
  // also handles a wall that's a degree or two off axis now that
  // snapToNearestWall works on real perimeter edges.
  const unit = deg => { const r = deg * Math.PI / 180; return { x: Math.cos(r), y: Math.sin(r) }; };
  const along = unit(angleDeg || 0);
  const into = unit(wallNormalDeg ?? 90);
  const jambA = { x: x - along.x * half, y: y - along.y * half };
  const jambB = { x: x + along.x * half, y: y + along.y * half };
  const hinge = flip ? jambB : jambA;
  const otherJamb = flip ? jambA : jambB;
  const width = widthFt || 3;
  const leafEnd = { x: hinge.x + into.x * width, y: hinge.y + into.y * width };
  // Quarter-circle arc points from leafEnd to otherJamb, centered at hinge —
  // computed by angle interpolation rather than an SVG arc-flag guess, since
  // the correct sweep direction varies by wall orientation/flip and this is
  // deterministic regardless.
  const a0 = Math.atan2(leafEnd.y - hinge.y, leafEnd.x - hinge.x);
  let a1 = Math.atan2(otherJamb.y - hinge.y, otherJamb.x - hinge.x);
  let da = a1 - a0;
  while (da > Math.PI) da -= 2 * Math.PI;
  while (da < -Math.PI) da += 2 * Math.PI;
  const arcPts = [];
  for (let i = 0; i <= 8; i++) {
    const a = a0 + da * (i / 8);
    arcPts.push({ x: hinge.x + width * Math.cos(a), y: hinge.y + width * Math.sin(a) });
  }
  return { jambA, jambB, hinge, leafEnd, otherJamb, arcPts, into };
}

// A pass-through in plan: a square box straddling the wall line (half in each
// room it connects), jamb to jamb along the wall — an SVG points string.
function passThroughBoxPts(g, widthFt) {
  const d = (widthFt || 2) / 2;
  const off = (p, s) => `${p.x + g.into.x * d * s},${p.y + g.into.y * d * s}`;
  return [off(g.jambA, -1), off(g.jambB, -1), off(g.jambB, 1), off(g.jambA, 1)].join(" ");
}

// SVG path for a filled door-swing wedge — hinge as the pie's center point,
// closed by looping back to the hinge (Z), so the whole quarter-circle the
// leaf sweeps through renders as a solid shape (OST's own convention) rather
// than just the three outline strokes we originally shipped.
function wedgePath(hinge, arcPts) {
  return `M ${hinge.x},${hinge.y} L ${arcPts.map(p => `${p.x},${p.y}`).join(" L ")} Z`;
}

// Double Door (2026-08-26): two independent half-width leaves, each hinged
// at its own outer jamb and swinging into the room to meet at the opening's
// midpoint — French-door style, matching OST's own "Double Door" shape.
// Computed directly rather than by generalizing symbolGeometry() further:
// two independent hinge/radius pairs don't fit that function's single-leaf
// contract.
function doubleDoorWedges(g, widthFt) {
  const half = widthFt / 2;
  const mid = { x: (g.jambA.x + g.jambB.x) / 2, y: (g.jambA.y + g.jambB.y) / 2 };
  function arc(hinge) {
    const openPt = { x: hinge.x + g.into.x * half, y: hinge.y + g.into.y * half };
    const a0 = Math.atan2(openPt.y - hinge.y, openPt.x - hinge.x);
    let a1 = Math.atan2(mid.y - hinge.y, mid.x - hinge.x);
    let da = a1 - a0;
    while (da > Math.PI) da -= 2 * Math.PI;
    while (da < -Math.PI) da += 2 * Math.PI;
    const pts = [];
    for (let i = 0; i <= 8; i++) {
      const a = a0 + da * (i / 8);
      pts.push({ x: hinge.x + half * Math.cos(a), y: hinge.y + half * Math.sin(a) });
    }
    return pts;
  }
  return { a: arc(g.jambA), b: arc(g.jambB) };
}

// Double Egress Door (2026-09-09): unlike Double Door's French-door
// convention (both leaves swing toward each other, meeting at the opening's
// midpoint), a true egress pair swings the SAME direction — both leaves
// open outward into the room/corridor of travel, independently hinged at
// their own outer jamb. Each leaf's closed position lies flat along the
// wall (hinge -> the OTHER jamb's direction, at half length) and its open
// position is straight into the room (the same `into` vector both jambs
// share), same 8-point arc-interpolation technique as doubleDoorWedges.
function doubleEgressWedges(g, widthFt) {
  const half = widthFt / 2;
  const fullLen = Math.hypot(g.jambB.x - g.jambA.x, g.jambB.y - g.jambA.y) || 1;
  const along = { x: (g.jambB.x - g.jambA.x) / fullLen, y: (g.jambB.y - g.jambA.y) / fullLen };
  function arc(hinge, closedDir) {
    const closedPt = { x: hinge.x + closedDir.x * half, y: hinge.y + closedDir.y * half };
    const openPt = { x: hinge.x + g.into.x * half, y: hinge.y + g.into.y * half };
    const a0 = Math.atan2(closedPt.y - hinge.y, closedPt.x - hinge.x);
    let a1 = Math.atan2(openPt.y - hinge.y, openPt.x - hinge.x);
    let da = a1 - a0;
    while (da > Math.PI) da -= 2 * Math.PI;
    while (da < -Math.PI) da += 2 * Math.PI;
    const pts = [];
    for (let i = 0; i <= 8; i++) {
      const a = a0 + da * (i / 8);
      pts.push({ x: hinge.x + half * Math.cos(a), y: hinge.y + half * Math.sin(a) });
    }
    return pts;
  }
  return { a: arc(g.jambA, along), b: arc(g.jambB, { x: -along.x, y: -along.y }) };
}

// OST parity pass (2026-08-26): a condition type's Pattern (linear stroke
// style) and Shape (count-style marker) — see db/37_condition_library.sql's
// column comment. A type's explicit Shape wins; NULL falls back to the
// legacy SyncsToCriteriaField guess so pre-existing Personnel Door/Vision
// Panel rows keep rendering exactly as before without a data backfill.
const PATTERN_DASH = { dashed: "3 2", dotted: "0.6 1.4", "dash-dot": "3 1.4 0.6 1.4" };

// Area fill patterns (AREA_PATTERN_OPTIONS, conditionCalcTypes.js — OST's
// own fill set, added 2026-09-11). A hatch is an SVG <pattern> of 1-px
// screen-constant lines in the condition's Color, one <pattern> per
// (hatch, color) pair actually on the canvas (areaPatternDefs below), so two
// areas of the same type share a def and a recolored type gets its own. The
// tile is sized in world-ft from pxPerFt so the hatch spacing stays a fixed
// ~9 screen px at every zoom instead of ballooning like the 5-ft grid does.
// "" / unknown -> the pre-existing translucent flat fill (unchanged for every
// area drawn before this existed); "none" -> outline only; "solid" -> opaque.
const AREA_HATCH_LINES = {
  horizontal:        ["M 0 0.5 H 1"],
  vertical:          ["M 0.5 0 V 1"],
  "backward-diag":   ["M 0 0 L 1 1", "M -0.5 0.5 L 0.5 1.5", "M 0.5 -0.5 L 1.5 0.5"],
  "forward-diag":    ["M 0 1 L 1 0", "M -0.5 0.5 L 0.5 -0.5", "M 0.5 1.5 L 1.5 0.5"],
  crosshatch:        ["M 0 0.5 H 1", "M 0.5 0 V 1"],
  "diag-crosshatch": ["M 0 0 L 1 1", "M 0 1 L 1 0"],
};
// spacingIn (Items/Assemblies.PatternSpacingIn, OST's "Spacing") pins the
// hatch pitch to real inches on the drawing; NULL keeps the screen-constant
// tile, so the id carries it to keep the two defs apart.
const areaPatternId = (hatch, color, spacingIn) => `area-hatch-${hatch}-${String(color).replace(/[^a-zA-Z0-9]/g, "")}-s${spacingIn > 0 ? String(spacingIn).replace(/[^0-9]/g, "") : 0}`;
const areaHatchTileFt = (spacingIn, pxPerFt) => (spacingIn > 0 ? spacingIn / 12 : 9 / pxPerFt);
// -> { fill, fillOpacity } for an area polygon given its Pattern + Color.
function areaFillPattern(pattern, color, selected, spacingIn) {
  if (pattern === "none") return { fill: "none", fillOpacity: 1 };
  if (pattern === "solid") return { fill: color, fillOpacity: selected ? 0.95 : 0.85 };
  if (AREA_HATCH_LINES[pattern]) return { fill: `url(#${areaPatternId(pattern, color, spacingIn)})`, fillOpacity: selected ? 1 : 0.85 };
  return { fill: color, fillOpacity: selected ? 0.34 : 0.2 }; // "" / legacy / unknown = transparent
}
// The tile-grid overlay def id for one drawn area (grid is anchored at that
// polygon's own bbox corner, so it's per instance, not per type).
const areaGridId = (cond) => `area-grid-${cond.TakeoffConditionDrawnID}`;
const resolveShape = resolveSymbolShape;

// A type's own DefaultWidthFt always wins when set (e.g. a "Pass-Through"
// type at 2ft places at 2ft every time) — this is only the fallback guess
// per shape family when it's unset, replacing the old single door-vs-not
// guess now that there are several wall-snapped widths and a much smaller
// default for a free-placed equipment icon than a door.
function defaultSymbolWidthFt(shape) {
  if (shape === "hepa-filter" || shape === "light-2x4") return 4;   // 2x4 ceiling module, long side
  if (shape === "hepa-filter-2x2" || shape === "light-2x2") return 2;
  if (shape === "double-door" || shape === "double-egress-door") return 6;
  if (shape === "bifold" || shape === "sliding-door") return 4;
  if (shape === "pass-through") return 2;
  if (shape === "window-3") return 5;
  if (shape === "window-2") return 4;
  if (WALL_SNAPPED_SHAPES.has(shape)) return 3;
  return 1.5; // free-placed marker default
}

// Regular N-gon point list, centered at (x,y), radius r, pointy-top (first
// vertex straight up) — shared by hexagon/octagon below.
function regularPolygonPts(x, y, r, sides) {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
    pts.push(`${x + r * Math.cos(a)},${y + r * Math.sin(a)}`);
  }
  return pts.join(" ");
}

// Free-placed symbol glyphs (2026-09-09 shape-catalog expansion) — every
// SHAPE_OPTIONS value NOT in WALL_SNAPPED_SHAPES (conditionCalcTypes.js)
// renders through here: a single point (x,y) + a size, no wall/jamb
// geometry involved (see the symbolMode placement branch below for the
// free-vs-wall-snapped placement split). Used identically by both the
// placed-symbol render block and the ghost-preview block so the two never
// drift apart. Every glyph here is a plain-SVG-primitive approximation of
// the real plan symbol it names — visually reviewed against a reference
// image, not pixel-verified in a live browser (this app is Entra/MSAL-gated
// with no route this sandbox can drive); expect some to need a redraw once
// Nick sees them live, and each is its own isolated branch to tweak.
// Selected-symbol marker: a ring with a dot at the centre of a placed door / window / symbol, white-haloed so it
// reads on any drawing. Sized in screen pixels (÷ pxPerFt), so it stays the same size at every zoom; no pointer events.
function SelectionMark({ x, y, pxPerFt, color }) {
  const r = 6 / pxPerFt;
  return (
    <g pointerEvents="none" data-testid="symbol-selection-mark">
      <circle cx={x} cy={y} r={r} fill="#fff" fillOpacity={0.55} stroke="#fff" strokeWidth={4} vectorEffect="non-scaling-stroke" />
      <circle cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      <circle cx={x} cy={y} r={2.2 / pxPerFt} fill={color} />
    </g>
  );
}

function SymbolGlyph({ shape, x, y, widthFt, color, sel, pattern, spacingIn }) {
  const strokeW = sel ? 2.8 : 1.3;
  const fillOp = sel ? 0.35 : 0.22;
  // A Count symbol's Pattern is a fill (same set as an Area's — see
  // conditionCalcTypes.js patternOptionsForStyle): "" keeps the historic
  // translucent fill, "solid"/"none"/hatches route through areaFillPattern()
  // (hatch defs are emitted per (pattern, color) in the <defs> block).
  const patternFill = pattern ? areaFillPattern(pattern, color, sel, spacingIn) : null;
  const commonFill = { fill: color, fillOpacity: fillOp, ...(patternFill || {}), stroke: color, strokeWidth: strokeW, vectorEffect: "non-scaling-stroke" };
  const commonStroke = { fill: "none", stroke: color, strokeWidth: strokeW, vectorEffect: "non-scaling-stroke" };

  // x/dot predate the free-placed catalog expansion (they've always been
  // point symbols) — kept at their exact historic fixed size regardless of
  // widthFt so already-placed instances don't visually jump.
  if (shape === "x") {
    return (
      <>
        <line x1={x - 0.6} y1={y - 0.6} x2={x + 0.6} y2={y + 0.6} stroke={color} strokeWidth={sel ? 2 : 1.4} vectorEffect="non-scaling-stroke" />
        <line x1={x - 0.6} y1={y + 0.6} x2={x + 0.6} y2={y - 0.6} stroke={color} strokeWidth={sel ? 2 : 1.4} vectorEffect="non-scaling-stroke" />
      </>
    );
  }
  if (shape === "dot") return <circle cx={x} cy={y} r={sel ? 1 : 0.7} fill={color} />;

  const rx = (widthFt || 1.5) / 2;
  const fs = Math.max(0.5, rx * 0.9); // text glyph size, scaled to the marker

  switch (shape) {
    // ── Basic markers ──────────────────────────────────────────────
    case "rect":
      return <rect x={x - rx} y={y - rx * 0.65} width={rx * 2} height={rx * 1.3} {...commonFill} />;
    case "square":
      return <rect x={x - rx} y={y - rx} width={rx * 2} height={rx * 2} {...commonFill} />;
    case "circle":
      return <circle cx={x} cy={y} r={rx} {...commonFill} />;
    case "triangle":
      return <polygon points={`${x},${y - rx} ${x - rx * 0.87},${y + rx * 0.5} ${x + rx * 0.87},${y + rx * 0.5}`} {...commonFill} />;
    case "diamond":
      return <polygon points={`${x},${y - rx} ${x + rx},${y} ${x},${y + rx} ${x - rx},${y}`} {...commonFill} />;
    case "ellipse":
      return <ellipse cx={x} cy={y} rx={rx} ry={rx * 0.6} {...commonFill} />;
    case "hexagon":
      return <polygon points={regularPolygonPts(x, y, rx, 6)} {...commonFill} />;
    case "octagon":
      return <polygon points={regularPolygonPts(x, y, rx, 8)} {...commonFill} />;
    case "check":
      return <polyline points={`${x - rx},${y} ${x - rx * 0.2},${y + rx} ${x + rx},${y - rx}`} fill="none" stroke={color} strokeWidth={sel ? 2.2 : 1.6} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />;

    // ── Fire Alarm ─────────────────────────────────────────────────
    case "fire-pull-station":
      return (
        <>
          <rect x={x - rx} y={y - rx} width={rx * 2} height={rx * 2} {...commonStroke} strokeWidth={strokeW} />
          <line x1={x - rx * 0.5} y1={y + rx * 0.5} x2={x + rx * 0.5} y2={y - rx * 0.5} stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
        </>
      );
    case "smoke-detector":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <circle cx={x} cy={y} r={rx * 0.45} fill="none" stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
        </>
      );
    case "fire-control-panel":
      return (
        <>
          <rect x={x - rx} y={y - rx} width={rx * 2} height={rx * 2} {...commonStroke} />
          <text x={x} y={y + fs * 0.35} textAnchor="middle" fontSize={fs * 0.7} fontWeight={800} fill={color}>FACP</text>
        </>
      );

    // ── Electrical ─────────────────────────────────────────────────
    case "outlet-single":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <line x1={x - rx * 0.35} y1={y - rx * 0.4} x2={x - rx * 0.35} y2={y + rx * 0.4} stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
          <line x1={x + rx * 0.35} y1={y - rx * 0.4} x2={x + rx * 0.35} y2={y + rx * 0.4} stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
        </>
      );
    case "outlet-duplex":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          {[-0.5, -0.17, 0.17, 0.5].map((f, i) => (
            <line key={i} x1={x + rx * f} y1={y - rx * 0.4} x2={x + rx * f} y2={y + rx * 0.4} stroke={color} strokeWidth={strokeW * 0.85} vectorEffect="non-scaling-stroke" />
          ))}
        </>
      );
    case "switch":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <text x={x} y={y + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight={800} fill={color}>S</text>
        </>
      );

    // ── Ceiling systems (HEPA & Lighting engine, autoLayoutEngine.js) ──
    // widthFt = long side; a 2x4 module is drawn 2:1, a 2x2 square. HEPA =
    // outlined module with an X and a center dot (the common terminal-filter
    // plan symbol); light = filled module with one diagonal and a lamp line.
    case "hepa-filter":
    case "hepa-filter-2x2": {
      const hw = rx, hh = shape === "hepa-filter" ? rx / 2 : rx;
      return (
        <>
          <rect x={x - hw} y={y - hh} width={hw * 2} height={hh * 2} {...commonStroke} />
          <line x1={x - hw} y1={y - hh} x2={x + hw} y2={y + hh} stroke={color} strokeWidth={strokeW * 0.8} vectorEffect="non-scaling-stroke" />
          <line x1={x - hw} y1={y + hh} x2={x + hw} y2={y - hh} stroke={color} strokeWidth={strokeW * 0.8} vectorEffect="non-scaling-stroke" />
          <circle cx={x} cy={y} r={Math.min(hw, hh) * 0.28} fill={color} fillOpacity={Math.min(1, fillOp + 0.4)} />
        </>
      );
    }
    case "light-2x4":
    case "light-2x2": {
      const hw = rx, hh = shape === "light-2x4" ? rx / 2 : rx;
      return (
        <>
          <rect x={x - hw} y={y - hh} width={hw * 2} height={hh * 2} {...commonFill} />
          <line x1={x - hw} y1={y + hh} x2={x + hw} y2={y - hh} stroke={color} strokeWidth={strokeW * 0.8} vectorEffect="non-scaling-stroke" />
          <line x1={x - hw * 0.7} y1={y} x2={x + hw * 0.7} y2={y} stroke={color} strokeWidth={strokeW * 1.6} vectorEffect="non-scaling-stroke" />
        </>
      );
    }

    // ── Mechanical ─────────────────────────────────────────────────
    case "fan-riser":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <polygon points={`${x},${y} ${x + rx * 0.8},${y - rx * 0.5} ${x + rx * 0.8},${y + rx * 0.5}`} fill={color} fillOpacity={fillOp} />
        </>
      );
    case "terminal-box":
      return (
        <>
          <rect x={x - rx} y={y - rx * 0.6} width={rx * 2} height={rx * 1.2} {...commonStroke} />
          <text x={x} y={y + fs * 0.3} textAnchor="middle" fontSize={fs * 0.75} fontWeight={800} fill={color}>VAV</text>
        </>
      );
    case "temp-sensor":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <text x={x} y={y + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight={800} fill={color}>T</text>
        </>
      );
    case "humidistat":
      return (
        <>
          <circle cx={x} cy={y} r={rx} {...commonStroke} />
          <text x={x} y={y + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight={800} fill={color}>H</text>
        </>
      );
    case "diffuser-supply-4way":
      return (
        <>
          <rect x={x - rx} y={y - rx} width={rx * 2} height={rx * 2} {...commonStroke} />
          <line x1={x - rx} y1={y - rx} x2={x + rx} y2={y + rx} stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
          <line x1={x - rx} y1={y + rx} x2={x + rx} y2={y - rx} stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
        </>
      );
    case "grille-return":
      return (
        <>
          <rect x={x - rx} y={y - rx * 0.7} width={rx * 2} height={rx * 1.4} {...commonStroke} />
          {[-0.6, -0.2, 0.2, 0.6].map((f, i) => (
            <line key={i} x1={x + rx * f - rx * 0.3} y1={y + rx * 0.6} x2={x + rx * f + rx * 0.3} y2={y - rx * 0.6} stroke={color} strokeWidth={strokeW * 0.7} vectorEffect="non-scaling-stroke" />
          ))}
        </>
      );
    case "diffuser-linear-supply": {
      const len = widthFt || 2.5, th = 0.35;
      return (
        <>
          <rect x={x - len / 2} y={y - th / 2} width={len} height={th} {...commonStroke} />
          {Array.from({ length: 5 }, (_, i) => -len / 2 + (len * (i + 0.5)) / 5).map((dx, i) => (
            <line key={i} x1={x + dx} y1={y - th / 2} x2={x + dx} y2={y + th / 2} stroke={color} strokeWidth={strokeW * 0.7} vectorEffect="non-scaling-stroke" />
          ))}
        </>
      );
    }
    case "diffuser-linear-return": {
      const len = widthFt || 2.5, th = 0.35;
      return (
        <>
          <rect x={x - len / 2} y={y - th / 2} width={len} height={th} {...commonFill} />
          {Array.from({ length: 4 }, (_, i) => -len / 2 + (len * (i + 1)) / 5).map((dx, i) => (
            <line key={i} x1={x + dx} y1={y - th / 2} x2={x + dx + th} y2={y + th / 2} stroke={color} strokeWidth={strokeW * 0.7} vectorEffect="non-scaling-stroke" />
          ))}
        </>
      );
    }
    case "flex-connection": {
      const len = widthFt || 2, zig = len / 6;
      const pts = [];
      for (let i = 0; i <= 6; i++) pts.push(`${x - len / 2 + i * zig},${y + (i % 2 === 0 ? -0.2 : 0.2)}`);
      return <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth={sel ? 2.2 : 1.6} vectorEffect="non-scaling-stroke" />;
    }

    // ── Plumbing ───────────────────────────────────────────────────
    case "valve-gate":
      return <polygon points={`${x - rx},${y - rx * 0.6} ${x - rx},${y + rx * 0.6} ${x},${y} ${x + rx},${y - rx * 0.6} ${x + rx},${y + rx * 0.6} ${x},${y}`} {...commonStroke} />;
    case "valve-ball":
      return (
        <>
          <polygon points={`${x - rx},${y - rx * 0.6} ${x - rx},${y + rx * 0.6} ${x},${y} ${x + rx},${y - rx * 0.6} ${x + rx},${y + rx * 0.6} ${x},${y}`} {...commonStroke} />
          <circle cx={x} cy={y} r={rx * 0.3} fill={color} />
        </>
      );
    case "valve-globe":
      return (
        <>
          <polygon points={`${x - rx},${y - rx * 0.6} ${x - rx},${y + rx * 0.6} ${x},${y} ${x + rx},${y - rx * 0.6} ${x + rx},${y + rx * 0.6} ${x},${y}`} {...commonStroke} />
          <circle cx={x} cy={y} r={rx * 0.45} fill="none" stroke={color} strokeWidth={strokeW} vectorEffect="non-scaling-stroke" />
        </>
      );

    default:
      return <circle cx={x} cy={y} r={rx * 0.5} fill={color} />;
  }
}

// Feet-and-inches, matching how a real dimension is printed on a drawing
// (e.g. "32'-6\"") — used only by the Measure tool, since that's the one
// place the readout gets compared directly against a printed dimension;
// everywhere else in the app uses decimal feet.
function fmtFtIn(totalFt) {
  const totalIn = Math.round(Math.abs(totalFt) * 12);
  return `${Math.floor(totalIn / 12)}'-${totalIn % 12}"`;
}

// "nice" round number <= target for the graphic scale bar (1/2/5 × 10^n)
function niceFeet(target) {
  if (!(target > 0)) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  const base = target / pow;
  const nice = base >= 5 ? 5 : base >= 2 ? 2 : 1;
  return nice * pow;
}

// Downscale an uploaded raster plan to a manageable JPEG data URL
function fileToDataUrl(file, maxDim = 2200) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * scale);
      c.height = Math.round(img.naturalHeight * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

// Manage Sheets (2026-08-26) — a table alternative to clicking pills one at
// a time, matching OST's own Plan Organizer dialog. The pill row handles
// day-to-day select/rename/delete fine; this is for reordering (which pills
// don't support at all) and seeing every sheet's scale at a glance once a
// revision has accumulated more than a couple.
function ManageSheetsDialog({ sheets, activeSheetId, isLocked, onSelect, onRename, onReorder, onDelete, onSaveMeta, onClose }) {
  const ordered = [...sheets].sort((a, b) => (a.DisplayOrder ?? 100) - (b.DisplayOrder ?? 100));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 10, width: 580, maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>Manage Sheets</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto" }}>
          {ordered.map((s, i) => {
            const scaleLabel = s.ImageFtPerInch ? nearestArchScale(s.ImageFtPerInch)?.scale.label
              : (s.ImageScaleFtPerPx ? "calibrated (raster)" : "—");
            const active = s.SheetID === activeSheetId;
            return (
              <div key={s.SheetID} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 18px",
                borderTop: i > 0 ? "1px solid var(--cs-border)" : "none",
                background: active ? "var(--tint-blue)" : "transparent",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <button onClick={() => onReorder?.(s.SheetID, -1)} disabled={isLocked || i === 0} title="Move earlier"
                    style={{ background: "none", border: "none", cursor: isLocked || i === 0 ? "default" : "pointer", fontSize: 10, color: isLocked || i === 0 ? "#d1d5db" : "var(--text3)", padding: 0, lineHeight: 1 }}>▲</button>
                  <button onClick={() => onReorder?.(s.SheetID, 1)} disabled={isLocked || i === ordered.length - 1} title="Move later"
                    style={{ background: "none", border: "none", cursor: isLocked || i === ordered.length - 1 ? "default" : "pointer", fontSize: 10, color: isLocked || i === ordered.length - 1 ? "#d1d5db" : "var(--text3)", padding: 0, lineHeight: 1 }}>▼</button>
                </div>
                <button onClick={() => onSelect(s.SheetID)} style={{
                  flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "var(--cs-blue)" : "var(--text)",
                }}>{s.SheetName}{active ? "  (active)" : ""}</button>
                {/* A3 — sheet number + discipline (db/38); blur / change saves, '' clears */}
                {!isLocked && onSaveMeta ? (
                  <>
                    <input key={`n${s.SheetID}:${s.SheetNumber || ""}`} defaultValue={s.SheetNumber || ""} placeholder="No." title="Sheet number as printed on the title block (e.g. A-101)"
                      onBlur={e => { const v = e.target.value.trim(); if (v !== (s.SheetNumber || "")) onSaveMeta(s.SheetID, { sheet_number: v }); }}
                      style={{ width: 64, fontSize: 11.5, padding: "2px 5px", border: "1px solid var(--cs-border)", borderRadius: 4 }} />
                    <select value={s.Discipline || ""} onChange={e => onSaveMeta(s.SheetID, { discipline: e.target.value })} title="Discipline"
                      style={{ fontSize: 11.5, padding: "2px 4px", border: "1px solid var(--cs-border)", borderRadius: 4, color: s.Discipline ? "var(--text)" : "var(--text3)" }}>
                      <option value="">— discipline —</option>
                      {SHEET_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                  </>
                ) : (
                  <span style={{ fontSize: 11.5, color: "var(--text3)", minWidth: 90 }}>{[s.SheetNumber, disciplineLabel(s.Discipline)].filter(Boolean).join(" · ")}</span>
                )}
                <span style={{ fontSize: 11.5, color: "var(--text3)", fontFamily: "monospace", minWidth: 100, textAlign: "right" }}>{scaleLabel}</span>
                {s.ImageIsPdf && s.ImagePage && <span style={{ fontSize: 11, color: "var(--text3)", minWidth: 30 }}>p.{s.ImagePage}</span>}
                {!isLocked && (
                  <>
                    <button onClick={() => { const n = window.prompt("Rename sheet:", s.SheetName); if (n) onRename(s.SheetID, n); }}
                      title="Rename" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, padding: "2px 4px", color: "var(--text3)" }}>✎</button>
                    <button onClick={() => onDelete(s.SheetID)}
                      title="Delete" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 4px", color: "var(--text3)" }}>🗑</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Right dock panels (2026-08-26, OST parity pass) — see the dockOpen/
// dockSectionsOpen state comment in the main component for why this exists.

// The built-in layers — each key gates one render block below. Since
// 2026-09-11 these are ALSO seeded as IsSystem rows in TakeoffLayers
// (db/66_takeoff_layers.sql, Setup > Estimate Engine > Takeoff Layers) under
// exactly these keys, so an admin can rename/recolor/reorder them and add
// custom layers alongside; this list is only the fallback while that
// catalog hasn't loaded (or on a DB that hasn't run db/66 yet). Keep the
// keys in sync with the seed if one is ever added.
const LAYER_DEFS = [
  { key: "background", label: "Background drawing", defaultVisible: true },
  { key: "edgeHalo", label: "Edge Halo", defaultVisible: true },
  { key: "rooms", label: "Rooms", defaultVisible: true },
  { key: "scope", label: "Draw scope runs", defaultVisible: true },
  { key: "conditions", label: "Draw condition runs", defaultVisible: true },
  { key: "symbols", label: "Placed symbols (doors/windows/markers)", defaultVisible: true },
  { key: "routes", label: "Utility / HVAC routes (conceptual)", defaultVisible: true },
  { key: "edgeDebug", label: "Edge debug — why did the engine think this?", defaultVisible: false },
];

function LayersPanel({ layerDefs, hiddenLayers, onToggle }) {
  return (
    <div style={{ padding: 10 }}>
      {layerDefs.map(l => (
        <label key={l.key} title={l.description || undefined} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 4px", fontSize: 12.5, color: "var(--text)", cursor: "pointer" }}>
          <input type="checkbox" checked={!hiddenLayers.has(l.key)} onChange={() => onToggle(l.key)} />
          {l.color && <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: l.color, border: "1px solid var(--cs-border)", flexShrink: 0 }} />}
          <span style={{ flex: 1 }}>{l.label}</span>
          {l.isSystem === false && <span style={{ fontSize: 10, color: "var(--text3)" }} title="Admin-added layer — hides everything whose Item/Assembly/Room Template is assigned to it">custom</span>}
        </label>
      ))}
    </div>
  );
}

// A small overview map showing where the current viewport sits within the
// whole layout, matching the keyplan thumbnail on a real architectural
// title block. Click anywhere on it to re-center the main view there.
// bgSrc/bgRect (the same PDF-raster/legacy-image source + world-ft rect the
// main canvas draws) render behind the room blocks, OST-style, when the
// background layer isn't hidden — bounds expand to cover the whole drawing
// sheet, not just the rooms, so a drawing wider than the placed rooms still
// shows in full.
function KeyplanPanel({ effRooms, pan, pxPerFt, canvasHeight, svgRef, onJump, bgSrc, bgRect, bgFrame, bgOpacity, view = null }) {
  const roomsBounds = layoutBounds(effRooms);
  // Framing (bw/bh/kscale/offset below) is deliberately keyed off bgFrame —
  // the whole sheet's stable world extent — never off bgRect, which for a
  // PDF is only the main canvas's current re-rasterized VIEWPORT crop (see
  // the "re-rasterize only the visible region" effect above) and would
  // otherwise make the entire mini-map resize/reposition as the main view
  // pans or zooms instead of staying a fixed overview.
  const b = bgFrame
    ? {
        minX: Math.min(bgFrame.x, roomsBounds?.minX ?? bgFrame.x),
        minY: Math.min(bgFrame.y, roomsBounds?.minY ?? bgFrame.y),
        maxX: Math.max(bgFrame.x + bgFrame.w, roomsBounds?.maxX ?? bgFrame.x + bgFrame.w),
        maxY: Math.max(bgFrame.y + bgFrame.h, roomsBounds?.maxY ?? bgFrame.y + bgFrame.h),
      }
    : roomsBounds;
  if (!b) return <div style={{ padding: 14, fontSize: 12, color: "var(--text3)" }}>No rooms yet.</div>;
  const KW = 236, KH = 200, pad = 10;
  const bw = Math.max(1, b.maxX - b.minX), bh = Math.max(1, b.maxY - b.minY);
  const kscale = Math.min((KW - pad * 2) / bw, (KH - pad * 2) / bh);
  const ox = pad - b.minX * kscale, oy = pad - b.minY * kscale;
  const svgW = svgRef.current?.getBoundingClientRect().width || 800;
  const vx0 = -pan.x / pxPerFt, vy0 = -pan.y / pxPerFt;
  const vx1 = (svgW - pan.x) / pxPerFt, vy1 = (canvasHeight - pan.y) / pxPerFt;
  function jump(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    onJump((e.clientX - rect.left - ox) / kscale, (e.clientY - rect.top - oy) / kscale);
  }
  return (
    <div onClick={jump} title="Click to jump the main view here" style={{ cursor: "pointer", padding: 10 }}>
      <svg width={KW} height={KH} style={{ display: "block", border: "1px solid var(--cs-border)", borderRadius: 6, background: view?.canvasBg || "transparent" }}>
        {bgSrc && bgRect && (
          <image href={bgSrc} x={ox + bgRect.x * kscale} y={oy + bgRect.y * kscale}
            width={Math.max(1, bgRect.w * kscale)} height={Math.max(1, bgRect.h * kscale)}
            opacity={bgOpacity ?? 0.5} preserveAspectRatio="none" style={{ filter: view?.rasterFilter || "none" }} />
        )}
        {effRooms.map(r => {
          const R = rectOf(r);
          if (!R) return null;
          return <rect key={r.RoomID} x={ox + R.x * kscale} y={oy + R.y * kscale}
            width={Math.max(1, R.w * kscale)} height={Math.max(1, R.h * kscale)}
            fill="#94a3b8" fillOpacity={0.6} stroke="#fff" strokeWidth={0.5} />;
        })}
        <rect x={ox + vx0 * kscale} y={oy + vy0 * kscale}
          width={Math.max(0, (vx1 - vx0) * kscale)} height={Math.max(0, (vy1 - vy0) * kscale)}
          fill="none" stroke="#2563eb" strokeWidth={1.5} />
      </svg>
    </div>
  );
}

// PolygonJson stores ABSOLUTE world-feet points, independent of CanvasX/
// CanvasY — so dragging a polygon room has to move its points along with
// its bounding box, or the rendered shape stays frozen at its old spot
// while the room's "official" position moves elsewhere.
function translatePolygonJson(polygonJson, dx, dy) {
  if (!polygonJson || (!dx && !dy)) return polygonJson;
  try {
    const geo = JSON.parse(polygonJson);
    if (!Array.isArray(geo.points)) return polygonJson;
    return JSON.stringify({
      ...geo,
      points: geo.points.map(p => ({ x: Math.round((p.x + dx) * 10) / 10, y: Math.round((p.y + dy) * 10) / 10 })),
    });
  } catch { return polygonJson; }
}

// A condition's Quantity1/2/3, computed the same way the server-side rollup
// does (sp_GetConditionTakeoffRollup) so this list never disagrees with the
// cost-code rollup strip: Q1 is QuantityOverride if set, else the drawn
// run's stored length (count-style symbols have no length — each placed
// instance is 1 EA); Q2 = Q1 * height; Q3 = Q1 * height * (thickness/12).
// Delegates to the shared CalcType engine (src/config/conditionCalcTypes.js)
// so the canvas label matches sp_GetConditionTakeoffRollup/
// sp_GetConditionRoomRollup's server-side math exactly — see that module's
// header for why there are two copies (SQL can't share JS code) and the
// "keep both in sync" note.
function conditionQuantities(c) {
  return computeConditionQuantities(c);
}

// Identifies "the same condition type" for the sub-grouping below — the
// real catalog/type identity, not just its displayed Code (which two
// different rows could theoretically share). Mirrors the 4-way resolution
// sp_GetTakeoffConditionsDrawn already does server-side (Kind/ItemID/
// AssemblyID first — the modern catalog path — then TakeoffConditionTypeID,
// then the legacy global TakeoffConditionLibraryID). Every truly-unassigned
// row collapses into one "unassigned" bucket.
function conditionTypeKey(c) {
  if (c.Kind === "item" && c.ItemID) return `item:${c.ItemID}`;
  if (c.Kind === "assembly" && c.AssemblyID) return `assembly:${c.AssemblyID}`;
  if (c.TakeoffConditionTypeID) return `type:${c.TakeoffConditionTypeID}`;
  if (c.TakeoffConditionLibraryID) return `lib:${c.TakeoffConditionLibraryID}`;
  return "unassigned";
}

// The eye (show / hide this condition on the drawing; Alt-click shows only it) — the one control left on a dock row. Pin to
// the strip, details, delete and the rest are on the row's right-click menu (conditionMenuItems). View state only.
function TypeViewButtons({ typeKey, view }) {
  if (!view) return null;
  const hidden = view.hiddenTypes.has(typeKey);
  const btnStyle = { flexShrink: 0, background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 3px" };
  return (
    <>
      <button type="button" aria-pressed={!hidden} aria-label={hidden ? "Show this condition" : "Hide this condition"}
        onClick={(e) => (e.altKey ? view.onIsolateType(typeKey) : view.onToggleType(typeKey))}
        title={hidden ? "Hidden on the drawing — click to show (Alt-click: show only this one)" : "Hide on the drawing (Alt-click: show only this one)"}
        style={{ ...btnStyle, opacity: hidden ? 0.45 : 1 }}>{hidden ? "🚫" : "👁"}</button>
    </>
  );
}

// The right-click menu of a Conditions dock row (a drawn run / symbol) or of a type's combined header (c = null, typeKey set).
function conditionMenuItems({ c, typeKey, view, onSelect, onEdit, onDelete, isLocked }) {
  const items = [];
  const catKey = catalogKeyOfDrawn(typeKey);
  const hidden = view?.hiddenTypes?.has(typeKey);
  const pinned = !!catKey && !!view?.pins?.includes(catKey);
  if (c) {
    const geo = parseGeom(c);
    const point = c.Style === "count" ? geo : geo?.points?.[0];
    items.push({ heading: c.Name || c.Code || "Condition" });
    items.push({ label: "Select and zoom to it", disabled: !point, why: !point ? "Manual condition — nothing drawn" : undefined, onClick: () => onSelect(c, point) });
    items.push({ label: "Details…", disabled: isLocked, why: isLocked ? "Locked revision" : undefined, onClick: () => onEdit(c) });
  }
  if (view) {
    items.push({ label: "Keep drawing this condition", hint: "Double-click", disabled: isLocked || !catKey, why: isLocked ? "Locked revision" : !catKey ? "This type is not in the catalog" : undefined, onClick: () => view.onArmType?.(typeKey) });
    items.push({ label: pinned ? "Unpin from the strip" : "Pin to the strip", disabled: !catKey, why: !catKey ? "This type is not in the catalog" : "Keys 1–9 arm pinned conditions", onClick: () => view.onTogglePin(catKey) });
    items.push({ label: hidden ? "Show on the drawing" : "Hide on the drawing", onClick: () => view.onToggleType(typeKey) });
    items.push({ label: "Show only this condition", onClick: () => view.onIsolateType(typeKey) });
  }
  if (c) {
    items.push({ separator: true });
    items.push({ label: "Delete", danger: true, disabled: isLocked || !!c.AutoWallKey,
      why: isLocked ? "Locked revision" : c.AutoWallKey ? "Generated from the room layout — assign it a type with Details instead" : undefined, onClick: () => onDelete(c) });
  }
  return items;
}

// One drawn/placed condition's own row — the leaf every group ultimately
// renders, whether flat (a type with only one instance in this cost-code
// group) or nested under a same-type subgroup's combined summary (see
// ConditionsPanel below).
function ConditionRow({ c, selected, onSelect, onEdit, onDelete, isLocked, indent, view, onMenu, nameW }) {
  const units = useUnits();
  const geo = parseGeom(c);
  // A manually-added condition (no drawn geometry — see "+Add Condition")
  // has no point to jump to on the canvas; it's still fully editable/
  // deletable from here, just not clickable-to-select like a drawn one.
  const point = c.Style === "count" ? geo : geo?.points?.[0];
  // An auto-generated wall segment (sp_SyncAutoWallConditions, db/37_
  // condition_library.sql) is re-upserted from the room layout on every
  // rooms/edgeOverrides change — deleting it here just deletes the row for
  // the ~500ms until the debounced sync effect (RoomTakeoff.jsx) recreates
  // it unassigned, which reads to an estimator as "delete did nothing." It
  // can't be removed without removing the wall itself; only assigning it a
  // type (via Edit) is a real, persistent action.
  const isAutoWall = !!c.AutoWallKey;
  const qtyStr = conditionQuantities(c).map(x => qtyText(x, units)).join(" / ");
  return (
    <div onContextMenu={e => { e.preventDefault(); onMenu?.(e, conditionMenuItems({ c, typeKey: conditionTypeKey(c), view, onSelect, onEdit, onDelete, isLocked })); }} style={{
      display: "flex", alignItems: "center", gap: 4, padding: `3px 6px 3px ${indent}px`,
      background: selected ? "var(--tint-blue)" : "transparent",
      borderLeft: selected ? "2px solid var(--cs-blue)" : "2px solid transparent",
    }}>
      <button onClick={() => point && onSelect(c, point)} disabled={!point}
        onDoubleClick={() => view?.onArmType?.(conditionTypeKey(c))}
        title={isLocked ? undefined : "Double-click to keep drawing this condition"}
        style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          textAlign: "left", padding: "2px 0",
          background: "none", border: "none",
          cursor: point ? "pointer" : "default", fontSize: 12, color: "var(--text)",
        }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", minWidth: 0, ...(nameW ? { width: Math.max(60, nameW - indent - 16), flex: "none" } : {}) }}>
          {c.Color && (
            <span title={c.Color} style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: c.Color, border: "1px solid var(--cs-border)", flexShrink: 0 }} />
          )}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {c.Style === "count" ? "🚪" : c.Style === "area" ? "🟩" : "📏"} {c.Name || c.Code || "Unassigned — pick a type"}{c.Name && c.Code && c.Code !== c.Name && <span style={{ color: "var(--text3)", fontWeight: 400 }}> · {c.Code}</span>}{!point && <span style={{ color: "var(--text3)", fontStyle: "italic" }}> (manual)</span>}
          </span>
        </span>
        <span style={{ flexShrink: 0, fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>{qtyStr}</span>
      </button>
      <TypeViewButtons typeKey={conditionTypeKey(c)} view={view} />
      {isAutoWall && (
        <span title="Generated from the room layout — it can't be deleted (it'll just come back). Right-click ▸ Details to assign it a type."
          style={{ flexShrink: 0, fontSize: 12, color: "var(--text3)", padding: "2px 4px", cursor: "default" }}>🔒</span>
      )}
    </div>
  );
}

// A live list of everything drawn on this revision, grouped by cost code —
// the direct analog of OST's own Conditions tree. Solves two things at
// once: browsing what's been drawn without hunting on the canvas, and
// picking the right one out of several overlapping symbols (a real problem
// once a wall has multiple doors close together). Collapsible per group
// and shows all three quantities per row, like OST's own Conditions tab.
//
// Within a cost-code group, same-type instances (e.g. every auto-detected
// W-1 wall segment between two specific rooms, or every placed D-1 door)
// sub-group under one combined summary line instead of listing each
// physical instance flatly — per direct feedback that "for like conditions,
// the system should just add to the existing" rather than showing e.g. four
// separate "W-1" rows. Each instance is still a real, independently-
// editable geometry record (a specific wall segment or a specific door's
// position) — that can't be merged away without losing per-instance
// edit/reassign/delete — so the subgroup is a collapsed, expandable
// SUMMARY over those rows, not a data merge; expand it to reach an
// individual instance the same way as before.
function ConditionsPanel({ conditions, selectedCondId, onSelect, onEdit, onDelete, isLocked, view }) {
  const units = useUnits();
  const [closedGroups, setClosedGroups] = useState(new Set());
  const [openTypeGroups, setOpenTypeGroups] = useState(new Set());
  const [menu, setMenu] = useState(null); // right-click menu on a row / type header
  // Width of the name column, dragged from the header's divider; per viewer (localStorage), like the dock's own width.
  const [nameW, setNameW] = useState(() => { try { const v = +localStorage.getItem("cs.takeoff.condNameW"); return Number.isFinite(v) && v >= 90 && v <= 700 ? v : 190; } catch { return 190; } });
  const startNameResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX, w0 = nameW;
    let w = w0;
    const move = (ev) => { w = Math.max(90, Math.min(700, w0 + ev.clientX - x0)); setNameW(w); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); try { localStorage.setItem("cs.takeoff.condNameW", String(Math.round(w))); } catch { /* ignore */ } };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const openMenu = (e, items) => setMenu({ x: e.clientX, y: e.clientY, items });
  const toggleGroup = key => setClosedGroups(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const toggleTypeGroup = key => setOpenTypeGroups(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  if (conditions.length === 0) return <div style={{ padding: 14, fontSize: 12, color: "var(--text3)" }}>Nothing drawn yet.</div>;
  const map = new Map();
  for (const c of conditions) {
    const key = c.CostCodeID ?? "none";
    if (!map.has(key)) map.set(key, { csi: c.CSICode, desc: c.CostCodeDescription, rows: [] });
    map.get(key).rows.push(c);
  }
  const groups = [...map.entries()].sort((a, b) => (a[1].csi || "").localeCompare(b[1].csi || ""));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", padding: "3px 6px 3px 10px", borderBottom: "1px solid var(--cs-border)" }}>
        <span style={{ width: nameW - 10, flex: "none", position: "relative" }}>Condition
          <span onPointerDown={startNameResize} onDoubleClick={() => { setNameW(190); try { localStorage.removeItem("cs.takeoff.condNameW"); } catch { /* ignore */ } }}
            title="Drag to resize the name column (double-click to reset)" style={{ position: "absolute", right: -3, top: -3, bottom: -3, width: 7, cursor: "col-resize", borderRight: "2px solid var(--cs-border)" }} />
        </span>
        <span style={{ paddingLeft: 8 }}>Quantity</span>
      </div>
      {groups.map(([key, g], i) => {
        const open = !closedGroups.has(key);
        const typeMap = new Map();
        for (const c of g.rows) {
          const tk = conditionTypeKey(c);
          if (!typeMap.has(tk)) typeMap.set(tk, []);
          typeMap.get(tk).push(c);
        }
        return (
          <div key={key} style={{ borderTop: i > 0 ? "1px solid var(--cs-border)" : "none" }}>
            <button onClick={() => toggleGroup(key)} style={{
              display: "flex", alignItems: "center", gap: 4, width: "100%", textAlign: "left",
              background: "none", border: "none", cursor: "pointer",
              padding: "6px 10px 2px", fontSize: 10, fontWeight: 700, color: "var(--text3)",
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              <span style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform 0.12s" }}>▸</span>
              {g.csi ? `${g.csi} — ${g.desc || ""}` : "Uncoded"}
            </button>
            {open && [...typeMap.entries()].map(([tk, rows]) => {
              if (rows.length === 1) {
                const c = rows[0];
                return (
                  <ConditionRow key={c.TakeoffConditionDrawnID} c={c} indent={10}
                    selected={selectedCondId === c.TakeoffConditionDrawnID}
                    onSelect={onSelect} onEdit={onEdit} onDelete={onDelete} isLocked={isLocked} view={view} onMenu={openMenu} nameW={nameW} />
                );
              }
              const typeGroupKey = `${key}::${tk}`;
              const expanded = openTypeGroups.has(typeGroupKey);
              const totals = new Map();
              for (const c of rows) for (const { q, u } of conditionQuantities(c)) totals.set(u, (totals.get(u) || 0) + q);
              const totalStr = [...totals.entries()].map(([u, q]) => qtyText({ q, u }, units)).join(" / ");
              const sample = rows[0];
              return (
                <div key={tk}>
                  <div style={{ display: "flex", alignItems: "center" }} onContextMenu={e => { e.preventDefault(); openMenu(e, [{ heading: sample.Name || sample.Code || "Condition type" }, ...conditionMenuItems({ c: null, typeKey: tk, view })]); }}>
                  <button onClick={() => toggleTypeGroup(typeGroupKey)} onDoubleClick={() => view?.onArmType?.(tk)}
                    title={isLocked ? undefined : "Double-click to keep drawing this condition"} style={{
                    display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left",
                    background: "none", border: "none", cursor: "pointer",
                    padding: "3px 6px 3px 10px", fontSize: 12, color: "var(--text)",
                  }}>
                    <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.12s", fontSize: 10, color: "var(--text3)", flexShrink: 0 }}>▸</span>
                    {sample.Color && (
                      <span title={sample.Color} style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: sample.Color, border: "1px solid var(--cs-border)", flexShrink: 0 }} />
                    )}
                    <span style={{ ...(nameW ? { width: Math.max(60, nameW - 28), flex: "none" } : { flex: 1 }), minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {sample.Style === "count" ? "🚪" : sample.Style === "area" ? "🟩" : "📏"} {sample.Name || sample.Code || "Unassigned"}{sample.Name && sample.Code && sample.Code !== sample.Name && <span style={{ color: "var(--text3)", fontWeight: 400 }}> · {sample.Code}</span>}
                      <span style={{ color: "var(--text3)", fontWeight: 400 }}> · {rows.length} runs</span>
                    </span>
                    <span style={{ flexShrink: 0, fontSize: 11, color: "var(--text3)", fontFamily: "monospace" }}>{totalStr}</span>
                  </button>
                    <TypeViewButtons typeKey={tk} view={view} />
                  </div>
                  {expanded && rows.map(c => (
                    <ConditionRow key={c.TakeoffConditionDrawnID} c={c} indent={24}
                      selected={selectedCondId === c.TakeoffConditionDrawnID}
                      onSelect={onSelect} onEdit={onEdit} onDelete={onDelete} isLocked={isLocked} view={view} onMenu={openMenu} nameW={nameW} />
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      <ContextMenu menu={menu} onClose={() => setMenu(null)} />
    </div>
  );
}

// Room-list navigation, the same idea as ConditionsPanel but for rooms —
// click to select + jump instead of scanning the drawing for a name. A
// polygon room (PolygonJson) shows "Irregular" only when that polygon is
// ACTUALLY non-rectangular (isPolygonRectangular) — AI auto-takeoff traces
// a room's real boundary even when it's a plain rectangle, so "has a
// polygon" alone isn't the right test. SF always shows either way, real
// polygon area when there is one.
function RoomsPanel({ rooms, selectedIds, onSelect, onEdit, onDelete, onAdd, onSetLocked, isLocked, roomTemplates }) {
  const units = useUnits();
  const [menu, setMenu] = useState(null); // right-click menu: room details, lock, delete, add
  const why = isLocked ? "Locked revision" : undefined;
  const openMenu = (e, r) => {
    e.preventDefault(); e.stopPropagation();
    const items = [];
    if (r) {
      items.push({ heading: r.RoomName || "Room" });
      items.push({ label: "Edit room…", hint: "double-click", disabled: isLocked, why, onClick: () => onEdit(r) });
      if (onSetLocked) items.push({ label: r.PositionLocked ? "Unlock position" : "Lock position", disabled: isLocked, why, onClick: () => onSetLocked([r], !r.PositionLocked) });
      items.push({ label: "Delete room", danger: true, disabled: isLocked, why, onClick: () => onDelete(r) });
      items.push({ separator: true });
    }
    items.push({ label: "Add room", disabled: isLocked || !onAdd, why, onClick: () => onAdd?.() });
    setMenu({ x: e.clientX, y: e.clientY, items });
  };
  if (rooms.length === 0) return <div onContextMenu={e => openMenu(e, null)} style={{ padding: 14, fontSize: 12, color: "var(--text3)" }}>No rooms yet.<ContextMenu menu={menu} onClose={() => setMenu(null)} /></div>;
  return (
    <div onContextMenu={e => openMenu(e, null)}>
      {rooms.map(r => {
        const R = rectOf(r);
        const poly = polygonOf(r);
        const isRect = !poly || isPolygonRectangular(poly);
        const areaFt2 = poly ? polygonArea(poly) : (R ? R.w * R.h : null);
        const dimsStr = !isRect ? "Irregular" : (R ? (isMetric(units) ? `${fmt(r.LengthFt * FT_TO_M, 1)}×${fmt(r.WidthFt * FT_TO_M, 1)} m` : `${fmt(r.LengthFt)}×${fmt(r.WidthFt)}`) : "—");
        const sel = selectedIds.has(r.RoomID);
        const roomColor = roomTemplates?.find(t => t.RoomTemplateID === r.RoomTemplateID)?.Color || "#94a3b8";
        return (
          <div key={r.RoomID} onContextMenu={e => openMenu(e, r)} onDoubleClick={() => !isLocked && onEdit(r)}
            title="Double-click to edit · right-click for more"
            style={{
              display: "flex", alignItems: "center", gap: 4, padding: "3px 6px 3px 10px",
              background: sel ? "var(--tint-blue)" : "transparent",
              borderTop: "1px solid var(--cs-border)", borderLeft: sel ? "2px solid var(--cs-blue)" : "2px solid transparent",
            }}>
            <button onClick={() => R && onSelect(r, R)} disabled={!R}
              style={{
                flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start",
                textAlign: "left", padding: "3px 0",
                background: "none", border: "none",
                cursor: R ? "pointer" : "default", fontSize: 12, color: "var(--text)",
              }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", width: "100%" }}>
                <span title={roomColor} style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: roomColor, border: "1px solid var(--cs-border)", flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.RoomName}</span>
              </span>
              <span style={{ color: "var(--text3)", fontSize: 10.5, fontFamily: "monospace" }}>
                {dimsStr}{areaFt2 != null ? ` · ${isMetric(units) ? fmtArea(areaFt2, units) : `${fmt(areaFt2)} SF`}` : ""}
              </span>
            </button>
          </div>
        );
      })}
      <ContextMenu menu={menu} onClose={() => setMenu(null)} />
    </div>
  );
}

// A live list of every detected joint whose classification confidence isn't
// high — conflict/needs_review/suggested (auto_apply and manual_override
// need no attention) — across the WHOLE floor plan, not just whatever's
// currently on screen. Resolving a wall from here is one click, no need to
// find and click its (sometimes tiny, easy-to-miss) joint line on the
// canvas first. Added 2026-09-10 per direct feedback that precision
// drag-to-classify is fundamentally fragile for an estimator who isn't a
// CAD user — this flips the workflow from "infer construction from exact
// geometry" to "the estimator confirms anything the system isn't
// confident about," reusing the same confidence tiers the Edge Halo
// already computes rather than a new judgment.
// The TakeoffConditionsDrawn row(s) sp_SyncAutoWallConditions keeps for one
// Edge Run — one row for a shared wall, one per face for a cavity. Same key
// format as computeAutoWallSegments (takeoffGeometry.js).
function wallRowsForEdge(edge, condByAutoKey) {
  const a = edge.a.RoomID, b = edge.b.RoomID, suffix = `:${edge.runKey}`;
  const keys = edge.type === "shared" ? [`shared:${Math.min(a, b)}:${Math.max(a, b)}${suffix}`]
    : edge.type === "cavity" ? [`cavity:${a}:${b}${suffix}`, `cavity:${b}:${a}${suffix}`] : [];
  return keys.map(k => condByAutoKey.get(k)).filter(Boolean);
}
// TypeKey of the construction assigned to a wall row's own (Face A) side, or
// null when it's still blank — same encoding as ConditionDetailFlyout.
function rowTypeKey(row) {
  if (row.Kind === "item") return `item:${row.ItemID}`;
  if (row.Kind === "assembly") return `asm:${row.AssemblyID}`;
  return row.TakeoffConditionLibraryID != null ? `ct:${row.TakeoffConditionLibraryID}` : null;
}
const ruleTypeKey = r => (r.kind === "item" ? `item:${r.itemId}` : `asm:${r.assemblyId}`);

// The exception workflow (audit §28): for each listed joint, besides the
// geometry buttons (shared / cavity / ignore / auto) the estimator gets a
// "What is this boundary?" chooser — the Construction Standard rules that
// match this pair and geometry first, in priority order (the engine's own
// best guesses, including ones it declined to auto-apply because two
// tied), then the whole linear catalog as a fallback. Picking one writes
// the construction onto the wall's condition row(s) exactly as the
// Condition Detail Flyout would, without finding the joint on the canvas.
function ReviewWallsPanel({ items, haloStyle, onJump, onSetEdgeType, isLocked, conditionTypes = [], layers = [], condByAutoKey, rulesByPair, onAssignConstruction, wallLearning = null }) {
  if (items.length === 0) {
    return (
      <div style={{ padding: 14, fontSize: 12, color: "var(--text3)" }}>
        Nothing needs review — every wall is confidently classified, has a construction, or is already decided.
      </div>
    );
  }
  const linearTypes = wallSystemTypes(conditionTypes, layers);   // walls only (feedback #25) — every linear type until a wall layer is assigned
  const nameOf = typeKey => conditionTypes.find(t => t.TypeKey === typeKey)?.Name || typeKey;
  return (
    <div>
      {items.map(({ edge, tier, label }, i) => {
        const mid = { x: (edge.seg.x1 + edge.seg.x2) / 2, y: (edge.seg.y1 + edge.seg.y2) / 2 };
        const color = haloStyle[tier]?.stroke || "#9ca3af";
        const rows = condByAutoKey && (edge.type === "shared" || edge.type === "cavity") ? wallRowsForEdge(edge, condByAutoKey) : [];
        const current = rows.length ? rowTypeKey(rows[0]) : null;
        const tA = edge.a.RoomTemplateID, tB = edge.b.RoomTemplateID;
        const candidates = [];
        if (rulesByPair && tA != null && tB != null) {
          for (const r of matchingConstructionRules(edge, rulesByPair.get(pairKey(tA, tB)))) {
            const typeKey = ruleTypeKey(r);
            if (!candidates.some(c => c.typeKey === typeKey)) candidates.push({ typeKey, priority: r.priority ?? 100 });
          }
        }
        // The two rooms' own wall systems (room substitution else template
        // default) — what the engine would have filled had they agreed; when
        // they differ (panel meets gypsum) this is exactly the choice the
        // estimator is being asked to make.
        for (const room of [edge.a, edge.b]) {
          const w = wallSystemForPosition(room._wallSystem, edge.type);   // the system for THIS kind of joint
          if (!w) continue;
          const typeKey = ruleTypeKey(w);
          if (!candidates.some(c => c.typeKey === typeKey)) candidates.push({ typeKey, priority: null, roomName: room.RoomName, source: w.source });
        }
        // What people have chosen at walls like this one before (wall learning — only human-decided walls feed it),
        // most-supported first; shown whatever the evidence bar, with the counts so the estimator can judge.
        if (wallLearning?.model) {
          for (const l of rankLearnedCandidates({ rel: edge.type, gapFt: edge.gapFt, tplA: tA, tplB: tB }, wallLearning.model, wallLearning.tunables)) {
            const existing = candidates.find(c => c.typeKey === l.typeKey);
            if (existing) existing.learned = l;
            else candidates.push({ typeKey: l.typeKey, priority: null, learned: l });
          }
        }
        const showChooser = !isLocked && onAssignConstruction && rows.length > 0;
        return (
          <div key={`${edge.a.RoomID}-${edge.b.RoomID}-${edge.runKey}`}
            style={{ padding: "6px 10px", borderTop: i > 0 ? "1px solid var(--cs-border)" : "none" }}>
            <button onClick={() => onJump(mid.x, mid.y)}
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 4 }}>
              <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 3, color: "#fff", background: color, whiteSpace: "nowrap" }}>{label}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {edge.a.RoomName} ↔ {edge.b.RoomName}
              </span>
            </button>
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginBottom: 4, fontFamily: "monospace" }}>
              {fmt(edge.overlapFt)}′ overlap · {fmt(edge.gapFt, 1)}′ gap · currently {edge.type}
            </div>
            {!isLocked && (
              <div style={{ display: "flex", gap: 4 }}>
                {REVIEW_ACTION_BUTTONS.map(({ type, label }) => (
                  <button key={type} onClick={() => onSetEdgeType(edge, type)}
                    style={{
                      flex: 1, fontSize: 10.5, fontWeight: 600, padding: "3px 0", cursor: "pointer",
                      borderRadius: 4, border: `1px solid ${edge.type === type ? "var(--cs-blue)" : "var(--cs-border)"}`,
                      background: edge.type === type ? "var(--tint-blue)" : "var(--surface)",
                      color: edge.type === type ? "var(--cs-blue)" : "var(--text2)",
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            )}
            {showChooser && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
                  What is this boundary?{edge.type === "cavity" ? " (both faces)" : ""}
                </div>
                {candidates.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
                    {candidates.map((c, k) => {
                      const active = current === c.typeKey;
                      return (
                        <button key={c.typeKey} onClick={() => onAssignConstruction(rows, c.typeKey)}
                          title={[
                            c.priority != null ? `Construction Standard rule match · priority ${c.priority}${k === 0 ? " · engine's top pick" : ""}` : (c.roomName ? `${c.roomName}'s wall system (${c.source === "room" ? "room substitution" : "room template default"})` : null),
                            c.learned ? `Chosen at ${c.learned.samples} of ${c.learned.total} similar walls (${Math.round(c.learned.share * 100)}%) in past estimates${c.learned.basis === "template" ? " — by room type, not this exact pair" : ""}` : null,
                          ].filter(Boolean).join(" · ")}
                          style={{
                            fontSize: 10.5, fontWeight: 600, padding: "3px 8px", cursor: "pointer", borderRadius: 10,
                            border: `1px solid ${active ? "#16a34a" : "var(--cs-border)"}`,
                            background: active ? "var(--tint-green)" : "var(--surface)", color: active ? "#166534" : "var(--text)",
                          }}>
                          {active ? "✓ " : ""}{nameOf(c.typeKey)}{c.learned ? ` · ${Math.round(c.learned.share * 100)}% (${c.learned.samples})` : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
                <select value={current ?? ""} onChange={e => e.target.value && onAssignConstruction(rows, e.target.value)}
                  style={{ width: "100%", fontSize: 11, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "var(--surface)", color: "var(--text)" }}>
                  <option value="">{current ? "— change to another construction —" : (candidates.length ? "— or pick any construction —" : "— pick a construction —")}</option>
                  {linearTypes.map(t => <option key={t.TypeKey} value={t.TypeKey}>{t.Name}{t.CSICode ? ` (${t.CSICode})` : ""}</option>)}
                </select>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
const REVIEW_ACTION_BUTTONS = [
  { type: "shared", label: "Shared" },
  { type: "cavity", label: "Cavity" },
  { type: "ignore", label: "Ignore" },
];

// A3 — sheet thumbnails: a raster sheet IS its own thumbnail (the browser
// scales it); a PDF sheet's page is rendered once per (sheet, page, image)
// and kept for the session so switching / reordering never re-rasterizes.
// The Files and Drawings strip has a FIXED size: two cards wide, one row tall, extra drawings scroll vertically.
// (Fixed on purpose — the tile must not grow or shrink as drawings are added, replaced or marked.)
const SHEET_STRIP_COLS = 2, SHEET_CARD_W = 250, SHEET_CARD_H = 96, SHEET_CARD_GAP = 6;
const sheetThumbCache = new Map();
function SheetThumb({ sheet, width = 92, height = 64 }) {
  const key = sheetThumbKey(sheet);
  const [url, setUrl] = useState(() => sheetThumbCache.get(key) || null);
  useEffect(() => {
    let cancelled = false;
    const cached = sheetThumbCache.get(key);
    if (cached) { setUrl(cached); return undefined; }
    if (!sheet?.BackgroundImage) { setUrl(null); return undefined; }
    if (!sheet.ImageIsPdf) {
      rotateImageUrl(sheet.BackgroundImage, sheet.ImageRotation)
        .then(u => { sheetThumbCache.set(key, u); if (!cancelled) setUrl(u); })
        .catch(() => { if (!cancelled) setUrl(null); });
      return () => { cancelled = true; };
    }
    setUrl(null);
    loadPdf(sheet.BackgroundImage, { page: sheet.ImagePage || 1, rotation: sheet.ImageRotation })
      .then(({ doc }) => renderPageThumbnail(doc, sheet.ImagePage || 1, 220, sheet.ImageRotation))
      .then(u => { if (!cancelled) { sheetThumbCache.set(key, u); setUrl(u); } })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ width, height, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 4, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {url ? <img src={url} alt="" draggable={false} style={{ maxWidth: "100%", maxHeight: "100%", display: "block" }} /> : <span style={{ fontSize: 10, color: "var(--text3)" }}>{sheet?.BackgroundImage ? "…" : "no drawing"}</span>}
    </div>
  );
}

// A3 — side-by-side: a READ-ONLY viewer of another sheet beside the working
// canvas (a plan next to its detail sheet, or a prior revision's drawing
// loaded as a sheet). Wheel zooms, drag pans; no rooms or conditions are drawn
// on it and nothing here writes. Swap makes it the working sheet.
function SheetComparePane({ sheet, height, onSwap, onClose, view }) {
  const key = sheetThumbKey(sheet);
  const [url, setUrl] = useState(null);
  const [z, setZ] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    setUrl(null); setZ({ k: 1, x: 0, y: 0 });
    if (!sheet?.BackgroundImage) return undefined;
    if (!sheet.ImageIsPdf) {
      rotateImageUrl(sheet.BackgroundImage, sheet.ImageRotation).then(u => { if (!cancelled) setUrl(u); }).catch(() => { if (!cancelled) setUrl(null); });
      return () => { cancelled = true; };
    }
    loadPdf(sheet.BackgroundImage, { page: sheet.ImagePage || 1, rotation: sheet.ImageRotation })
      .then(({ doc }) => renderPageFull(doc, sheet.ImagePage || 1, 2400, sheet.ImageRotation))
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Native wheel listener so we can preventDefault (React onWheel is passive) — same as the main canvas.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const box = el.getBoundingClientRect();
      const mx = e.clientX - box.left, my = e.clientY - box.top;
      const f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setZ(p => { const k = Math.max(0.2, Math.min(12, p.k * f)); const r = k / p.k; return { k, x: mx - (mx - p.x) * r, y: my - (my - p.y) * r }; });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  const cal = sheetCalibration(sheet);
  return (
    <div style={{ flex: "0 0 42%", minWidth: 0, height, display: "flex", flexDirection: "column", border: "1px solid #a855f7", borderRadius: 8, overflow: "hidden", background: view?.canvasBg || "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", background: "var(--surface)", fontSize: 11.5 }}>
        <span style={{ fontWeight: 700, color: "#7e22ce", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${sheetTitle(sheet)} — read-only reference beside the working sheet`}>⇆ {sheetTitle(sheet)}</span>
        <span style={{ color: cal.status === "none" ? "#b45309" : "var(--text3)" }}>{cal.label}</span>
        {sheet?.ImageIsPdf && sheet?.ImagePage > 1 && <span style={{ color: "var(--text3)" }}>p.{sheet.ImagePage}</span>}
        <button onClick={() => setZ({ k: 1, x: 0, y: 0 })} title="Fit" style={iconBtn}>⤢</button>
        <button onClick={onSwap} title="Swap — work on this sheet and keep the current one beside it" style={iconBtn}>⇄</button>
        <button onClick={onClose} title="Close side-by-side" style={iconBtn}>✕</button>
      </div>
      <div ref={boxRef} style={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative", cursor: "grab", touchAction: "none" }}
        onPointerDown={e => { drag.current = { x: e.clientX, y: e.clientY, ox: z.x, oy: z.y }; e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={e => { const d = drag.current; if (!d) return; setZ(p => ({ ...p, x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) })); }}
        onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
        {url
          ? <img src={url} alt="" draggable={false} style={{ position: "absolute", left: 0, top: 0, maxWidth: "100%", maxHeight: "100%", transformOrigin: "0 0", transform: `translate(${z.x}px, ${z.y}px) scale(${z.k})`, userSelect: "none", filter: view?.rasterFilter || "none" }} />
          : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text3)", fontSize: 12 }}>{sheet?.BackgroundImage ? "Rendering…" : "This sheet has no drawing loaded."}</div>}
      </div>
    </div>
  );
}

// Track J — the Review queue's tiny per-item action buttons.
const iconBtn = { border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 4, fontSize: 11, lineHeight: 1, padding: "2px 4px", cursor: "pointer" };

export default function TakeoffCanvas({
  rooms, zones, overrides, chaseAttrs = {}, isLocked,
  tunables = DEFAULT_TUNABLES, // admin-managed room-boundary/snap constants (Setup > Estimate Engine > Engine Variables) — see takeoffGeometry.js's own DEFAULT_TUNABLES comment
  rulesByPair = new Map(),     // Construction Standard rules per room-template pair (RoomTakeoff.jsx) — the Review Walls chooser's suggested constructions
  wallLearning = null,         // { model, tunables, predictWall } — what past human decisions say about a wall (utils/learning/wallLearning.js)
  wallSegments, // {hSegs,vSegs} in world feet for the active sheet, or null/undefined — see snapPosition()
  sheets = [], activeSheetId, onSelectSheet, // named drawing sheets (multiple per revision)
  onCreateOneOff,           // (draft) => Promise<TypeKey | null> — create a one-off condition for this estimate (db/87)
  onSaveSheetLegend,        // (sheetId, legendJson) — save the drawing's legend settings (db/38 LegendJson; allowed on a locked revision)
  rawConditionTypes = [],   // the catalog types WITHOUT this estimate's overrides (the Add Condition window shows them as the defaults)
  onSaveTypeOverride,       // (type, patch | null) — save / reset THIS estimate's settings for one catalog type (db/37 TakeoffConditionOverrides)
  sheetTakeoffCounts = {},  // { [SheetID]: { rooms, conditions } } — what is drawn on every drawing (the canvas itself only gets the working one's rows)
  onSaveSheetMeta,          // (sheetId, { sheet_number?, discipline? }) — A3 sheet metadata (db/38 SheetNumber / Discipline)
  onAddSheet,       // ({ sheetName, backgroundImage, imageIsPdf }) -> creates a new sheet, always
  onDuplicateSheet, // (sheetId) -> copies a drawing (file, scale, position — NO rooms / conditions) as a new sheet and switches to it
  onLoadSheetVersions, onRestoreSheetVersion, // a drawing's file history (replaced files kept) and restoring one
  onRenameSheet,    // (sheetId, name)
  onReorderSheet,   // (sheetId, direction) -> -1 moves up/earlier, +1 moves down/later
  onDeleteSheet,    // (sheetId)
  onPrefillSheet,   // () -> AI room prefill against the active sheet (PDF sheets only)
  analyzing = false, // an AI prefill job is in flight
  onMoveRoom,       // (room, xFt, yFt, rot) -> persists
  onMoveRooms,      // ([{ room, x, y, rot }]) -> persists a whole group drag as ONE undo step
  onAcceptMachine,  // ({ rooms, conditions, elements }) -> accepts machine output (review gate; sp_AcceptMachineItems)
  onSaveCanvas,     // (partialPayload) -> persists the ACTIVE sheet
  onCycleEdge,      // (edge) -> cycles the pair override
  onSetEdgeType,    // (edge, 'shared'|'cavity'|'ignore'|null) -> sets the pair override directly (Review Walls dock panel)
  onSaveChaseAttributes, // (edge, { chaseType, clearWidthFt, isAccessible, notes }) -> persists chase detail on a cavity edge run
  onAddRoom,        // ({ name, lengthFt, widthFt, x, y, polygon }) -> creates + places a room in one step (legacy draw-then-name flow)
  onCreateRoom,     // ({ name }) -> Promise<roomId>: creates an UNPLACED room for the "+ Add Room" set-up-first flow
  onPlaceDrawnRoom, // (room, { x, y, lengthFt, widthFt, polygon }) -> places an existing (unplaced) room from a finished drawing
  onResizeRoom,     // (room, lengthFt, widthFt) -> persists a canvas resize
  onDeleteRooms,    // ([room, ...]) -> removes one or more rooms
  onEditRoomPolygon, // (room, points) -> persists an edited/added polygon vertex
  scopeItems = [],  // placeable register items [{ ScopeItemID, Activity }]
  scopeApps = [],   // scope applications (drawn runs render here)
  onDeleteScopeApp, // (app)
  conditionTypes = [],    // Condition Library types [{ TakeoffConditionLibraryID, Name, Style, Unit, DefaultHeightFt, Color, CSICode, ... }]
  takeoffConditions: savedTakeoffConditions = [], // ALL drawn rows incl. back-outs (MeasureRole='deduct', D-2) — split just below
  onCreateTakeoffCondition, // ({ conditionTypeId, points, lengthFt })
  onCreateAreaCondition,    // ({ conditionTypeId, points, areaFt2, perimeterFt, roomId })
  onCreateBackOut,          // ({ roomElementId, roomId, points, areaFt2 }) — a manual back-out deducted from one Room Element's area (Takeoff Canvas 2.0 D-2)
  undoInfo = null,          // { canUndo, canRedo, undoLabel, redoLabel } — RoomTakeoff's session undo stack (A2)
  onUndo, onRedo,           // () — reverse / re-apply the last committed edit through the same procs
  reviewStates = [],        // Track J — sp_GetTakeoffReviewStates rows (db/80): acknowledged / waived / assigned state per computed review item
  onSaveReviewState,        // (payload) — one sp_SaveTakeoffReviewState upsert (revision id added by RoomTakeoff)
  currentUserEmail = null,  // who acknowledges / waives / assigns (recorded as UpdatedBy; default assignee)
  onDeleteTakeoffCondition, // (condition)
  onDeleteTakeoffConditions, // (conditions[]) — several at once, ONE undo step
  onSetRoomLocked,           // (rooms, locked) — lock / unlock rooms' position (db/21 PositionLocked)
  onTransformConditions,     // ({ rows, op, spin }) → notice — rotate / spin / flip drawn conditions, ONE undo step (utils/rotateConditions.js)
  onDuplicateConditions,     // ({ items: [{ row, dx, dy, geometryJson }], sheetId }) — paste / duplicate, ONE undo step
  onSaveTakeoffCondition,   // (condition, patch) — attribute-only edit, no geometry change
  onCreateManualCondition,  // (draft) — a condition with no drawn geometry ("+Add Condition")
  saving = false,         // a save is in flight (status bar "saving…")
  revisionId,             // the revision's id (count by example reads a drawing server-side)
  sweepConditions = [],   // ALL drawn rows on every drawing (count by example does not place over a symbol already there)
  snapshotsSlot,          // optional node: the revision's Snapshots menu, shown in Find & review
  onCountByExample,       // ({ conditionTypeId, placements: [{ sheetId, x, y, angleDeg, flip }], widthFt }) -> { placed } — one undo step
  onPlaceSymbol,          // ({ conditionTypeId, x, y, angleDeg, wallNormalDeg, widthFt, roomId }) — door/window (Style='count')
  onFlipSymbol,           // (condition) — toggles a placed symbol's swing side
  onMoveSymbol,           // (condition, snap) — drags a placed symbol to a new wall position
  criteria = {}, scopeByDiscipline = {}, scopeExclusions, // discipline-criteria state (Room Detail Flyout)
  onSaveRoom, onSaveCriterion, onToggleRoomScope, // Room Detail Flyout handlers
  roomTemplates, onChangeRoomTemplate, // Room Detail Flyout — Room Templates picker (db/55_room_templates.sql)
  systemsCtx, // Room Detail Flyout — HEPA/lighting engineering block (src/utils/roomSystemsEngine.js: { bodAttrs, settings, tunables })
  onResetAutoLayout, // Room Detail Flyout — "Regenerate layout" for one room+system's generated ceiling symbols (autoLayoutEngine.js)
  roomElements = [],  // Room Build-Up (db/70): this revision's RoomElements — the flyout's per-discipline element cards
  catalog = { items: [], assemblies: [] }, // Estimate Engine catalog for the flyout's "+ Add Element" picker
  onSaveRoomElement, onDeleteRoomElement,  // (payload) / (roomElementId) — see RoomTakeoff.jsx
  layers = [],      // active TakeoffLayers rows (db/66) — the Layers dock catalog; [] falls back to LAYER_DEFS
  haloColors, onSaveHaloColors, // per-revision Edge Halo tier color overrides (db/61_takeoff_halo_settings.sql)
  roomScopeItems = [], onSaveRoomScopeItem, onDeleteRoomScopeItem, // Room Detail Flyout architectural scope list
  costCodes = [], // CostCodes catalog — groups the Room Detail Flyout's scope list, same as Condition Library
  // Phase G canvas routing (2026-09-13): the revision's conceptual distribution
  // route segments (utility-owned + HVAC-owned UtilityRouteSegments) prepared
  // by RoomTakeoff — [{ segmentId, label, systemLabel, color, geometry, calculatedLF, overrideLF, effectiveLF }].
  // Tracing one on the canvas stores its polyline as GeometryJson → calculated
  // LF + elbow count in pipeSizing.evaluateUtilityRoute; design flow is never
  // read from geometry.
  routes = [], onDrawRouteSegment, onClearRouteGeometry,
}) {
  // Back-outs (Takeoff Canvas 2.0 Track D-2): deduct polygons drawn against
  // ONE Room Element (TakeoffConditionsDrawn.MeasureRole='deduct' +
  // AppliesToRoomElementID). They are SCOPE geometry — "this finish is not
  // applied here" — so they never join the conditions list, the dock, the
  // rollups or the wall / symbol logic: every `takeoffConditions` read below
  // sees only additive rows. The Room Detail Flyout gets the full list so
  // resolveElementQuantity can compose Gross − back-outs = Net.
  // Double-click edit mode for a drawn linear / area condition: drag it to move it, drag its corner handle to make it larger
  // or smaller. `condReshape` is the drag in flight ({ kind: 'move' | 'scale', id, start, pivot, startDist, op }); while it
  // runs the row is shown at its live geometry (so labels and quantities follow), and the release saves it once.
  const [editCondId, setEditCondId] = useState(null);
  const [condReshape, setCondReshape] = useState(null);
  const allTakeoffConditions = useMemo(() => {
    if (!condReshape?.op) return savedTakeoffConditions;
    return savedTakeoffConditions.map(c => c.TakeoffConditionDrawnID === condReshape.id
      ? { ...c, GeometryJson: transformGeometryJson(c.GeometryJson, condReshape.op, condReshape.pivot) } : c);
  }, [savedTakeoffConditions, condReshape]);
  const backOuts = useMemo(() => allTakeoffConditions.filter(isBackOut), [allTakeoffConditions]);
  const takeoffConditions = useMemo(() => allTakeoffConditions.filter(c => !isBackOut(c)), [allTakeoffConditions]);
  const [backOutMode, setBackOutMode] = useState(false);
  const [backOutTarget, setBackOutTarget] = useState(null); // { roomElementId, roomId, label }
  const [backOutPickerOpen, setBackOutPickerOpen] = useState(false); // command-bar Back-Out: the element picker (which element does the deduction apply to)
  const [backOutPts, setBackOutPts] = useState([]);         // in-progress polygon (world ft)
  const [backOutCursor, setBackOutCursor] = useState(null);
  const svgRef = useRef(null);
  const fileRef = useRef(null);
  const addSheetRef = useRef(null);
  const toolbarWrapRef = useRef(null);
  const [canvasHeight, setCanvasHeight] = useState(620); // filled to the viewport once mounted (see effect below)
  const wrapRef = useRef(null);
  const [pxPerFt, setPxPerFt] = useState(5);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [drag, setDrag] = useState(null);        // { roomId, dx, dy, anchors } (world ft offsets)
  const [dragSnapTier, setDragSnapTier] = useState(null); // 'wall' | 'room' | 'grid' | 'free' (Alt held) — live cursor label while dragging
  const [vertexDragFree, setVertexDragFree] = useState(false); // Alt held while dragging a single polygon vertex — see snapVertex's disableSnap
  const [panning, setPanning] = useState(null);  // { startX, startY, panX, panY } (px)
  const [livePos, setLivePos] = useState({});    // roomId -> {x,y} during drag
  const [liveSize, setLiveSize] = useState({});  // roomId -> {w,h} (rect ft) during resize
  const [resize, setResize] = useState(null);    // { roomId, edge, x, y, startW, startH }
  const [liveVertices, setLiveVertices] = useState({}); // roomId -> [{x,y}...] during a polygon vertex drag
  const [vertexDrag, setVertexDrag] = useState(null);   // { roomId, index } — which point in liveVertices[roomId] is being dragged
  // Track I: rows lit up by "Highlight geometry" on an element card — transient.
  const [highlightCondIds, setHighlightCondIds] = useState(() => new Set());
  useEffect(() => { if (!highlightCondIds.size) return; const t = setTimeout(() => setHighlightCondIds(new Set()), 8000); return () => clearTimeout(t); }, [highlightCondIds]);
  // Track C: why an outline was refused (self-crossing, sliver …) — transient, never stored.
  const [editNotice, setEditNotice] = useState(null);
  useEffect(() => { if (!editNotice) return; const t = setTimeout(() => setEditNotice(null), 6000); return () => clearTimeout(t); }, [editNotice]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectedId = selectedIds.size === 1 ? [...selectedIds][0] : null; // single-target ops (rotate/resize)
  const [imgDims, setImgDims] = useState(null);  // natural px of raster background
  const [imgMode, setImgMode] = useState(false); // dragging the image underlay
  const [calib, setCalib] = useState(null);      // null | { points: [{wx,wy}] } world ft
  // Track B (2026-09-14): the two clicked points are MEASURED at the current
  // scale first and compared with the printed dimension — error % + verdict
  // (scaleVerification.js) — and only an explicit "Re-calibrate" writes the
  // sheet. `calibPrompt` is the open verification panel; `scaleCheck` is the
  // last verdict for this sheet, session-only (a persisted verification flag
  // would be a TakeoffDrawingSheets column — deferred until it is needed
  // after reload).
  const [calibPrompt, setCalibPrompt] = useState(null); // null | { worldDist, input }
  const [scaleCheck, setScaleCheck] = useState(null);   // null | { verdict, errorPct, expectedFt, measuredFt, recalibrated }
  const [colorMode, setColorMode] = useState("zone"); // "zone" (default, by AHU) | "type" (by Room Type/ISO classification)
  // Takeoff Canvas 2.0 A1 — dark / negative plan view. A render preference
  // (per viewer, localStorage), never estimate data: the drawing raster is
  // inverted by a CSS filter, the ground goes dark and the light-ground chrome
  // (label halos, dark text, grid) follows; catalog colors are untouched and
  // the source PDF is never modified (src/utils/canvasTheme.js).
  const [darkDrawing, setDarkDrawing] = useState(() => readDarkDrawingPreference());
  const view = drawingView(darkDrawing);
  const [uiTheme, , toggleUiTheme] = useTakeoffUiTheme();   // the interface (not the drawing) light / dark
  const toggleDarkDrawing = () => setDarkDrawing(d => { writeDarkDrawingPreference(!d); return !d; });
  const [showManageSheets, setShowManageSheets] = useState(false);
  // Right dock (2026-08-26, OST parity pass) — a persistent multi-panel
  // sidebar alongside the canvas, replacing the old floating Keyplan corner.
  // Mirrors OST's own left sidebar: a panel (Keyplan/Rooms/Conditions/Layers,
  // stacked as four always-visible sections rather than a tab switcher)
  // always visible next to the drawing, not separate full-page views like
  // the existing By Room/By Discipline/etc. tabs above the canvas.
  // Window-size defaults (utils/viewportTier.js): a small window gets a tabbed ribbon, a one-column dock, and — when narrow —
  // a dock that slides over the drawing. A viewer's own choices (pin, dock columns, dock width) always win.
  const tierInfo = useViewportTier();
  const [ribbonWidth, setRibbonWidth] = useState(0);   // the ribbon's own width (it sits beside the dock, so it can be narrower than the window)
  const tabbed = ribbonTabbed({ tierTabbed: tierInfo.tabbedRibbon, ribbonWidth }), drawerDock = tierInfo.dockMode === "drawer";
  const [dockOpen, setDockOpen] = useState(() => !drawerDock);
  useEffect(() => { if (drawerDock) setDockOpen(false); }, [drawerDock]);
  const [focusMode, setFocusMode] = useState(false);   // drawing only: the ribbon, strips and dock hidden (M / Esc to return)
  const summaryShown = !tierInfo.compact && !focusMode; // the one-line wall summary under the drawing
  // Dock width — drag its left edge. Per viewer (localStorage), never estimate data.
  const DOCK_MIN_W = 220, DOCK_MAX_W = 760;
  const [dockWidth, setDockWidth] = useState(() => {
    try { const v = +localStorage.getItem("cs.takeoff.dockWidth"); return v >= DOCK_MIN_W && v <= DOCK_MAX_W ? v : (effectiveDockLayout(readDockLayout(), tierInfo.dockMode, hasExplicitDockColumns()).twoCol ? DOCK_TWO_COL_W : DOCK_ONE_COL_W); } catch { return DOCK_TWO_COL_W; }
  });
  function startDockResize(e) {
    e.preventDefault();
    const startX = e.clientX, startW = dockWidth;
    let w = startW;
    const move = ev => { w = Math.max(DOCK_MIN_W, Math.min(DOCK_MAX_W, startW + (startX - ev.clientX))); setDockWidth(w); };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      try { localStorage.setItem("cs.takeoff.dockWidth", String(Math.round(w))); } catch { /* per-viewer convenience only */ }
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  }
  // Floating dock (feedback #34): ⇱ pops the whole dock out into a window dragged by its title bar and resized from its
  // corner; ⇲ docks it back. Per viewer (localStorage). Not offered in a narrow window, where the dock already slides over the drawing.
  const viewportSize = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [dockFloat, setDockFloatState] = useState(() => readDockFloat(viewportSize(), { w: dockWidth }));
  const dockFloating = dockFloat.floating && !drawerDock;
  const setDockFloat = (patch) => setDockFloatState(prev => {
    const next = normalizeDockFloat({ ...prev, ...patch }, viewportSize());
    writeDockFloat(next);
    return next;
  });
  useEffect(() => {
    // a window made smaller (or a new monitor) keeps the floating dock reachable
    const onResize = () => setDockFloatState(prev => normalizeDockFloat(prev, viewportSize()));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  function startDockFloatGesture(e, kind) {
    if (kind === "move" && e.target.closest("button")) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, start = dockFloat;
    let last = start;
    const move = ev => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      last = normalizeDockFloat(kind === "move" ? { ...start, x: start.x + dx, y: start.y + dy } : { ...start, w: start.w + dx, h: start.h + dy }, viewportSize());
      setDockFloatState(last);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); writeDockFloat(last); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  }
  // Per-section collapse — same chevron-toggle convention already used by
  // ConditionsPanel's own cost-code groups (closedGroups) and
  // TakeoffCriteriaPanel.jsx's RoomCriteriaExpand (openDisc).
  const [dockSectionsOpen, setDockSectionsOpen] = useState(() => initialSectionsOpen(readDockLayout()));
  const [compareSheetId, setCompareSheetId] = useState(null); // A3 — a second sheet shown read-only beside the canvas (null = off)
  const [layoutReviewRoomId, setLayoutReviewRoomId] = useState(null); // Track H — room whose HEPA / light footprints + conflicts are drawn (null = off)
  // The toolbar ribbon: pinned (docked above the drawing, the default) or a slim bar whose tiles float down on hover.
  // The pin: the viewer's own choice when they made one, else the window-size default (pinned unless the window is short).
  const [ribbonPinChoice, setRibbonPinChoice] = useState(() => readRibbonPinChoice());
  const ribbonPinned = ribbonPinChoice ?? tierInfo.defaultRibbonPinned;
  const [ribbonHover, setRibbonHover] = useState(false);
  const [ribbonFocus, setRibbonFocus] = useState(false);
  const [ribbonSticky, setRibbonSticky] = useState(false);   // a tabbed, unpinned ribbon opened by clicking a tab — stays until clicked away
  const [ribbonTab, setRibbonTabState] = useState(() => readRibbonTab());
  const ribbonWrapRef = useRef(null);
  const ribbonCloseTimer = useRef(null);
  const setRibbonPinned = (v) => { setRibbonPinChoice(v); writeRibbonPinned(v); if (v) { setRibbonHover(false); setRibbonSticky(false); } };
  const hoverOpens = !ribbonPinned && !tabbed;   // a tabbed ribbon opens on a click, never by the pointer passing over it
  const openRibbon = () => { clearTimeout(ribbonCloseTimer.current); setRibbonHover(true); };
  const closeRibbonSoon = () => { clearTimeout(ribbonCloseTimer.current); ribbonCloseTimer.current = setTimeout(() => setRibbonHover(false), RIBBON_CLOSE_DELAY_MS); };
  const closeRibbonFloat = () => { setRibbonHover(false); setRibbonFocus(false); setRibbonSticky(false); };
  const pickRibbonTab = (k) => { setRibbonTabState(k); writeRibbonTab(k); if (!ribbonPinned) setRibbonSticky(sticky => (ribbonTab === k ? !sticky : true)); };
  useEffect(() => () => clearTimeout(ribbonCloseTimer.current), []);
  useEffect(() => {
    const el = ribbonWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // width only: tabbing changes the ribbon's height, never the width it is given, so this cannot flip back and forth
    const ro = new ResizeObserver(([e]) => { const w = Math.round(e.contentRect.width); if (w > 0) setRibbonWidth(prev => (prev === w ? prev : w)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [focusMode]);
  const ribbonOpen = ribbonVisible({ pinned: ribbonPinned, hover: ribbonHover, focusWithin: ribbonFocus }) || (tabbed && ribbonSticky);
  const showTab = (tab) => groupVisible({ tabbed, active: ribbonTab, tab });
  // Which dock panes show, their order and one / two columns — a per-viewer view setting (utils/dockLayout.js).
  const [dockLayout, setDockLayoutState] = useState(() => readDockLayout());
  const effDockLayout = effectiveDockLayout(dockLayout, tierInfo.dockMode, hasExplicitDockColumns());   // what this window shows (viewportTier.js)
  const setDockLayout = (next) => {
    setDockLayoutState(next); writeDockLayout(next);
    // Switching the column count moves a dock that was left at the other mode's width to a sensible one (still the viewer's to resize).
    const widen = next.twoCol && !effDockLayout.twoCol && dockWidth < 620, narrow = !next.twoCol && effDockLayout.twoCol && dockWidth >= 600;
    if (widen || narrow) { const w = widen ? DOCK_TWO_COL_W : DOCK_ONE_COL_W; setDockWidth(w); try { localStorage.setItem("cs.takeoff.dockWidth", String(w)); } catch { /* preference only */ } }
  };
  // Auto-collapse (▦ Panels ▸ "Auto-collapse other panels", off by default): opening a pane collapses the others in its column
  // so it fills the dock; Shift / Ctrl / Cmd-click keeps the others open. Off = every pane opens and closes on its own.
  const toggleDockSection = (key, e) => {
    if (!dockLayout.autoCollapse) { setDockSectionsOpen(prev => ({ ...prev, [key]: !prev[key] })); return; }
    const col = dockColumns(effDockLayout).find(c => c.includes(key)) || [key];
    setDockSectionsOpen(prev => toggleDockPane(prev, key, col, { keep: !!(e && (e.shiftKey || e.ctrlKey || e.metaKey)) }));
  };
  const dockHeaderTip = dockLayout.autoCollapse
    ? "Click to open or close. Opening a panel closes the others in this column — hold Shift (or Ctrl / Cmd) while clicking to keep them open."
    : "Click to open or close this panel.";
  // Anything that needs a pane (Add Condition, drawing a route) opens the dock, shows the pane if it was hidden, and expands it.
  const openDockPanel = (key) => {
    setDockOpen(true);
    setDockLayoutState(l => { if (!l.hidden.includes(key)) return l; const n = setDockPanelHidden(l, key, false); writeDockLayout(n); return n; });
    setDockSectionsOpen(prev => ({ ...prev, [key]: true }));
  };
  const [hiddenLayers, setHiddenLayers] = useState(new Set(["edgeDebug"])); // layer keys currently hidden — see LAYER_DEFS; the debug overlay is opt-in
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set()); // condition types hidden with the eye (drawn keys) — view only, this session
  // The Layers dock catalog: the admin-managed TakeoffLayers rows (db/66)
  // when loaded, else the built-in LAYER_DEFS. A system row's key is one of
  // the render-block keys below; a custom row's key ('layer_<id>') only
  // matters through the entities assigned to it (entityLayerHidden).
  const layerDefs = useMemo(() => {
    if (!layers?.length) return LAYER_DEFS.map(l => ({ ...l, isSystem: true }));
    return [...layers]
      .sort((a, b) => (a.DisplayOrder - b.DisplayOrder) || String(a.Name).localeCompare(String(b.Name)))
      .map(l => ({ key: l.LayerKey, label: l.Name, description: l.Description, color: l.Color, isSystem: !!l.IsSystem, defaultVisible: !!l.DefaultVisible, layerId: l.LayerID }));
  }, [layers]);
  // Apply each layer's admin-set "Default visible" once, when the catalog
  // first arrives — never again, so a checkbox the estimator toggled this
  // session isn't reset by a later re-render.
  const layerDefaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (layerDefaultsAppliedRef.current || !layers?.length) return;
    layerDefaultsAppliedRef.current = true;
    setHiddenLayers(prev => {
      const next = new Set(prev);
      layerDefs.forEach(l => { if (l.defaultVisible === false) next.add(l.key); else next.delete(l.key); });
      return next;
    });
  }, [layers, layerDefs]);
  const layerKeyById = useMemo(() => new Map(layerDefs.filter(l => l.layerId != null).map(l => [l.layerId, l.key])), [layerDefs]);
  // An entity (Item/Assembly via a drawn row's LayerID, or a Room Template
  // via a room's RoomTemplateID) assigned to a hidden layer hides — on top
  // of the built-in layer its render block already gates on.
  const entityLayerHidden = useCallback((layerId) => layerId != null && hiddenLayers.has(layerKeyById.get(layerId)), [hiddenLayers, layerKeyById]);
  const roomLayerVisible = useCallback((room) => {
    const t = room?.RoomTemplateID != null ? roomTemplates?.find(x => x.RoomTemplateID === room.RoomTemplateID) : null;
    return !entityLayerHidden(t?.LayerID);
  }, [roomTemplates, entityLayerHidden]);
  const condLayerVisible = useCallback((c) => !entityLayerHidden(c?.LayerID) && !hiddenTypes.has(conditionTypeKey(c)), [entityLayerHidden, hiddenTypes]);
  // Per-revision Edge Halo color overrides (haloColors, from
  // TakeoffHaloSettings) layered on top of HALO_STYLE's own defaults — a
  // tier with no override (or an empty haloColors, the common case) just
  // uses its built-in stroke/width/opacity unchanged.
  const haloStyle = useMemo(() => {
    const merged = {};
    for (const tier of Object.keys(HALO_STYLE)) merged[tier] = HALO_STYLE[tier];
    for (const { tier, settingsKey } of HALO_TIER_FIELDS) {
      const override = haloColors?.[settingsKey];
      if (override) merged[tier] = { ...HALO_STYLE[tier], stroke: override };
    }
    return merged;
  }, [haloColors]);
  // Drag an already-placed door/window symbol to a new wall position — click
  // (no movement past CLICK_PX) still just selects/deselects it, same as
  // before; movement past that starts a live snapped drag, committed on
  // release. { cond, sx, sy, live } — sx/sy are the pointerdown's screen
  // coords (for click-vs-drag), live is the current snapToNearestWall()
  // result once dragging has actually started (null until then).
  const [symbolDrag, setSymbolDrag] = useState(null);
  // Measure tool (2026-08-26): a read-only ruler for sanity-checking a
  // drawing's scale against a dimension already printed on it — distinct
  // from "Set/Verify scale" above, which actually RE-CALIBRATES on the
  // distance you type in. Drag once, compare the live readout against the
  // plan's printed dimension, done — nothing is written to the sheet.
  const [measureMode, setMeasureMode] = useState(false);
  const [measureLine, setMeasureLine] = useState(null); // {x0,y0,x1,y1} world ft — stays after release until the next drag
  const [measuring, setMeasuring] = useState(false);     // true only while the pointer is actually down
  const [drawMode, setDrawMode] = useState(false);
  const [drawRect, setDrawRect] = useState(null); // { x0,y0,x1,y1,sx,sy } world ft (+ screen px start) while dragging a box
  const [roomPts, setRoomPts] = useState([]);      // in-progress traced room outline (world ft) — click-click-click alternative to drag
  const [roomCursor, setRoomCursor] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfPages, setPdfPages] = useState(null); // [{ wPt, hPt }]
  const [versionsOpen, setVersionsOpen] = useState(false); // Versions panel for the active drawing
  const [searchOpen, setSearchOpen] = useState(false);   // Search drawings panel
  // Count by example: the marquee tool, the example it drew, and the found-symbol markers on the active drawing.
  const [sweepMode, setSweepMode] = useState(false);
  const [sweepBox, setSweepBox] = useState(null);        // {x0,y0,x1,y1,sx,sy} world ft while / after dragging
  const [sweepDragging, setSweepDragging] = useState(false);
  const [sweepSeed, setSweepSeed] = useState(null);      // { sheetId, box } — set when a box is finished; opens the panel
  const [sweepMarkers, setSweepMarkers] = useState([]);  // result rows from the panel (all drawings)
  const [pendingFocus, setPendingFocus] = useState(null);  // { sheetId, page, x, y } — a search hit to centre on once its drawing is showing
  const [searchMark, setSearchMark] = useState(null);      // { x, y } world ft — ring on the last jumped-to hit (transient)
  const searchPageSentRef = useRef(false);
  const [scaleNotes, setScaleNotes] = useState(null); // { suggestion, candidates, ambiguous } read from the page's printed scale note — SUGGEST ONLY, never applied automatically
  const [pdfError, setPdfError] = useState(null); // set when the active sheet's PDF can't be read — replaces the endless "Rendering PDF…" chip
  const [renderUrl, setRenderUrl] = useState(null);
  const [renderRect, setRenderRect] = useState(null); // world-ft rect the texture covers
  const lastRenderRef = useRef(null);                 // { x0,y0,x1,y1,scale }
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [condMode, setCondMode] = useState(false);
  const [condPick, setCondPick] = useState(null);    // TypeKey — see RoomTakeoff.jsx's decodeTypeKey comment
  const [condPts, setCondPts] = useState([]);        // in-progress polyline (world ft)
  const [condCursor, setCondCursor] = useState(null);
  // Phase G canvas routing — tracing a route SEGMENT's path (same click-corner tracer as conditions)
  const [routeMode, setRouteMode] = useState(false);
  const [routePick, setRoutePick] = useState(null);  // segmentId being traced
  const [routePts, setRoutePts] = useState([]);
  const [routeCursor, setRouteCursor] = useState(null);
  const routePickSeg = routePick != null ? routes.find(rt => rt.segmentId === routePick) : null;
  // Conditions can be multi-selected (Shift / Ctrl-click). `selectedCondId` is the single selection (null when none or several)
  // and `setSelectedCondId` replaces the selection with one — so every existing caller works unchanged.
  const [selectedCondIds, setSelectedCondIds] = useState(() => new Set());
  const selectedCondId = selectedCondIds.size === 1 ? [...selectedCondIds][0] : null;
  const setSelectedCondId = (v) => setSelectedCondIds(prevSet => {
    const prev = prevSet.size === 1 ? [...prevSet][0] : null;
    const next = typeof v === "function" ? v(prev) : v;
    return next == null ? new Set() : new Set([next]);
  });
  const toggleCondSelected = (id) => setSelectedCondIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clipboardRef = useRef(null);   // copied drawn conditions (rows), kept across drawings so a paste can land on another one
  const lastWorldRef = useRef(null);   // the pointer's last position in feet — where a paste lands
  // Draw area condition (2026-08-28) — same click-corner tracer as "Draw
  // room" (drawMode/roomPts above), scoped to Style='area' condition types
  // instead of TakeoffRooms.
  const [areaMode, setAreaMode] = useState(false);
  const [areaPick, setAreaPick] = useState(null);    // TypeKey (Style='area')
  const [areaPts, setAreaPts] = useState([]);        // in-progress polygon (world ft)
  const [areaCursor, setAreaCursor] = useState(null);
  // Full-canvas crosshair guide lines (world ft) — shown only while in one of
  // the precision draw/place/measure/calibrate modes (same set the cursor
  // already switches to the OS "crosshair" icon for), so lining up a new
  // room/condition/symbol against something elsewhere on the sheet doesn't
  // require eyeballing it. Cleared on pointer-leave so a stale line doesn't
  // sit at the canvas edge after the mouse leaves it.
  const [crosshairPos, setCrosshairPos] = useState(null);
  const [crosshairStyle, setCrosshairStyle] = useState(() => readCrosshairStyle()); // 'bold' | 'standard' | 'off' — per-viewer
  const [crosshairTuning, setCrosshairTuning] = useState(() => readCrosshairTuning()); // line width / opacity / precision arm length — per-viewer
  const [crosshairLayers, setCrosshairLayers] = useState(() => readCrosshairLayers()); // ring / tool colour / snap preview / angle guide / readout / snap points — per-viewer
  const cursorElRef = useRef(null);      // the status bar's cursor readout, written on every move without re-rendering
  const scaleSelectRef = useRef(null);
  const [units, , toggleDisplayUnits] = useUnitsPreference(); // imperial | metric — a display preference, per viewer
  const [paletteOpen, setPaletteOpen] = useState(false); // Ctrl+K command palette
  const [guideOpen, setGuideOpen] = useState(false);     // ? keyboard shortcut guide
  const [addWindowOpen, setAddWindowOpen] = useState(false);   // "+ Add Condition" floating window
  const [pins, setPins] = useState(() => readPins());     // pinned condition types (catalog keys), per viewer — keys 1–9
  const [counterOpen, setCounterOpen] = useState(() => readCounterOpen()); // floating totals
  const [symbolMode, setSymbolMode] = useState(false);
  const [symbolPick, setSymbolPick] = useState(null); // TypeKey (Style='count')
  const [symbolHover, setSymbolHover] = useState(null); // wall-snap preview while placing
  const [detailRoomId, setDetailRoomId] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null); // right-click menu on a room / drawn condition: { x, y, items }
  const [pickGeom, setPickGeom] = useState(null); // a finished drawing waiting for "which room is this?"
  const [placingRoomId, setPlacingRoomId] = useState(null); // room the current draw session will PLACE ("+ Add Room" sets it together with opening the flyout; the flyout's "Draw this room" sets it for any unplaced room)
  const [editingCondId, setEditingCondId] = useState(null); // Condition Detail Flyout — edit mode
  const [addingCondition, setAddingCondition] = useState(false); // Condition Detail Flyout — create mode ("+Add Condition")
  const [chaseEdgeKey, setChaseEdgeKey] = useState(null); // edgeOverrideKey of the cavity edge open in Chase Detail Flyout — re-found from the live `edges` list each render, not stored as an object (edges are recomputed every render, not stable references)

  const scopeItemsById = useMemo(() => {
    const m = {};
    scopeItems.forEach(i => { m[i.ScopeItemID] = i; });
    return m;
  }, [scopeItems]);
  const linearConditionTypes = useMemo(() => conditionTypes.filter(c => c.Style === "linear"), [conditionTypes]);
  const condPickType = condPick ? linearConditionTypes.find(c => c.TypeKey === condPick) : null;
  const symbolConditionTypes = useMemo(() => conditionTypes.filter(c => c.Style === "count"), [conditionTypes]);
  const symbolPickType = symbolPick ? symbolConditionTypes.find(c => c.TypeKey === symbolPick) : null;
  const areaConditionTypes = useMemo(() => conditionTypes.filter(c => c.Style === "area"), [conditionTypes]);
  const areaPickType = areaPick ? areaConditionTypes.find(c => c.TypeKey === areaPick) : null;
  // The Conditions dock's single "+ Add Condition" entry point (beginConditionDraw,
  // see that function's own comment) needs to reason about all three draw
  // mechanics together — which one is currently active, which type it's
  // drawing, and the combined list to offer in its type picker.
  // The retired first-generation Condition Library rows ('ct:' keys) are NOT offered: each duplicates a catalog Item
  // (db/56 moved every drawn row onto the Items; none resolves through the library any more), so they only showed up as
  // a second "2\" Ceiling Panel System". They stay loaded for anything that still decodes a 'ct:' key.
  const drawableConditionTypes = useMemo(
    () => [...linearConditionTypes, ...symbolConditionTypes, ...areaConditionTypes].filter(t => !String(t.TypeKey).startsWith("ct:")),
    [linearConditionTypes, symbolConditionTypes, areaConditionTypes]
  );
  const conditionDrawActive = condMode || symbolMode || areaMode;
  const activeConditionStyle = condMode ? "linear" : symbolMode ? "count" : areaMode ? "area" : null;
  const activeConditionPickId = condMode ? condPick : symbolMode ? symbolPick : areaMode ? areaPick : null;

  // The active sheet's settings — everything below (isPdf, pageNum, the whole
  // PDF-rasterization effect chain) already treats this as "the current
  // drawing's settings," so switching sheets is just switching which object
  // this resolves to; the existing effects (keyed on settings?.BackgroundImage
  // etc.) naturally re-run for the new sheet.
  const settings = sheets.find(s => s.SheetID === activeSheetId) || null;
  const compareSheet = compareSheetId != null && compareSheetId !== activeSheetId ? (sheets.find(s => s.SheetID === compareSheetId) || null) : null;

  // Refs mirror view state so the native (non-passive) wheel handler stays fresh
  const panRef = useRef(pan); panRef.current = pan;
  const zRef = useRef(pxPerFt); zRef.current = pxPerFt;

  const isPdf = !!settings?.ImageIsPdf && isPdfSource(settings?.BackgroundImage);
  const pageNum = settings?.ImagePage || 1;
  // Page numbering across every drawing: each PDF's page count is read once (the working one is already open).
  const [sheetPageCounts, setSheetPageCounts] = useState({});
  const pageCountKeyRef = useRef({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const s of sheets) {
        if (!s.ImageIsPdf || !s.BackgroundImage || isBlobPointer(s.BackgroundImage)) continue; // raster / stored page = 1
        const k = `${s.SheetID}:${s.BackgroundImage.length}`;
        if (pageCountKeyRef.current[s.SheetID] === k) continue;
        pageCountKeyRef.current[s.SheetID] = k;
        try { const n = await countPdfPages(s.BackgroundImage); if (!cancelled) setSheetPageCounts(c => ({ ...c, [s.SheetID]: n })); }
        catch { pageCountKeyRef.current[s.SheetID] = null; }
      }
    })();
    return () => { cancelled = true; };
  }, [sheets]);
  const pageCounts = (isPdf && pdfPages?.length && activeSheetId != null) ? { ...sheetPageCounts, [activeSheetId]: pdfPages.length } : sheetPageCounts;
  const pageTotals = pagePosition(sheets, pageCounts, activeSheetId, pageNum);
  // Key sheets — starred, or with takeoff on them. A mark and a view option; every drawing stays in the package.
  const keyList = keySheets(sheets, sheetTakeoffCounts);
  const [keyOnly, setKeyOnlyState] = useState(() => { try { return localStorage.getItem("cs.takeoff.sheetStrip.keyOnly") === "1"; } catch { return false; } });
  const setKeyOnly = (v) => { setKeyOnlyState(v); try { localStorage.setItem("cs.takeoff.sheetStrip.keyOnly", v ? "1" : "0"); } catch { /* preference only */ } };
  const [keyMenuOpen, setKeyMenuOpen] = useState(false);
  const [sheetMenuOpen, setSheetMenuOpen] = useState(false);   // the quick strip's drawing switcher (a small window)
  // The strip scrolls vertically: keep the working drawing's card in view when it changes (▲ ▼, [ ], the jump list).
  useEffect(() => {
    if (activeSheetId == null) return;
    document.querySelector(`[data-sheet-card="${activeSheetId}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeSheetId, keyOnly]);
  const pendingPageRef = useRef(null); // { sheetId, page } — a step that crossed into another drawing
  const goPage = (dir) => {
    const r = stepPage(sheets, pageCounts, activeSheetId, pageNum, dir);
    if (!r) return;
    if (r.sheetId === activeSheetId) { onSaveCanvas({ image_page: r.page }); return; }
    pendingPageRef.current = r;
    onSelectSheet?.(r.sheetId);
  };
  const jumpToPage = (raw) => {
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n)) return;
    const r = pageAt(sheets, pageCounts, n);
    if (!r || (r.sheetId === activeSheetId && r.page === pageNum)) return;
    if (r.sheetId === activeSheetId) { onSaveCanvas({ image_page: r.page }); return; }
    pendingPageRef.current = r;
    onSelectSheet?.(r.sheetId);
  };
  useEffect(() => {
    const p = pendingPageRef.current;
    if (!p || p.sheetId !== activeSheetId) return;
    if (!isPdf) { pendingPageRef.current = null; return; }   // a single-page drawing: nothing to turn to
    if (!pdfPages) return;                                    // still opening — try again when its pages are known
    pendingPageRef.current = null;
    if (p.page !== pageNum && p.page <= pdfPages.length) onSaveCanvas({ image_page: p.page });
  }, [activeSheetId, isPdf, pdfPages]); // eslint-disable-line react-hooks/exhaustive-deps
  const rot = +settings?.ImageRotation || 0;   // the drawing is shown turned this many degrees clockwise (0 / 90 / 180 / 270)

  // Sheet switch is a bigger context change than a page/zoom change — drop
  // any in-flight calibration/move-drawing interaction so it doesn't carry
  // over onto the new drawing.
  useEffect(() => { setCalib(null); setCalibPrompt(null); setScaleCheck(null); setImgMode(false); }, [activeSheetId]);

  // Fill the canvas to the remaining viewport height below the sticky toolbar
  // (measured, not guessed — the toolbar wraps to a different number of lines
  // depending on screen width and what's active) instead of a fixed height,
  // so working the canvas needs a lot less scrolling.
  useEffect(() => {
    const el = toolbarWrapRef.current;
    if (!el) return;
    function recompute() {
      const bottom = el.getBoundingClientRect().bottom;
      // a small window keeps less slack below the drawing and may go shorter than the full-size minimum
      setCanvasHeight(Math.max(tierInfo.compact ? 260 : 360, Math.round(window.innerHeight - bottom - (tierInfo.compact ? 10 : 24) - STATUS_BAR_H - (summaryShown ? WALL_SUMMARY_H : 0))));
    }
    recompute();
    window.addEventListener("resize", recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => { window.removeEventListener("resize", recompute); ro.disconnect(); };
  }, [sheets.length, isPdf, pdfPages?.length, condMode, tierInfo.compact, summaryShown, focusMode]);

  // A tabbed ribbon opened with a click folds away when the next click lands outside it.
  useEffect(() => {
    if (!ribbonSticky) return undefined;
    const onDown = (e) => { if (ribbonWrapRef.current && !ribbonWrapRef.current.contains(e.target)) setRibbonSticky(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [ribbonSticky]);
  // The slide-over dock closes when the drawing is clicked (the click still reaches the drawing).
  useEffect(() => {
    if (!(drawerDock && dockOpen)) return undefined;
    const onDown = (e) => { if (svgRef.current && svgRef.current.contains(e.target)) setDockOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [drawerDock, dockOpen]);

  const zoneColor = useMemo(() => {
    const map = {};
    zones.forEach((z, i) => { map[z.AHUZoneID] = ZONE_COLORS[i % ZONE_COLORS.length]; });
    return map;
  }, [zones]);

  const effRooms = useMemo(() =>
    rooms.map(r => {
      let rr = r;
      if (livePos[r.RoomID]) {
        const dx = livePos[r.RoomID].x - (+r.CanvasX || 0);
        const dy = livePos[r.RoomID].y - (+r.CanvasY || 0);
        rr = { ...rr, CanvasX: livePos[r.RoomID].x, CanvasY: livePos[r.RoomID].y };
        // Only a plain drag (no resize in progress) translates the polygon —
        // a resize already clears it below, so there's nothing to move.
        if (!liveSize[r.RoomID]) rr = { ...rr, PolygonJson: translatePolygonJson(rr.PolygonJson, dx, dy) };
      }
      if (liveSize[r.RoomID]) {
        const rot90 = rr.CanvasRot === 90;
        const { w, h } = liveSize[r.RoomID];
        // Live preview matches the committed behavior (see resizeRoom in
        // RoomTakeoff.jsx): resizing a polygon room reverts it to a plain
        // rectangle, so the preview shouldn't keep showing the old shape.
        rr = { ...rr, LengthFt: rot90 ? h : w, WidthFt: rot90 ? w : h, PolygonJson: null };
      }
      if (liveVertices[r.RoomID]) {
        rr = { ...rr, PolygonJson: JSON.stringify({ points: liveVertices[r.RoomID] }) };
      }
      return rr;
    }),
    [rooms, livePos, liveSize, liveVertices]);

  // Derived wall geometry is O(n²) over rooms — recomputing it on every
  // mousemove during a drag stutters. Defer it: the room rectangles render
  // live from effRooms, while adjacency/joint/takeoff math trails a frame
  // behind under load and settles as soon as the drag pauses.
  const deferredRooms = useDeferredValue(effRooms);
  const edges = useMemo(() => detectEdges(deferredRooms, overrides, tunables), [deferredRooms, overrides, tunables]);
  // The Room Detail Flyout's "Neighbors & walls" readout: this room's joints on this drawing, the pair rules
  // that apply and what the engine would fill (the engine's own fill, so it cannot disagree with the sync).
  const neighborWalls = useMemo(() => {
    if (detailRoomId == null) return [];
    const wallSystemByRoom = new Map(deferredRooms.filter(r => r._wallSystem).map(r => [r.RoomID, r._wallSystem]));
    return neighborWallReadout({ roomId: detailRoomId, edges, rooms: deferredRooms, rulesByPair, wallSystemByRoom, tunables, predictWall: wallLearning?.predictWall || null });
  }, [detailRoomId, edges, deferredRooms, rulesByPair, tunables, wallLearning]);
  const takeoff = useMemo(() => wallTakeoff(deferredRooms, edges), [deferredRooms, edges]);
  const unplaced = rooms.filter(r => !rectOf(r) && +r.LengthFt > 0 && +r.WidthFt > 0);

  // A room being drawn (click-corner or drag-box, before it's committed)
  // gets a provisional bounding-box "room" so the Edge Halo can react to it
  // live -- per the vision doc's own halo-as-QA description (Section 6): as
  // a new outline nears a neighbor, the halo should show how the engine
  // would classify that joint before the estimator even finishes drawing.
  // Deliberately kept OUT of edges/deferredRooms/takeoff above -- those
  // drive real committed quantities and must never reflect a room that
  // doesn't exist yet, only the halo (a pure visual QA layer) does.
  const DRAFT_ROOM_ID = "__draft__";
  const draftRoom = useMemo(() => {
    if (!drawMode) return null;
    if (roomPts.length > 0) {
      // Click-corner tracing — bounding box of every committed corner plus
      // the live cursor position.
      const chain = roomCursor ? [...roomPts, roomCursor] : roomPts;
      const xs = chain.map(p => p.x), ys = chain.map(p => p.y);
      const bx = Math.min(...xs), by = Math.min(...ys);
      const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;
      if (!(bw > 0.1) || !(bh > 0.1)) return null; // too small yet to mean anything
      return { RoomID: DRAFT_ROOM_ID, CanvasX: bx, CanvasY: by, CanvasRot: 0, LengthFt: bw, WidthFt: bh };
    }
    if (drawRect) {
      // Drag-box — the other way to create a room (click-and-hold, drag out
      // a rectangle). Same live-halo reactivity as the click-corner path
      // above, just sourced from drawRect's own live x0/y0/x1/y1 instead of
      // roomPts/roomCursor. Without this, dragging a box (the faster of the
      // two ways to draw a rectangular room) never fed a draft room to the
      // halo at all — it only ever saw roomPts, so nothing reacted while
      // dragging even though it worked fine once the room was committed.
      const bw = Math.abs(drawRect.x1 - drawRect.x0), bh = Math.abs(drawRect.y1 - drawRect.y0);
      if (!(bw > 0.1) || !(bh > 0.1)) return null;
      return {
        RoomID: DRAFT_ROOM_ID, CanvasRot: 0, LengthFt: bw, WidthFt: bh,
        CanvasX: Math.min(drawRect.x0, drawRect.x1), CanvasY: Math.min(drawRect.y0, drawRect.y1),
      };
    }
    return null;
  }, [drawMode, roomPts, roomCursor, drawRect]);

  // Includes the draft against every COMMITTED room so a neighbor's own
  // halo reacts too (vision doc: "the affected halo region changes
  // appearance"), not just the room being drawn.
  const haloEdges = useMemo(
    () => draftRoom ? detectEdges([...deferredRooms, draftRoom], overrides, tunables) : edges,
    [draftRoom, deferredRooms, overrides, edges, tunables]
  );
  const haloRooms = draftRoom ? [...effRooms, draftRoom] : effRooms;

  // Auto-generated wall-condition rows (sp_SyncAutoWallConditions) carry a
  // stable AutoWallKey ('shared:lo:hi' / 'cavity:roomId:otherId' /
  // 'ext:roomId') matching detectEdges()'s own classification — this is the
  // join key the wall-assembly ring below uses to find, for any given wall
  // face, which Condition Type (if any) the estimator has assigned to it.
  const condByAutoKey = useMemo(() => {
    const m = new Map();
    for (const c of takeoffConditions) if (c.AutoWallKey) m.set(c.AutoWallKey, c);
    return m;
  }, [takeoffConditions]);

  // Review Walls dock panel data — see REVIEW_TIERS / ReviewWallsPanel.
  // classifyConfidence() is cheap (no geometry work of its own beyond what
  // detectEdges already did), so recomputing it for every edge alongside the
  // halo's own per-room call is fine. A confidently-classified wall whose
  // condition row(s) still carry no construction is listed as 'unassigned'
  // (only once its row exists — the auto-wall sync lands ~0.5 s after load).
  const reviewItems = useMemo(() => {
    const rank = new Map(REVIEW_TIERS.map((t, i) => [t.tier, i]));
    const labelOf = tier => REVIEW_TIERS.find(t => t.tier === tier).label;
    const items = [];
    for (const edge of edges) {
      const tier = classifyConfidence(edge, tunables);
      if (rank.has(tier)) { items.push({ edge, tier, label: labelOf(tier) }); continue; }
      if (tier === "auto_apply" && (edge.type === "shared" || edge.type === "cavity")) {
        const rows = wallRowsForEdge(edge, condByAutoKey);
        if (rows.length && rows.some(r => !rowTypeKey(r))) items.push({ edge, tier: "unassigned", label: labelOf("unassigned") });
      }
    }
    return items.sort((a, b) => rank.get(a.tier) - rank.get(b.tier));
  }, [edges, tunables, condByAutoKey]);

  // Review Walls chooser → the same attribute save the Condition Detail
  // Flyout uses, applied to every row of the joint (both faces of a cavity).
  const assignConstruction = useCallback((rows, typeKey) => {
    if (!onSaveTakeoffCondition) return;
    for (const row of rows) onSaveTakeoffCondition(row, { conditionTypeId: typeKey });
  }, [onSaveTakeoffCondition]);

  const imgX = settings?.ImageX || 0;
  const imgY = settings?.ImageY || 0;

  // ── Raster background natural size (non-PDF path) ────────────────────────────
  useEffect(() => {
    setImgDims(null); // never keep the previous sheet's pixel size while the new image decodes
    if (isPdf || !settings?.BackgroundImage) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setImgDims({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.src = settings.BackgroundImage;
    return () => { cancelled = true; };
  }, [settings?.BackgroundImage, isPdf]);

  // A rotated raster is drawn from a turned copy (so the keyplan and the canvas share one picture and one extent).
  const [rotatedRaster, setRotatedRaster] = useState(null);
  useEffect(() => {
    setRotatedRaster(null);
    if (isPdf || !settings?.BackgroundImage || !rot) return undefined;
    let cancelled = false;
    rotateImageUrl(settings.BackgroundImage, rot).then(u => { if (!cancelled) setRotatedRaster(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [settings?.BackgroundImage, isPdf, rot]);
  const rasterSrc = !settings?.BackgroundImage ? null : (rot ? rotatedRaster : settings.BackgroundImage);
  const rotDims = imgDims && (rot === 90 || rot === 270 ? { w: imgDims.h, h: imgDims.w } : imgDims);

  // ── PDF: load the document, then rasterize the page live at the current zoom ──
  useEffect(() => {
    // Drop the PREVIOUS sheet's document, page sizes and texture the instant
    // the drawing changes — otherwise the region render below can pair the old
    // PDF with the new sheet's page number / scale and paint the wrong drawing
    // until the new document finishes loading.
    setPdfDoc(null); setPdfPages(null); setPdfError(null);
    setRenderUrl(null); setRenderRect(null); lastRenderRef.current = null;
    if (!isPdf) return;
    let cancelled = false;
    loadPdf(settings.BackgroundImage, { page: settings.ImagePage || 1, rotation: rot })
      .then(({ doc, pageSizes }) => { if (!cancelled) { setPdfDoc(doc); setPdfPages(pageSizes); } })
      .catch(e => { console.error("PDF load failed:", e); if (!cancelled) setPdfError("Could not read this sheet's PDF — replace the drawing with a valid file."); });
    return () => { cancelled = true; };
  }, [settings?.BackgroundImage, isPdf, rot]);

  // A different PDF page is a different picture: drop the old page's texture
  // now so it can't sit under the new page (or stay forever if its render fails).
  useEffect(() => { setRenderUrl(null); setRenderRect(null); lastRenderRef.current = null; }, [pageNum, rot]);

  // Read the page's printed scale note (text layer) for a SUGGESTION. It is never applied
  // automatically: a note can describe a detail, and a sheet can print several scales.
  useEffect(() => {
    setScaleNotes(null);
    if (!pdfDoc) return;
    let cancelled = false;
    extractPageText(pdfDoc, pageNum, rot)
      .then(({ items, page }) => { if (!cancelled) setScaleNotes(detectScaleNotes(items, page)); })
      .catch(() => { if (!cancelled) setScaleNotes(null); });
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, rot]);

  // PDF page geometry in world feet
  const pageSize = isPdf && pdfPages ? pdfPages[pageNum - 1] : null;
  const wIn = pageSize ? pageSize.wPt / 72 : 0;
  const hIn = pageSize ? pageSize.hPt / 72 : 0;
  // Provisional scale (until calibrated): show the sheet ~100 ft wide so it's visible
  const ftPerInch = settings?.ImageFtPerInch || (wIn > 0 ? 100 / wIn : 8);
  const worldW = wIn * ftPerInch;
  const worldH = hIn * ftPerInch;

  // Raster world scale (feet per pixel of the stored raster)
  const imgScale = settings?.ImageScaleFtPerPx || 0.05;

  // World-ft extent of the active drawing's underlay (null until it has loaded).
  const sheetWorldW = isPdf ? worldW : (rotDims ? rotDims.w * imgScale : 0);
  const sheetWorldH = isPdf ? worldH : (rotDims ? rotDims.h * imgScale : 0);

  // Frame the whole drawing once per (sheet, page) when it first has a size —
  // zoom and pan are canvas-wide, so without this a switch left the view
  // wherever the previous sheet was. Not repeated when the scale or offset
  // later changes (the estimator is looking at it then).
  const fittedSheetRef = useRef(null);
  function fitToSheet() {
    if (!svgRef.current || !(sheetWorldW > 0) || !(sheetWorldH > 0)) return false;
    const box = svgRef.current.getBoundingClientRect();
    if (!(box.width > 0) || !(box.height > 0)) return false;
    const z = clamp(Math.min((box.width - 80) / sheetWorldW, (box.height - 80) / sheetWorldH), MIN_Z, MAX_Z);
    setPxPerFt(z);
    setPan({ x: 40 - imgX * z, y: 40 - imgY * z });
    return true;
  }
  useEffect(() => {
    if (!settings || !(sheetWorldW > 0)) return;
    const key = `${activeSheetId}:${pageNum}:${rot}`;
    if (fittedSheetRef.current === key) return;
    if (fitToSheet()) fittedSheetRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheetId, pageNum, rot, sheetWorldW, sheetWorldH]);

  // Jump to a search hit: once the hit's drawing and page are showing and have a size, centre on it
  // (declared AFTER the fit-to-sheet effect so it wins when both fire for a freshly opened sheet).
  function goToSearchHit(h) {
    const sheet = sheets.find(s => s.SheetID === h.entry.sheetId);
    if (!sheet) return;
    const ftPerInch = sheet.ImageFtPerInch || 100 / (h.entry.pageW / 72); // the provisional scale a not-yet-scaled sheet is shown at
    const w = hitToWorld(h, { imgX: sheet.ImageX || 0, imgY: sheet.ImageY || 0, ptsPerFt: 72 / ftPerInch });
    if (!w) return;
    searchPageSentRef.current = false;
    setPendingFocus({ sheetId: sheet.SheetID, page: h.entry.page, x: w.x, y: w.y });
    if (sheet.SheetID !== activeSheetId) onSelectSheet?.(sheet.SheetID);
  }
  useEffect(() => {
    if (!pendingFocus || activeSheetId !== pendingFocus.sheetId) return;
    if (pageNum !== pendingFocus.page) {
      if (!searchPageSentRef.current) { searchPageSentRef.current = true; onSaveCanvas?.({ image_page: pendingFocus.page }); }
      return;
    }
    if (!(sheetWorldW > 0) || !svgRef.current) return; // wait for the drawing to load
    const box = svgRef.current.getBoundingClientRect();
    const z = clamp(Math.max(pxPerFt, 14), MIN_Z, MAX_Z);
    setPxPerFt(z);
    setPan({ x: box.width / 2 - pendingFocus.x * z, y: box.height / 2 - pendingFocus.y * z });
    setSearchMark({ x: pendingFocus.x, y: pendingFocus.y });
    setPendingFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFocus, activeSheetId, pageNum, sheetWorldW, sheetWorldH]);
  useEffect(() => { if (!searchMark) return; const t = setTimeout(() => setSearchMark(null), 6000); return () => clearTimeout(t); }, [searchMark]);

  // Re-rasterize only the VISIBLE region of the page at device resolution on
  // each pan/zoom (debounced) — like a PDF viewer, so the underlay stays crisp
  // at any zoom instead of scaling one fixed full-page texture.
  useEffect(() => {
    if (!pdfDoc || !pageSize || !svgRef.current || !(worldW > 0)) return;
    let cancelled = false; // set by cleanup: a render that outlives a pan / zoom / sheet switch must not paint
    const t = setTimeout(async () => {
      try {
        const box = svgRef.current.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        // Visible world rect
        const vx0 = -pan.x / pxPerFt, vy0 = -pan.y / pxPerFt;
        const vx1 = (box.width - pan.x) / pxPerFt, vy1 = (box.height - pan.y) / pxPerFt;
        // Render a padded window so small pans don't force an immediate re-render
        const padX = (vx1 - vx0) * 0.3, padY = (vy1 - vy0) * 0.3;
        const x0 = Math.max(imgX, vx0 - padX), y0 = Math.max(imgY, vy0 - padY);
        const x1 = Math.min(imgX + worldW, vx1 + padX), y1 = Math.min(imgY + worldH, vy1 + padY);
        if (x1 <= x0 || y1 <= y0) return; // page off-screen — keep last texture

        const ptsPerFt = pageSize.wPt / worldW;
        const cropWpt = (x1 - x0) * ptsPerFt, cropHpt = (y1 - y0) * ptsPerFt;
        let scale = (pxPerFt * dpr) / ptsPerFt;                 // device px per point
        scale = Math.min(scale, 3400 / Math.max(cropWpt, cropHpt)); // bound canvas size

        // Skip if the last render already covers the visible rect at adequate res
        const L = lastRenderRef.current;
        if (L && L.page === pageNum && L.x0 <= vx0 && L.y0 <= vy0 && L.x1 >= vx1 && L.y1 >= vy1 && L.scale >= scale * 0.9) return;

        const url = await renderPageRegion(pdfDoc, pageNum, scale, (x0 - imgX) * ptsPerFt, (y0 - imgY) * ptsPerFt, cropWpt, cropHpt, rot);
        if (cancelled) return;
        lastRenderRef.current = { x0, y0, x1, y1, scale, page: pageNum };
        setRenderUrl(url);
        setRenderRect({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
      } catch (e) { console.error("PDF region render failed:", e); }
    }, 140);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pdfDoc, pageSize, pageNum, rot, worldW, worldH, imgX, imgY, pan, pxPerFt]);

  // How close (in feet at the current zoom) two clicks of a trace must be to count as the same click — a few screen pixels, so a
  // double-click's second click never leaves a stray extra corner (cleanTracePoints).
  const traceTolFt = () => TRACE_CLICK_TOL_PX / pxPerFt;
  const toWorld = (e) => {
    const box = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - box.left - pan.x) / pxPerFt,
      y: (e.clientY - box.top - pan.y) / pxPerFt,
    };
  };

  const scaleIsSet = isPdf ? !!settings?.ImageFtPerInch : !!settings?.ImageScaleFtPerPx;

  // UI Phase A / Track J — the unified review queue, COMPUTED from what this
  // canvas already holds (nothing persisted): scale state, the Review Walls
  // exceptions, unplaced / template-less rooms, unmarked openings, and every
  // Room Element warning from the same resolver the cards use.
  const elementReviewWarnings = useMemo(() => {
    if (!roomElements?.length || !systemsCtx) return [];
    const out = [];
    const systemsByRoom = new Map();
    for (const el of roomElements) {
      const room = rooms.find(r => r.RoomID === el.RoomID);
      if (!room) continue;
      try {
        if (!systemsByRoom.has(room.RoomID)) systemsByRoom.set(room.RoomID, calcRoomSystemsForReview(room, criteria, systemsCtx));
        const q = resolveElementQuantityForReview({
          element: el, room, systems: systemsByRoom.get(room.RoomID), placed: placedCountForReview(el, room.RoomID, allTakeoffConditions),
          formulas: catalog?.formulas || [], criteria, ctx: systemsCtx,
          backOuts: backOutsForReview(el, allTakeoffConditions), openings: openingsForReview(room, allTakeoffConditions, rooms),
        });
        for (const w of q.warnings || []) {
          out.push({ roomId: room.RoomID, roomName: room.RoomName, elementId: el.RoomElementID, label: el.Name || el.Code, code: w.code, message: w.message,
            conditionIds: w.id != null ? [w.id] : (q.net?.deductions || []).map(d => d.id).filter(id => id != null),
            severity: w.code === "element_unplaced" || w.code === "element_override_differs" ? "info" : "warn" });
        }
        // Track J — "unpriced RoomElement": a quantity the estimate would carry
        // at $0 because its catalog row has no material, labor or vendor cost.
        const ent = el.Kind === "assembly" ? (catalog?.assemblies || []).find(a => a.AssemblyID === el.AssemblyID) : (catalog?.items || []).find(i => i.ItemID === el.ItemID);
        if (ent && (+q.effective || 0) > 0 && !(+ent.MaterialCostPerUnit > 0) && !(+ent.LaborHrPerUnit > 0) && !(+ent.VendorSubCostPerUnit > 0))
          out.push({ roomId: room.RoomID, roomName: room.RoomName, elementId: el.RoomElementID, label: el.Name || el.Code, code: "element_unpriced", category: "pricing", severity: "warn",
            message: `${ent.Code || ""} has no material, labor or vendor cost in the catalog — it prices at $0.` });
      } catch { /* a room the resolver cannot evaluate yet is not a review item */ }
    }
    return out;
  }, [roomElements, rooms, criteria, systemsCtx, allTakeoffConditions, catalog]);
  // Track H — HEPA / light layout review per placed room: governing logic,
  // placed vs effective, footprint conflicts. Computed from the drawn rows
  // and the same engine result the cards use; nothing stored.
  const layoutReviewByRoom = useMemo(() => {
    const m = new Map();
    if (!systemsCtx) return m;
    for (const room of rooms) {
      if (room.CanvasX == null || room.CanvasX === "") continue;
      const symbols = allTakeoffConditions.filter(c => c.RoomID === room.RoomID && c.Style === "count" && (c.EntityAutoSystem || c.AutoSystem));
      let systems = null;
      try { systems = calcRoomSystemsForReview(room, criteria, systemsCtx); } catch { systems = null; }
      if (!symbols.length && !(systems?.hvac?.hepa?.enabled || systems?.electrical?.lighting?.enabled)) continue;
      m.set(room.RoomID, reviewRoomLayout({ room, symbols, systems, tunables }));
    }
    return m;
  }, [rooms, allTakeoffConditions, criteria, systemsCtx, tunables]);
  const layoutConflicts = useMemo(() => {
    const out = [];
    for (const [roomId, r] of layoutReviewByRoom) { const name = rooms.find(x => x.RoomID === roomId)?.RoomName; for (const c of r.conflicts) out.push({ ...c, roomName: name }); }
    return out;
  }, [layoutReviewByRoom, rooms]);
  // Review gate (utils/machineReview.js): generated symbols, rule-applied walls and template-seeded elements nobody has
  // reviewed yet, on THIS drawing. AI rooms stay in the Proposals category; they are never bulk-accepted.
  const machineSummary = useMemo(() => summarizeUnreviewed({ rooms, conditions: allTakeoffConditions, elements: roomElements }), [rooms, allTakeoffConditions, roomElements]);
  const machineGroups = useMemo(() => machineSummary.groups.filter(g => g.kind !== "room"), [machineSummary]);
  const generatedUnreviewed = machineSummary.counts.symbols + machineSummary.counts.walls + machineSummary.counts.elements;
  const canAcceptMachine = !isLocked && !!onAcceptMachine;
  async function acceptMachine(accept) {
    try { await onAcceptMachine(accept); } catch (e) { alert("Could not accept: " + (e.message || e)); }
  }
  const overlapFindings = useMemo(() => findOverlaps(allTakeoffConditions, { rooms, tunables }), [allTakeoffConditions, rooms, tunables]);
  const computedReviewQueue = useMemo(() => buildReviewQueue({
    scale: { isSet: scaleIsSet, check: scaleCheck }, hasDrawing: !!settings?.BackgroundImage,
    wallExceptions: reviewItems.length, rooms, conditions: allTakeoffConditions, elementWarnings: elementReviewWarnings, layoutConflicts, machineGroups, overlaps: overlapFindings,
  }), [scaleIsSet, scaleCheck, settings?.BackgroundImage, reviewItems.length, rooms, allTakeoffConditions, elementReviewWarnings, layoutConflicts, machineGroups, overlapFindings]);
  // Track J — the persisted workflow state (acknowledged / waived / assigned)
  // laid over the computed queue; waived items hide unless asked for.
  const [showWaived, setShowWaived] = useState(false);
  const reviewQueue = useMemo(() => applyReviewStates(computedReviewQueue, reviewStates, { showWaived }), [computedReviewQueue, reviewStates, showWaived]);
  const canReview = !isLocked && !!onSaveReviewState;
  const setReviewState = (item, patch) => onSaveReviewState?.(reviewStatePayload(item, patch, currentUserEmail));
  const waiveItem = (item) => {
    const why = window.prompt(`Waive "${item.message}"?\n\nWhy is this acceptable as-is? (recorded with your name; the item stays computed and can be reopened)`, item.state?.note || "");
    if (why == null) return;
    setReviewState(item, { status: "waived", note: why.trim() || null });
  };
  const assignItem = (item) => {
    const who = window.prompt("Assign this review item to (email). Leave empty to unassign.", item.state?.assignedTo || currentUserEmail || "");
    if (who == null) return;
    setReviewState(item, { assigned_to: who.trim() || null });
  };
  const anyToolActive = sweepMode || drawMode || !!calib || !!calibPrompt || imgMode || measureMode || condMode || routeMode || areaMode || symbolMode || backOutMode;
  // UI Phase A — Pointer: leave every tool, keep the selection.
  // A different drawing: drop everything IN PROGRESS on the old sheet (half-
  // traced outlines, a pending symbol drag, a room being placed, a back-out)
  // and the selection / open flyouts, so nothing half-done can be committed
  // onto the new sheet. The ARMED tool and its picked condition type are kept —
  // a condition (lights, doors, HEPAs …) is taken off across many drawings
  // and must not need re-arming on every page. Undo history is revision-wide
  // and is deliberately kept.
  useEffect(() => {
    setDrawRect(null); setRoomPts([]); setRoomCursor(null); setPlacingRoomId(null);
    setCondPts([]); setCondCursor(null);
    setAreaPts([]); setAreaCursor(null);
    setRoutePts([]); setRouteCursor(null);
    setSymbolDrag(null); setSymbolHover(null);
    setMeasureLine(null); setMeasuring(false);
    setSweepBox(null); setSweepDragging(false);
    cancelBackOut();
    setSelectedIds(new Set()); setSelectedAppId(null); setSelectedCondId(null);
    setDetailRoomId(null); setEditingCondId(null); setAddingCondition(false); setChaseEdgeKey(null);
    setVersionsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheetId]);

  // A drawing with an image but no scale cannot be measured: refuse the measuring tools
  // (not Verify scale / Move drawing — those are how a scale gets set) and say why.
  const measuringToolActive = drawMode || condMode || areaMode || symbolMode || routeMode || backOutMode || measureMode;
  const needsScale = sheetNeedsScale(settings);
  useEffect(() => {
    if (!needsScale || !measuringToolActive) return;
    pointerTool();
    setEditNotice(SCALE_REQUIRED_MESSAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsScale, measuringToolActive]);

  // The measuring / scale / drawing-position / count-by-example tools: each toolbar button and the command palette call these.
  function toggleMeasure() {
    setMeasureMode(m => !m); setMeasureLine(null); setMeasuring(false); setCalib(null); setImgMode(false); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setAreaMode(false); setAreaPts([]); setAreaCursor(null);
  }
  function toggleCalibrate() {
    setCalib({ points: [] }); setImgMode(false); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null); setAreaMode(false); setAreaPts([]); setAreaCursor(null);
  }
  function toggleMoveDrawing() {
    setImgMode(m => !m); setCalib(null); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null); setAreaMode(false); setAreaPts([]); setAreaCursor(null);
  }
  // Rotate the DRAWING (not a room - that is the R key). Allowed only while nothing is drawn on this drawing: the
  // takeoff is measured in the rotated frame, so turning it afterwards would leave every shape misplaced. The
  // database enforces the same rule; this just says so before the round trip.
  const drawingHasTakeoffs = !!activeSheetId && (
    rooms.some(r => r.SheetID === activeSheetId && r.CanvasX != null) ||
    allTakeoffConditions.some(c => c.SheetID === activeSheetId && c.GeometryJson));
  const rotateBlockedReason = isLocked ? "This revision is locked."
    : !settings?.BackgroundImage ? "Load a drawing first."
    : drawingHasTakeoffs ? "This drawing has takeoffs on it. Delete them first - rotate right after loading a drawing, before measuring."
    : null;
  function rotateDrawing(deltaDeg) {
    if (rotateBlockedReason) { setEditNotice(rotateBlockedReason); return; }
    onSaveCanvas({ image_rotation: (((rot + deltaDeg) % 360) + 360) % 360 });
  }
  function toggleSweep() {
    setSweepSeed(null); setSweepBox(null); setSweepDragging(false); setMeasureMode(false); setMeasureLine(null); setMeasuring(false); setCalib(null); setImgMode(false); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setAreaMode(false); setAreaPts([]); setAreaCursor(null); setSweepMode(m => !m);
  }

  // Condition strip / eye / totals. Pins are catalog keys; the eye works on the drawn keys of the rows on this drawing.
  const resolvedPins = useMemo(() => resolvePins(pins, conditionTypes), [pins, conditionTypes]);
  const drawnTypeKeys = useMemo(() => [...new Set(takeoffConditions.map(conditionTypeKey).filter(k => k !== "unassigned"))], [takeoffConditions]);
  const toggleTypeVisible = (key) => setHiddenTypes(h => toggleHidden(h, key));
  const isolateType = (key) => setHiddenTypes(h => isolate(h, key, drawnTypeKeys));
  function toggleTypePin(key) {
    const r = togglePin(pins, key);
    if (r.full) { setEditNotice(`Nine conditions are already pinned (keys 1–${MAX_PINS}) — unpin one first.`); return; }
    setPins(r.pins); writePins(r.pins);
  }
  // Double-click a Conditions dock row (or a type's header) = arm that condition again, so it can be drawn without setup.
  const armDrawnType = (drawnKey) => {
    if (isLocked) return;
    const type = conditionTypes.find(t => t.TypeKey === catalogKeyOfDrawn(drawnKey));
    if (!type) return;
    setSelectedCondIds(new Set());
    beginConditionDraw(type);
  };
  const conditionView = { hiddenTypes, pins, onToggleType: toggleTypeVisible, onIsolateType: isolateType, onTogglePin: toggleTypePin, onArmType: armDrawnType };
  const toggleCounter = () => { const next = !counterOpen; setCounterOpen(next); writeCounterOpen(next); };
  // The LEGEND is part of the drawing (utils/takeoffLegend.js): its settings are stored with the active sheet (LegendJson,
  // db/38) so everyone sees the same one and a snapshot / printout carries it. `legendDragPos` is the position while it is
  // being dragged (saved on release). It lists what the canvas is SHOWING: a condition on a hidden Takeoff Layer, hidden
  // with the eye, or whose built-in layer (Draw condition runs / Placed symbols) is off is not in it.
  const [legendDragPos, setLegendDragPos] = useState(null);
  const storedLegend = useMemo(() => parseLegend(settings?.LegendJson), [settings?.LegendJson]);
  const legend = useMemo(() => (legendDragPos ? { ...storedLegend, pos: legendDragPos } : storedLegend), [storedLegend, legendDragPos]);
  const setLegend = (patch) => { if (activeSheetId != null) onSaveSheetLegend?.(activeSheetId, legendToJson({ ...storedLegend, ...patch })); };
  const legendFtPerInch = settings?.ImageFtPerInch || (settings?.ImageScaleFtPerPx ? settings.ImageScaleFtPerPx * 96 : 8);
  const legendDefaultPos = { x: (+settings?.ImageX || 0) + legendFtPerInch * 0.4, y: (+settings?.ImageY || 0) + legendFtPerInch * 0.4 };
  const legendList = useMemo(() => (legend.on
    ? legendEntries(takeoffConditions, { keyOf: conditionTypeKey, isVisible: c => condLayerVisible(c) && !hiddenLayers.has(c.Style === "count" ? "symbols" : "conditions"), showCode: legend.code })
    : []), [legend.on, legend.code, takeoffConditions, condLayerVisible, hiddenLayers]);
  const counterHere = useMemo(() => (counterOpen ? conditionTotals(takeoffConditions, { keyOf: conditionTypeKey, quantitiesOf: conditionQuantities, hidden: hiddenTypes }) : []), [counterOpen, takeoffConditions, hiddenTypes]);
  const counterAll = useMemo(() => (counterOpen ? conditionTotals(sweepConditions, { keyOf: conditionTypeKey, quantitiesOf: conditionQuantities, hidden: hiddenTypes }) : []), [counterOpen, sweepConditions, hiddenTypes]);

  // Ctrl+K opens the command palette from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "k" || e.key === "K")) { e.preventDefault(); setPaletteOpen(o => !o); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function pointerTool() {
    setDrawMode(false); setPlacingRoomId(null); setCalib(null); setCalibPrompt(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null); setMeasuring(false); setSymbolDrag(null);
    setRouteMode(false); setRoutePick(null); setRoutePts([]); setRouteCursor(null);
    setCondMode(false); setCondPts([]); setCondCursor(null);
    setSymbolMode(false); setSymbolHover(null);
    setAreaMode(false); setAreaPts([]); setAreaCursor(null);
    cancelBackOut();
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────
  const zoomAt = useCallback((clientX, clientY, factor) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const prev = zRef.current;
    const next = clamp(prev * factor, MIN_Z, MAX_Z);
    if (next === prev) return;
    const p = panRef.current;
    const wx = (clientX - box.left - p.x) / prev;
    const wy = (clientY - box.top - p.y) / prev;
    setPan({ x: clientX - box.left - wx * next, y: clientY - box.top - wy * next });
    setPxPerFt(next);
  }, []);

  const zoomCentre = useCallback((factor) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    zoomAt(box.left + box.width / 2, box.top + box.height / 2, factor);
  }, [zoomAt]);

  // Native wheel listener so we can preventDefault (React onWheel is passive).
  // Plain scroll PANS (matches every other canvas tool — Figma, Google Maps,
  // Miro); every wheel event used to zoom regardless, which made scrolling
  // around a large sheet without a trackpad-pinch or the +/- buttons
  // impractical. Zoom requires Ctrl/Cmd+wheel — a browser also reports
  // trackpad pinch-to-zoom as a wheel event with ctrlKey=true, so pinch still
  // zooms with no separate handling needed. Shift+wheel pans horizontally —
  // read deltaY explicitly for this rather than relying on deltaX, since a
  // standard Windows mouse wheel has no horizontal axis at all and the OS
  // does NOT remap Shift+wheel into deltaX the way some trackpads/macOS do;
  // assuming it would left Shift+scroll doing nothing on a plain mouse.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
        zoomAt(e.clientX, e.clientY, factor);
      } else if (e.shiftKey) {
        const dx = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        setPan(p => ({ x: p.x - dx, y: p.y }));
      } else {
        setPan(p => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
      }
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // ── Pointer handlers ─────────────────────────────────────────────────────────
  function onPointerDown(e) {
    if (e.button !== 0) return;
    const pt = toWorld(e);
    if (condMode && !isLocked) {
      setCondPts(p => [...p, tracePoint(condPts[condPts.length - 1], pt, e.shiftKey, e.altKey)]);
      return;
    }
    if (routeMode && !isLocked) {
      setRoutePts(p => [...p, tracePoint(routePts[routePts.length - 1], pt, e.shiftKey, e.altKey)]);
      return;
    }
    if (backOutMode && !isLocked) {
      setBackOutPts(p => [...p, tracePoint(backOutPts[backOutPts.length - 1], pt, e.shiftKey, e.altKey)]);
      return;
    }
    if (areaMode && !isLocked) {
      setAreaPts(p => [...p, tracePoint(areaPts[areaPts.length - 1], pt, e.shiftKey, e.altKey)]);
      return;
    }
    if (symbolMode && !isLocked) {
      // Single click, immediate placement — not a multi-point draw like
      // Draw condition. Wall-snapped shapes (doors/windows/bifold/
      // sliding-door) need a nearby wall — no wall close enough is a no-op,
      // not an error, and the mode stays on so several can be placed in a
      // row. Every other shape is free-placed: it always succeeds at the
      // clicked point, room resolved by simple containment instead of wall
      // proximity (roomContainingPolygon treats a single point as its own
      // "centroid").
      const wallSnapped = WALL_SNAPPED_SHAPES.has(resolveShape(symbolPickType));
      const snap = wallSnapped
        ? snapToNearestWall(pt.x, pt.y, effRooms, tunables)
        : { x: pt.x, y: pt.y, angleDeg: 0, wallNormalDeg: 0, roomId: roomContainingPolygon([pt], effRooms) };
      if (snap && symbolPick && onPlaceSymbol) {
        onPlaceSymbol({
          conditionTypeId: symbolPick, x: snap.x, y: snap.y,
          angleDeg: snap.angleDeg, wallNormalDeg: snap.wallNormalDeg,
          widthFt: symbolPickType?.DefaultWidthFt || defaultSymbolWidthFt(resolveShape(symbolPickType)), roomId: snap.roomId,
        });
      }
      return;
    }
    if (sweepMode) {
      // Count by example: drag a box around ONE example symbol. Reads nothing and writes nothing.
      setSweepBox({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y, sx: e.clientX, sy: e.clientY });
      setSweepDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (measureMode) {
      // Not gated on isLocked — this never writes anything, so it stays
      // usable even against a locked/finalized revision.
      setMeasureLine({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
      setMeasuring(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (drawMode && !isLocked) {
      if (roomPts.length > 0) {
        // Already tracing an outline — every further click adds the next
        // corner, square to the previous one unless Shift is held.
        setRoomPts(p => [...p, tracePoint(roomPts[roomPts.length - 1], pt, e.shiftKey, e.altKey)]);
        return;
      }
      // Don't know yet whether this is a click (start tracing corners) or a
      // drag (draw a box) — onPointerUp decides from how far the pointer
      // actually moved. Track screen px too since world-ft movement alone
      // isn't a reliable click/drag signal at high zoom. The start point is
      // snapped now, at click precision — it becomes the first corner.
      const start = snapVertex(pt.x, pt.y, e.altKey);
      setDrawRect({ x0: start.x, y0: start.y, x1: start.x, y1: start.y, sx: e.clientX, sy: e.clientY });
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (calib) {
      // Record calibration point in world feet
      const points = [...calib.points, { wx: pt.x, wy: pt.y }];
      if (points.length < 2) { setCalib({ points }); return; }
      setCalib(null);
      const worldDist = Math.hypot(points[1].wx - points[0].wx, points[1].wy - points[0].wy);
      // Track B: measure first, then verify against the printed dimension in
      // the panel below — nothing is written until "Re-calibrate" is clicked.
      if (worldDist > 0) setCalibPrompt({ worldDist, input: "" });
      return;
    }
    if (imgMode && settings?.BackgroundImage) {
      setPanning({ startX: e.clientX, startY: e.clientY, imgX, imgY, isImage: true });
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    setSelectedIds(new Set());
    setSelectedAppId(null);
    setPanning({ startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y });
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function startRoomDrag(e, room) {
    if (e.button !== 0) return; // right-click opens the context menu, it never starts a drag
    if (isLocked || calib || imgMode || drawMode || condMode || routeMode || symbolMode) return;
    e.stopPropagation();
    setSelectedAppId(null);
    if (e.shiftKey) {
      setSelectedIds(prev => {
        const n = new Set(prev);
        if (n.has(room.RoomID)) n.delete(room.RoomID); else n.add(room.RoomID);
        return n;
      });
      return; // shift-click only toggles selection, never starts a drag
    }
    const groupIds = (selectedIds.has(room.RoomID) && selectedIds.size > 1) ? selectedIds : new Set([room.RoomID]);
    setSelectedIds(groupIds);
    // A position-locked room (or a group holding one) only selects; right-click ▸ Unlock position to move it.
    if ([...groupIds].some(id => rooms.find(x => x.RoomID === id)?.PositionLocked)) return;
    const anchors = {};
    for (const id of groupIds) {
      const r = rooms.find(x => x.RoomID === id);
      if (r) anchors[id] = { x: r.CanvasX, y: r.CanvasY };
    }
    const pt = toWorld(e);
    setDrag({ roomId: room.RoomID, dx: pt.x - room.CanvasX, dy: pt.y - room.CanvasY, anchors, sx: e.clientX, sy: e.clientY, started: false });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function startResize(e, room, edge) {
    if (isLocked || calib || imgMode || drawMode || condMode || routeMode || symbolMode) return;
    e.stopPropagation();
    const R = rectOf(room);
    if (!R) return;
    setResize({ roomId: room.RoomID, edge, x: R.x, y: R.y, startW: R.w, startH: R.h });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  // Track C — one message per refusal reason (polygonValidity, takeoffGeometry.js).
  const validityMessage = (v) => v.reasons.includes("self_intersecting") ? "That outline crosses itself — move the corner so the edges don't overlap."
    : v.reasons.includes("zero_area") ? "That outline has no area."
    : v.reasons.includes("duplicate_vertices") ? "Two corners sit on top of each other — remove one."
    : "A room needs at least three corners.";
  // Track A2 — double-click a corner handle to remove it (never below a
  // triangle, never into a self-crossing outline).
  function deleteVertex(e, room, points, index) {
    if (isLocked || !onEditRoomPolygon) return;
    e.stopPropagation();
    const r = removePolygonVertex(points, index);
    if (!r.ok) { setEditNotice(r.reason === "too_few_points" ? "A room needs at least three corners." : "That corner can't be removed — the outline would cross itself or collapse."); return; }
    onEditRoomPolygon(room, r.points);
  }

  // Drag an existing polygon corner to reshape the room. Seeds a live copy
  // of the whole point list so onPointerMove can update just this one index
  // without re-parsing PolygonJson every frame.
  function startVertexDrag(e, room, points, index) {
    if (isLocked || calib || imgMode || drawMode || condMode || routeMode || symbolMode) return;
    e.stopPropagation();
    setSelectedIds(new Set([room.RoomID]));
    setLiveVertices(p => ({ ...p, [room.RoomID]: points.map(pt => ({ ...pt })) }));
    setVertexDrag({ roomId: room.RoomID, index });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  // Drag a segment's midpoint to insert a new corner there — same
  // interaction as OST/most vector editors' "add a point on this edge".
  // Inserts immediately (at the exact midpoint) so dragging from that
  // instant feels the same as dragging an existing vertex.
  function startMidpointDrag(e, room, points, segIndex) {
    if (isLocked || calib || imgMode || drawMode || condMode || routeMode || symbolMode) return;
    e.stopPropagation();
    setSelectedIds(new Set([room.RoomID]));
    const a = points[segIndex], b = points[(segIndex + 1) % points.length];
    const mid = { x: Math.round(((a.x + b.x) / 2) * 10) / 10, y: Math.round(((a.y + b.y) / 2) * 10) / 10 };
    const next = [...points.slice(0, segIndex + 1), mid, ...points.slice(segIndex + 1)];
    setLiveVertices(p => ({ ...p, [room.RoomID]: next }));
    setVertexDrag({ roomId: room.RoomID, index: segIndex + 1 });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  // ── Double-click edit mode for a drawn linear / area condition ──────────────────────────────────────────────────
  // Double-click arms it; then drag the shape to move it, or drag the corner handle to make it larger / smaller. One
  // save per drag (an undo step), through the same transform path as rotate / flip. Esc or selecting something else ends it.
  const reshapeToolBusy = sweepMode || drawMode || !!calib || imgMode || measureMode || symbolMode || condMode || routeMode || areaMode || backOutMode;
  function beginEditCondition(c) {
    if (isLocked || reshapeToolBusy || !onTransformConditions) return;
    if (!reshapeEligible(c)) { setEditNotice("Only a condition you drew yourself can be moved or resized here. Generated walls and symbols follow their rooms."); return; }
    setSelectedCondId(c.TakeoffConditionDrawnID);
    setEditCondId(c.TakeoffConditionDrawnID);
  }
  function startReshape(e, kind) {
    const saved = savedTakeoffConditions.find(r => r.TakeoffConditionDrawnID === editCondId);
    const b = saved && reshapeBounds(saved);
    e.stopPropagation();
    if (isLocked || !b || condReshape) return;
    // The scale handle starts wherever it was grabbed, so the shape does not jump by the handle's own offset.
    const start = toWorld(e), pivot = { x: b.minX, y: b.minY };
    setCondReshape({ kind, id: saved.TakeoffConditionDrawnID, row: saved, start, sx: e.clientX, sy: e.clientY, pivot, handle: start, op: null, committing: false });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  // Pointer handlers shared by a linear run's and an area's clickable twin.
  const reshapeHit = (c, sel) => {
    const id = c.TakeoffConditionDrawnID, editing = editCondId === id;
    return {
      style: { cursor: isLocked ? "default" : editing ? "move" : "pointer" },
      onPointerDown: ev => { ev.stopPropagation(); if (editing && !reshapeToolBusy && ev.button === 0) startReshape(ev, "move"); },
      onDoubleClick: ev => { ev.stopPropagation(); beginEditCondition(c); },
      onClick: (e) => {
        if (isLocked || editing) return; // an edit-mode click (or the click that ends a drag) must not toggle the selection off
        if (e.shiftKey || e.ctrlKey || e.metaKey) toggleCondSelected(id); else setSelectedCondId(sel && selectedCondIds.size === 1 ? null : id);
      },
    };
  };
  useEffect(() => {
    if (editCondId != null && !(selectedCondIds.size === 1 && selectedCondIds.has(editCondId))) { setEditCondId(null); setCondReshape(null); }
  }, [selectedCondIds, editCondId]);

  function onPointerMove(e) {
    const ptNow = toWorld(e);
    lastWorldRef.current = ptNow;
    if (condReshape) {
      if (condReshape.committing) return;
      const r = condReshape;
      if (r.kind === "move") {
        if (!r.op && Math.hypot(e.clientX - r.sx, e.clientY - r.sy) < CLICK_PX) return;
        setCondReshape(p => (p ? { ...p, op: { type: "translate", dx: ptNow.x - r.start.x, dy: ptNow.y - r.start.y } } : p));
      } else {
        const f = scaleFactorFor(r.pivot, r.handle, ptNow);
        if (f != null) setCondReshape(p => (p ? { ...p, op: { type: "scale", factor: f, pivot: r.pivot } } : p));
      }
      return;
    }
    if (cursorElRef.current) cursorElRef.current.textContent = cursorText(ptNow, units);
    if (sweepMode || condMode || routeMode || areaMode || backOutMode || symbolMode || drawMode || calib || measureMode) {
      setCrosshairPos(toWorld(e));
    } else if (crosshairPos) {
      setCrosshairPos(null);
    }
    if (condMode) { setCondCursor(tracePoint(condPts[condPts.length - 1], toWorld(e), e.shiftKey, e.altKey)); return; }
    if (routeMode) { setRouteCursor(tracePoint(routePts[routePts.length - 1], toWorld(e), e.shiftKey, e.altKey)); return; }
    if (areaMode) { setAreaCursor(tracePoint(areaPts[areaPts.length - 1], toWorld(e), e.shiftKey, e.altKey)); return; }
    if (backOutMode) { setBackOutCursor(tracePoint(backOutPts[backOutPts.length - 1], toWorld(e), e.shiftKey, e.altKey)); return; }
    if (symbolMode) {
      const pt = toWorld(e);
      const wallSnapped = WALL_SNAPPED_SHAPES.has(resolveShape(symbolPickType));
      setSymbolHover(wallSnapped
        ? snapToNearestWall(pt.x, pt.y, effRooms, tunables)
        : { x: pt.x, y: pt.y, angleDeg: 0, wallNormalDeg: 0, roomId: roomContainingPolygon([pt], effRooms) });
      return;
    }
    if (drawMode && roomPts.length > 0) { setRoomCursor(tracePoint(roomPts[roomPts.length - 1], toWorld(e), e.shiftKey, e.altKey)); return; }
    if (sweepDragging) { const pt = toWorld(e); setSweepBox(b => (b ? { ...b, x1: pt.x, y1: pt.y } : b)); return; }
    if (measuring) { const pt = toWorld(e); setMeasureLine(m => (m ? { ...m, x1: pt.x, y1: pt.y } : m)); return; }
    if (symbolDrag) {
      const movedPx = Math.hypot(e.clientX - symbolDrag.sx, e.clientY - symbolDrag.sy);
      if (movedPx > CLICK_PX) {
        const pt = toWorld(e);
        const wallSnapped = WALL_SNAPPED_SHAPES.has(resolveShape(symbolDrag.cond));
        // A door / window slides ALONG the wall line it is already on (continuous, nothing to jump to) so it can be
        // lined up with the drawing underneath; hold Alt to pull it onto another wall instead.
        const stored = wallSnapped && !e.altKey ? parseGeom(symbolDrag.cond) : null;
        const slide = stored && Number.isFinite(+stored.x) && Number.isFinite(+stored.y) ? (() => {
          const rad = ((stored.angleDeg || 0) * Math.PI) / 180;
          const ux = Math.cos(rad), uy = Math.sin(rad);
          const t = (pt.x - stored.x) * ux + (pt.y - stored.y) * uy;
          const r3 = v => Math.round(v * 1000) / 1000;
          return { x: r3(stored.x + ux * t), y: r3(stored.y + uy * t), angleDeg: stored.angleDeg || 0, wallNormalDeg: stored.wallNormalDeg ?? 90, roomId: symbolDrag.cond.RoomID ?? null };
        })() : null;
        const snap = slide || (wallSnapped
          ? snapToNearestWall(pt.x, pt.y, effRooms, tunables)
          : { x: pt.x, y: pt.y, angleDeg: 0, wallNormalDeg: 0, roomId: roomContainingPolygon([pt], effRooms) });
        if (snap) setSymbolDrag(d => (d ? { ...d, live: snap } : d));
      }
      return;
    }
    if (drag && !drag.started) {
      if (Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) < ROOM_DRAG_PX) return;
      setDrag(d => (d ? { ...d, started: true } : d));
    }
    if (drawRect) {
      const pt = toWorld(e);
      setDrawRect(r => ({ ...r, x1: pt.x, y1: pt.y }));
    } else if (resize) {
      // resize.x/y/startW/startH are the rect's state at drag-start — the
      // anchor edge/corner (the one NOT being dragged) stays fixed, so a
      // west/north drag has to shift the origin as well as the dimension,
      // not just grow it like east/south do. Each moving edge snaps against
      // a neighboring room's facing wall (or the drawing's own real wall
      // line) via snapEdge() — the resize-drag equivalent of snapPosition(),
      // added because this used to compute w/h/x/y purely from the raw
      // cursor and only grid-round to the nearest 0.5 ft on release, with no
      // awareness of a neighbor's actual wall face (see snapEdge()'s own
      // comment in takeoffGeometry.js for the exact bug this fixes).
      const pt = toWorld(e);
      let w = resize.startW, h = resize.startH, x = resize.x, y = resize.y;
      const others = effRooms.filter(r => r.RoomID !== resize.roomId);
      if (resize.edge.includes("e")) {
        const snapped = snapEdge("x", pt.x, [y, y + h], others, wallSegments, e.altKey, tunables);
        w = Math.max(1, snapped.value - resize.x);
      }
      if (resize.edge.includes("w")) {
        const snapped = snapEdge("x", pt.x, [y, y + h], others, wallSegments, e.altKey, tunables);
        w = Math.max(1, resize.x + resize.startW - snapped.value);
        x = resize.x + resize.startW - w;
      }
      if (resize.edge.includes("s")) {
        const snapped = snapEdge("y", pt.y, [x, x + w], others, wallSegments, e.altKey, tunables);
        h = Math.max(1, snapped.value - resize.y);
      }
      if (resize.edge.includes("n")) {
        const snapped = snapEdge("y", pt.y, [x, x + w], others, wallSegments, e.altKey, tunables);
        h = Math.max(1, resize.y + resize.startH - snapped.value);
        y = resize.y + resize.startH - h;
      }
      setLiveSize(p => ({ ...p, [resize.roomId]: { w, h } }));
      setLivePos(p => ({ ...p, [resize.roomId]: { x, y } }));
    } else if (vertexDrag) {
      const pt = toWorld(e);
      const snapped = snapVertex(pt.x, pt.y, e.altKey);
      setVertexDragFree(e.altKey);
      setLiveVertices(p => {
        const arr = p[vertexDrag.roomId];
        if (!arr) return p;
        const next = [...arr];
        next[vertexDrag.index] = snapped;
        return { ...p, [vertexDrag.roomId]: next };
      });
    } else if (drag) {
      // Snap LIVE during the drag (not only on release, as before) — the
      // primary dragged room's raw cursor-tracked position runs through the
      // same snapPosition() the drop commits with, so what the estimator
      // sees while dragging is exactly what they'll get, including which
      // tier resolved it (wall > room > grid, or "free" while Alt is held to
      // reject a bad candidate — see dragSnapTier, rendered as a small
      // cursor-side label below).
      const pt = toWorld(e);
      const anchor = drag.anchors[drag.roomId];
      const deltaX = (pt.x - drag.dx) - anchor.x, deltaY = (pt.y - drag.dy) - anchor.y;
      const room = rooms.find(r => r.RoomID === drag.roomId);
      const rawX = anchor.x + deltaX, rawY = anchor.y + deltaY;
      let snapDeltaX = 0, snapDeltaY = 0;
      if (room) {
        const groupRoomIds = new Set(Object.keys(drag.anchors).map(Number));
        const others = effRooms.filter(r => !groupRoomIds.has(r.RoomID));
        const snapped = snapPosition(room, rawX, rawY, others, wallSegments, e.altKey, tunables);
        snapDeltaX = snapped.x - rawX;
        snapDeltaY = snapped.y - rawY;
        setDragSnapTier(snapped.xSource === "free" || snapped.ySource === "free" ? "free"
          : snapped.xSource === "wall" || snapped.ySource === "wall" ? "wall"
          : snapped.xSource === "room" || snapped.ySource === "room" ? "room" : "grid");
      }
      setLivePos(() => {
        const np = {};
        for (const [id, a] of Object.entries(drag.anchors)) {
          np[id] = { x: a.x + deltaX + snapDeltaX, y: a.y + deltaY + snapDeltaY };
        }
        return np;
      });
    } else if (panning?.isImage) {
      const dxFt = (e.clientX - panning.startX) / pxPerFt;
      const dyFt = (e.clientY - panning.startY) / pxPerFt;
      setPanning(p => ({ ...p, dxFt, dyFt }));
    } else if (panning) {
      setPan({ x: panning.panX + (e.clientX - panning.startX), y: panning.panY + (e.clientY - panning.startY) });
    }
  }

  function onPointerUp(e) {
    if (condReshape) {
      const r = condReshape;
      if (r.committing) return;
      if (!r.op) { setCondReshape(null); return; }
      // Keep showing the live shape until the save has landed (no snap-back), then drop the preview.
      setCondReshape({ ...r, committing: true });
      Promise.resolve(onTransformConditions?.({ rows: [r.row], op: r.op, spin: false })).finally(() => setCondReshape(null));
      return;
    }
    if (sweepDragging) {
      setSweepDragging(false);
      const b = sweepBox;
      // a click, not a drag, is not a box
      if (!b || Math.hypot(e.clientX - b.sx, e.clientY - b.sy) < 6) { setSweepBox(null); return; }
      setSweepMode(false);
      setSweepSeed({ sheetId: activeSheetId, box: { x0: b.x0, y0: b.y0, x1: b.x1, y1: b.y1 } });
      return;
    }
    if (measuring) { setMeasuring(false); return; }
    if (symbolDrag) {
      const { cond, live, multi } = symbolDrag;
      setSymbolDrag(null);
      if (live && onMoveSymbol) {
        onMoveSymbol(cond, live);
        setSelectedCondId(cond.TakeoffConditionDrawnID);
      } else {
        // No real movement — a plain click (same toggle as before), or Shift / Ctrl-click adding it to the selection.
        if (multi) toggleCondSelected(cond.TakeoffConditionDrawnID);
        else setSelectedCondId(prev => prev === cond.TakeoffConditionDrawnID ? null : cond.TakeoffConditionDrawnID);
      }
      return;
    }
    if (drawRect) {
      const movedPx = Math.hypot((e?.clientX ?? drawRect.sx) - drawRect.sx, (e?.clientY ?? drawRect.sy) - drawRect.sy);
      // The start corner was snapped at click time (onPointerDown) and is
      // kept exactly; only the dragged size is rounded (to 0.1 ft).
      const w = Math.round(Math.abs(drawRect.x1 - drawRect.x0) * 10) / 10;
      const h = Math.round(Math.abs(drawRect.y1 - drawRect.y0) * 10) / 10;
      const x = r2(Math.min(drawRect.x0, drawRect.x1));
      const y = r2(Math.min(drawRect.y0, drawRect.y1));
      const start = { x: drawRect.x0, y: drawRect.y0 };
      setDrawRect(null);
      if (movedPx < CLICK_PX) {
        // Barely moved — this was a click, not a drag. Start tracing an
        // outline instead of committing a zero-size box.
        setRoomPts([start]);
        return;
      }
      if (w >= 1 && h >= 1) commitDrawnRoom({ x, y, lengthFt: w, widthFt: h, polygon: null });
      return;
    }
    if (resize) {
      const room = rooms.find(r => r.RoomID === resize.roomId);
      const sz = liveSize[resize.roomId];
      const pos = livePos[resize.roomId];
      if (room && sz && onResizeRoom) {
        // sz/pos are already snapEdge()'s own output (magnetic wall/room
        // target, or its 0.5 ft grid fallback) from the last onPointerMove —
        // committing them as-is, not re-rounding to the grid here, is what
        // actually lets a magnetically-snapped edge land exactly flush
        // (gap 0) against a neighbor that isn't itself grid-aligned; blindly
        // grid-rounding the release position (the old behavior) could pull
        // an already-correct snap back off by up to ~0.25 ft.
        const rot90 = room.CanvasRot === 90;
        // West/north edges shift the room's anchor too (see onPointerMove): size and anchor go together,
        // as ONE save and one Undo step (two racing saves used to be able to revert the size).
        const moved = pos && (pos.x !== +room.CanvasX || pos.y !== +room.CanvasY);
        onResizeRoom(room, rot90 ? sz.h : sz.w, rot90 ? sz.w : sz.h, moved ? { x: pos.x, y: pos.y } : undefined);
      }
      setLiveSize(p => { const n = { ...p }; delete n[resize.roomId]; return n; });
      setLivePos(p => { const n = { ...p }; delete n[resize.roomId]; return n; });
      setResize(null);
      return;
    }
    if (vertexDrag) {
      const room = rooms.find(r => r.RoomID === vertexDrag.roomId);
      const points = liveVertices[vertexDrag.roomId];
      if (room && points && points.length >= 3 && onEditRoomPolygon) {
        const v = polygonValidity(points);
        if (v.ok) onEditRoomPolygon(room, points);
        else setEditNotice(`${validityMessage(v)} The corner was put back.`);
      }
      setLiveVertices(p => { const n = { ...p }; delete n[vertexDrag.roomId]; return n; });
      setVertexDrag(null);
      setVertexDragFree(false);
      return;
    }
    if (drag) {
      const room = rooms.find(r => r.RoomID === drag.roomId);
      const live = livePos[drag.roomId];
      if (room && live) {
        const groupRoomIds = new Set(Object.keys(drag.anchors).map(Number));
        const others = effRooms.filter(r => !groupRoomIds.has(r.RoomID));
        const snapped = snapPosition(room, live.x, live.y, others, wallSegments, false, tunables);
        const deltaX = snapped.x - room.CanvasX, deltaY = snapped.y - room.CanvasY;
        // the dragged room plus the rest of the selected group: one gesture, one Undo step
        const moves = [{ room, x: snapped.x, y: snapped.y, rot: room.CanvasRot || 0 }];
        for (const [idStr, a] of Object.entries(drag.anchors)) {
          const id = Number(idStr);
          if (id === room.RoomID) continue;
          const r = rooms.find(x => x.RoomID === id);
          if (r) moves.push({ room: r, x: Math.round((a.x + deltaX) * 2) / 2, y: Math.round((a.y + deltaY) * 2) / 2, rot: r.CanvasRot || 0 });
        }
        if (onMoveRooms) onMoveRooms(moves); else moves.forEach(m => onMoveRoom(m.room, m.x, m.y, m.rot));
        setLivePos({});
      }
      setDrag(null);
      setDragSnapTier(null);
    }
    if (panning?.isImage && (panning.dxFt || panning.dyFt)) {
      onSaveCanvas({ image_x: imgX + panning.dxFt, image_y: imgY + panning.dyFt });
    }
    setPanning(null);
  }

  // ── Keyboard shortcuts (canvas must be focused) ───────────────────────────────
  function onKeyDown(e) {
    if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA") return;
    if (e.key === "?") { e.preventDefault(); setGuideOpen(o => !o); return; }
    // Rotate / spin / flip the selected drawn conditions — OST's keys: Ctrl+R rotate (Shift = the other way), Ctrl+S spin in place
    if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "r" || e.key === "R" || e.key === "s" || e.key === "S" || ((e.key === "h" || e.key === "H" || e.key === "v" || e.key === "V") && e.shiftKey))) {
      e.preventDefault(); // the browser's own reload / save / history must not fire over the drawing
      const rows = takeoffConditions.filter(c => selectedCondIds.has(c.TakeoffConditionDrawnID));
      const k = e.key.toLowerCase();
      if (k === "r") transformSelectedConditions(rows, { type: "rotate", deg: e.shiftKey ? -ROTATE_STEP_DEG : ROTATE_STEP_DEG });
      else if (k === "s") { if (rows.length && !isLocked) { const deg = spinPrompt(); if (deg != null) transformSelectedConditions(rows, { type: "rotate", deg }, true); } else transformSelectedConditions(rows, null); }
      else transformSelectedConditions(rows, { type: k === "h" ? "flipH" : "flipV" });
      return;
    }
    // Copy / paste / duplicate drawn conditions, trace another like the selected one (utils/conditionClipboard.js)
    if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === "c" || e.key === "C") && selectedCondIds.size > 0) {
      e.preventDefault();
      const r = copyableRows(takeoffConditions.filter(c => selectedCondIds.has(c.TakeoffConditionDrawnID)));
      clipboardRef.current = r.rows.length ? r.rows : clipboardRef.current;
      setEditNotice(copyNotice(r.rows.length, r.skipped));
      return;
    }
    if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === "v" || e.key === "V") && clipboardRef.current?.length && onDuplicateConditions) {
      e.preventDefault();
      if (!isLocked) onDuplicateConditions({ items: pasteItems(clipboardRef.current, { at: lastWorldRef.current }), sheetId: activeSheetId });
      return;
    }
    if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (e.key === "d" || e.key === "D") && selectedCondIds.size > 0 && onDuplicateConditions) {
      e.preventDefault();
      if (isLocked) return;
      const r = copyableRows(takeoffConditions.filter(c => selectedCondIds.has(c.TakeoffConditionDrawnID)));
      if (r.rows.length) onDuplicateConditions({ items: pasteItems(r.rows, {}), sheetId: activeSheetId });
      else setEditNotice(copyNotice(0, r.skipped));
      return;
    }
    if (!e.ctrlKey && !e.metaKey && !e.altKey && (e.key === "t" || e.key === "T") && selectedCondId && !isLocked) {
      const cond = takeoffConditions.find(c => c.TakeoffConditionDrawnID === selectedCondId);
      const type = cond && conditionTypes.find(t => t.TypeKey === catalogKeyOfDrawn(conditionTypeKey(cond)));
      if (type) { e.preventDefault(); setSelectedCondIds(new Set()); beginConditionDraw(type); return; }
    }
    // 1–9 arm the pinned condition with that number (the strip under the toolbar)
    if (!e.ctrlKey && !e.metaKey && !e.altKey && /^[1-9]$/.test(e.key) && !isLocked) {
      const t = typeForDigit(resolvedPins, +e.key);
      if (t) { e.preventDefault(); beginConditionDraw(t); return; }
    }
    // A2 — undo / redo of committed edits (RoomTakeoff's session stack).
    if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) { e.preventDefault(); if (isLocked) return; if (e.shiftKey) { if (undoInfo?.canRedo) onRedo?.(); } else if (undoInfo?.canUndo) onUndo?.(); return; }
    if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) { e.preventDefault(); if (!isLocked && undoInfo?.canRedo) onRedo?.(); return; }
    // A3 — fast sheet switching: [ previous · ] next, in display order, wrapping.
    if (!e.ctrlKey && !e.metaKey && !e.altKey && (e.key === "[" || e.key === "]") && sheets.length > 1) {
      e.preventDefault(); const id = adjacentSheetId(sheets, activeSheetId, e.key === "]" ? 1 : -1); if (id != null) onSelectSheet?.(id); return;
    }
    if (drawMode && roomPts.length > 0) {
      if (e.key === "Enter") { e.preventDefault(); finishRoomPolyline(); return; }
      // Escape undoes just the last corner (same as Backspace) so a
      // mis-click doesn't cost the whole outline — only once there's
      // nothing left to undo does it back all the way out of the tool.
      if (e.key === "Escape") {
        e.preventDefault();
        if (roomPts.length > 1) { setRoomPts(p => p.slice(0, -1)); return; }
        setRoomPts([]); setRoomCursor(null); setDrawMode(false); return;
      }
      if (e.key === "Backspace") { e.preventDefault(); setRoomPts(p => p.slice(0, -1)); return; }
    }
    if (backOutMode) {
      if (e.key === "Enter") { e.preventDefault(); finishBackOut(); return; }
      if (e.key === "Escape") {
        if (backOutPts.length > 1) { e.preventDefault(); setBackOutPts(p => p.slice(0, -1)); return; }
        cancelBackOut(); return;
      }
      if (e.key === "Backspace") { e.preventDefault(); setBackOutPts(p => p.slice(0, -1)); return; }
    }
    if (areaMode) {
      if (e.key === "Enter") { e.preventDefault(); finishAreaCondition(); return; }
      if (e.key === "Escape") {
        if (areaPts.length > 1) { e.preventDefault(); setAreaPts(p => p.slice(0, -1)); return; }
        setAreaPts([]); setAreaCursor(null); setAreaMode(false); return;
      }
      if (e.key === "Backspace") { e.preventDefault(); setAreaPts(p => p.slice(0, -1)); return; }
    }
    if (routeMode) {
      if (e.key === "Enter") { e.preventDefault(); finishRouteDraw(); return; }
      if (e.key === "Escape") {
        if (routePts.length > 1) { e.preventDefault(); setRoutePts(p => p.slice(0, -1)); return; }
        setRoutePts([]); setRouteCursor(null); setRouteMode(false); setRoutePick(null); return;
      }
      if (e.key === "Backspace") { e.preventDefault(); setRoutePts(p => p.slice(0, -1)); return; }
    }
    if (condMode) {
      if (e.key === "Enter") { e.preventDefault(); finishConditionRun(); return; }
      if (e.key === "Escape") {
        if (condPts.length > 1) { e.preventDefault(); setCondPts(p => p.slice(0, -1)); return; }
        setCondPts([]); setCondCursor(null); setCondMode(false); return;
      }
      if (e.key === "Backspace") { e.preventDefault(); setCondPts(p => p.slice(0, -1)); return; }
    }
    if (symbolMode && e.key === "Escape") { setSymbolMode(false); setSymbolHover(null); return; }
    if (selectedAppId && (e.key === "Delete" || e.key === "Backspace") && !isLocked) {
      const app = scopeApps.find(a => a.ScopeApplicationID === selectedAppId);
      if (app) { e.preventDefault(); onDeleteScopeApp?.(app); setSelectedAppId(null); return; }
    }
    if (selectedCondIds.size > 1 && (e.key === "Delete" || e.key === "Backspace") && !isLocked) {
      e.preventDefault();
      const rows = takeoffConditions.filter(c => selectedCondIds.has(c.TakeoffConditionDrawnID) && !c.AutoWallKey);
      if (rows.length) onDeleteTakeoffConditions?.(rows);
      setSelectedCondIds(new Set());
      return;
    }
    if (selectedCondId && (e.key === "Delete" || e.key === "Backspace") && !isLocked) {
      const cond = takeoffConditions.find(c => c.TakeoffConditionDrawnID === selectedCondId);
      if (cond && !cond.AutoWallKey) { e.preventDefault(); onDeleteTakeoffCondition?.(cond); setSelectedCondId(null); return; }
    }
    if (selectedCondId && (e.key === "s" || e.key === "S") && !isLocked && onFlipSymbol) {
      const cond = takeoffConditions.find(c => c.TakeoffConditionDrawnID === selectedCondId && c.Style === "count");
      if (cond) { e.preventDefault(); onFlipSymbol(cond); return; }
    }
    // Rooms are deleted from the right-click menu only (no key, no on-canvas button).
    if (e.key === "=" || e.key === "+") { e.preventDefault(); zoomCentre(ZOOM_STEP); }
    else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomCentre(1 / ZOOM_STEP); }
    else if (e.key === "f" || e.key === "F") { e.preventDefault(); fitView(); }
    else if ((e.key === "z" || e.key === "Z") && selectedIds.size > 0) { e.preventDefault(); fitSelection(); }
    else if ((e.key === "r" || e.key === "R") && selectedId) { e.preventDefault(); rotateSelected(); }
    else if ((e.key === "d" || e.key === "D") && !isLocked && !e.ctrlKey && !e.metaKey) { e.preventDefault(); toggleDrawMode(); }
    else if ((e.key === "m" || e.key === "M") && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); setFocusMode(f => !f); }
    else if (e.key === "Escape") {
      // Esc cancels whatever canvas tool is active first; only when nothing
      // is active does it also close the (non-modal) Room Details flyout —
      // so cancelling a mis-clicked corner mid-trace doesn't throw away the
      // room set-up sitting in the flyout next to it.
      const toolActive = sweepMode || drawMode || !!calib || imgMode || measureMode || measuring || !!symbolDrag || roomPts.length > 0 || symbolMode || condMode || routeMode || areaMode || backOutMode || editCondId != null;
      setEditCondId(null); setCondReshape(null);
      setDrawMode(false); setPlacingRoomId(null); setCalib(null); setCalibPrompt(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null); setMeasuring(false); setSymbolDrag(null); setSelectedIds(new Set()); setSelectedAppId(null); setSelectedCondId(null);
      setSweepMode(false); setSweepBox(null); setSweepDragging(false);
      setRouteMode(false); setRoutePick(null); setRoutePts([]); setRouteCursor(null);
      cancelBackOut();
      if (!toolActive) { setDetailRoomId(null); setFocusMode(false); }
    }
  }

  // ── Toolbar actions ──────────────────────────────────────────────────────────
  // Shared file-reading for both "Replace/Load drawing" (this sheet) and
  // "+ Add sheet" (a new one) — throws with a user-facing message on failure.
  async function readDrawingFile(file) {
    if (file.type === "application/pdf") {
      const dataUrl = await pdfFileToDataUrl(file);
      if (dataUrl.length > PDF_MAX_LEN) throw new Error("That PDF is too large to store. Export a single sheet (or a lower-detail PDF) and try again.");
      return { dataUrl, isPdf: true };
    }
    const dataUrl = await fileToDataUrl(file);
    if (dataUrl.length > 4_500_000) throw new Error("Image is too large even after downscaling — crop the plan and try again.");
    return { dataUrl, isPdf: false };
  }

  async function uploadDrawing(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { dataUrl, isPdf: pdf } = await readDrawingFile(file);
      const fileHash = await sha256Hex(dataUrl);
      if (settings?.BackgroundImage) {
        // Replacing a drawing's FILE: the very same file is a no-op; a different one is archived as a version.
        if (sameFile(fileHash, settings.FileHash)) { setEditNotice("That is the same file already on this drawing — nothing was changed."); return; }
        const hasTakeoffs = rooms.some(r => r.CanvasX != null) || allTakeoffConditions.some(c => c.GeometryJson);
        const msg = hasTakeoffs
          ? "Replace this drawing's file?\n\nIts takeoffs were measured on the current file. The current file is kept as a version (Versions > Restore), the scale is kept, and the drawing is marked Needs review until you check the takeoffs still line up."
          : "Replace this drawing's file? The current file is kept as a version you can restore.";
        if (!window.confirm(msg)) return;
      }
      // The FIRST drawing of a revision is named from its file (and its sheet number / discipline read from the file
      // name), the same as "+ Add sheet" does — it used to be called "Sheet 1" (feedback #17 / #18).
      const firstName = settings?.SheetID ? {} : (() => { const h = parseSheetMetaFromName(file.name); return { sheet_name: (h.name || file.name.replace(/\.[^.]+$/, "")).slice(0, 200), sheet_number: h.sheetNumber || null, discipline: h.discipline || null }; })();
      onSaveCanvas({ ...(pdf ? { background_image: dataUrl, image_is_pdf: 1, image_page: 1 } : { background_image: dataUrl, image_is_pdf: 0 }), file_hash: fileHash, file_name: file.name.slice(0, 255), ...firstName });
      setImgMode(false); setCalib(null); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    } catch (err) { alert(err.message); }
  }

  async function addSheetFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onAddSheet) return;
    // A3 — a sheet number / discipline read from the file name is only a hint; ⚙ Manage edits both.
    const hint = parseSheetMetaFromName(file.name);
    const name = window.prompt(`Name this sheet (e.g. "Site Plan", "Electrical Single-Line"):${hint.sheetNumber ? `\n\nSheet number ${hint.sheetNumber}${hint.discipline ? ` (${disciplineLabel(hint.discipline)})` : ""} read from the file name — edit it under ⚙ Manage if wrong.` : ""}`, hint.name || file.name.replace(/\.[^.]+$/, ""));
    if (!name || !name.trim()) return;
    try {
      const { dataUrl, isPdf: pdf } = await readDrawingFile(file);
      const fileHash = await sha256Hex(dataUrl);
      const dup = sheets.find(s => sameFile(s.FileHash, fileHash));
      if (dup && !window.confirm(`This file is already loaded as "${dup.SheetName}". Add it again as a new drawing?`)) return;
      onAddSheet({ sheetName: name.trim(), backgroundImage: dataUrl, imageIsPdf: pdf, sheetNumber: hint.sheetNumber, discipline: hint.discipline, fileHash, fileName: file.name.slice(0, 255) });
      setImgMode(false); setCalib(null); setDrawMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    } catch (err) { alert(err.message); }
  }

  function pickScale(ftPerInchValue) {
    if (isLocked) return;
    // prevScale = the scale the drawing is shown at NOW (incl. the provisional one), so the page can
    // rescale the takeoffs already drawn on it when the scale changes.
    onSaveCanvas({ image_ft_per_inch: ftPerInchValue }, { prevScale: ftPerInch });
  }

  function placeRoom(room) {
    if (isLocked) return;
    const b = layoutBounds(effRooms);
    const x = b ? b.maxX + 4 : 0;
    const y = b ? b.minY : 0;
    onMoveRoom(room, x, y, room.CanvasRot || 0);
  }

  function rotateSelected() {
    const room = rooms.find(r => r.RoomID === selectedId);
    if (!room || isLocked || !rectOf(room)) return;
    onMoveRoom(room, room.CanvasX, room.CanvasY, room.CanvasRot === 90 ? 0 : 90);
  }

  // Re-centers the main view on a world point — the dock's Conditions/Rooms
  // panels use this to "jump to" a row without hunting for it on the canvas.
  function jumpTo(worldX, worldY) {
    const svgW = svgRef.current?.getBoundingClientRect().width || 800;
    setPan({ x: svgW / 2 - worldX * pxPerFt, y: canvasHeight / 2 - worldY * pxPerFt });
  }

  // Track I — select the room and light up the drawn rows a quantity came from.
  // Rotate / spin / flip the selected drawn conditions (the OST Rotate/Flip menu). Same function for the keys and the menu.
  async function transformSelectedConditions(rows, op, spin = false) {
    if (isLocked) { setEditNotice("This revision is locked."); return; }
    if (!rows?.length) { setEditNotice("Select a drawn condition first."); return; }
    if (!onTransformConditions) return;
    const notice = await onTransformConditions({ rows, op, spin });
    if (notice) setEditNotice(notice);
  }
  function spinPrompt() {
    const raw = window.prompt("Spin by how many degrees? Positive turns clockwise, negative counter-clockwise.", "90");
    const deg = raw == null ? null : Number(String(raw).replace(/[°\s]/g, ""));
    return Number.isFinite(deg) && deg !== 0 ? deg : null;
  }

  // Right-click on a room or a drawn condition: a menu of what can be done to it. It adds no behavior of its own — every
  // item calls the handler a toolbar button / key already uses. While a drawing tool is armed the click is left alone.
  function onCanvasContextMenu(ev) {
    if (sweepMode || condMode || routeMode || symbolMode || drawMode || areaMode || backOutMode || calib || measureMode || imgMode) return;
    const el = ev.target?.closest?.("[data-cond-id],[data-room-id]");
    if (!el) return;
    const locked = isLocked;
    const items = [];
    if (el.dataset.condId) {
      const id = Number(el.dataset.condId);
      const c = takeoffConditions.find(x => x.TakeoffConditionDrawnID === id);
      if (!c) return;
      const group = selectedCondIds.has(id) && selectedCondIds.size > 1 ? takeoffConditions.filter(x => selectedCondIds.has(x.TakeoffConditionDrawnID)) : [c];
      if (!selectedCondIds.has(id)) setSelectedCondIds(new Set([id])); // selection follows the right-click, as on the desktop
      const type = conditionTypes.find(t => t.TypeKey === catalogKeyOfDrawn(conditionTypeKey(c)));
      const copyable = copyableRows(group);
      items.push({ heading: group.length > 1 ? `${group.length} conditions` : c.Name || c.Code || "Condition" });
      items.push({ label: "Details…", onClick: () => setEditingCondId(id) });
      items.push({ label: "Duplicate", hint: "Ctrl+D", disabled: locked || !onDuplicateConditions || !copyable.rows.length, why: locked ? "Locked revision" : !copyable.rows.length ? "Generated rows are not copied" : undefined,
        onClick: () => onDuplicateConditions({ items: pasteItems(copyable.rows, {}), sheetId: activeSheetId }) });
      items.push({ label: "Copy", hint: "Ctrl+C", disabled: !copyable.rows.length, why: !copyable.rows.length ? "Generated rows are not copied" : undefined,
        onClick: () => { clipboardRef.current = copyable.rows; setEditNotice(copyNotice(copyable.rows.length, copyable.skipped)); } });
      items.push({ label: "Trace another like this", hint: "T", disabled: locked || !type || group.length > 1, why: locked ? "Locked revision" : !type ? "This type is not in the catalog" : undefined,
        onClick: () => { setSelectedCondIds(new Set()); beginConditionDraw(type); } });
      const canXform = !locked && !!onTransformConditions && copyable.rows.length > 0;
      const xwhy = locked ? "Locked revision" : !copyable.rows.length ? "Generated rows are not rotated" : undefined;
      items.push({ separator: true });
      items.push({ heading: "Rotate / flip" });
      items.push({ label: "Rotate right 90°", hint: "Ctrl+R", disabled: !canXform, why: xwhy, onClick: () => transformSelectedConditions(group, { type: "rotate", deg: ROTATE_STEP_DEG }) });
      items.push({ label: "Rotate left 90°", hint: "Ctrl+Shift+R", disabled: !canXform, why: xwhy, onClick: () => transformSelectedConditions(group, { type: "rotate", deg: -ROTATE_STEP_DEG }) });
      items.push({ label: "Spin in place…", hint: "Ctrl+S", disabled: !canXform, why: xwhy, onClick: () => { const deg = spinPrompt(); if (deg != null) transformSelectedConditions(group, { type: "rotate", deg }, true); } });
      items.push({ label: "Flip horizontal", hint: "Ctrl+Shift+H", disabled: !canXform, why: xwhy, onClick: () => transformSelectedConditions(group, { type: "flipH" }) });
      items.push({ label: "Flip vertical", hint: "Ctrl+Shift+V", disabled: !canXform, why: xwhy, onClick: () => transformSelectedConditions(group, { type: "flipV" }) });
      items.push({ separator: true });
      items.push({ label: "Show in Conditions dock", onClick: () => openDockPanel?.("conditions") });
      items.push({ separator: true });
      const deletable = group.filter(x => !x.AutoWallKey);
      items.push({ label: group.length > 1 ? `Delete ${deletable.length}` : "Delete", hint: "Del", danger: true, disabled: locked || !deletable.length, why: locked ? "Locked revision" : "Rule-applied walls are changed on Review · Walls",
        onClick: () => { if (deletable.length === 1) onDeleteTakeoffCondition?.(deletable[0]); else onDeleteTakeoffConditions?.(deletable); setSelectedCondIds(new Set()); } });
    } else {
      const id = Number(el.dataset.roomId);
      const room = rooms.find(r => r.RoomID === id);
      if (!room) return;
      const group = selectedIds.has(id) && selectedIds.size > 1 ? rooms.filter(r => selectedIds.has(r.RoomID)) : [room];
      if (!selectedIds.has(id)) setSelectedIds(new Set([id]));
      items.push({ heading: group.length > 1 ? `${group.length} rooms` : `${room.RoomNumber ? `${room.RoomNumber} · ` : ""}${room.RoomName || "Room"}` });
      items.push({ label: "Room details…", disabled: group.length > 1, onClick: () => setDetailRoomId(id) });
      const allLocked = group.every(r => r.PositionLocked);
      items.push({ label: allLocked ? "Unlock position" : "Lock position", hint: allLocked ? "" : "stops accidental moves", disabled: locked || !onSetRoomLocked, why: locked ? "Locked revision" : undefined,
        onClick: () => onSetRoomLocked(group, !allLocked) });
      items.push({ label: "Zoom to room", disabled: group.length > 1, onClick: () => fitToRooms([room]) });
      items.push({ separator: true });
      items.push({ label: group.length > 1 ? `Delete ${group.length} rooms` : "Delete room", danger: true, disabled: locked || !onDeleteRooms, why: locked ? "Locked revision" : undefined,
        onClick: () => { onDeleteRooms(group); setSelectedIds(new Set()); } });
    }
    ev.preventDefault();
    ev.stopPropagation();
    setCtxMenu({ x: ev.clientX, y: ev.clientY, items });
  }

  function highlightGeometry({ roomId, conditionIds = [] }) {
    const room = effRooms.find(r => r.RoomID === roomId);
    if (room) {
      setSelectedIds(new Set([room.RoomID]));
      const R = rectOf(room);
      if (R && Number.isFinite(R.x)) jumpTo(R.x + R.w / 2, R.y + R.h / 2);
    }
    setHighlightCondIds(new Set(conditionIds.filter(id => id != null)));
  }

  // Track A2 — frame a set of rooms (Fit = every placed room; Zoom sel / Z = the selection).
  function fitToRooms(list) {
    const b = boundsOfRooms(list);
    if (!b || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    const scale = Math.min((box.width - 80) / Math.max(1, b.maxX - b.minX), (box.height - 80) / Math.max(1, b.maxY - b.minY));
    const z = clamp(scale, MIN_Z, MAX_Z);
    setPxPerFt(z);
    setPan({ x: 40 - b.minX * z, y: 40 - b.minY * z });
  }
  // Fit = the placed rooms when there are any, otherwise the whole drawing.
  function fitView() { if (boundsOfRooms(effRooms)) fitToRooms(effRooms); else fitToSheet(); }
  function fitSelection() {
    const sel = effRooms.filter(r => selectedIds.has(r.RoomID));
    if (sel.length) fitToRooms(sel); else fitView();
  }

  // Commits a traced room outline as a room. A real traced shape (3+ clicked
  // corners) carries its own polygon through — same storage convention an
  // AI-extracted room already uses (PolygonJson: {points,areaFt2,perimeterFt},
  // see db/21_room_takeoff.sql) — instead of collapsing to its bounding box;
  // the canvas already knows how to render a room's true polygon shape. A
  // 2-click "opposite corners" shortcut (same as the drag-box path) is just
  // a rectangle — there's no extra shape to capture. LengthFt/WidthFt/
  // CanvasX/CanvasY are still the polygon's bounding box either way, so
  // every rect-only feature (resize handles, adjacency, wall-symbol snap)
  // keeps working unchanged.
  function finishRoomPolyline() {
    // Dedupe consecutive coincident points (double-click adds a repeat)
    const pts = cleanTracePoints(roomPts, { tolFt: traceTolFt(), closed: true });
    setRoomPts([]); setRoomCursor(null);
    if (pts.length < 2) return;
    if (pts.length >= 3) { const v = polygonValidity(pts); if (!v.ok) { setEditNotice(`${validityMessage(v)} Nothing was placed.`); return; } }
    // The bounding box is the traced shape's exact extent (0.01 ft) — it used
    // to round to 0.5 ft, which put the box up to 3" off the polygon it
    // supposedly bounds.
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    const x = snapToSixteenth(Math.min(...xs)), y = snapToSixteenth(Math.min(...ys));
    const w = snapToSixteenth(Math.max(...xs) - Math.min(...xs));
    const h = snapToSixteenth(Math.max(...ys) - Math.min(...ys));
    if (w < 1 || h < 1) return;
    const polygon = pts.length >= 3
      ? {
          points: pts.map(p => ({ x: r2(p.x), y: r2(p.y) })),
          areaFt2: Math.round(polygonArea(pts) * 10) / 10,
          perimeterFt: Math.round(polygonPerimeter(pts) * 10) / 10,
        }
      : null;
    commitDrawnRoom({ x, y, lengthFt: w, widthFt: h, polygon });
  }

  // A finished drawing (box or traced outline) becomes a room one of two ways:
  // - Placing flow: the room already exists (created unplaced by "+ Add Room",
  //   or picked from the schedule) and this drawing just PLACES it
  //   (placingRoomId). One room per draw session, so draw mode exits
  //   afterwards — the Room Details flyout, if open, STAYS open and its L/W
  //   fields refresh from the placed geometry.
  // - Legacy/direct draw mode: prompt for a name and create the room in one go.
  function commitDrawnRoom(geom) {
    const placing = placingRoomId != null ? rooms.find(r => r.RoomID === placingRoomId) : null;
    if (placing && onPlaceDrawnRoom) {
      onPlaceDrawnRoom(placing, geom);
      setPlacingRoomId(null);
      setDrawMode(false);
      return;
    }
    if (!onAddRoom) return;
    setPickGeom(geom);   // choose an unplaced room from the schedule, or name a new one (RoomPickDialog)
  }

  // Enter draw mode targeting one existing room (place it, or re-trace it).
  // Shared by "+ Add Room" and the flyout's "Draw this room" button.
  function startDrawingRoom(roomId) {
    setPlacingRoomId(roomId);
    if (!drawMode) toggleDrawMode();
  }

  // "+ Add Room" (Rooms dock): create the room unplaced, open Room Details
  // AND enter draw mode for it at the same time — the flyout is non-modal, so
  // the estimator sets the room up (name, template → ACH/classification,
  // height, criteria) while tracing where it is on the canvas, in parallel.
  // Per direct feedback (2026-09-11): the earlier version made drawing wait
  // until the flyout closed, which forced a sequence that isn't how anyone
  // works. Falls back to the plain draw-then-name flow if the parent didn't
  // provide onCreateRoom.
  async function startAddRoom() {
    if (!onCreateRoom) { toggleDrawMode(); return; }
    const id = await onCreateRoom({ name: "New Room" });
    if (id == null) return;
    setDetailRoomId(id);
    startDrawingRoom(id);
  }
  // Closing the flyout leaves any in-progress drawing alone — the placing
  // hint on the canvas still names the room.
  function closeRoomDetail() {
    setDetailRoomId(null);
  }

  // Room Build-Up "Add & Place" / "Place": enter symbol-placement mode for an
  // element's own Item/Assembly (its Style='count' symbol) with the flyout
  // still open — click the wall/room location on the canvas to place each one.
  function placeElement(entity) {
    const key = entity.Kind === "item" ? `item:${entity.ItemID}` : `asm:${entity.AssemblyID}`;
    const type = symbolConditionTypes.find(c => c.TypeKey === key);
    if (!type) {
      alert(`"${entity.Name || entity.Code}" has no canvas symbol yet — give it Style = Count and a Shape under Setup ▸ Estimate Engine ▸ Items.`);
      return;
    }
    beginConditionDraw(type);
  }

  // Entry point for room drawing — was previously only a top-toolbar button;
  // now also triggered by the Rooms dock's "+ Add Room" (see the dock render
  // below), so it needs to be a named function like its condition/area
  // siblings rather than inlined onto one button.
  function toggleDrawMode() {
    const on = !drawMode;
    setDrawMode(on); setCondMode(false); setAreaMode(false); setAreaPts([]); setAreaCursor(null); cancelBackOut();
    setCalib(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    if (on) { setSymbolMode(false); setSymbolHover(null); }
    else setPlacingRoomId(null); // leaving draw mode abandons a pending placement — the room stays in the schedule, unplaced
  }

  // Snap a scope/condition/room vertex to nearby hard geometry — an existing
  // room's real corner, the drawing's own wall-line endpoints/intersections,
  // or (1-D) a wall line — within tunables.VERTEX_SNAP_PX at the current zoom (see
  // snapDrawnPoint, takeoffGeometry.js). Precision follows zoom: at 20 px/ft
  // that's ~5", at 2 px/ft ~4 ft. Replaces a fixed 1.4 ft corner tolerance
  // that, zoomed in, yanked a click placed exactly on one corner onto the
  // corner next to it. Otherwise the raw point rounded to 0.01 ft (float
  // noise only — no grid; a 0.5 ft grid used to shift traced corners by up
  // to 3"). disableSnap (Alt held — see snapPosition's own comment in
  // takeoffGeometry.js) skips snapping entirely.
  const r2 = v => Math.round(v * 100) / 100;
  function snapVertex(x, y, disableSnap = false) {
    if (disableSnap) return { x: r2(x), y: r2(y) };
    const s = snapDrawnPoint({ x, y }, effRooms, wallSegments, tunables.VERTEX_SNAP_PX / pxPerFt);
    return { x: r2(s.x), y: r2(s.y) };
  }

  // Next vertex of any traced polyline (room / scope / condition / area):
  // snapped as above and, unless Shift is held (free angle), angle-locked to
  // the nearest DRAW_ANGLE_STEP_DEG increment from the previous vertex — the snap
  // contributes only its component ALONG the locked ray (orthoSnap), so a
  // nearby snap target can slide the point along the line but never tilt it.
  // With no previous vertex (first click) it's just the snapped point.
  function tracePoint(prev, raw, freeAngle, disableSnap) {
    const snapped = snapVertex(raw.x, raw.y, disableSnap);
    if (!prev || freeAngle) return snapped;
    const o = orthoSnap(prev, raw, snapped, tunables.DRAW_ANGLE_STEP_DEG);
    return { x: r2(o.x), y: r2(o.y) };
  }

  // ── Condition Library drawing (cost-code takeoff) ─────────────────────────────
  // A linear run (trace-a-line), a placed count symbol (click-to-place), and a
  // closed area (trace-a-polygon) are all just different Style values on the
  // same TakeoffConditionLibrary type — so the Conditions dock's "+ Add
  // Condition" (and its type picker) is a single entry point for all three;
  // picking a type is enough to know which underlying mode to enter. Replaces
  // the three separate top-toolbar buttons ("Draw condition"/"Place symbol"/
  // "Draw area") this used to take three different toggle*Mode functions to
  // drive.
  // ── Phase G canvas routing — trace a distribution route segment ───────────────
  function beginRouteDraw(segmentId) {
    if (segmentId == null || isLocked) return;
    setDrawMode(false); setCalib(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    stopConditionDraw();
    setRoutePick(segmentId); setRoutePts([]); setRouteCursor(null); setRouteMode(true);
    openDockPanel("routes");
  }
  function finishRouteDraw() {
    const pts = cleanTracePoints(routePts, { tolFt: traceTolFt() });
    const segmentId = routePick;
    setRoutePts([]); setRouteCursor(null); setRouteMode(false); setRoutePick(null);
    if (pts.length >= 2 && segmentId != null && onDrawRouteSegment) {
      const lengthFt = Math.round(polylineLength(pts) * 10) / 10;
      onDrawRouteSegment({ segmentId, points: pts.map(p => ({ x: +p.x.toFixed(2), y: +p.y.toFixed(2) })), lengthFt });
    }
  }

  function beginConditionDraw(type) {
    if (!type) return;
    setRouteMode(false); setRoutePick(null); setRoutePts([]); setRouteCursor(null);
    setDrawMode(false); setCalib(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    setCondMode(false); setCondPts([]); setCondCursor(null);
    setSymbolMode(false); setSymbolHover(null);
    setAreaMode(false); setAreaPts([]); setAreaCursor(null);
    cancelBackOut();
    if (type.Style === "count") { setSymbolPick(type.TypeKey); setSymbolMode(true); }
    else if (type.Style === "area") { setAreaPick(type.TypeKey); setAreaMode(true); }
    else { setCondPick(type.TypeKey); setCondMode(true); }
  }

  function stopConditionDraw() {
    setCondMode(false); setCondPts([]); setCondCursor(null);
    setSymbolMode(false); setSymbolHover(null);
    setAreaMode(false); setAreaPts([]); setAreaCursor(null);
  }

  // Switching to another type of the SAME style (one linear wall system to another) just re-tags what is being traced,
  // without resetting in-progress points; switching STYLE (linear / count / area) is a different drawing mechanic, so
  // that starts fresh. Shared by the dock's type picker and the Add Condition window.
  function switchConditionType(type) {
    if (!type) return;
    if (type.Style === activeConditionStyle) {
      if (type.Style === "count") setSymbolPick(type.TypeKey);
      else if (type.Style === "area") setAreaPick(type.TypeKey);
      else setCondPick(type.TypeKey);
    } else {
      beginConditionDraw(type);
    }
  }

  function finishConditionRun() {
    const pts = cleanTracePoints(condPts, { tolFt: traceTolFt() });
    setCondPts([]); setCondCursor(null);
    if (pts.length >= 2 && condPick && onCreateTakeoffCondition) {
      const lengthFt = Math.round(polylineLength(pts) * 10) / 10;
      const { primaryRoomId } = roomsAlongPolyline(pts, effRooms);
      onCreateTakeoffCondition({
        conditionTypeId: condPick,
        points: pts.map(p => ({ x: +p.x.toFixed(2), y: +p.y.toFixed(2) })),
        lengthFt,
        roomId: primaryRoomId,
      });
    }
  }

  // ── Back-out drawing (Takeoff Canvas 2.0 D-2) ────────────────────────────
  // Entered from a Room Element card's "+ Add Back-Out" (RoomDetailFlyout):
  // the target element and its room are known, so the polygon needs no type
  // picker and no room detection. One polygon per entry; the flyout stays
  // open beside it (non-modal), and Esc cancels the trace before it closes
  // anything. Same tracer as every other click-to-trace tool (tracePoint:
  // 22.5° lock, snap, Shift / Alt).
  function beginBackOutDraw(el) {
    if (isLocked || !onCreateBackOut || !el?.RoomElementID) return;
    setRouteMode(false); setRoutePick(null); setRoutePts([]); setRouteCursor(null);
    setDrawMode(false); setCalib(null); setImgMode(false); setDrawRect(null); setRoomPts([]); setRoomCursor(null); setMeasureMode(false); setMeasureLine(null);
    setCondMode(false); setCondPts([]); setCondCursor(null);
    setSymbolMode(false); setSymbolHover(null);
    setAreaMode(false); setAreaPts([]); setAreaCursor(null);
    setBackOutTarget({ roomElementId: el.RoomElementID, roomId: el.RoomID, label: el.Name || el.Code || "element" });
    setBackOutPts([]); setBackOutCursor(null); setBackOutMode(true);
  }
  function cancelBackOut() { setBackOutMode(false); setBackOutTarget(null); setBackOutPts([]); setBackOutCursor(null); }
  function finishBackOut() {
    const pts = cleanTracePoints(backOutPts, { tolFt: traceTolFt(), closed: true });
    const target = backOutTarget;
    cancelBackOut();
    if (pts.length >= 3) { const v = polygonValidity(pts); if (!v.ok) { setEditNotice(`${validityMessage(v)} No back-out was drawn.`); return; } }
    if (pts.length >= 3 && target && onCreateBackOut) {
      onCreateBackOut({
        roomElementId: target.roomElementId, roomId: target.roomId,
        points: pts.map(p => ({ x: +p.x.toFixed(2), y: +p.y.toFixed(2) })),
        areaFt2: Math.round(polygonArea(pts) * 10) / 10,
      });
    }
  }

  // ── Area condition drawing (Condition Library Style='area') ───────────────────
  function finishAreaCondition() {
    const pts = cleanTracePoints(areaPts, { tolFt: traceTolFt(), closed: true });
    setAreaPts([]); setAreaCursor(null);
    if (pts.length >= 3) { const v = polygonValidity(pts); if (!v.ok) { setEditNotice(`${validityMessage(v)} Nothing was drawn.`); return; } }
    if (pts.length >= 3 && areaPick && onCreateAreaCondition) {
      const areaFt2 = Math.round(polygonArea(pts) * 10) / 10;
      const perimeterFt = Math.round(polygonPerimeter(pts) * 10) / 10;
      const roomId = roomContainingPolygon(pts, effRooms);
      onCreateAreaCondition({
        conditionTypeId: areaPick,
        points: pts.map(p => ({ x: +p.x.toFixed(2), y: +p.y.toFixed(2) })),
        areaFt2, perimeterFt, roomId,
      });
    }
  }

  const groupCap = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em" };
  const btn = (on) => ({
    padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    background: on ? "var(--cs-blue)" : "var(--surface)",
    color: on ? "#fff" : "var(--text2)",
    border: `1px solid ${on ? "var(--cs-blue)" : "var(--cs-border)"}`, borderRadius: 6,
  });

  const qbtn = (on) => ({ ...btn(on), padding: "3px 8px" });   // the quick strip's smaller buttons
  // Toolbar group card: a captioned, bordered cluster so related controls read as one unit.
  const divider = { width: 1, height: 18, background: "var(--cs-border)", margin: "0 2px" };
  const grp = (caption, children, captionExtra) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "4px 8px 6px", border: "1px solid var(--cs-border)", borderRadius: 8, background: "var(--surface)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={groupCap}>{caption}</span>
        {captionExtra}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>{children}</div>
    </div>
  );

  // Options: its buttons listed vertically. In the plain ribbon it sits in the right-hand column directly under Help (both
  // tiles the same width); a tabbed ribbon keeps it in its own tab.
  const optionsTile = grp("Options", <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch", width: "100%" }}>

          <button onClick={toggleDarkDrawing} style={btn(darkDrawing)} aria-pressed={darkDrawing} title="Drawing view — inverts the PLAN only (white lines on black) for long sessions. A view setting; the drawing file is untouched. The screen itself is set by the Interface button.">{darkDrawing ? "🖼 Drawing: 🌙 Dark" : "🖼 Drawing: ☀️ Light"}</button>
          <button onClick={toggleUiTheme} style={btn(uiTheme === "dark")} aria-pressed={uiTheme === "dark"} title="Interface — light or dark for the toolbars, panels and windows of the takeoff engine (just for you). The drawing has its own Drawing button.">{uiTheme === "dark" ? "🖥 Interface: 🌙 Dark" : "🖥 Interface: ☀️ Light"}</button>
          <DockPanelsMenu layout={effDockLayout} onChange={setDockLayout} btn={btn} />
        </div>);

  // Graphic scale bar: a "nice" number of feet ~120px wide at the current zoom
  const barFeet = niceFeet(120 / pxPerFt);
  const barPx = barFeet * pxPerFt;
  const inchPerFt = 96 / pxPerFt; // css px per inch ≈ 96
  const currentArch = isPdf && settings?.ImageFtPerInch ? nearestArchScale(settings.ImageFtPerInch) : null;
  const dxFt = panning?.isImage ? (panning.dxFt || 0) : 0;
  const dyFt = panning?.isImage ? (panning.dyFt || 0) : 0;

  // The dock's seven panes, each as an element the layout (order / visibility / columns) arranges.
  const dockPanelNodes = {
    keyplan: (
      <>
          {/* Keyplan — fixed-size minimap, never needs to compete for space */}
          <div style={{ display: "flex", flexDirection: "column", flexShrink: 0, borderBottom: "1px solid var(--cs-border)" }}>
            <div onClick={e => toggleDockSection("keyplan", e)} title={dockHeaderTip} style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.keyplan ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
                Keyplan
              </span>
              <button onClick={e => { e.stopPropagation(); setDockOpen(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 12, padding: 0 }}>✕</button>
            </div>
            {dockSectionsOpen.keyplan && (
              <div style={{ overflowY: "auto" }}>
                <KeyplanPanel
                  effRooms={effRooms} pan={pan} pxPerFt={pxPerFt} canvasHeight={canvasHeight} svgRef={svgRef} onJump={jumpTo} view={view}
                  bgSrc={hiddenLayers.has("background") ? null : (isPdf ? renderUrl : (rasterSrc || null))}
                  bgRect={hiddenLayers.has("background") ? null : (isPdf
                    ? (renderRect && { x: renderRect.x + dxFt, y: renderRect.y + dyFt, w: renderRect.w, h: renderRect.h })
                    : (rotDims && { x: imgX + dxFt, y: imgY + dyFt, w: rotDims.w * imgScale, h: rotDims.h * imgScale }))}
                  bgFrame={hiddenLayers.has("background") ? null : (isPdf
                    ? (worldW > 0 && worldH > 0 && { x: imgX + dxFt, y: imgY + dyFt, w: worldW, h: worldH })
                    : (rotDims && { x: imgX + dxFt, y: imgY + dyFt, w: rotDims.w * imgScale, h: rotDims.h * imgScale }))}
                  bgOpacity={settings?.ImageOpacity}
                />
              </div>
            )}
          </div>
      </>
    ),
    queue: (
      <>

          {/* Review Walls — every low-confidence joint across the whole floor
              plan, not just what's on screen; see ReviewWallsPanel's own
              comment. Shares remaining dock space the same flex:1 + internal-
              scroll way Rooms/Conditions/Layers do (see Layers' own comment
              on why a fixed/unbounded section starves the others). */}
          {/* Review — UI Phase A / Track J: every exception the canvas can compute
              (scale, walls, rooms, openings, element quantities) in one queue,
              each item selecting / highlighting its record. Walls keep their own
              chooser section below. */}
          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.queue ? (dockLayout.autoCollapse ? 1 : "0 1 auto") : "0 0 auto", minHeight: 0, maxHeight: dockSectionsOpen.queue && !dockLayout.autoCollapse ? 260 : undefined, borderBottom: "1px solid var(--cs-border)" }}>
            <div onClick={e => toggleDockSection("queue", e)} title={dockHeaderTip} style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0 }}>
              <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.queue ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Review</span>
              {reviewQueue.total > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: reviewQueue.warnCount ? "#dc2626" : "#64748b", borderRadius: 10, padding: "1px 7px", marginLeft: 2 }} title={`${reviewQueue.warnCount} need attention · ${reviewQueue.total - reviewQueue.warnCount} informational`}>{reviewQueue.total}</span>
              )}
            </div>
            {dockSectionsOpen.queue && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 10px 8px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                  {REVIEW_CATEGORIES.map(c => (
                    <span key={c.key} style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 999, border: "1px solid var(--cs-border)", color: reviewQueue.counts[c.key] ? "var(--text)" : "var(--text3)", background: reviewQueue.counts[c.key] ? "var(--surface2, #f8fafc)" : "transparent" }}>
                      {c.label} {reviewQueue.counts[c.key]}
                    </span>
                  ))}
                </div>
                {/* Track J — workflow strip: acknowledged / assigned / waived counts, waived toggle */}
                {(reviewQueue.statusCounts.acknowledged > 0 || reviewQueue.statusCounts.waived > 0 || reviewQueue.statusCounts.assigned > 0) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 10.5, color: "var(--text3)", marginBottom: 6 }}>
                    {reviewQueue.statusCounts.acknowledged > 0 && <span>✓ {reviewQueue.statusCounts.acknowledged} acknowledged</span>}
                    {reviewQueue.statusCounts.assigned > 0 && <span>👤 {reviewQueue.statusCounts.assigned} assigned</span>}
                    {reviewQueue.statusCounts.waived > 0 && (
                      <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                        <input type="checkbox" checked={showWaived} onChange={e => setShowWaived(e.target.checked)} style={{ margin: 0 }} /> show {reviewQueue.statusCounts.waived} waived
                      </label>
                    )}
                  </div>
                )}
                {reviewQueue.total === 0 && (
                  <div style={{ fontSize: 11.5, color: "var(--text3)" }}>
                    {reviewQueue.statusCounts.waived > 0 ? `Nothing open — ${reviewQueue.statusCounts.waived} waived item${reviewQueue.statusCounts.waived === 1 ? "" : "s"} hidden.` : "Nothing to review — scale set, every room placed, every element quantity resolved."}
                  </div>
                )}
                {canAcceptMachine && generatedUnreviewed > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, padding: "4px 0" }}>
                    <span style={{ color: "var(--text2)", flex: 1 }}>{generatedUnreviewed} generated item{generatedUnreviewed === 1 ? "" : "s"} on this drawing not yet reviewed (still priced).</span>
                    <button onClick={() => { if (window.confirm(`Mark ${generatedUnreviewed} generated item(s) — ${machineSummary.counts.symbols} symbol(s), ${machineSummary.counts.walls} wall(s), ${machineSummary.counts.elements} element(s) — as reviewed by you?\n\nThey stay in the estimate either way. AI-proposed rooms are not included; accept those one by one.`)) acceptMachine(acceptPayload(machineGroups)); }}
                      style={{ ...btn(false), whiteSpace: "nowrap" }} title="Mark every generated symbol, auto wall and template element on this drawing as reviewed">Accept all generated</button>
                  </div>
                )}
                {reviewQueue.visible.slice(0, 60).map(it => {
                  const st = it.state || { status: "open" };
                  const waived = st.status === "waived", acked = st.status === "acknowledged";
                  return (
                    <div key={it.id} onClick={() => { if (it.roomId != null || it.conditionIds?.length) highlightGeometry({ roomId: it.roomId, conditionIds: it.conditionIds || [] }); if (it.roomId != null && (it.category === "elements" || it.category === "pricing" || it.category === "proposals" || it.category === "machine")) setDetailRoomId(it.roomId); if (it.roomId != null && it.category === "layout") setLayoutReviewRoomId(it.roomId); }}
                      style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 11.5, padding: "3px 0", borderTop: "1px solid var(--cs-border)", cursor: it.roomId != null || it.conditionIds?.length ? "pointer" : "default", opacity: waived ? 0.6 : 1 }}
                      title={it.roomId != null ? "Click to select and highlight on the canvas" : ""}>
                      <span style={{ color: waived ? "var(--text3)" : acked ? "#15803d" : it.severity === "warn" ? "#b45309" : "var(--text3)", flexShrink: 0 }} title={waived ? "Waived" : acked ? "Acknowledged" : it.severity === "warn" ? "Needs attention" : "Informational"}>{waived ? "⊘" : acked ? "✓" : it.severity === "warn" ? "⚠" : "ℹ"}</span>
                      <span style={{ color: "var(--text2)", flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", marginRight: 4 }}>{it.category}</span>{it.message}
                        {(st.assignedTo || st.note) && (
                          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 1 }}>
                            {st.assignedTo && <span>👤 {st.assignedTo}</span>}{st.assignedTo && st.note && " · "}{st.note && <span style={{ fontStyle: "italic" }}>{st.note}</span>}
                            {st.updatedBy && <span> — {st.updatedBy.split("@")[0]}</span>}
                          </div>
                        )}
                      </span>
                      {canAcceptMachine && it.accept && (
                        <span style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => acceptMachine(it.accept)} style={{ ...iconBtn, color: "#15803d", fontWeight: 700, padding: "0 6px" }} title="Accept — marks this as reviewed by you (it stays in the estimate either way)">Accept</button>
                        </span>
                      )}
                      {canReview && (
                        <span style={{ display: "flex", gap: 3, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          {st.status === "open" && <button onClick={() => setReviewState(it, { status: "acknowledged" })} title="Acknowledge — seen, still to fix (recorded with your name)" style={{ ...iconBtn, color: "#15803d" }}>✓</button>}
                          {(acked || waived) && <button onClick={() => setReviewState(it, { is_delete: 1 })} title="Reopen — clear the acknowledged / waived state" style={iconBtn}>↩</button>}
                          {!waived && <button onClick={() => waiveItem(it)} title="Waive — accept as-is with a reason; hidden from the queue but still computed" style={iconBtn}>⊘</button>}
                          <button onClick={() => assignItem(it)} title={st.assignedTo ? `Assigned to ${st.assignedTo} — click to change` : "Assign to someone"} style={{ ...iconBtn, color: st.assignedTo ? "#1d4ed8" : undefined }}>👤</button>
                        </span>
                      )}
                    </div>
                  );
                })}
                {reviewQueue.visible.length > 60 && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>… {reviewQueue.visible.length - 60} more</div>}
                {/* Track J — states whose item no longer computes: the cause was fixed since the estimator touched it. Derived, cleared by hand. */}
                {reviewQueue.resolved.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: "1px dashed var(--cs-border)", paddingTop: 6 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", marginBottom: 3 }}>Resolved since review · {reviewQueue.resolved.length}</div>
                    {reviewQueue.resolved.slice(0, 20).map(r => (
                      <div key={r.id} style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 11, color: "var(--text3)", padding: "2px 0" }}>
                        <span style={{ color: "#15803d", flexShrink: 0 }}>✔</span>
                        <span style={{ flex: 1, minWidth: 0, textDecoration: "line-through" }}>{r.message || `${r.category} · ${r.code}`}</span>
                        {canReview && <button onClick={() => onSaveReviewState?.({ item_key: r.id, is_delete: 1 })} title="Clear this resolved item" style={iconBtn}>✕</button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
      </>
    ),
    review: (
      <>

          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.review ? 1 : "0 0 auto", minHeight: 0, borderBottom: "1px solid var(--cs-border)" }}>
            <div onClick={e => toggleDockSection("review", e)} title={dockHeaderTip} style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0 }}>
              <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.review ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>Review · Walls</span>
              {reviewItems.length > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: "#fff", background: "#dc2626",
                  borderRadius: 10, padding: "1px 7px", marginLeft: 2,
                }}>{reviewItems.length}</span>
              )}
            </div>
            {dockSectionsOpen.review && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <ReviewWallsPanel
                  items={reviewItems} haloStyle={haloStyle} onJump={jumpTo}
                  onSetEdgeType={onSetEdgeType} isLocked={isLocked}
                  conditionTypes={conditionTypes} layers={layers} condByAutoKey={condByAutoKey} rulesByPair={rulesByPair} wallLearning={wallLearning}
                  onAssignConstruction={assignConstruction}
                />
              </div>
            )}
          </div>
      </>
    ),
    rooms: (
      <>

          {/* Rooms — unbounded list, shares remaining space evenly with Conditions */}
          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.rooms ? 1 : "0 0 auto", minHeight: 0, borderBottom: "1px solid var(--cs-border)" }}>
            <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <span onClick={e => toggleDockSection("rooms", e)} title={dockHeaderTip} style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.rooms ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
                Rooms
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* "+ Add Room" creates the room, opens Room Details AND enters
                    draw mode for it together (see startAddRoom) — the old
                    top-toolbar "Draw room" button is gone, so this dock and
                    the flyout's own "Draw this room" are the entry points. */}
                {!isLocked && !drawMode && onAddRoom && (
                  <button onClick={startAddRoom}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cs-blue)", fontSize: 11, fontWeight: 700, padding: 0 }}>
                    + Add Room
                  </button>
                )}
                {drawMode && (
                  <button onClick={toggleDrawMode}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 11, fontWeight: 700, padding: 0 }}>
                    ✕ Stop Drawing
                  </button>
                )}
              </div>
            </div>
            {dockSectionsOpen.rooms && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {drawMode && (
                  <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--cs-border)", fontSize: 11, color: "var(--text3)" }}>
                    ✏️ Drag a box, or click to trace corners · Enter/double-click finish · Esc to cancel
                  </div>
                )}
                <RoomsPanel
                  rooms={effRooms} selectedIds={selectedIds} isLocked={isLocked} roomTemplates={roomTemplates}
                  onSelect={(r, R) => { setSelectedIds(new Set([r.RoomID])); jumpTo(R.x + R.w / 2, R.y + R.h / 2); }}
                  onEdit={r => setDetailRoomId(r.RoomID)}
                  onDelete={r => onDeleteRooms?.([r])}
                  onAdd={startAddRoom} onSetLocked={onSetRoomLocked}
                />
              </div>
            )}
          </div>
      </>
    ),
    conditions: (
      <>

          {/* Conditions ("Items") — unbounded list, shares remaining space evenly with Rooms */}
          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.conditions ? 1 : "0 0 auto", minHeight: 0, borderBottom: "1px solid var(--cs-border)" }}>
            <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <span onClick={e => toggleDockSection("conditions", e)} title={dockHeaderTip} style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.conditions ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
                Conditions
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* "+ Add Condition" jumps straight into the same click-corner
                    canvas draw mode the old top-toolbar "Draw condition"
                    button triggered — that toolbar button is gone, so this
                    dock is the only entry point. Manual (no-geometry)
                    condition entry — a genuinely separate, still-useful path
                    (e.g. a lump-sum line with no wall run to trace) — stays
                    reachable via the smaller secondary link. */}
                {!isLocked && !conditionDrawActive && (
                  <>
                    {drawableConditionTypes.length > 0 && (
                      <button onClick={() => setAddWindowOpen(true)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cs-blue)", fontSize: 11, fontWeight: 700, padding: 0 }}>
                        + Add Condition
                      </button>
                    )}
                    {onCreateManualCondition && (
                      <button onClick={() => setAddingCondition(true)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 11, fontWeight: 600, padding: 0 }}>
                        (manual)
                      </button>
                    )}
                  </>
                )}
                {conditionDrawActive && !addWindowOpen && drawableConditionTypes.length > 0 && (
                  <button onClick={() => setAddWindowOpen(true)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--cs-blue)", fontSize: 11, fontWeight: 700, padding: 0 }}>
                    ⚙ Settings
                  </button>
                )}
                {conditionDrawActive && (
                  <button onClick={stopConditionDraw}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 11, fontWeight: 700, padding: 0 }}>
                    ✕ Stop Drawing
                  </button>
                )}
              </div>
            </div>
            {dockSectionsOpen.conditions && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {conditionDrawActive && (
                  <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--cs-border)" }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                      {symbolMode ? (WALL_SNAPPED_SHAPES.has(resolveShape(symbolPickType)) ? "Click a wall to place · Esc to stop" : "Click anywhere in a room to place · Esc to stop")
                        : areaMode ? "Click each corner · Enter to finish (3+) · Esc to cancel"
                        : "Click along the walls · Enter to finish · Esc to cancel"}
                    </div>
                    <select
                      value={activeConditionPickId ?? ""}
                      onChange={e => switchConditionType(drawableConditionTypes.find(c => c.TypeKey === e.target.value))}
                      style={{ width: "100%", fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "3px 4px", background: "var(--surface)", color: (condPickType || symbolPickType || areaPickType)?.Color || "var(--text)", fontWeight: 700, boxSizing: "border-box" }}
                    >
                      {sortByCostCode(drawableConditionTypes).map(c => <option key={c.TypeKey} value={c.TypeKey}>{typeOptionLabel(c)} [{styleLabel(c)}]</option>)}
                    </select>
                  </div>
                )}
                {hiddenSummary(hiddenTypes, drawnTypeKeys) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", fontSize: 11, color: "#b45309", background: "rgba(217,119,6,0.1)" }}>
                    <span style={{ flex: 1 }}>{hiddenSummary(hiddenTypes, drawnTypeKeys)} on the drawing</span>
                    <button onClick={() => setHiddenTypes(new Set())} style={{ fontSize: 11, fontWeight: 700, border: "1px solid #b45309", borderRadius: 4, background: "var(--surface)", color: "#b45309", cursor: "pointer", padding: "0 6px" }}>Show all</button>
                  </div>
                )}
                {/* Long names scroll sideways instead of being cut off. */}
                <div style={{ overflowX: "auto" }}><div style={{ minWidth: "max-content" }}>
                <ConditionsPanel
                  view={conditionView}
                  conditions={takeoffConditions} selectedCondId={selectedCondId} isLocked={isLocked}
                  onSelect={(c, point) => { setSelectedCondId(c.TakeoffConditionDrawnID); jumpTo(point.x, point.y); }}
                  onEdit={c => setEditingCondId(c.TakeoffConditionDrawnID)}
                  onDelete={c => onDeleteTakeoffCondition?.(c)}
                />
                </div></div>
              </div>
            )}
          </div>
      </>
    ),
    routes: (
      <>

          {/* Layers — grew from "a handful of checkboxes" once the Edge Halo
              Colors picker + legend landed here, so (2026-09-09) it now
              shares/scrolls its space the same way Rooms/Conditions do
              instead of claiming whatever fixed height its content needs —
              that used to starve Rooms/Conditions of room when Layers was
              open, since they were the only sections treated as flexible. */}
          {/* Routes (Phase G canvas routing) — the revision's conceptual distribution segments;
              "Draw" traces one on the canvas (its length → calculated LF, its turns → elbows),
              "Clear" removes the drawing (an override LF, if any, still stands). */}
          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.routes ? 1 : "0 0 auto", minHeight: 0, borderBottom: "1px solid var(--cs-border)" }} data-testid="routes-dock">
            <div onClick={e => toggleDockSection("routes", e)} title={dockHeaderTip} style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.routes ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
                Routes <span style={{ fontWeight: 400, color: "var(--text3)" }}>({routes.filter(rt => rt.geometry?.points?.length >= 2).length} of {routes.length} drawn)</span>
              </span>
            </div>
            {dockSectionsOpen.routes && (
              <div style={{ overflowY: "auto", minHeight: 0, padding: "0 6px 8px" }}>
                {routes.length === 0 && <div style={{ fontSize: 11, color: "var(--text3)", padding: "2px 4px" }}>No route segments yet — generate a route on Assumed Equipment ▸ Distribution or under a scenario's HVAC system.</div>}
                {Object.entries(routes.reduce((acc, rt) => { (acc[rt.systemLabel || "Routes"] ||= []).push(rt); return acc; }, {})).map(([sysLabel, list]) => (
                  <div key={sysLabel} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: list[0]?.color || "var(--text3)", textTransform: "uppercase", letterSpacing: "0.03em", padding: "4px 4px 2px" }}>{sysLabel}</div>
                    {list.map(rt => {
                      const drawn = rt.geometry?.points?.length >= 2;
                      return (
                        <div key={rt.segmentId} data-testid={`route-dock-${rt.segmentId}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, padding: "2px 4px", borderRadius: 4, background: routeMode && routePick === rt.segmentId ? "#f5f3ff" : "transparent" }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rt.label}>{rt.label}</span>
                          <span style={{ color: rt.effectiveLF == null ? "#b45309" : "var(--text3)", whiteSpace: "nowrap" }} title={rt.lfSource === "override" ? "estimator override LF" : rt.lfSource === "geometry" ? "drawn route LF" : "no LF yet"}>
                            {rt.effectiveLF == null ? "LF required" : `${fmt(rt.effectiveLF)}′${rt.lfSource === "override" ? "*" : ""}`}{drawn && rt.turns ? ` · ${rt.turns}∠` : ""}
                          </span>
                          {!isLocked && <button style={{ fontSize: 10.5, padding: "1px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "var(--surface)", cursor: "pointer" }} onClick={() => beginRouteDraw(rt.segmentId)} title="Trace this segment's path on the canvas">{drawn ? "Redraw" : "Draw"}</button>}
                          {!isLocked && drawn && onClearRouteGeometry && <button style={{ fontSize: 10.5, padding: "1px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "var(--surface)", cursor: "pointer" }} onClick={() => onClearRouteGeometry(rt.segmentId)} title="Remove the drawn path (an override LF still stands)">Clear</button>}
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div style={{ fontSize: 10, color: "var(--text3)", padding: "4px 4px 0" }}>Drawn length = the segment's calculated LF (an estimator override wins, marked *); turns = elbow count (a count for the fittings allowance, not priced here). Design flow never comes from the drawing.</div>
              </div>
            )}
          </div>
      </>
    ),
    layers: (
      <>

          <div style={{ display: "flex", flexDirection: "column", flex: dockSectionsOpen.layers ? 1 : "0 0 auto", minHeight: 0 }}>
            <div onClick={e => toggleDockSection("layers", e)} title={dockHeaderTip} style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", flexShrink: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", transition: "transform 0.1s", transform: dockSectionsOpen.layers ? "rotate(0deg)" : "rotate(-90deg)" }}>▾</span>
                Layers
              </span>
            </div>
            {dockSectionsOpen.layers && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <LayersPanel
                  layerDefs={layerDefs}
                  hiddenLayers={hiddenLayers}
                  onToggle={key => setHiddenLayers(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; })}
                />
                <div style={{ padding: "0 14px 6px", fontSize: 10.5, color: "var(--text3)" }}>
                  Layers are managed under Setup ▸ Estimate Engine ▸ Takeoff Layers; assign Items, Assemblies and Room Templates to one there.
                </div>
                {onSaveHaloColors && (
                  <div style={{ padding: "4px 10px 10px", borderTop: "1px solid var(--cs-border)", marginTop: 4 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", padding: "6px 0 4px" }}>
                      Edge Halo Colors
                    </div>
                    {HALO_TIER_FIELDS.map(({ tier, label, settingsKey, payloadKey }) => (
                      <div key={tier} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
                        <input
                          type="color"
                          title={label}
                          value={haloColors?.[settingsKey] || HALO_STYLE[tier].stroke}
                          onChange={e => onSaveHaloColors({
                            ...Object.fromEntries(HALO_TIER_FIELDS.map(f => [f.payloadKey, haloColors?.[f.settingsKey] || null])),
                            [payloadKey]: e.target.value,
                          })}
                          style={{ width: 20, height: 20, padding: 0, border: "1px solid var(--cs-border)", borderRadius: 3, cursor: "pointer", flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 12, color: "var(--text)", flex: 1 }}>{label}</span>
                        {haloColors?.[settingsKey] && (
                          <button
                            onClick={() => onSaveHaloColors({
                              ...Object.fromEntries(HALO_TIER_FIELDS.map(f => [f.payloadKey, haloColors?.[f.settingsKey] || null])),
                              [payloadKey]: null,
                            })}
                            title="Reset to default"
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--text3)", padding: 0 }}
                          >↺</button>
                        )}
                      </div>
                    ))}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0 0", fontSize: 11, color: "var(--text3)" }}>
                      <span style={{ display: "inline-block", width: 14, height: 0, borderTop: `2px solid ${EDGE_STYLE.shared.stroke}` }} />
                      shared wall
                      <span style={{ display: "inline-block", width: 14, height: 0, borderTop: `2px dashed ${EDGE_STYLE.cavity.stroke}` }} />
                      cavity / chase
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
      </>
    ),
  };

  return (
    <UnitsContext.Provider value={units}>
    <div ref={wrapRef} tabIndex={0} onKeyDown={onKeyDown} style={{ outline: "none" }}>
      {/* Toolbar + drawing-sheet controls — pinned to the top of the page's
          own scrolling content area once scrolled to, instead of scrolling
          away, so switching tools/sheets/pages never needs a scroll back up.
          top: 0, not the nav bars' height — EstimateDetail's content column is
          already sized to calc(100vh - navHeight) via useNavHeight() and
          starts right below them, so its own scroll-container top already
          sits at that point. */}
      <div ref={toolbarWrapRef} style={{ position: "sticky", top: 0, zIndex: 20, background: "var(--surface)", paddingTop: 2, paddingBottom: 2 }}>
      <div ref={ribbonWrapRef} style={focusMode ? { display: "none" } : { position: "relative" }}>
      {/* The ribbon bar: always visible. Pinned = the tiles below stay docked; unpinned = hover this bar to float them down over
          the drawing (they fold away when the pointer leaves), or click 📌 to pin them back. The pinned-conditions strip stays
          visible either way. */}
      <div onMouseEnter={() => { if (hoverOpens) openRibbon(); }} onMouseLeave={() => { if (hoverOpens) closeRibbonSoon(); }}
        style={{ display: "flex", alignItems: "center", gap: tabbed ? 6 : 10, flexWrap: tabbed ? "wrap" : undefined, padding: "2px 4px", marginBottom: ribbonOpen && ribbonPinned ? 4 : 0, fontSize: 11 }}>
        {tabbed ? (
          <div role="tablist" aria-label="Ribbon tabs" style={{ display: "flex", gap: 2 }}>
            {RIBBON_TABS.map(t => {
              const on = ribbonTab === t.key && (ribbonPinned || ribbonSticky);
              return (
                <button key={t.key} role="tab" aria-selected={on} onClick={() => pickRibbonTab(t.key)}
                  style={{ border: "none", borderBottom: `2px solid ${on ? "var(--cs-blue)" : "transparent"}`, background: "transparent", color: on ? "var(--cs-blue)" : "var(--text2)", fontWeight: 700, fontSize: 12, padding: "4px 8px", cursor: "pointer" }}>{t.label}</button>
              );
            })}
          </div>
        ) : (
          <span style={{ fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{ribbonPinned ? "Ribbon" : (ribbonOpen ? "Ribbon" : "▾ Ribbon — hover for tools")}</span>
        )}
        {tabbed && (
          <>
            <span style={divider} />
            <button onClick={() => { closeRibbonFloat(); pointerTool(); }} style={qbtn(!anyToolActive)} title="Pointer — select, move, resize (Esc leaves any tool)" aria-label="Pointer">↖</button>
            <button onClick={() => { closeRibbonFloat(); toggleDrawMode(); }} disabled={isLocked} style={{ ...qbtn(drawMode), opacity: isLocked ? 0.45 : 1 }} title="Draw a room — drag a box or click each corner (D)" aria-label="Draw room">▭</button>
            <button onClick={() => { closeRibbonFloat(); openDockPanel("conditions"); }} style={qbtn(condMode || symbolMode || areaMode)} title="Draw a linear run, an area, or place a count symbol — pick the type in the Conditions panel" aria-label="Condition">📐</button>
            <span style={divider} />
            <button onClick={onUndo} disabled={!undoInfo?.canUndo || isLocked} style={{ ...qbtn(false), opacity: undoInfo?.canUndo && !isLocked ? 1 : 0.45 }} title={undoInfo?.canUndo ? `Undo ${undoInfo.undoLabel} (Ctrl+Z)` : "Nothing to undo in this session"} aria-label="Undo">↶</button>
            <button onClick={onRedo} disabled={!undoInfo?.canRedo || isLocked} style={{ ...qbtn(false), opacity: undoInfo?.canRedo && !isLocked ? 1 : 0.45 }} title={undoInfo?.canRedo ? `Redo ${undoInfo.redoLabel} (Ctrl+Y)` : "Nothing to redo"} aria-label="Redo">↷</button>
            <span style={divider} />
            <button onClick={() => zoomCentre(1 / ZOOM_STEP)} style={qbtn(false)} title="Zoom out (−)" aria-label="Zoom out">−</button>
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", minWidth: 36, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{`${Math.round(pxPerFt * 20)}%`}</span>
            <button onClick={() => zoomCentre(ZOOM_STEP)} style={qbtn(false)} title="Zoom in (+)" aria-label="Zoom in">+</button>
            <button onClick={fitView} style={qbtn(false)} title="Fit all rooms (F)">Fit</button>
            {sheets.length > 0 && (() => {
              const cur = sheets.find(s => s.SheetID === activeSheetId) || null;
              return (
                <span style={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
                  <span style={divider} />
                  {sheets.length > 1 && <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, -1))} style={iconBtn} title="Previous drawing  ( [ )" aria-label="Previous drawing">‹</button>}
                  <button onClick={() => setSheetMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={sheetMenuOpen}
                    style={{ ...qbtn(sheetMenuOpen), maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title="Switch drawing">
                    {cur ? sheetTitle(cur) : "Drawings"} ▾
                  </button>
                  {sheets.length > 1 && <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, 1))} style={iconBtn} title="Next drawing  ( ] )" aria-label="Next drawing">›</button>}
                  {pageTotals.total > 1 && !isLocked && activeSheetId != null && (
                    <>
                      <button onClick={() => goPage(-1)} style={iconBtn} disabled={!stepPage(sheets, pageCounts, activeSheetId, pageNum, -1)} title="Previous page" aria-label="Previous page">‹</button>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", fontVariantNumeric: "tabular-nums" }} title={`Page ${pageTotals.position} of ${pageTotals.total} across all drawings`}>{pageTotals.position}/{pageTotals.total}</span>
                      <button onClick={() => goPage(1)} style={iconBtn} disabled={!stepPage(sheets, pageCounts, activeSheetId, pageNum, 1)} title="Next page" aria-label="Next page">›</button>
                    </>
                  )}
                  {sheetMenuOpen && (
                    <>
                      <div onClick={() => setSheetMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                      <div role="menu" style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 50, minWidth: 280, maxHeight: 340, overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: 4 }}>
                        {orderedSheets(sheets).map(s => {
                          const cal = sheetCalibration(s);
                          return (
                            <button key={s.SheetID} role="menuitem" onClick={() => { onSelectSheet?.(s.SheetID); setSheetMenuOpen(false); }}
                              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", padding: "5px 8px", border: "none", borderRadius: 6, background: s.SheetID === activeSheetId ? "var(--tint-blue)" : "transparent", cursor: "pointer", fontSize: 12 }}>
                              <span style={{ color: s.IsKeySheet ? "#d97706" : "var(--text3)" }}>{s.IsKeySheet ? "★" : "·"}</span>
                              <span style={{ flex: 1, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sheetTitle(s)}</span>
                              <span style={{ fontSize: 11, color: cal.status === "none" ? "#b45309" : "var(--text3)" }}>{cal.status === "none" ? "⚠ no scale" : takeoffBadgeText(sheetTakeoffCounts, s.SheetID) || "✓"}</span>
                            </button>
                          );
                        })}
                        <button onClick={() => { setSheetMenuOpen(false); pickRibbonTab("drawings"); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 8px", marginTop: 2, border: "none", borderTop: "1px solid var(--cs-border)", background: "transparent", color: "var(--cs-blue)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Manage drawings, scale and files…</button>
                      </div>
                    </>
                  )}
                </span>
              );
            })()}
            {settings?.BackgroundImage && (
              <button onClick={() => { setRibbonTabState("drawings"); writeRibbonTab("drawings"); if (!ribbonPinned) setRibbonSticky(true); }}
                style={{ ...qbtn(false), color: scaleIsSet ? "#15803d" : "#d97706", borderColor: scaleIsSet ? "var(--cs-border)" : "#d97706" }}
                title={scaleIsSet ? "This drawing's scale — click to open the Drawings tab" : "No scale set — measuring is off. Click to open the Drawings tab and set it"}>
                {scaleIsSet ? `✓ ${currentArch ? currentArch.scale.label : "scaled"}` : "⚠ no scale"}
              </button>
            )}
          </>
        )}
        <span style={{ color: "var(--text2)", fontWeight: 600 }} title="The armed tool">{(() => { const m = modeLabel({ sweep: sweepMode, calib: !!calib, imgMode, measure: measureMode, cond: condMode, area: areaMode, route: routeMode, backOut: backOutMode, symbol: symbolMode, draw: drawMode }); return m && m !== "select" ? `· ${m}` : ""; })()}</span>
        {tabbed && <button onClick={() => setGuideOpen(o => !o)} aria-label="Keyboard shortcuts" aria-pressed={guideOpen} title="Keyboard and mouse shortcuts — stays open beside the drawing (?)" style={{ marginLeft: "auto", border: "1px solid var(--cs-blue)", background: guideOpen ? "var(--cs-blue)" : "var(--tint-blue)", color: guideOpen ? "#fff" : "var(--cs-blue)", borderRadius: 999, fontSize: 12, fontWeight: 800, lineHeight: 1, padding: "3px 9px", cursor: "pointer" }}>?</button>}
        <button onClick={() => setFocusMode(true)} aria-label="Focus mode"
          title="Focus mode — hide the ribbon and panels so only the drawing shows (M, or Esc, brings them back)"
          style={{ marginLeft: tabbed ? 0 : "auto", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 6, fontSize: 12, lineHeight: 1, padding: "3px 8px", cursor: "pointer" }}>⤢</button>
        <button onClick={() => setRibbonPinned(!ribbonPinned)} aria-pressed={ribbonPinned} aria-label={ribbonPinned ? "Unpin the ribbon" : "Pin the ribbon open"}
          title={ribbonPinned ? "Pinned open. Click to fold it away — it floats down when you hover the bar." : "Hover the bar to float the tools down; click to pin them open permanently."}
          style={{ border: `1px solid ${ribbonPinned ? "var(--cs-blue)" : "var(--cs-border)"}`, background: ribbonPinned ? "var(--cs-blue)" : "var(--surface)", color: ribbonPinned ? "#fff" : "var(--text2)", borderRadius: 6, fontSize: 12, lineHeight: 1, padding: "3px 8px", cursor: "pointer", transform: ribbonPinned ? "none" : "rotate(45deg)" }}>📌</button>
      </div>
      {ribbonOpen && (
      <div onMouseEnter={() => { if (hoverOpens) openRibbon(); }} onMouseLeave={() => { if (hoverOpens) closeRibbonSoon(); }}
        onFocusCapture={(e) => { if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) setRibbonFocus(true); }} onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setRibbonFocus(false); }}
        onChangeCapture={(e) => { if (e.target.tagName === "SELECT") e.target.blur?.(); }}
        onKeyDown={(e) => { if (e.key === "Escape" && !ribbonPinned) { closeRibbonFloat(); e.currentTarget.querySelector(":focus")?.blur?.(); } }}
        onClick={(e) => { if (!ribbonPinned && tabbed && e.target.closest?.("[data-ribbon-close]")) closeRibbonFloat(); }}
        style={{ display: "flex", flexWrap: "wrap", gap: tabbed ? 8 : 6, alignItems: "flex-start", paddingBottom: ribbonPinned ? 4 : 0, ...(ribbonPinned ? null : { position: "absolute", left: 0, right: 0, top: "100%", zIndex: 30, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 10px 28px rgba(0,0,0,0.28)", padding: "6px 6px 2px" }) }}>
      {/* Toolbar — reorganized 2026-10-13 (Feedback: "too much hunting and pecking").
          Two ribbons of captioned group cards instead of one wrapping row:
            Row 1 — what you DO: Tools · Edit · View · Find & review
            Row 2 — the ACTIVE DRAWING: file · Scale · Position · Automation
          then one line for Drawings (sheets) + Page. Every control is the same
          one it was with the same handler; only placement and grouping changed. */}
      <div style={{ display: "contents" }}>
        {showTab("draw") && grp("Tools", <>
          <button data-ribbon-close onClick={pointerTool} style={btn(!anyToolActive)} title="Pointer — select, move, resize (Esc leaves any tool)">↖ Pointer</button>
          <button data-ribbon-close onClick={toggleDrawMode} disabled={isLocked} style={{ ...btn(drawMode), opacity: isLocked ? 0.45 : 1 }} title="Draw a room — drag a box or click each corner (D)">▭ Room</button>
          <button data-ribbon-close onClick={() => openDockPanel("conditions")} style={btn(condMode || symbolMode || areaMode)} title="Draw a linear run, an area, or place a count symbol — pick the type in the Conditions dock">📐 Condition</button>
          {onCreateBackOut && (
            <button onClick={() => setBackOutPickerOpen(o => !o)} disabled={isLocked} style={{ ...btn(backOutMode || backOutPickerOpen), opacity: isLocked ? 0.45 : 1 }}
              title="Back-Out — trace an area to deduct from ONE Room Element's floor or ceiling quantity; pick the element first (a selected room's elements are listed first)">⊟ Back-Out</button>
          )}
          {settings?.BackgroundImage && (
            <button data-ribbon-close onClick={toggleMeasure} style={btn(measureMode)}
              title="Drag between two points to read the distance at the current scale — a read-only check against a dimension already printed on the drawing; unlike Set/Verify scale, this never changes the calibration">
              📏 {measureMode ? "Measuring… (Esc)" : "Measure"}
            </button>
          )}
          {sheets.some(s => s.BackgroundImage && s.ImageIsPdf) && onCountByExample && (
            <button
              data-ribbon-close
              onClick={toggleSweep}
              style={btn(sweepMode || !!sweepSeed)}
              title="Draw a box around ONE example symbol (a light, a door tag, a device), then count every repeat on this drawing, chosen drawings or all drawings and place them in one step"
            >🎯 {sweepMode ? "Draw a box around one example… (Esc)" : "Count by example"}</button>
          )}
        </>)}
        {showTab("draw") && grp("Edit", <>
          <button onClick={onUndo} disabled={!undoInfo?.canUndo || isLocked} style={{ ...btn(false), opacity: undoInfo?.canUndo && !isLocked ? 1 : 0.45 }}
            title={undoInfo?.canUndo ? `Undo ${undoInfo.undoLabel} (Ctrl+Z) — reverses the saved edit through the same procedure; this session only` : "Nothing to undo in this session (room deletes are not undoable — they cascade)"}>↶ Undo</button>
          <button onClick={onRedo} disabled={!undoInfo?.canRedo || isLocked} style={{ ...btn(false), opacity: undoInfo?.canRedo && !isLocked ? 1 : 0.45 }}
            title={undoInfo?.canRedo ? `Redo ${undoInfo.redoLabel} (Ctrl+Y)` : "Nothing to redo"}>↷ Redo</button>
          <button onClick={rotateSelected} disabled={!selectedId || isLocked} style={{ ...btn(false), opacity: selectedId && !isLocked ? 1 : 0.45 }} title="Rotate the selected room (R)">⟳ Rotate room</button>
          {(() => {
            // A visible way to end the run being traced (Enter / double-click still work).
            const n = condMode ? condPts.length : areaMode ? areaPts.length : routeMode ? routePts.length : backOutMode ? backOutPts.length : drawMode ? roomPts.length : 0;
            if (n < 1) return null;
            const min = areaMode || backOutMode || drawMode ? 3 : 2;
            const finish = condMode ? finishConditionRun : areaMode ? finishAreaCondition : routeMode ? finishRouteDraw : backOutMode ? finishBackOut : finishRoomPolyline;
            return (
              <button onClick={finish} disabled={n < min} style={{ ...btn(true), opacity: n < min ? 0.55 : 1 }}
                title={n < min ? `Needs at least ${min} points (${n} so far)` : "Finish this shape (Enter or double-click also finish it)"}>
                ✓ Finish ({n})
              </button>
            );
          })()}
        </>)}
        {showTab("view") && grp("View", <>
          <button onClick={() => zoomCentre(1 / ZOOM_STEP)} style={btn(false)} title="Zoom out (−)">−</button>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", minWidth: 42, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
            {`${Math.round(pxPerFt * 20)}%`}
          </span>
          <button onClick={() => zoomCentre(ZOOM_STEP)} style={btn(false)} title="Zoom in (+)">+</button>
          <button onClick={fitView} style={btn(false)} title="Fit all rooms (F)">Fit</button>
          {selectedIds.size > 0 && <button onClick={fitSelection} style={btn(false)} title="Zoom to the selected room(s) (Z)">Zoom sel</button>}
          <span style={divider} />
          <span style={{ ...groupCap, textTransform: "none", fontWeight: 600 }}>Color by</span>
          <button onClick={() => setColorMode("zone")} style={btn(colorMode === "zone")} title="Color rooms by their assigned AHU zone">AHU zone</button>
          <button onClick={() => setColorMode("type")} style={btn(colorMode === "type")} title="Color rooms by Room Template">Room template</button>
        </>)}
        {showTab("review") && grp("Find & review", <>
          <button onClick={() => setPaletteOpen(true)} style={btn(paletteOpen)} title="Find a tool, condition, drawing, scale or action by typing (Ctrl+K)">🔎 Find an action <span style={{ opacity: 0.6, fontWeight: 400 }}>Ctrl+K</span></button>
          {sheets.some(s => s.BackgroundImage) && (
            <button onClick={() => setSearchOpen(o => !o)} style={btn(searchOpen)} title="Search the printed text on every drawing (door tags, notes, room names…) and jump to a result">🔎 Search drawings</button>
          )}
          <button onClick={toggleCounter} style={btn(counterOpen)} title="Floating totals: what has been taken off per condition, on this drawing and on all drawings">Σ Totals</button>
          {snapshotsSlot}
        </>)}
        {tabbed && showTab("view") && optionsTile}
        {/* Help sits last, all the way on the right of the row */}
        <div style={{ display: tabbed ? "none" : "grid", width: SIDE_TILE_W }}>   {/* a tabbed ribbon has the ? button on its bar instead */}
          {grp("Help", <>
          <button onClick={() => setGuideOpen(o => !o)} style={btn(guideOpen)} title="Keyboard and mouse shortcuts — stays open beside the drawing (?)" aria-label="Keyboard shortcuts" aria-pressed={guideOpen}>⌨ ?</button>
        </>)}
        </div>
      </div>

      {/* Row 2 — the active drawing: its sheets + file, scale, position and automation */}
      <div style={{ display: "contents" }}>
      {showTab("drawings") && (sheets.length > 0 || onAddSheet) && grp("Files and Drawings", <>
          {/* Sheets — thumbnail cards: name, page, scale status; click to work on one, ⇆ to open one beside it, ‹ › / [ ] to step */}
          {sheets.length > 1 && (
            <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, -1))} title="Previous sheet  ( [ )" aria-label="Previous sheet" style={iconBtn}>▲</button>
              <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, 1))} title="Next sheet  ( ] )" aria-label="Next sheet" style={iconBtn}>▼</button>
            </span>
          )}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${SHEET_STRIP_COLS}, ${SHEET_CARD_W}px)`, gridAutoRows: SHEET_CARD_H, gap: SHEET_CARD_GAP, height: SHEET_CARD_H, overflowY: "auto", overflowX: "hidden", scrollSnapType: "y mandatory" }}>
            {orderedSheets(sheets).filter(s => !keyOnly || s.IsKeySheet || hasTakeoff(sheetTakeoffCounts, s.SheetID) || s.SheetID === activeSheetId).map(s => {
              const active = s.SheetID === activeSheetId, comparing = s.SheetID === compareSheetId && !active;
              const cal = sheetCalibration(s);
              return (
                <div key={s.SheetID} data-sheet-card={s.SheetID} onClick={() => onSelectSheet?.(s.SheetID)}
                  style={{ display: "flex", gap: 8, alignItems: "center", padding: 4, borderRadius: 6, cursor: "pointer", boxSizing: "border-box", width: SHEET_CARD_W, height: SHEET_CARD_H, overflow: "hidden", scrollSnapAlign: "start", border: `1px solid ${active ? "var(--cs-blue)" : comparing ? "#a855f7" : "var(--cs-border)"}`, background: active ? "var(--tint-blue)" : "var(--surface)" }}
                  title={active ? "Working sheet" : "Click to work on this sheet"}>
                  <SheetThumb sheet={s} width={56} height={40} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 11 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
                      <button onClick={e => { e.stopPropagation(); if (!isLocked) onSaveSheetMeta?.(s.SheetID, { is_key_sheet: s.IsKeySheet ? 0 : 1 }); }} disabled={isLocked}
                        title={s.IsKeySheet ? "Key sheet — click to unmark" : "Mark as a key sheet so it is easy to jump back to"} aria-pressed={!!s.IsKeySheet}
                        style={{ border: "none", background: "none", padding: 0, fontSize: 14, lineHeight: 1, cursor: isLocked ? "default" : "pointer", color: s.IsKeySheet ? "#d97706" : "var(--text3)", flexShrink: 0 }}>{s.IsKeySheet ? "★" : "☆"}</button>
                      <div style={{ fontWeight: 700, color: active ? "var(--cs-blue)" : "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={sheetTitle(s)}>{sheetTitle(s)}</div>
                    </div>
                    <div style={{ color: "var(--text3)", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {s.Discipline && <span>{disciplineLabel(s.Discipline)}</span>}
                      {s.ImageIsPdf && s.ImagePage > 1 && <span>p.{s.ImagePage}</span>}
                      <span style={{ color: cal.status === "none" ? "#b45309" : "#15803d" }}>{cal.status === "none" ? `⚠ ${cal.label}` : `✓ ${cal.label}`}</span>
                    </div>
                    {hasTakeoff(sheetTakeoffCounts, s.SheetID) && <div style={{ color: "#1d4ed8" }} title="Takeoff already drawn on this drawing">● {takeoffBadgeText(sheetTakeoffCounts, s.SheetID)}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }} onClick={e => e.stopPropagation()}>
                    {!active && (
                      <button onClick={() => setCompareSheetId(comparing ? null : s.SheetID)} title={comparing ? "Close side-by-side" : "Open beside the working sheet (read-only reference)"}
                        style={{ ...iconBtn, color: comparing ? "#7e22ce" : undefined, borderColor: comparing ? "#a855f7" : undefined }}>⇆</button>
                    )}
                    {!isLocked && (
                      <>
                        <button onClick={() => { const n = window.prompt("Rename sheet:", s.SheetName); if (n) onRenameSheet(s.SheetID, n); }} title="Rename" style={iconBtn}>✎</button>
                        <button onClick={() => onDeleteSheet(s.SheetID)} title="Delete sheet" style={iconBtn}>✕</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Multi-page PDF selector — pages WITHIN the active sheet's PDF (same line as the sheets) */}
        </>, ((!isLocked && onAddSheet) || sheets.length > 1) ? (
          <>
            {!isLocked && onAddSheet && (
              <>
                <input ref={addSheetRef} type="file" accept="application/pdf,image/png,image/jpeg" onChange={addSheetFile} style={{ display: "none" }} />
                <button onClick={() => addSheetRef.current?.click()} style={{ ...btn(false), padding: "2px 10px", fontSize: 11.5 }} title="Load another separate drawing (site plan, electrical single-line, …) as a new sheet">
                  + Add file
                </button>
              </>
            )}
            {!isLocked && onDuplicateSheet && activeSheetId != null && (
              <button onClick={() => onDuplicateSheet(activeSheetId)} disabled={!canDuplicateSheet(settings)} style={{ ...btn(false), padding: "2px 10px", fontSize: 11.5 }}
                title="Copy this drawing as a new sheet (same file, scale and position) with none of its rooms or conditions, to do another takeoff on it">
                ⧉ Duplicate drawing
              </button>
            )}
            {sheets.length > 1 && (
              <button onClick={() => setShowManageSheets(true)} style={{ ...btn(false), padding: "2px 10px", fontSize: 11.5 }} title="Reorder sheets and see each one's scale at a glance">
                ⚙ Manage
              </button>
            )}
            {pageTotals.total > 1 && !isLocked && activeSheetId != null && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={divider} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Page:</span>
                <button onClick={() => goPage(-1)} style={btn(false)} disabled={!stepPage(sheets, pageCounts, activeSheetId, pageNum, -1)} title="Previous page (continues into the previous drawing)">‹</button>
                <input key={pageTotals.position} defaultValue={pageTotals.position} inputMode="numeric" aria-label="Go to page number"
                  title={`Type a page number (1–${pageTotals.total}) and press Enter to jump there`}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => { e.stopPropagation(); if (e.key === "Enter") { jumpToPage(e.target.value); e.target.blur(); } else if (e.key === "Escape") { e.target.value = pageTotals.position; e.target.blur(); } }}
                  onBlur={e => { if (Number(e.target.value) !== pageTotals.position) jumpToPage(e.target.value); else e.target.value = pageTotals.position; }}
                  style={{ width: 36, textAlign: "center", fontSize: 12, fontWeight: 700, padding: "3px 2px", border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text2)", fontVariantNumeric: "tabular-nums" }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)" }} title={`Across all ${sheets.length} drawing${sheets.length === 1 ? "" : "s"}`}>/ {pageTotals.total}</span>
                <button onClick={() => goPage(1)} style={btn(false)} disabled={!stepPage(sheets, pageCounts, activeSheetId, pageNum, 1)} title="Next page (continues into the next drawing)">›</button>
              </span>
            )}
            {sheets.length > 1 && (
              <span style={{ display: "flex", alignItems: "center", gap: 4, position: "relative" }}>
                <span style={divider} />
                <button onClick={() => setKeyMenuOpen(o => !o)} style={{ ...btn(keyMenuOpen), padding: "2px 10px", fontSize: 11.5 }} title="Jump to a key sheet: one you starred, or one with takeoff already on it">★ Key ({keyList.length})</button>
                <button onClick={() => setKeyOnly(!keyOnly)} style={{ ...btn(keyOnly), padding: "2px 10px", fontSize: 11.5 }} aria-pressed={keyOnly} title="Show only key sheets in the drawing strip (every drawing stays in the package — Manage, the page counter and search still cover them all)">{keyOnly ? "Key only" : "All"}</button>
                {keyMenuOpen && (
                  <>
                    <div onClick={() => setKeyMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                    <div role="menu" style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 50, minWidth: 260, maxHeight: 320, overflowY: "auto", background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: 4 }}>
                      {keyList.length === 0 && <div style={{ padding: 8, fontSize: 12, color: "var(--text3)" }}>No key sheets yet — click ☆ on a drawing to mark it. A drawing with takeoff on it shows up here by itself.</div>}
                      {keyList.map(s => (
                        <button key={s.SheetID} role="menuitem" onClick={() => { onSelectSheet?.(s.SheetID); setKeyMenuOpen(false); }}
                          style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", padding: "5px 8px", border: "none", borderRadius: 6, background: s.SheetID === activeSheetId ? "var(--tint-blue)" : "transparent", cursor: "pointer", fontSize: 12 }}>
                          <span style={{ color: s.IsKeySheet ? "#d97706" : "var(--text3)" }}>{s.IsKeySheet ? "★" : "●"}</span>
                          <span style={{ flex: 1, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sheetTitle(s)}</span>
                          <span style={{ fontSize: 11, color: "var(--text3)" }}>{takeoffBadgeText(sheetTakeoffCounts, s.SheetID)}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </span>
            )}
          </>
        ) : null)}
        {!isLocked && (
          <>
            <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" onChange={uploadDrawing} style={{ display: "none" }} />
            {showTab("drawings") && grp("Drawing file", <>
              <button onClick={() => fileRef.current?.click()} style={btn(false)} title="Load a PDF (rendered crisp at any zoom) or an image into the selected drawing">
                🖼 {settings?.BackgroundImage ? "Replace drawing" : "Load drawing (PDF)"}
              </button>
              {settings?.BackgroundImage && (settings.VersionNumber > 1 || settings.NeedsReview) && onLoadSheetVersions && (
                <button onClick={() => setVersionsOpen(o => !o)} style={btn(versionsOpen)} title="This drawing's file has been replaced. See the earlier versions and restore one.">🕘 Versions (v{settings.VersionNumber || 1})</button>
              )}
              {settings?.BackgroundImage && (
                <button onClick={() => { if (window.confirm("Remove the background drawing?")) onSaveCanvas({ clear_image: 1 }); }} style={{ ...btn(false), color: "#dc2626" }} title="Remove the background drawing from the selected sheet">✕</button>
              )}
            </>)}
          </>
        )}
        {settings?.BackgroundImage && (
          <>
            {showTab("drawings") && grp("Scale", <>
              {isPdf && (
                <select
                  ref={scaleSelectRef}
                  value={settings?.ImageFtPerInch || ""}
                  disabled={isLocked}
                  onChange={e => e.target.value && pickScale(+e.target.value)}
                  style={{ fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "3px 4px", background: "var(--surface)", color: scaleIsSet ? "var(--text)" : "#d97706" }}
                  title="Pick the sheet's architectural scale — the drawing calibrates to feet instantly"
                >
                  <option value="">— set scale —</option>
                  <optgroup label="Architectural">
                    {ARCH_SCALES.map(s => <option key={s.label} value={s.ftPerInch}>{s.label}</option>)}
                  </optgroup>
                  <optgroup label="Engineering / site">
                    {ENGINEERING_SCALES.map(s => <option key={s.label} value={s.ftPerInch}>{s.label}</option>)}
                  </optgroup>
                </select>
              )}
              {isPdf && scaleNotes?.suggestion && !isLocked && !(scaleIsSet && Math.abs(settings.ImageFtPerInch - scaleNotes.suggestion.ftPerInch) / scaleNotes.suggestion.ftPerInch < 0.01) && (
                <button onClick={() => pickScale(scaleNotes.suggestion.ftPerInch)} style={{ ...btn(false), borderColor: "#16a34a", color: "#166534" }}
                  title="This sheet prints a scale note. Nothing is set until you click — a note can describe a detail rather than the whole sheet.">
                  Plan says {scaleNotes.suggestion.text} — use it
                </button>
              )}
              {isPdf && scaleNotes?.ambiguous && !scaleIsSet && (
                <span style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }} title={scaleNotes.candidates.map(c => c.text).join("  ·  ")}>Several scales are printed on this sheet — pick the right one</span>
              )}
              {isPdf && scaleIsSet && scaleNotes?.suggestion && Math.abs(settings.ImageFtPerInch - scaleNotes.suggestion.ftPerInch) / scaleNotes.suggestion.ftPerInch >= 0.01 && (
                <span style={{ fontSize: 11, color: "#b45309", fontWeight: 700 }} title="The scale you set differs from the one printed on the sheet. Fine for a detail sheet; check it if this is a plan.">
                  ≠ plan prints {scaleNotes.suggestion.text}
                </span>
              )}
              <button data-ribbon-close onClick={toggleCalibrate} style={btn(!!calib)} disabled={isLocked}
                title="Click two points a known distance apart, then enter the real feet — verifies or overrides the scale">
                {calib ? `Click point ${calib.points.length + 1} of 2…` : (isPdf ? "📐 Verify scale" : "📐 Set scale")}
              </button>
              {scaleCheck && (
                <span title={`Last check on this sheet: measured ${fmt(scaleCheck.measuredFt, 2)} ft vs printed ${fmt(scaleCheck.expectedFt, 2)} ft${scaleCheck.recalibrated ? " — re-calibrated to the printed dimension" : " — calibration kept"}. Session only; check again after a reload.`}
                  style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: scaleCheck.verdict === "good" ? "var(--tint-green)" : scaleCheck.verdict === "review" ? "var(--tint-amber)" : "var(--tint-red)", color: scaleCheck.verdict === "good" ? "#166534" : scaleCheck.verdict === "review" ? "#92400e" : "#991b1b" }}>
                  {scaleCheck.recalibrated ? "Scale ✓ re-calibrated" : scaleCheck.verdict === "good" ? `Scale ✓ ${fmtPct(scaleCheck.errorPct)}` : scaleCheck.verdict === "review" ? `Scale ⚠ ${fmtPct(scaleCheck.errorPct)} off` : `Scale ✕ ${fmtPct(scaleCheck.errorPct)} off`}
                </span>
              )}
              {!scaleIsSet && (
                <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
                  Not to scale yet — {isPdf ? "pick a scale" : "use 📐 Set scale"}
                </span>
              )}
              {currentArch && (
                <span style={{ fontSize: 11, color: currentArch.errPct < 2 ? "#16a34a" : "#d97706", fontWeight: 600 }}
                  title="Nearest standard architectural scale to the current calibration">
                  ≈ {currentArch.scale.label}{currentArch.errPct >= 2 ? ` (${fmt(currentArch.errPct, 0)}% off)` : " ✓"}
                </span>
              )}
            </>)}
            {showTab("drawings") && grp("Position", <>
              <button data-ribbon-close onClick={toggleMoveDrawing} style={btn(imgMode)} disabled={isLocked}
                title="Drag to reposition the drawing under your rooms">✥ Move drawing</button>
              <button onClick={() => rotateDrawing(-90)} style={{ ...btn(false), opacity: rotateBlockedReason ? 0.45 : 1 }} aria-disabled={!!rotateBlockedReason}
                title={rotateBlockedReason || "Turn the DRAWING 90° counter-clockwise (rooms and conditions are not turned - use R for a selected room)"}>⟲ Rotate drawing</button>
              <button onClick={() => rotateDrawing(90)} style={{ ...btn(false), opacity: rotateBlockedReason ? 0.45 : 1 }} aria-disabled={!!rotateBlockedReason}
                title={rotateBlockedReason || "Turn the DRAWING 90° clockwise (rooms and conditions are not turned - use R for a selected room)"}>⟳ Rotate drawing</button>
              {rot !== 0 && <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 999, background: "var(--surface)", border: "1px solid var(--cs-border)", color: "var(--text2)" }} title="The drawing is shown turned clockwise by this much">Turned {rot}°</span>}
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text3)" }}>
                Opacity
                <input type="range" min={10} max={100} defaultValue={(settings?.ImageOpacity ?? 0.5) * 100}
                  onMouseUp={e => onSaveCanvas({ image_opacity: +e.target.value / 100 })}
                  onTouchEnd={e => onSaveCanvas({ image_opacity: +e.target.value / 100 })}
                  style={{ width: 80 }} disabled={isLocked} />
              </label>
            </>)}
            {showTab("review") && isPdf && onPrefillSheet && !isLocked && grp("Automation", (
              <button onClick={onPrefillSheet} disabled={analyzing} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: analyzing ? "default" : "pointer",
                background: analyzing ? "var(--surface)" : "#f5f3ff",
                color: analyzing ? "var(--text3)" : "#5b21b6",
                border: "1px solid " + (analyzing ? "var(--cs-border)" : "#ddd6fe"),
                borderRadius: 6, opacity: analyzing ? 0.8 : 1,
              }} title={scaleIsSet
                ? "AI reads the rooms off this sheet's currently-viewed page, using its already-calibrated scale"
                : "AI reads the rooms off this sheet — set a scale first for accurate dimensions, or it'll have to guess one from the sheet"}>
                {analyzing ? <><span className="spinner spinner-dark" style={{ width: 11, height: 11 }} />Analyzing…</> : <>🪄 Auto-Takeoff</>}
              </button>
            ))}
          </>
        )}
        {showTab("view") && grp("Legend", <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, cursor: activeSheetId == null ? "default" : "pointer", opacity: activeSheetId == null ? 0.5 : 1 }} title="Draw a legend ON the drawing — the conditions and their quantities, part of the markup (it is in snapshots and printouts)">
            <input type="checkbox" checked={legend.on} disabled={activeSheetId == null} onChange={e => setLegend({ on: e.target.checked })} aria-label="Show legend" /> Show
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: legend.on ? "pointer" : "default", opacity: legend.on ? 1 : 0.5 }} title="Show each condition's item code (like W-1 or HEPA-A-1) before its name in the legend">
            <input type="checkbox" checked={legend.code} disabled={!legend.on} onChange={e => setLegend({ code: e.target.checked })} aria-label="Legend item code" /> Item code
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, opacity: legend.on ? 1 : 0.5 }} title="Legend text size in paper points — it prints at this size on the sheet, whatever the zoom">
            Size
            <select value={legend.fontPt} disabled={!legend.on} onChange={e => setLegend({ fontPt: +e.target.value })} aria-label="Legend font size"
              style={{ width: "auto", fontSize: 12, padding: "2px 4px", border: "1px solid var(--cs-border)", borderRadius: 5, background: "var(--surface)", color: "var(--text)" }}>
              {LEGEND_FONT_SIZES.map(n => <option key={n} value={n}>{n} pt</option>)}
            </select>
          </label>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          {[1, 2, 3].map(n => (
            <label key={n} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: legend.on ? "pointer" : "default", opacity: legend.on ? 1 : 0.5 }} title={`Include Quantity ${n} (the condition's ${n === 1 ? "first / priced" : n === 2 ? "second" : "third"} reading) in the legend`}>
              <input type="checkbox" checked={legend[`q${n}`]} disabled={!legend.on} onChange={e => setLegend({ [`q${n}`]: e.target.checked })} aria-label={`Legend quantity ${n}`} /> Qty {n}
            </label>
          ))}
          </div>
        </div>)}
        {/* Options: right-hand end of this row, directly below Help (same width) */}
        {!tabbed && <div style={{ display: "grid", width: SIDE_TILE_W }}>{optionsTile}</div>}
      </div>
      </div>
      )}
      </div>

      {!focusMode && <ConditionStrip resolved={resolvedPins} armedKey={condMode ? condPick : areaMode ? areaPick : symbolMode ? symbolPick : null} locked={isLocked}
        onArm={beginConditionDraw} onUnpin={toggleTypePin} />}

      {/* Back-Out element picker (2026-09-14) — the command-bar entry to the
          D-2 back-out tool. A back-out deducts from ONE Room Element's area, so
          the estimator names the element here; the element card's own
          "+ Add Back-Out" stays the direct path from inside a room. */}
      {backOutPickerOpen && (() => {
        const list = backOutEligibleElements(roomElements, rooms, { selectedRoomIds: selectedIds });
        return (
          <div style={{ border: "1px solid #fca5a5", background: "var(--tint-red)", borderRadius: 8, padding: "8px 10px", marginBottom: 8, fontSize: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <strong style={{ color: "#991b1b" }}>⊟ Back-Out — which element does it deduct from?</strong>
              <span style={{ color: "var(--text3)", flex: 1, minWidth: 200 }}>Scope geometry on one floor- or ceiling-area element; the room boundary is untouched. Gross − back-outs = calculated net.</span>
              <button onClick={() => setBackOutPickerOpen(false)} style={iconBtn} title="Close">✕</button>
            </div>
            {list.length === 0 && (
              <div style={{ color: "var(--text2)" }}>No eligible element — a back-out needs a floor- or ceiling-area Room Element whose quantity is calculated from the room (not fixed, placed or a formula) on a room placed on the canvas. Add one from Room Details.</div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {list.slice(0, 40).map(x => (
                <button key={x.element.RoomElementID} onClick={() => { setBackOutPickerOpen(false); beginBackOutDraw(x.element); }}
                  style={{ ...btn(false), borderColor: x.selected ? "var(--cs-blue)" : undefined, textAlign: "left" }}
                  title={`Trace a back-out against ${x.element.Name || x.element.Code} in ${x.room.RoomName}`}>
                  <span style={{ fontWeight: 700 }}>{x.room.RoomName}</span> · {x.element.Name || x.element.Code} <span style={{ color: "var(--text3)" }}>({x.basis === "ceiling_area" ? "ceiling" : "floor"})</span>
                </button>
              ))}
              {list.length > 40 && <span style={{ color: "var(--text3)", alignSelf: "center" }}>… {list.length - 40} more — select a room on the canvas to narrow the list</span>}
            </div>
          </div>
        );
      })()}

      {showManageSheets && (
        <ManageSheetsDialog
          sheets={sheets} activeSheetId={activeSheetId} isLocked={isLocked}
          onSelect={id => { onSelectSheet(id); setShowManageSheets(false); }}
          onRename={onRenameSheet} onReorder={onReorderSheet} onDelete={onDeleteSheet} onSaveMeta={onSaveSheetMeta}
          onClose={() => setShowManageSheets(false)}
        />
      )}

      </div>

      {/* Unplaced tray */}
      {unplaced.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Not placed:</span>
          {unplaced.map(r => (
            <button key={r.RoomID} onClick={() => placeRoom(r)} disabled={isLocked}
              title="Click to place on the canvas, then drag into position"
              style={{ padding: "3px 10px", fontSize: 12, fontWeight: 600, background: "var(--tint-amber)", border: "1px dashed #f3d689", borderRadius: 999, cursor: "pointer", color: "var(--text2)" }}>
              {r.RoomName} <span style={{ color: "var(--text3)" }}>{fmt(r.LengthFt)}×{fmt(r.WidthFt)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Canvas + right dock */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", position: "relative" }}>
      <div style={{ flex: 1, minWidth: 0, border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden", background: view.canvasBg, position: "relative" }}>
        <svg
          ref={svgRef}
          style={{ width: "100%", height: canvasHeight, display: "block", cursor: sweepMode || condMode || routeMode || symbolMode || drawMode || calib || measureMode ? "crosshair" : imgMode ? "move" : panning && !panning.isImage ? "grabbing" : "grab", touchAction: "none" }}
          onPointerDown={onPointerDown}
          onContextMenu={onCanvasContextMenu}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={() => { setCrosshairPos(null); if (cursorElRef.current) cursorElRef.current.textContent = "—"; }}
          onDoubleClick={() => { if (condMode) finishConditionRun(); else if (routeMode) finishRouteDraw(); else if (areaMode) finishAreaCondition(); else if (backOutMode) finishBackOut(); else if (drawMode && roomPts.length > 0) finishRoomPolyline(); }}
        >
          <g transform={`translate(${pan.x},${pan.y}) scale(${pxPerFt})`}>
            {/* Background drawing — PDF (live raster) or legacy raster */}
            {!hiddenLayers.has("background") && (isPdf
              ? (renderUrl && renderRect && (
                  <image href={renderUrl} x={renderRect.x + dxFt} y={renderRect.y + dyFt}
                    width={renderRect.w} height={renderRect.h}
                    opacity={settings?.ImageOpacity ?? 0.5} preserveAspectRatio="none" style={{ filter: view.rasterFilter }} />
                ))
              : (rasterSrc && rotDims && (
                  <image href={rasterSrc} x={imgX + dxFt} y={imgY + dyFt}
                    width={rotDims.w * imgScale} height={rotDims.h * imgScale}
                    opacity={settings?.ImageOpacity ?? 0.5} preserveAspectRatio="none" style={{ filter: view.rasterFilter }} />
                )))}

            {/* 5 ft grid */}
            <defs>
              <pattern id="takeoff-grid" width="5" height="5" patternUnits="userSpaceOnUse">
                <path d="M 5 0 L 0 0 0 5" fill="none" stroke={view.gridStroke} strokeWidth="0.05" opacity={view.gridOpacity} />
              </pattern>
              {/* Hatch fills — one def per (hatch, color, spacing) in use by a drawn
                  area OR a placed count symbol (plus the symbol type being
                  placed, for the ghost preview); see areaFillPattern() */}
              {(() => {
                const seen = new Set();
                const sources = [
                  ...takeoffConditions.filter(c => (c.Style === "area" || c.Style === "count") && AREA_HATCH_LINES[c.Pattern]),
                  ...(symbolMode && symbolPickType && AREA_HATCH_LINES[symbolPickType.Pattern] ? [{ Pattern: symbolPickType.Pattern, Color: symbolPickType.Color || "#b45309", PatternSpacingIn: symbolPickType.PatternSpacingIn }] : []),
                ];
                return sources.map((c, idx) => {
                  const color = c.Color || "#0d9488";
                  const id = areaPatternId(c.Pattern, color, c.PatternSpacingIn);
                  if (seen.has(id)) return null;
                  seen.add(id);
                  const tile = areaHatchTileFt(c.PatternSpacingIn, pxPerFt); // world-ft per hatch repeat: real inches when set, else 9 screen px
                  return (
                    <pattern key={`${id}-${idx}`} id={id} width={tile} height={tile} patternUnits="userSpaceOnUse">
                      <g transform={`scale(${tile})`}>
                        {AREA_HATCH_LINES[c.Pattern].map((d, i) => (
                          <path key={i} d={d} fill="none" stroke={color} strokeWidth={1 / (pxPerFt * tile)} />
                        ))}
                      </g>
                    </pattern>
                  );
                });
              })()}
              {/* Tile-grid overlays (OST "Display Pattern While Drawing") — one per
                  drawn area whose type defines GridTileWFt/HFt (db/30): a tile
                  outline repeating at the pitch (tile + gap), anchored at the
                  polygon's bbox corner exactly like gridTileTakeoff() counts it,
                  so what's drawn is what's counted. */}
              {takeoffConditions.filter(c => c.Style === "area" && c.GridTileWFt > 0 && c.GridTileHFt > 0).map(c => {
                const g = parseGeom(c);
                if (!g?.points?.length) return null;
                const minX = Math.min(...g.points.map(p => p.x)), minY = Math.min(...g.points.map(p => p.y));
                const gapFt = Math.max(0, +c.GridGapIn || 0) / 12;
                const pw = +c.GridTileWFt + gapFt, ph = +c.GridTileHFt + gapFt;
                const color = c.Color || "#0d9488";
                return (
                  <pattern key={areaGridId(c)} id={areaGridId(c)} x={minX} y={minY} width={pw} height={ph} patternUnits="userSpaceOnUse">
                    <rect x={0} y={0} width={+c.GridTileWFt} height={+c.GridTileHFt} fill="none" stroke={color} strokeWidth={1 / pxPerFt} opacity={0.7} />
                  </pattern>
                );
              })}
            </defs>
            <rect x={-2000} y={-2000} width={4000} height={4000} fill="url(#takeoff-grid)" pointerEvents="none" />

            {/* Calibration markers (world ft) */}
            {calib?.points.map((p, i) => (
              <circle key={i} cx={p.wx} cy={p.wy} r={4 / pxPerFt} fill="#dc2626" />
            ))}

            {/* Count by example — the marquee while it is drawn, then a numbered marker per found symbol on THIS drawing */}
            {sweepBox && sweepMode && (
              <rect x={Math.min(sweepBox.x0, sweepBox.x1)} y={Math.min(sweepBox.y0, sweepBox.y1)} width={Math.abs(sweepBox.x1 - sweepBox.x0)} height={Math.abs(sweepBox.y1 - sweepBox.y0)}
                fill="rgba(37,99,235,0.12)" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="5 3" vectorEffect="non-scaling-stroke" pointerEvents="none" />
            )}
            {sweepSeed && sweepSeed.sheetId === activeSheetId && (
              <rect x={Math.min(sweepSeed.box.x0, sweepSeed.box.x1)} y={Math.min(sweepSeed.box.y0, sweepSeed.box.y1)} width={Math.abs(sweepSeed.box.x1 - sweepSeed.box.x0)} height={Math.abs(sweepSeed.box.y1 - sweepSeed.box.y0)}
                fill="none" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="5 3" vectorEffect="non-scaling-stroke" pointerEvents="none" />
            )}
            {sweepMarkers.filter(m => m.sheetId === activeSheetId).map((m, i) => {
              const col = m.alreadyPlaced ? "#6b7280" : m.tier === "match" ? "#16a34a" : "#d97706";
              return (
                <g key={m.id} pointerEvents="none" opacity={m.checked ? 1 : 0.5}>
                  <circle cx={m.x} cy={m.y} r={9 / pxPerFt} fill={m.checked ? col : "none"} fillOpacity={0.85} stroke={col} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  <text x={m.x} y={m.y} dy={3.5 / pxPerFt} textAnchor="middle" fontSize={10 / pxPerFt} fontWeight={800} fill={m.checked ? "#fff" : col}>{i + 1}</text>
                </g>
              );
            })}

            {/* Measure tool — read-only ruler, stays visible after release until
                the next drag so the readout can actually be compared against
                the drawing's printed dimension */}
            {measureLine && (() => {
              const distFt = Math.hypot(measureLine.x1 - measureLine.x0, measureLine.y1 - measureLine.y0);
              const mid = { x: (measureLine.x0 + measureLine.x1) / 2, y: (measureLine.y0 + measureLine.y1) / 2 };
              const angle = Math.atan2(measureLine.y1 - measureLine.y0, measureLine.x1 - measureLine.x0) * 180 / Math.PI;
              const flipped = angle > 90 || angle < -90;
              return (
                <g pointerEvents="none">
                  <line x1={measureLine.x0} y1={measureLine.y0} x2={measureLine.x1} y2={measureLine.y1}
                    stroke="#dc2626" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  <circle cx={measureLine.x0} cy={measureLine.y0} r={2.5 / pxPerFt} fill="#dc2626" />
                  <circle cx={measureLine.x1} cy={measureLine.y1} r={2.5 / pxPerFt} fill="#dc2626" />
                  <text x={mid.x} y={mid.y} dy={-0.8} textAnchor="middle"
                    transform={`rotate(${flipped ? angle + 180 : angle}, ${mid.x}, ${mid.y})`}
                    fontSize={Math.min(1.6, 13 / pxPerFt)} fontWeight={800} fill="#dc2626"
                    style={{ ...haloStroke(view, Math.min(1.6, 13 / pxPerFt)) }}
                  >{isMetric(units) ? fmtLength(distFt, units) : `${fmtFtIn(distFt)} (${fmt(distFt, 1)}′)`}</text>
                </g>
              );
            })()}

            {/* Room being drawn */}
            {drawRect && (() => {
              const x = Math.min(drawRect.x0, drawRect.x1), y = Math.min(drawRect.y0, drawRect.y1);
              const w = Math.abs(drawRect.x1 - drawRect.x0), h = Math.abs(drawRect.y1 - drawRect.y0);
              return (
                <g pointerEvents="none">
                  <rect x={x} y={y} width={w} height={h} fill="#2563eb" fillOpacity={0.12} stroke="#2563eb" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeDasharray="4 3" />
                  <text x={x + w / 2} y={y + h / 2} textAnchor="middle" fontSize={Math.max(1.1, Math.min(2, w / 10))} fontWeight="700" fill="#1e3a5f">
                    {dimText(w, units, { denom: 8 })} × {dimText(h, units, { denom: 8 })}
                  </text>
                </g>
              );
            })()}

            {/* Room being traced as a clicked outline — an alternative to
                drag-a-box; the room it creates is still the bounding
                rectangle of the clicked corners (rooms have no polygon
                shape of their own). */}
            {(() => {
              const trace = drawMode && roomPts.length ? { pts: roomPts, cursor: roomCursor }
                : condMode && condPts.length ? { pts: condPts, cursor: condCursor }
                : routeMode && routePts.length ? { pts: routePts, cursor: routeCursor }
                : areaMode && areaPts.length ? { pts: areaPts, cursor: areaCursor }
                : backOutMode && backOutPts.length ? { pts: backOutPts, cursor: backOutCursor } : null;
              if (!trace?.cursor) return null;
              // what is being traced, measured as far as the cursor: a run's length, an outline's area
              const kind = condMode || routeMode ? "linear" : "area";
              const code = condMode ? condPickType?.Code : areaMode ? areaPickType?.Code : null;
              const q = formatQuantity(runningQuantity(kind, trace.pts, trace.cursor), units);
              const extra = q ? `${code ? `${code} · ` : ""}${q}` : "";
              return <TraceReadout prev={trace.pts[trace.pts.length - 1]} cursor={trace.cursor} raw={crosshairPos} pxPerFt={pxPerFt} view={view} extra={extra} />;
            })()}
            {drawMode && roomPts.length > 0 && (() => {
              const chain = roomCursor ? [...roomPts, roomCursor] : roomPts;
              const ptsStr = chain.map(p => `${p.x},${p.y}`).join(" ");
              const xs = chain.map(p => p.x), ys = chain.map(p => p.y);
              const bx = Math.min(...xs), by = Math.min(...ys);
              const bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;
              return (
                <g pointerEvents="none">
                  <rect x={bx} y={by} width={bw} height={bh} fill="#2563eb" fillOpacity={0.06} stroke="#2563eb" strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray="2 2" />
                  <polyline points={ptsStr} fill="none" stroke="#2563eb" strokeWidth={3} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  {roomPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3 / pxPerFt} fill="#2563eb" />)}
                  <RightAngleGlyph chain={chain} pxPerFt={pxPerFt} color="#2563eb" />
                  {roomCursor && (
                    <text x={roomCursor.x + 1.5} y={roomCursor.y - 1} fontSize={Math.min(1.4, 13 / pxPerFt)} fontWeight={800} fill="#2563eb">
                      {fmt(Math.round(bw * 2) / 2)}′ × {fmt(Math.round(bh * 2) / 2)}′
                    </text>
                  )}
                </g>
              );
            })()}

            {/* Edge Halo (Room Boundary Intelligence, vision doc Section 4/6)
                — a translucent FILL band hugging each room's own boundary
                and extending outward by HALO_DISPLAY_DEPTH_FT (a purely
                visual depth, decoupled from CAVITY_MAX_GAP_FT's detection
                radius as of 2026-09-10) — see roomHaloFillSegments() above. Rendered first/furthest back so
                rooms and joint lines all sit on top of it. `haloRooms`/
                `haloEdges` fold in the room currently being drawn (see
                draftRoom above), so the halo reacts live while the
                estimator is still tracing an outline — both the new room's
                own halo AND the affected stretch of an already-placed
                neighbor's halo update as the outline nears it, exactly the
                "halo as visual QA while drawing" interaction the vision doc
                describes (Section 6). A stretch with a real construction
                assigned (`tier==="assigned"`) fills with that construction's
                own catalog color; otherwise it falls back to the confidence-
                tier palette (`haloStyle`), folding in what used to be the
                separate "Wall assembly colors (ring)" layer. A second,
                independent channel — the border — reuses EDGE_STYLE's own
                shared=green/cavity=blue (solid vs. dashed) so a common wall
                vs. a free-standing/chase wall is visible without disturbing
                the fill's own confidence/assignment signal; an 'ext' stretch
                (no other room — nothing to classify as shared or cavity)
                gets no border, same as before. A 'shared' stretch is one
                physical wall shared by both rooms — each room's own call to
                roomHaloFillSegments() independently computes the identical
                pairKey/color for it, so `sharedPairsDrawn` below dedupes to a
                single rect per pairKey (rendered once, by whichever room's
                turn in haloRooms comes first) instead of both rooms stacking
                their own halo-depth margin on top of each other — for
                two rooms with a near-zero gap that used to render as a
                visibly doubled/overlapping band right on the joint. That
                single rect is centered on the joint's true midpoint (sg.mid)
                rather than hanging off one room's own wall face, so it reads
                as the one shared wall it actually is. Cavity is untouched —
                it's genuinely two independent wall faces, not one wall
                counted twice, so both keep rendering. */}
            {!hiddenLayers.has("edgeHalo") && (() => {
              const sharedPairsDrawn = new Set();
              const depth = tunables.HALO_DISPLAY_DEPTH_FT;
              return haloRooms.map(room => {
                if (!rectOf(room) || !roomLayerVisible(room)) return null;
                const halo = roomHaloFillSegments(room, haloEdges, condByAutoKey, tunables);
                return (
                  <g key={`halo-${room.RoomID}`} pointerEvents="none">
                    {halo.map((sg, i) => {
                      if (sg.pairKey) {
                        if (sharedPairsDrawn.has(sg.pairKey)) return null;
                        sharedPairsDrawn.add(sg.pairKey);
                      }
                      const isAssigned = sg.tier === "assigned";
                      const style = isAssigned ? null : (haloStyle[sg.tier] || haloStyle.none);
                      const color = isAssigned ? sg.color : style.stroke;
                      const opacity = isAssigned ? HALO_ASSIGNED_OPACITY : style.opacity;
                      const typeStyle = sg.type && EDGE_STYLE[sg.type];
                      // Extrude the perimeter stretch outward along its own
                      // normal: a shared stretch is centered on the joint
                      // midline (offset), anything else hugs the face.
                      const [lo, hi] = sg.pairKey ? [sg.offset - depth / 2, sg.offset + depth / 2] : [0, depth];
                      const n = sg.normal;
                      const pts = [
                        [sg.p1.x + n.x * lo, sg.p1.y + n.y * lo], [sg.p2.x + n.x * lo, sg.p2.y + n.y * lo],
                        [sg.p2.x + n.x * hi, sg.p2.y + n.y * hi], [sg.p1.x + n.x * hi, sg.p1.y + n.y * hi],
                      ].map(p => p.join(",")).join(" ");
                      return (
                        <polygon key={i} points={pts} fill={color} opacity={opacity}
                          stroke={typeStyle?.stroke} strokeOpacity={typeStyle ? 0.85 : 0}
                          strokeWidth={typeStyle ? 1.5 : 0} vectorEffect="non-scaling-stroke"
                          strokeDasharray={sg.type === "cavity" ? "5 3" : "none"} />
                      );
                    })}
                  </g>
                );
              });
            })()}

            {/* Rooms */}
            {!hiddenLayers.has("rooms") && effRooms.map(room => {
              const R = rectOf(room);
              if (!R || !roomLayerVisible(room)) return null; // a room whose template sits on a hidden Takeoff Layer (db/66) hides too
              // Room Templates Phase F-2: a template's own admin-picked Color
              // (db/55_room_templates.sql) is the only source now — the fixed
              // legacy ROOM_TYPE_COLORS palette (only ever covered the ~12
              // hardcoded RoomType values, couldn't represent an admin-
              // created template) is retired. A room with no template, or a
              // template with no color set, gets a neutral gray.
              const color = colorMode === "type"
                ? (roomTemplates?.find(t => t.RoomTemplateID === room.RoomTemplateID)?.Color || "#94a3b8")
                : (room.AHUZoneID ? (zoneColor[room.AHUZoneID] || "#3b82f6") : "#94a3b8");
              const selected = selectedIds.has(room.RoomID);
              const soloSelected = !isLocked && selectedId === room.RoomID && !calib && !imgMode && !drawMode && !condMode && !routeMode && !symbolMode;
              // Screen-constant (never balloons at high zoom, same technique
              // as iconR/hs below) AND content-fit (accounts for the name's
              // actual length against the room's real footprint, not just
              // room width) — was previously R.w/10 alone, which reacted
              // only to width, so a long name in a modest-width room
              // overflowed straight into a neighboring room's own label.
              // Falls back to an ellipsis-truncated name if even the
              // minimum readable size can't fit.
              const AVG_CHAR_W = 0.62; // same glyph-width approximation used for labelW elsewhere in this file
              const nameCapFt = 12 / pxPerFt;
              const nameWidthFt = (R.w * 0.85) / Math.max(1, room.RoomName.length * AVG_CHAR_W);
              const nameHeightFt = R.h * 0.3;
              const fontFt = Math.max(6 / pxPerFt, Math.min(nameCapFt, nameWidthFt, nameHeightFt));
              const maxChars = Math.max(3, Math.floor((R.w * 0.85) / (fontFt * AVG_CHAR_W)));
              const displayName = room.RoomName.length > maxChars ? room.RoomName.slice(0, maxChars - 1) + "…" : room.RoomName;
              const hs = 3.2 / pxPerFt; // handle half-size, screen-constant
              // Edit/delete badges, screen-constant like the resize handles
              // above — previously a flat world-ft radius, so they'd balloon
              // to cover the whole room (and overlap each other, making
              // "edit" unclickable) at high zoom.
              // AI auto-takeoff can trace a room's real (non-rectangular)
              // boundary from the drawing's own vector walls (PolygonJson).
              // The VISUAL fill/stroke follows that true shape when present;
              // resize handles, drag, and every other interaction below
              // still operate on R (rectOf's bounding box) unchanged — see
              // Platform_Reference.md's explicitly-deferred list for why.
              const poly = polygonOf(room);
              const polyPointsStr = poly ? poly.map(p => `${p.x},${p.y}`).join(" ") : null;
              // Track G: an AI-proposed room is drawn dashed with a tag until accepted
              const proposed = room.DetectionStatus === "proposed";
              const dash = proposed ? "6 4" : undefined;
              return (
                <g key={room.RoomID} data-room-id={room.RoomID}>
                  <g
                    onPointerDown={e => startRoomDrag(e, room)}
                    style={{ cursor: isLocked || drawMode || room.PositionLocked ? "default" : "move" }}>
                    {polyPointsStr ? (
                      <polygon points={polyPointsStr}
                        fill={color} fillOpacity={proposed ? 0.08 : 0.16}
                        stroke={selected ? "#1e3a5f" : color} strokeDasharray={dash}
                        strokeWidth={selected ? 2.5 : 1.5} vectorEffect="non-scaling-stroke" />
                    ) : (
                      <rect x={R.x} y={R.y} width={R.w} height={R.h} rx={0.3}
                        fill={color} fillOpacity={proposed ? 0.08 : 0.16}
                        stroke={selected ? "#1e3a5f" : color} strokeDasharray={dash}
                        strokeWidth={selected ? 2.5 : 1.5} vectorEffect="non-scaling-stroke" />
                    )}
                    {proposed && (
                      <text x={R.x + R.w / 2} y={R.y + R.h / 2 - fontFt * 1.4} textAnchor="middle" fontSize={fontFt * 0.7} fontWeight={800} fill="#b45309"
                        style={{ userSelect: "none", ...haloStroke(view, fontFt * 0.7) }}>
                        🤖 AI PROPOSAL{room.DetectionConfidence != null ? ` · ${Math.round(+room.DetectionConfidence * 100)}%` : ""}
                      </text>
                    )}
                    <text x={R.x + R.w / 2} y={R.y + R.h / 2 - fontFt * 0.3} textAnchor="middle"
                      fontSize={fontFt} fontWeight="700" fill={view.textFill} style={{ userSelect: "none" }}>
                      <title>{room.RoomName}</title>
                      {displayName}
                    </text>
                    <text x={R.x + R.w / 2} y={R.y + R.h / 2 + fontFt * 1.1} textAnchor="middle"
                      fontSize={fontFt * 0.75} fill="var(--text3)" style={{ userSelect: "none" }}>
                      {dimText(room.LengthFt, units, { denom: 8 })} × {dimText(room.WidthFt, units, { denom: 8 })}
                    </text>
                  </g>
                  {soloSelected && onResizeRoom && (
                    <>
                      {[
                        { edge: "n",  x: R.x + R.w / 2, y: R.y,         cursor: "ns-resize" },
                        { edge: "s",  x: R.x + R.w / 2, y: R.y + R.h,   cursor: "ns-resize" },
                        { edge: "e",  x: R.x + R.w,      y: R.y + R.h / 2, cursor: "ew-resize" },
                        { edge: "w",  x: R.x,            y: R.y + R.h / 2, cursor: "ew-resize" },
                        { edge: "ne", x: R.x + R.w,      y: R.y,         cursor: "nesw-resize" },
                        { edge: "nw", x: R.x,            y: R.y,         cursor: "nwse-resize" },
                        { edge: "se", x: R.x + R.w,      y: R.y + R.h,   cursor: "nwse-resize" },
                        { edge: "sw", x: R.x,            y: R.y + R.h,   cursor: "nesw-resize" },
                      ].map(h => (
                        <rect key={h.edge} x={h.x - hs} y={h.y - hs} width={hs * 2} height={hs * 2}
                          fill="#1e3a5f" stroke="#fff" strokeWidth={0.3} vectorEffect="non-scaling-stroke"
                          style={{ cursor: h.cursor }} onPointerDown={e => startResize(e, room, h.edge)} />
                      ))}
                    </>
                  )}
                  {soloSelected && poly && onEditRoomPolygon && (
                    <>
                      {poly.map((p, i) => {
                        const next = poly[(i + 1) % poly.length];
                        const mid = { x: (p.x + next.x) / 2, y: (p.y + next.y) / 2 };
                        return (
                          <g key={`vtx${i}`}>
                            {/* Segment midpoint — drag to insert a new corner there */}
                            <circle cx={mid.x} cy={mid.y} r={hs * 0.7}
                              fill="#93c5fd" stroke="#fff" strokeWidth={0.3} vectorEffect="non-scaling-stroke"
                              style={{ cursor: "copy" }} onPointerDown={e => startMidpointDrag(e, room, poly, i)}>
                              <title>Drag to add a corner</title>
                            </circle>
                            {/* Existing corner — drag to move it */}
                            <rect x={p.x - hs} y={p.y - hs} width={hs * 2} height={hs * 2}
                              fill="#1e3a5f" stroke="#fff" strokeWidth={0.3} vectorEffect="non-scaling-stroke"
                              style={{ cursor: "move" }} onPointerDown={e => startVertexDrag(e, room, poly, i)} onDoubleClick={e => deleteVertex(e, room, poly, i)}>
                              <title>Drag to move this corner · double-click to remove it</title>
                            </rect>
                          </g>
                        );
                      })}
                    </>
                  )}
                  {/* Room details and Delete live on the right-click menu — no on-canvas buttons to hit by accident. */}
                </g>
              );
            })}

            {/* Live drag-snap indicator — a small label riding just above the
                room being dragged, confirming which snap tier resolved its
                CURRENT (already-snapped-live, see onPointerMove's `drag`
                branch) position: real wall > neighboring room > coarse 0.5 ft
                grid fallback. Answers "did I actually hit the wall, or just
                the grid" without needing to drop and check afterward. */}
            {drag && dragSnapTier && (() => {
              const dragRoom = effRooms.find(r => r.RoomID === drag.roomId);
              const R = dragRoom && rectOf(dragRoom);
              if (!R) return null;
              const label = dragSnapTier === "free" ? "✋ free (no snap)"
                : dragSnapTier === "wall" ? "🧲 snapped to wall"
                : dragSnapTier === "room" ? "🧲 snapped to room" : "0.5′ grid";
              const color = dragSnapTier === "free" ? "#7c3aed" : dragSnapTier === "wall" ? "#16a34a" : dragSnapTier === "room" ? "#2563eb" : "#78818f";
              const fontFt = 11 / pxPerFt;
              const labelW = label.length * fontFt * 0.62 + (8 / pxPerFt);
              return (
                <g transform={`translate(${R.x + R.w / 2},${R.y - 8 / pxPerFt})`} pointerEvents="none">
                  <rect x={-labelW / 2} y={-fontFt * 1.7} width={labelW} height={fontFt * 1.7} rx={3 / pxPerFt} fill={color} opacity={0.94} />
                  <text x={0} y={-fontFt * 0.55} textAnchor="middle" fontSize={fontFt} fontWeight={800} fill="#fff" style={{ userSelect: "none" }}>
                    {label}
                  </text>
                </g>
              );
            })()}

            {/* Same live feedback for single-vertex dragging — previously
                only whole-room drag got a tier label; now that both share the
                Alt-to-override mechanic, a vertex drag needs one too so the
                override is visually confirmed here as well. */}
            {vertexDrag && (() => {
              const pts = liveVertices[vertexDrag.roomId];
              const p = pts?.[vertexDrag.index];
              if (!p) return null;
              const label = "✋ free (no snap)";
              const fontFt = 11 / pxPerFt;
              const labelW = label.length * fontFt * 0.62 + (8 / pxPerFt);
              return vertexDragFree ? (
                <g transform={`translate(${p.x},${p.y - 8 / pxPerFt})`} pointerEvents="none">
                  <rect x={-labelW / 2} y={-fontFt * 1.7} width={labelW} height={fontFt * 1.7} rx={3 / pxPerFt} fill="#7c3aed" opacity={0.94} />
                  <text x={0} y={-fontFt * 0.55} textAnchor="middle" fontSize={fontFt} fontWeight={800} fill="#fff" style={{ userSelect: "none" }}>
                    {label}
                  </text>
                </g>
              ) : null;
            })()}

            {/* Joints: shared walls + return cavities */}
            {edges.map((e, i) => {
              const s = EDGE_STYLE[e.type] || EDGE_STYLE.ignore;
              const midX = (e.seg.x1 + e.seg.x2) / 2, midY = (e.seg.y1 + e.seg.y2) / 2;
              const chase = e.type === "cavity" ? edgeLookup(chaseAttrs, e) : null;
              return (
                <g key={i} onPointerDown={ev => ev.stopPropagation()}
                  onClick={() => !isLocked && onCycleEdge(e)}
                  onContextMenu={ev => {
                    // Right-click opens Chase Details on a cavity edge —
                    // deliberately a different gesture than the plain click
                    // (which cycles shared/cavity/ignore/auto) so annotating
                    // a chase never accidentally re-classifies it.
                    if (e.type !== "cavity" || !onSaveChaseAttributes) return;
                    ev.preventDefault();
                    ev.stopPropagation();
                    setChaseEdgeKey(edgeOverrideKey(e.a.RoomID, e.b.RoomID, e.runKey));
                  }}
                  style={{ cursor: isLocked ? "default" : "pointer" }}>
                  {e.type === "cavity" && <title>Right-click for Chase Details</title>}
                  {e.type === "conflict" && <title>These rooms are drawn overlapping — move one. Click to silence.</title>}
                  {e.type === "cavity" && e.gapFt > 0.05 && (
                    <rect
                      x={e.orientation === "v" ? e.seg.x1 - e.gapFt / 2 : e.seg.x1}
                      y={e.orientation === "v" ? e.seg.y1 : e.seg.y1 - e.gapFt / 2}
                      width={e.orientation === "v" ? e.gapFt : e.seg.x2 - e.seg.x1}
                      height={e.orientation === "v" ? e.seg.y2 - e.seg.y1 : e.gapFt}
                      fill={s.stroke} fillOpacity={0.18} />
                  )}
                  <line x1={e.seg.x1} y1={e.seg.y1} x2={e.seg.x2} y2={e.seg.y2}
                    stroke={s.stroke} strokeWidth={3} vectorEffect="non-scaling-stroke"
                    strokeDasharray={e.type === "ignore" ? "4 3" : "none"} />
                  <line x1={e.seg.x1} y1={e.seg.y1} x2={e.seg.x2} y2={e.seg.y2}
                    stroke="transparent" strokeWidth={12} vectorEffect="non-scaling-stroke" />
                  {(() => {
                    // Screen-capped (never balloons at high zoom) and sized
                    // to the actual text — the gap/chase-type suffix can
                    // make this longer than a fixed pill would allow. Every
                    // dimension here derives from jointFontFt (itself already
                    // screen-capped) rather than mixing in flat world-feet
                    // constants — the pill's own width-floor/height/offsets
                    // used to be flat (Math.max(6.4,...), height={2}, etc.),
                    // which ballooned into a huge box around small capped
                    // text at high zoom even after the text itself was fixed.
                    const jointFontFt = Math.min(1.15, 10 / pxPerFt);
                    const jointLabelText = `${lenShort(e.overlapFt, units)} ${s.label}${e.type === "cavity" ? ` · ${lenShort(e.gapFt, units, 1)} gap` : ""}${chase?.chaseType ? ` · ${chase.chaseType}` : ""}`;
                    const jointPadFt = jointFontFt * 0.35;
                    const jointLabelW = jointLabelText.length * jointFontFt * 0.62 + jointPadFt * 2;
                    const jointLabelH = jointFontFt * 1.7;
                    const vertical = e.orientation === "v";
                    return (
                      <g transform={`translate(${midX},${midY})`}>
                        <rect x={vertical ? jointPadFt : -jointLabelW / 2} y={vertical ? -jointLabelH / 2 : jointPadFt}
                          width={jointLabelW} height={jointLabelH}
                          rx={jointFontFt * 0.35} fill="var(--surface)" stroke={s.stroke} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                        <text x={vertical ? jointPadFt + jointLabelW / 2 : 0} y={vertical ? jointLabelH * 0.4 : jointPadFt + jointLabelH * 0.72} textAnchor="middle"
                          fontSize={jointFontFt} fontWeight="700" fill={s.stroke} style={{ userSelect: "none" }}>
                          {jointLabelText}
                        </text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {/* Edge debug overlay (opt-in Layers toggle) — the "why did the
                engine think this?" readout per detected Edge Run: which
                perimeter edge of each room paired, the signed gap along A's
                outward normal, projected overlap, parallel/facing scores,
                auto vs. effective type and confidence tier, plus each face's
                outward normal drawn as a short arrow (1 ft, world). Read-only. */}
            {!hiddenLayers.has("edgeDebug") && edges.map((e, i) => {
              const midX = (e.seg.x1 + e.seg.x2) / 2, midY = (e.seg.y1 + e.seg.y2) / 2;
              const font = Math.min(0.8, 9 / pxPerFt);
              const name = r => r.RoomName || r.RoomNumber || `#${r.RoomID}`;
              const num = (v, d = 2) => (v == null ? "—" : (+v).toFixed(d));
              const lines = [
                `${name(e.a)} E${e.edgeAIndex ?? "?"} ↔ ${name(e.b)} E${e.edgeBIndex ?? "?"} · run "${e.runKey}"`,
                `signed gap ${num(e.signedGapFt)}′ · overlap ${num(e.overlapFt, 1)}′`,
                `parallel ${num(e.parallelScore, 3)} · facing ${num(e.facingScore, 3)}`,
                `${e.auto} → ${e.type} · ${classifyConfidence(e, tunables)}`,
              ];
              const lineH = font * 1.35, pad = font * 0.4;
              const w = Math.max(...lines.map(l => l.length)) * font * 0.6 + pad * 2;
              const h = lines.length * lineH + pad * 2;
              const arrows = [];
              for (const [room, idx, fixed] of [[e.a, e.edgeAIndex, e.aFixed], [e.b, e.edgeBIndex, e.bFixed]]) {
                if (idx == null || fixed == null) continue;
                const pe = getRoomEdges(room).find(x => x.edgeIndex === idx);
                if (!pe) continue;
                const from = e.orientation === "v" ? { x: fixed, y: midY } : { x: midX, y: fixed };
                arrows.push({ from, to: { x: from.x + pe.outwardNormal.x, y: from.y + pe.outwardNormal.y } });
              }
              return (
                <g key={`dbg-${i}`} pointerEvents="none">
                  {arrows.map((a, k) => (
                    <g key={k}>
                      <line x1={a.from.x} y1={a.from.y} x2={a.to.x} y2={a.to.y} stroke="#7c3aed" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                      <circle cx={a.to.x} cy={a.to.y} r={font * 0.25} fill="#7c3aed" />
                    </g>
                  ))}
                  <g transform={`translate(${midX + font},${midY + font})`}>
                    <rect x={0} y={0} width={w} height={h} rx={font * 0.3} fill="var(--surface)" fillOpacity={0.92}
                      stroke="#7c3aed" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                    {lines.map((l, k) => (
                      <text key={k} x={pad} y={pad + lineH * (k + 0.8)} fontSize={font} fontFamily="ui-monospace, monospace"
                        fill={view.textFill} style={{ userSelect: "none" }}>{l}</text>
                    ))}
                  </g>
                </g>
              );
            })}

            {/* Drawn scope runs (model-based scope with TargetType='drawn') */}
            {!hiddenLayers.has("scope") && scopeApps.filter(a => a.TargetType === "drawn").map(a => {
              const g = parseGeom(a);
              if (!g?.points?.length) return null;
              const item = scopeItemsById[a.ScopeItemID];
              const set = item ? optionSetForItem(item) : null;
              const opt = set ? optionByKey(set, a.OptionKey) : null;
              const color = opt?.color || "#7c3aed";
              const ptsStr = g.points.map(p => `${p.x},${p.y}`).join(" ");
              const mid = g.points[Math.floor(g.points.length / 2)];
              const sel = selectedAppId === a.ScopeApplicationID;
              return (
                <g key={a.ScopeApplicationID}>
                  <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={sel ? 5 : 3.5}
                    strokeLinejoin="round" strokeLinecap="round" opacity={0.9} vectorEffect="non-scaling-stroke" />
                  <polyline points={ptsStr} fill="none" stroke="transparent" strokeWidth={14}
                    vectorEffect="non-scaling-stroke" style={{ cursor: isLocked ? "default" : "pointer" }}
                    onPointerDown={ev => ev.stopPropagation()}
                    onClick={() => !isLocked && setSelectedAppId(sel ? null : a.ScopeApplicationID)} />
                  {mid && (() => {
                    // Screen-capped and sized off the actual text — was a
                    // flat 14×2.4 world-feet box regardless of zoom, same
                    // bug as the wall/condition and joint labels above.
                    const scopeLabelFontFt = Math.min(1.2, 11 / pxPerFt);
                    const scopeLabelText = `${(opt?.label || "scope").split(" ")[0]} · ${lenShort(g.lengthFt, units)}`;
                    const scopeLabelW = scopeLabelText.length * scopeLabelFontFt * 0.62 + scopeLabelFontFt * 0.8;
                    return (
                      <g transform={`translate(${mid.x},${mid.y})`} pointerEvents="none">
                        <rect x={-scopeLabelW / 2} y={-scopeLabelFontFt * 2.2} width={scopeLabelW} height={scopeLabelFontFt * 2} rx={scopeLabelFontFt * 0.35} fill="var(--surface)" stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                        <text x={0} y={-scopeLabelFontFt * 0.7} textAnchor="middle" fontSize={scopeLabelFontFt} fontWeight={700} fill={color}>
                          {scopeLabelText}
                        </text>
                      </g>
                    );
                  })()}
                  {sel && !isLocked && (
                    <g transform={`translate(${g.points[0].x},${g.points[0].y})`}
                      onClick={() => { onDeleteScopeApp?.(a); setSelectedAppId(null); }}
                      style={{ cursor: "pointer" }}>
                      <circle r={1.7} fill="#dc2626" />
                      <text x={0} y={0.6} textAnchor="middle" fontSize={2.1} fontWeight={800} fill="#fff" pointerEvents="none">×</text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* Drawn conditions (Condition Library — cost-code + room takeoff) */}
            {!hiddenLayers.has("conditions") && takeoffConditions.filter(condLayerVisible).map(c => {
              const g = parseGeom(c);
              // Area conditions (closed polygons) have their own filled-polygon
              // render block below — skip them here. An UNASSIGNED auto-generated
              // wall condition (TargetType starts with 'auto_wall_', no type
              // picked yet — see sp_SyncAutoWallConditions) has Style=NULL but a
              // real 2-point line from the edge it was generated from (shared/
              // cavity segments only — an exterior-remainder segment has no
              // points at all and is a lump quantity, not drawable), so it's
              // deliberately NOT excluded here — it still renders as a plain
              // line, just visually flagged (dashed grey) as needing a type.
              if (!g?.points?.length || c.Style === "area") return null;
              const unassigned = !c.TakeoffConditionLibraryID && !c.TakeoffConditionTypeID && !c.Kind;
              const color = unassigned ? "#94a3b8" : (c.Color || "#0d9488");
              const ptsStr = g.points.map(p => `${p.x},${p.y}`).join(" ");
              const mid = g.points[Math.floor(g.points.length / 2)];
              const sel = selectedCondIds.has(c.TakeoffConditionDrawnID);
              const qty = c.QuantityOverride ?? Math.round(g.lengthFt);
              const room = c.RoomID ? rooms.find(r => r.RoomID === c.RoomID) : null;
              // A short Code (e.g. "W-1", matching OST's own condition-code
              // convention) keeps the drawn label compact; fall back to the
              // full Name for a type that hasn't been given one yet, or a
              // short "Unassigned" marker for an auto-wall segment that
              // still needs a type — the full instruction lives in the
              // hover title below instead of spelled out on the canvas
              // itself, which used to span most of the visible width.
              const faceBTyped = c.TakeoffConditionLibraryIDFaceB || c.TakeoffConditionTypeIDFaceB;
              const baseLabel = c.Code || c.Name || (unassigned ? "Unassigned" : "Condition");
              // A shared wall with an independently-assigned Face B (see
              // ConditionDetailFlyout's second picker) shows both codes on
              // the centerline — this line is one physical wall, but two
              // different assemblies, and the ring alone (rendered per room,
              // not per line) isn't visible when this label is the only
              // thing on screen at a low zoom.
              const label = faceBTyped && c.FaceBCode && c.FaceBCode !== c.Code ? `${baseLabel} / ${c.FaceBCode}` : baseLabel;
              // Screen-capped (never balloons at high zoom, same technique as
              // the joint label below) AND sized off the actual rendered text
              // (including the qty/room suffix, which the old flat labelW
              // formula never accounted for at all) — was a flat world-feet
              // Math.max(18, ...) with no pxPerFt scaling, so at high zoom the
              // pill grew into a huge box around comparatively tiny text.
              const labelFontFt = Math.min(1.2, 11 / pxPerFt);
              const labelText = `${label}${c.Quantity1Unit ? ` · ${qtyText({ q: qty, u: c.Quantity1Unit }, units)}` : ""}${room ? ` · ${room.RoomName}` : " · no room"}`;
              const labelPadFt = labelFontFt * 0.4;
              const labelW = labelText.length * labelFontFt * 0.62 + labelPadFt * 2;
              const dash = unassigned ? "2 1.5" : (c.Pattern ? PATTERN_DASH[c.Pattern] : undefined);
              return (
                <g key={c.TakeoffConditionDrawnID} data-cond-id={c.TakeoffConditionDrawnID}>
                  <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={sel ? 5 : 3.5}
                    strokeDasharray={dash} strokeLinejoin="round" strokeLinecap={dash ? "butt" : "round"} opacity={0.9} vectorEffect="non-scaling-stroke" />
                  <polyline points={ptsStr} fill="none" stroke="transparent" strokeWidth={14}
                    vectorEffect="non-scaling-stroke" {...reshapeHit(c, sel)} />
                  {/* Per-segment length callouts, OST-style — only while selected, to
                      avoid cluttering the canvas with every run's individual segments
                      at once (the aggregate label below always shows). */}
                  {sel && g.points.length > 1 && g.points.slice(1).map((p, i) => {
                    const p0 = g.points[i];
                    const segLen = Math.hypot(p.x - p0.x, p.y - p0.y);
                    if (segLen < 0.5) return null;
                    const segMid = { x: (p0.x + p.x) / 2, y: (p0.y + p.y) / 2 };
                    const angle = Math.atan2(p.y - p0.y, p.x - p0.x) * 180 / Math.PI;
                    const flipped = angle > 90 || angle < -90;
                    return (
                      <text key={i} x={segMid.x} y={segMid.y} dy={-0.6} textAnchor="middle"
                        transform={`rotate(${flipped ? angle + 180 : angle}, ${segMid.x}, ${segMid.y})`}
                        fontSize={Math.min(1.1, 10 / pxPerFt)} fontWeight={700} fill={color} pointerEvents="none"
                        style={{ ...haloStroke(view, Math.min(1.1, 10 / pxPerFt)) }}
                      >{fmt(segLen, 1)}′</text>
                    );
                  })}
                  {mid && (
                    <g transform={`translate(${mid.x},${mid.y})`} pointerEvents="none">
                      <rect x={-labelW / 2} y={-labelFontFt * 2.2} width={labelW} height={labelFontFt * 2} rx={labelFontFt * 0.35} fill="var(--surface)" stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                      {/* pointerEvents="auto" overrides the parent group above
                          so the hover title below actually fires — needed
                          now that "Unassigned wall — pick a type" moved from
                          always-visible text into this tooltip. */}
                      <text x={0} y={-labelFontFt * 0.7} textAnchor="middle" fontSize={labelFontFt} fontWeight={700} fill={color} pointerEvents="auto">
                        <title>{unassigned ? "Click to pick a condition type for this wall" : c.Name}</title>
                        {labelText}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* Area conditions (Condition Library Style='area') — closed
                polygon fills, same visual idiom as a room's own PolygonJson
                render above (fill+stroke from the type's Color, unassigned
                falls back to grey). */}
            {!hiddenLayers.has("conditions") && takeoffConditions.filter(c => c.Style === "area" && condLayerVisible(c)).map(c => {
              const g = parseGeom(c);
              if (!g?.points?.length || g.points.length < 3) return null;
              const unassigned = !c.TakeoffConditionLibraryID && !c.TakeoffConditionTypeID && !c.Kind;
              const color = unassigned ? "#94a3b8" : (c.Color || "#0d9488");
              const ptsStr = g.points.map(p => `${p.x},${p.y}`).join(" ");
              const cx = g.points.reduce((s, p) => s + p.x, 0) / g.points.length;
              const cy = g.points.reduce((s, p) => s + p.y, 0) / g.points.length;
              const sel = selectedCondIds.has(c.TakeoffConditionDrawnID);
              const room = c.RoomID ? rooms.find(r => r.RoomID === c.RoomID) : null;
              const label = c.Code || c.Name || "Area condition";
              const qtyStr = conditionQuantities(c).map(x => qtyText(x, units)).join(" / ");
              // Fill per the type's Pattern (OST area fill set); an unassigned
              // auto-generated area has no type, so it keeps the flat fill.
              const fillStyle = areaFillPattern(unassigned ? "" : c.Pattern, color, sel, c.PatternSpacingIn);
              const hasGrid = !unassigned && c.GridTileWFt > 0 && c.GridTileHFt > 0;
              return (
                <g key={c.TakeoffConditionDrawnID} data-cond-id={c.TakeoffConditionDrawnID}>
                  {/* A "none"/hatched fill has no solid interior to click — the
                      invisible twin below keeps the whole area clickable. */}
                  <polygon points={ptsStr} fill="#000" fillOpacity={0} stroke="none" {...reshapeHit(c, sel)} />
                  {hasGrid && <polygon points={ptsStr} fill={`url(#${areaGridId(c)})`} stroke="none" pointerEvents="none" />}
                  <polygon points={ptsStr} fill={fillStyle.fill} fillOpacity={fillStyle.fillOpacity}
                    stroke={color} strokeWidth={sel ? 3 : 1.6} vectorEffect="non-scaling-stroke" {...reshapeHit(c, sel)} />
                  <g transform={`translate(${cx},${cy})`} pointerEvents="none">
                    <text x={0} y={-0.8} textAnchor="middle" fontSize={Math.min(1.2, 11 / pxPerFt)} fontWeight={700} fill={color}
                      style={{ ...haloStroke(view, Math.min(1.2, 11 / pxPerFt)) }}>
                      <title>{c.Name}</title>
                      {label}{qtyStr ? ` · ${qtyStr}` : ""}{room ? ` · ${room.RoomName}` : " · no room"}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Double-click edit mode: a dashed box round the shape being moved / resized, with a corner handle
                (bottom-right) that scales it uniformly about the opposite corner. Sized in screen pixels. */}
            {editCondId != null && (() => {
              const row = allTakeoffConditions.find(r => r.TakeoffConditionDrawnID === editCondId);
              const b = row && reshapeBounds(row);
              if (!b) return null;
              const pad = 6 / pxPerFt, hs = 10 / pxPerFt, fs = Math.min(1.1, 10 / pxPerFt);
              return (
                <g data-reshape-for={editCondId}>
                  <rect x={b.minX - pad} y={b.minY - pad} width={b.maxX - b.minX + 2 * pad} height={b.maxY - b.minY + 2 * pad}
                    fill="none" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="5 3" vectorEffect="non-scaling-stroke" pointerEvents="none" />
                  <text x={b.minX - pad} y={b.minY - pad * 1.8} fontSize={fs} fontWeight={700} fill="#2563eb" pointerEvents="none"
                    style={{ ...haloStroke(view, fs) }}>Drag to move · drag the corner to resize · Esc to finish</text>
                  <rect data-reshape-handle x={b.maxX + pad - hs / 2} y={b.maxY + pad - hs / 2} width={hs} height={hs} fill="#fff" stroke="#2563eb"
                    strokeWidth={2} vectorEffect="non-scaling-stroke" style={{ cursor: "nwse-resize" }}
                    onPointerDown={ev => { if (ev.button === 0) startReshape(ev, "scale"); }} />
                </g>
              );
            })()}

            {/* Placed door/window/marker symbols (Condition Library Style='count') */}
            {!hiddenLayers.has("symbols") && takeoffConditions.filter(c => c.Style === "count" && condLayerVisible(c)).map(c => {
              const stored = parseGeom(c);
              if (!stored) return null;
              // While this specific symbol is mid-drag, render it at the live
              // snapped position instead of its stored one — the drag isn't
              // persisted until release (see onPointerUp's symbolDrag branch).
              const dragging = symbolDrag?.cond.TakeoffConditionDrawnID === c.TakeoffConditionDrawnID && symbolDrag.live;
              const geom = dragging ? { ...stored, ...symbolDrag.live } : stored;
              const color = c.Color || (c.SyncsToCriteriaField === "door_count" ? "#b45309" : c.SyncsToCriteriaField === "window_count" ? "#0284c7" : "#6b7280");
              const shape = resolveShape(c);
              const sel = selectedCondIds.has(c.TakeoffConditionDrawnID);
              const beginDrag = ev => {
                ev.stopPropagation();
                if (ev.button !== 0) return;
                if (isLocked || drawMode || condMode || routeMode || symbolMode || calib || imgMode || measureMode) return;
                setSymbolDrag({ cond: c, sx: ev.clientX, sy: ev.clientY, live: null, multi: ev.shiftKey || ev.ctrlKey || ev.metaKey });
                ev.currentTarget.setPointerCapture(ev.pointerId);
              };
              // Delete lives on the right-click menu and the Delete key — an on-canvas × was too easy to hit.
              const deleteBtn = () => null;

              // ── Free-placed marker (basic shapes, fire/electrical/
              // mechanical/plumbing symbols) — a point + a size, no wall/
              // jamb geometry at all. Hit-area and label both anchor off the
              // stored (x,y) directly. ─────────────────────────────────────
              if (!WALL_SNAPPED_SHAPES.has(shape)) {
                const widthFt = geom.widthFt || defaultSymbolWidthFt(shape);
                const hitR = Math.max(widthFt / 2, 0.9);
                return (
                  <g key={c.TakeoffConditionDrawnID} data-cond-id={c.TakeoffConditionDrawnID}>
                    <SymbolGlyph shape={shape} x={geom.x} y={geom.y} widthFt={widthFt} color={color} sel={sel} pattern={c.Pattern} spacingIn={c.PatternSpacingIn} />
                    {sel && <SelectionMark x={geom.x} y={geom.y} pxPerFt={pxPerFt} color={color} />}
                    {c.Code && (
                      <text x={geom.x} y={geom.y + hitR + 1.1}
                        textAnchor="middle" fontSize={Math.min(1.1, 10 / pxPerFt)} fontWeight={700} fill={color} pointerEvents="none">
                        <title>{c.Name}</title>
                        {c.Code}
                      </text>
                    )}
                    <circle cx={geom.x} cy={geom.y} r={hitR} fill="transparent"
                      style={{ cursor: isLocked ? "default" : "grab" }} onPointerDown={beginDrag}>
                      <title>{c.Name}</title>
                    </circle>
                    {deleteBtn(geom.x + hitR * 0.9, geom.y - hitR * 0.9)}
                  </g>
                );
              }

              // ── Wall-snapped (door/window family) — unchanged jamb-based
              // model, extended with the newer shape variants. ────────────
              // Stored widthFt comes from the type's DefaultWidthFt at
              // placement time (onPointerDown's symbolMode branch), falling
              // back to a per-shape default (defaultSymbolWidthFt) when the
              // type has none set.
              const effWidthFt = geom.widthFt || defaultSymbolWidthFt(shape);
              const g = symbolGeometry({ ...geom, widthFt: effWidthFt });
              const doubleWedges = shape === "double-door" ? doubleDoorWedges(g, effWidthFt) : null;
              const egressWedges = shape === "double-egress-door" ? doubleEgressWedges(g, effWidthFt) : null;
              return (
                <g key={c.TakeoffConditionDrawnID} data-cond-id={c.TakeoffConditionDrawnID}>
                  {shape === "door" && (
                    <>
                      <path d={wedgePath(g.hinge, g.arcPts)} fill={color} fillOpacity={sel ? 0.32 : 0.2} stroke="none" />
                      <polyline points={g.arcPts.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color} strokeWidth={sel ? 2.4 : 1} strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" />
                      <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                        stroke={color} strokeWidth={sel ? 4.5 : 2} vectorEffect="non-scaling-stroke" />
                    </>
                  )}
                  {(shape === "double-door" || shape === "double-egress-door") && (doubleWedges || egressWedges) && (() => {
                    const w = doubleWedges || egressWedges;
                    return (
                      <>
                        <path d={wedgePath(g.jambA, w.a)} fill={color} fillOpacity={sel ? 0.32 : 0.2} stroke="none" />
                        <path d={wedgePath(g.jambB, w.b)} fill={color} fillOpacity={sel ? 0.32 : 0.2} stroke="none" />
                        <polyline points={w.a.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color} strokeWidth={sel ? 2.4 : 1} strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" />
                        <polyline points={w.b.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color} strokeWidth={sel ? 2.4 : 1} strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" />
                        <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                          stroke={color} strokeWidth={sel ? 4.5 : 2} vectorEffect="non-scaling-stroke" />
                      </>
                    );
                  })()}
                  {shape === "window" && (
                    <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                      stroke={color} strokeWidth={sel ? 5 : 2.5} vectorEffect="non-scaling-stroke" />
                  )}
                  {(shape === "window-2" || shape === "window-3") && (() => {
                    const n = shape === "window-2" ? 2 : 3;
                    const perpX = -(g.jambB.y - g.jambA.y) / effWidthFt, perpY = (g.jambB.x - g.jambA.x) / effWidthFt;
                    const off = 0.25;
                    return Array.from({ length: n }, (_, i) => {
                      const t = n === 1 ? 0 : i / (n - 1) - 0.5;
                      const dx = perpX * off * (n - 1) * t * 2, dy = perpY * off * (n - 1) * t * 2;
                      return (
                        <line key={i} x1={g.jambA.x + dx} y1={g.jambA.y + dy} x2={g.jambB.x + dx} y2={g.jambB.y + dy}
                          stroke={color} strokeWidth={sel ? 2.4 : 1.8} vectorEffect="non-scaling-stroke" />
                      );
                    });
                  })()}
                  {shape === "bifold" && (() => {
                    const mid = { x: (g.jambA.x + g.jambB.x) / 2, y: (g.jambA.y + g.jambB.y) / 2 };
                    const foldPt = { x: mid.x + g.into.x * (effWidthFt / 4), y: mid.y + g.into.y * (effWidthFt / 4) };
                    return (
                      <>
                        <polyline points={`${g.jambA.x},${g.jambA.y} ${foldPt.x},${foldPt.y} ${g.jambB.x},${g.jambB.y}`}
                          fill="none" stroke={color} strokeWidth={sel ? 2.6 : 1.8} vectorEffect="non-scaling-stroke" />
                        <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                          stroke={color} strokeWidth={sel ? 1.4 : 1} strokeDasharray="1 1.2" vectorEffect="non-scaling-stroke" />
                      </>
                    );
                  })()}
                  {shape === "sliding-door" && (() => {
                    const off = 0.5;
                    const panelA = { x: g.jambA.x + g.into.x * off, y: g.jambA.y + g.into.y * off };
                    const panelB = { x: g.jambB.x + g.into.x * off, y: g.jambB.y + g.into.y * off };
                    return (
                      <>
                        <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                          stroke={color} strokeWidth={sel ? 1.4 : 1} strokeDasharray="1 1.2" vectorEffect="non-scaling-stroke" />
                        <line x1={panelA.x} y1={panelA.y} x2={panelB.x} y2={panelB.y}
                          stroke={color} strokeWidth={sel ? 3 : 2.2} vectorEffect="non-scaling-stroke" />
                      </>
                    );
                  })()}
                  {shape === "pass-through" && (
                    <polygon points={passThroughBoxPts(g, effWidthFt)}
                      fill={color} fillOpacity={sel ? 0.35 : 0.22} stroke={color} strokeWidth={sel ? 2.8 : 1.6} vectorEffect="non-scaling-stroke" />
                  )}
                  {sel && <SelectionMark x={geom.x} y={geom.y} pxPerFt={pxPerFt} color={color} />}
                  {(c.Mark || c.Code) && (
                    <text
                      x={geom.x + g.into.x * effWidthFt * 1.1} y={geom.y + g.into.y * effWidthFt * 1.1}
                      textAnchor="middle" fontSize={Math.min(1.1, 10 / pxPerFt)} fontWeight={700} fill={color} pointerEvents="none"
                    >
                      <title>{[c.Mark, c.Code, c.Name].filter(Boolean).join(" · ")}</title>
                      {/* An opening's Mark (P1 Phase C) is its identity — it labels the symbol; the type Code stays in the title */}
                      {c.Mark || c.Code}
                    </text>
                  )}
                  {/* Wide invisible hit-area — click to select, drag to reposition
                      along the nearest wall (see symbolDrag state above) */}
                  <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y}
                    stroke="transparent" strokeWidth={10} vectorEffect="non-scaling-stroke"
                    style={{ cursor: isLocked ? "default" : "grab" }}
                    onPointerDown={beginDrag}>
                    <title>{c.Name}</title>
                  </line>
                  {deleteBtn(geom.x + g.into.x * effWidthFt * 0.5, geom.y + g.into.y * effWidthFt * 0.5)}
                </g>
              );
            })}

            {/* Symbol placement ghost preview */}
            {symbolMode && symbolHover && (() => {
              const shape = resolveShape(symbolPickType);
              const color = symbolPickType?.Color || "#b45309";
              if (!WALL_SNAPPED_SHAPES.has(shape)) {
                const widthFt = symbolPickType?.DefaultWidthFt || defaultSymbolWidthFt(shape);
                return (
                  <g pointerEvents="none" opacity={0.55}>
                    <SymbolGlyph shape={shape} x={symbolHover.x} y={symbolHover.y} widthFt={widthFt} color={color} sel={false} pattern={symbolPickType?.Pattern} spacingIn={symbolPickType?.PatternSpacingIn} />
                  </g>
                );
              }
              const effWidthFt = symbolPickType?.DefaultWidthFt || defaultSymbolWidthFt(shape);
              const g = symbolGeometry({ ...symbolHover, widthFt: effWidthFt, flip: false });
              return (
                <g pointerEvents="none" opacity={0.55}>
                  <line x1={g.jambA.x} y1={g.jambA.y} x2={g.jambB.x} y2={g.jambB.y} stroke={color} strokeWidth={3} vectorEffect="non-scaling-stroke" />
                  {shape === "pass-through" && (
                    <polygon points={passThroughBoxPts(g, effWidthFt)} fill={color} fillOpacity={0.2} stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
                  )}
                  {shape === "door" && (
                    <>
                      <path d={wedgePath(g.hinge, g.arcPts)} fill={color} fillOpacity={0.2} stroke="none" />
                      <polyline points={g.arcPts.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={color} strokeWidth={1} strokeDasharray="1.2 1" vectorEffect="non-scaling-stroke" />
                    </>
                  )}
                  {(shape === "double-door" || shape === "double-egress-door") && (() => {
                    const dw = shape === "double-door" ? doubleDoorWedges(g, effWidthFt) : doubleEgressWedges(g, effWidthFt);
                    return (
                      <>
                        <path d={wedgePath(g.jambA, dw.a)} fill={color} fillOpacity={0.2} stroke="none" />
                        <path d={wedgePath(g.jambB, dw.b)} fill={color} fillOpacity={0.2} stroke="none" />
                      </>
                    );
                  })()}
                </g>
              );
            })()}

            {/* Phase G canvas routing — drawn conceptual route segments (UtilityRouteSegments.GeometryJson).
                Dashed so they read as conceptual, not coordinated; label = effective LF (+ elbow count). */}
            {!hiddenLayers.has("routes") && routes.filter(rt => Array.isArray(rt.geometry?.points) && rt.geometry.points.length >= 2).map(rt => {
              const pts = rt.geometry.points;
              const ptsStr = pts.map(pt => `${pt.x},${pt.y}`).join(" ");
              const mid = pts[Math.floor(pts.length / 2)];
              const color = rt.color || "#7c3aed";
              const active = routeMode && routePick === rt.segmentId;
              return (
                <g key={`route-${rt.segmentId}`} data-testid={`route-line-${rt.segmentId}`} opacity={active ? 0.35 : 1}>
                  <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={2.5} strokeDasharray="6 3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                  {pts.slice(1, -1).map((pt, i) => <circle key={i} cx={pt.x} cy={pt.y} r={2.5 / pxPerFt} fill="#fff" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />)}
                  <text x={mid.x + 1} y={mid.y - 0.8} fontSize={Math.min(1.2, 11 / pxPerFt)} fontWeight={700} fill={color} pointerEvents="none">
                    {rt.systemLabel ? `${rt.systemLabel} · ` : ""}{rt.label} · {fmt(rt.calculatedLF)}′{rt.turns ? ` · ${rt.turns} elbow${rt.turns === 1 ? "" : "s"}` : ""}
                  </text>
                </g>
              );
            })}
            {routeMode && routePts.length > 0 && (() => {
              const color = routePickSeg?.color || "#7c3aed";
              const chain = routeCursor ? [...routePts, routeCursor] : routePts;
              const ptsStr = chain.map(pt => `${pt.x},${pt.y}`).join(" ");
              const runLen = polylineLength(chain);
              return (
                <g pointerEvents="none">
                  <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={3} strokeDasharray="6 3" vectorEffect="non-scaling-stroke" />
                  {routePts.map((pt, i) => <circle key={i} cx={pt.x} cy={pt.y} r={3 / pxPerFt} fill={color} />)}
                  <RightAngleGlyph chain={chain} pxPerFt={pxPerFt} color={color} />
                  {routeCursor && <text x={routeCursor.x + 1.5} y={routeCursor.y - 1} fontSize={Math.min(1.4, 13 / pxPerFt)} fontWeight={800} fill={color}>{fmt(runLen)}′</text>}
                </g>
              );
            })()}

            {/* In-progress condition polyline */}
            {condMode && condPts.length > 0 && (() => {
              const color = condPickType?.Color || "#0d9488";
              const chain = condCursor ? [...condPts, condCursor] : condPts;
              const ptsStr = chain.map(p => `${p.x},${p.y}`).join(" ");
              const runLen = polylineLength(chain);
              return (
                <g pointerEvents="none">
                  <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={3} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  {condPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3 / pxPerFt} fill={color} />)}
                  <RightAngleGlyph chain={chain} pxPerFt={pxPerFt} color={color} />
                  {condCursor && (
                    <text x={condCursor.x + 1.5} y={condCursor.y - 1} fontSize={Math.min(1.4, 13 / pxPerFt)} fontWeight={800} fill={color}>{fmt(runLen)}′</text>
                  )}
                </g>
              );
            })()}

            {/* Back-outs (D-2): one Room Element's deduct polygons, drawn as their
                own hatched shapes over the room — never as a hole in it (UI brief
                §7). Not selectable here; the element card lists and removes them. */}
            {/* Plan search — ring on the hit just jumped to */}
            {searchMark && (
              <g pointerEvents="none">
                <circle cx={searchMark.x} cy={searchMark.y} r={22 / pxPerFt} fill="#ef4444" fillOpacity={0.12} stroke="#dc2626" strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
                <circle cx={searchMark.x} cy={searchMark.y} r={36 / pxPerFt} fill="none" stroke="#dc2626" strokeWidth={1.5} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
              </g>
            )}
            {/* Track I — highlight rings on the rows a quantity was built from */}
            {highlightCondIds.size > 0 && allTakeoffConditions.filter(c => highlightCondIds.has(c.TakeoffConditionDrawnID)).map(c => {
              let g = null; try { g = JSON.parse(c.GeometryJson || "null"); } catch { g = null; }
              const pt = Array.isArray(g?.points) && g.points.length ? { x: g.points.reduce((a, p) => a + p.x, 0) / g.points.length, y: g.points.reduce((a, p) => a + p.y, 0) / g.points.length } : (g && Number.isFinite(+g.x) ? { x: +g.x, y: +g.y } : null);
              if (!pt) return null;
              const r = 14 / pxPerFt;
              return (
                <g key={`hl-${c.TakeoffConditionDrawnID}`} pointerEvents="none">
                  <circle cx={pt.x} cy={pt.y} r={r} fill="#facc15" fillOpacity={0.18} stroke="#ca8a04" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  <circle cx={pt.x} cy={pt.y} r={r * 1.6} fill="none" stroke="#ca8a04" strokeWidth={1} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
                </g>
              );
            })}
            {/* Track H — layout review overlay for one room: every HEPA / light
                footprint (green = clear, red = in a conflict), a line between
                conflicting pairs, the lighting spacing limit as a ring around
                each light. Read-only; the flyout's Review layout button and the
                Layout review items toggle it. */}
            {layoutReviewRoomId != null && layoutReviewByRoom.get(layoutReviewRoomId) && (() => {
              const r = layoutReviewByRoom.get(layoutReviewRoomId);
              const maxSp = r.systems.light?.maxSpacingFt;
              return (
                <g pointerEvents="none">
                  {r.footprints.map(f => (
                    <g key={`lr-${f.id}`}>
                      <rect x={f.x - f.w / 2} y={f.y - f.d / 2} width={f.w} height={f.d} transform={f.angleDeg ? `rotate(${f.angleDeg} ${f.x} ${f.y})` : undefined}
                        fill={f.state === "conflict" ? "#dc2626" : "#16a34a"} fillOpacity={0.12} stroke={f.state === "conflict" ? "#dc2626" : "#16a34a"} strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
                      {f.system === "light" && maxSp > 0 && <circle cx={f.x} cy={f.y} r={maxSp / 2} fill="none" stroke="#f59e0b" strokeWidth={1} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" opacity={0.7} />}
                    </g>
                  ))}
                  {r.conflicts.filter(c => c.conditionIds?.length === 2).map((c, i) => {
                    const a = r.footprints.find(f => f.id === c.conditionIds[0]), b = r.footprints.find(f => f.id === c.conditionIds[1]);
                    if (!a || !b) return null;
                    return <line key={`lrc-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={c.severity === "warn" ? "#dc2626" : "#f59e0b"} strokeWidth={2} vectorEffect="non-scaling-stroke" />;
                  })}
                  {r.conflicts.filter(c => c.at && c.conditionIds?.length === 1).map((c, i) => (
                    <text key={`lrt-${i}`} x={c.at.x} y={c.at.y - (r.footprints.find(f => f.id === c.conditionIds[0])?.d || 2) / 2 - 0.4} textAnchor="middle" fontSize={11 / pxPerFt} fontWeight={800} fill={c.severity === "warn" ? "#dc2626" : "#b45309"}
                      style={{ ...haloStroke(view, 11 / pxPerFt) }}>{c.code === "layout_outside_room" ? "outside room" : c.code === "layout_crosses_wall" ? "crosses wall" : "review"}</text>
                  ))}
                </g>
              );
            })()}
            {!hiddenLayers.has("conditions") && backOuts.map(c => {
              const ring = backOutRing(c.GeometryJson);
              if (!ring) return null;
              const el = roomElements.find(r => r.RoomElementID === c.AppliesToRoomElementID);
              const ptsStr = ring.map(p => `${p.x},${p.y}`).join(" ");
              const cx = ring.reduce((a, p) => a + p.x, 0) / ring.length, cy = ring.reduce((a, p) => a + p.y, 0) / ring.length;
              return (
                <g key={`bo-${c.TakeoffConditionDrawnID}`} pointerEvents="none">
                  <polygon points={ptsStr} fill="#dc2626" fillOpacity={0.10} stroke="#dc2626" strokeWidth={1.4} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
                  <text x={cx} y={cy} textAnchor="middle" fontSize={Math.min(1.1, 10 / pxPerFt)} fontWeight={700} fill="#b91c1c" style={{ ...haloStroke(view, Math.min(1.1, 10 / pxPerFt)) }}>
                    ⛔ Back-out{el ? ` · ${el.Name || el.Code}` : " · element removed"} · −{fmt(polygonArea(ring))} SF
                  </text>
                </g>
              );
            })}
            {backOutMode && backOutPts.length > 0 && (() => {
              const color = "#dc2626";
              const chain = backOutCursor ? [...backOutPts, backOutCursor] : backOutPts;
              const ptsStr = chain.map(p => `${p.x},${p.y}`).join(" ");
              const areaFt2 = chain.length >= 3 ? polygonArea(chain) : 0;
              return (
                <g pointerEvents="none">
                  {chain.length >= 3
                    ? <polygon points={ptsStr} fill={color} fillOpacity={0.14} stroke={color} strokeWidth={2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                    : <polyline points={ptsStr} fill="none" stroke={color} strokeWidth={2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />}
                  {backOutPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3 / pxPerFt} fill={color} />)}
                  {backOutCursor && chain.length >= 3 && (
                    <text x={backOutCursor.x} y={backOutCursor.y - 0.6} fontSize={Math.min(1.1, 11 / pxPerFt)} fontWeight={700} fill={color} style={{ ...haloStroke(view, Math.min(1.1, 11 / pxPerFt)) }}>−{fmt(areaFt2)} SF</text>
                  )}
                </g>
              );
            })()}
            {areaMode && areaPts.length > 0 && (() => {
              const color = areaPickType?.Color || "#0d9488";
              const chain = areaCursor ? [...areaPts, areaCursor] : areaPts;
              const ptsStr = chain.map(p => `${p.x},${p.y}`).join(" ");
              const closedStr = chain.length >= 3 ? `${ptsStr} ${chain[0].x},${chain[0].y}` : ptsStr;
              const areaFt2 = chain.length >= 3 ? polygonArea(chain) : 0;
              return (
                <g pointerEvents="none">
                  {chain.length >= 3 ? (
                    <polygon points={ptsStr} fill={color} fillOpacity={0.14} stroke={color} strokeWidth={2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  ) : (
                    <polyline points={closedStr} fill="none" stroke={color} strokeWidth={2} strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                  )}
                  {areaPts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3 / pxPerFt} fill={color} />)}
                  <RightAngleGlyph chain={chain} pxPerFt={pxPerFt} color={color} />
                  {areaCursor && chain.length >= 3 && (
                    <text x={areaCursor.x + 1.5} y={areaCursor.y - 1} fontSize={Math.min(1.4, 13 / pxPerFt)} fontWeight={800} fill={color}>{fmt(areaFt2)} SF</text>
                  )}
                </g>
              );
            })()}
            {/* The legend is part of the drawing: drawn here, in drawing space, so it scales with the sheet and is in any snapshot / printout. */}
            {legend.on && activeSheetId != null && (
              <TakeoffLegend entries={legendList} legend={legend} ftPerInch={legendFtPerInch} defaultPos={legendDefaultPos} pxPerFt={pxPerFt}
                draggable={!anyToolActive} onMove={setLegendDragPos} onMoveEnd={(pos) => { setLegend({ pos }); setLegendDragPos(null); }} />
            )}
          </g>

          {/* Graphic scale bar (screen-fixed overlay) */}
          <g transform={`translate(16, ${canvasHeight - 28})`} pointerEvents="none">
            <rect x={-8} y={-22} width={barPx + 90} height={30} rx={4} fill="var(--surface)" opacity={0.85} />
            <line x1={0} y1={0} x2={barPx} y2={0} stroke="var(--text)" strokeWidth={2} />
            <line x1={0} y1={-5} x2={0} y2={0} stroke="var(--text)" strokeWidth={2} />
            <line x1={barPx} y1={-5} x2={barPx} y2={0} stroke="var(--text)" strokeWidth={2} />
            <text x={barPx / 2} y={-8} textAnchor="middle" fontSize={11} fontWeight={700} fill={view.scaleBarStroke}>{fmt(barFeet)} ft</text>
            <text x={barPx + 8} y={2} fontSize={10} fill="var(--text3)">≈ 1&Prime; : {fmt(inchPerFt, inchPerFt < 10 ? 1 : 0)} ft</text>
          </g>

          {/* Full-canvas crosshair guide (screen-fixed overlay, same pattern
              as the scale bar above) — a horizontal + vertical line through
              the cursor spanning the WHOLE visible canvas, not just a small
              native cursor icon, so lining up a new room/condition/symbol
              against something elsewhere on the sheet doesn't need
              eyeballing it. Only shown in the precision draw/place/measure/
              calibrate modes (see onPointerMove's own gating) — the same
              set that already switches the OS cursor to a crosshair icon. */}
          {crosshairPos && (() => {
            // Solid coloured line over a contrasting halo (reads on black linework and on the dark view), with an empty
            // gap and a ring at the pick point. Layers (status bar ▸ crosshair menu): the ring is the real snap reach,
            // the line takes the tool's colour, a snap preview marks where a click will land and names it, an angle
            // guide shows the locked angle, and optional X/Y/Δ box and nearby snap points.
            const xh = crosshairSpec(crosshairStyle, view.key === "dark", crosshairTuning);
            if (!xh) return null;
            const L = crosshairLayers;
            const mode = modeLabel({ sweep: sweepMode, calib: !!calib, imgMode, measure: measureMode, cond: condMode, area: areaMode, route: routeMode, backOut: backOutMode, symbol: symbolMode, draw: drawMode });
            const toolCol = L.toolColor ? crosshairToolColor({ mode, colors: { cond: condPickType?.Color, area: areaPickType?.Color, route: routePickSeg?.color, symbol: symbolPickType?.Color } }) : null;
            const line = toolCol || xh.line;
            const sx = (x) => x * pxPerFt + pan.x, sy = (y) => y * pxPerFt + pan.y;
            const cx = sx(crosshairPos.x), cy = sy(crosshairPos.y);
            const W = svgRef.current?.clientWidth || 4000;
            const tr = drawMode && roomPts.length ? { pts: roomPts, cursor: roomCursor }
              : condMode && condPts.length ? { pts: condPts, cursor: condCursor }
              : routeMode && routePts.length ? { pts: routePts, cursor: routeCursor }
              : areaMode && areaPts.length ? { pts: areaPts, cursor: areaCursor }
              : backOutMode && backOutPts.length ? { pts: backOutPts, cursor: backOutCursor } : null;
            const snapsHere = condMode || routeMode || areaMode || backOutMode || drawMode; // the tools whose clicks snap to corners / wall lines
            const tolFt = tunables.VERTEX_SNAP_PX / pxPerFt;
            const snap = snapsHere ? snapDrawnPoint(crosshairPos, effRooms, wallSegments, tolFt) : null;
            const target = tr?.cursor || (snap?.kind ? snap : null);
            const tgt = target ? { x: sx(target.x), y: sy(target.y) } : null;
            const ringR = L.ring && snapsHere ? tunables.VERTEX_SNAP_PX : xh.ring;
            const gap = Math.max(xh.gap, ringR + 2);
            const arm = xh.arm || null; // Precision cursor: short arms; Bold / Standard span the whole drawing
            const segs = [[cx, arm ? Math.max(0, cy - arm) : 0, cx, cy - gap], [cx, cy + gap, cx, arm ? Math.min(canvasHeight, cy + arm) : canvasHeight], [arm ? Math.max(0, cx - arm) : 0, cy, cx - gap, cy], [cx + gap, cy, arm ? Math.min(W, cx + arm) : W, cy]];
            const halo = { stroke: xh.halo, opacity: xh.haloOpacity };
            const label = (x, y, text, color = line, anchor = "start") => (
              <text x={x} y={y} fontSize={12} fontWeight={800} fill={color} textAnchor={anchor} style={{ paintOrder: "stroke", stroke: xh.halo, strokeWidth: 4 }}>{text}</text>
            );

            const prev = tr ? tr.pts[tr.pts.length - 1] : null;
            const prevS = prev ? { x: sx(prev.x), y: sy(prev.y) } : null;
            const showSnap = L.snapPreview && tgt && (snap?.kind || Math.hypot(tgt.x - cx, tgt.y - cy) > 2);
            const arcInfo = L.angleGuide && prev && tr.cursor && Math.hypot(tr.cursor.x - prev.x, tr.cursor.y - prev.y) > 0.05 ? angleArc(prevS, segmentAngleDeg(prev, tr.cursor), 28) : null;
            const dots = L.magnetic && snapsHere ? nearbySnapTargets(crosshairPos, effRooms, wallSegments, tolFt * 3, 12) : [];

            let readout = null;
            if (L.readout) {
              const at = target || crosshairPos;
              const rows = [`X ${fmtFeetInches(at.x)}`, `Y ${fmtFeetInches(at.y)}`];
              if (prev) rows.push(`Δ ${fmtFeetInches(Math.hypot(at.x - prev.x, at.y - prev.y))}`);
              if (snap?.kind) rows.push(snapKindLabel(snap.kind));
              const bw = 138, bh = rows.length * 16 + 10;
              const bx = cx + 18 + bw > W ? cx - 18 - bw : cx + 18, by = cy + 18 + bh > canvasHeight ? cy - 18 - bh : cy + 18;
              readout = (
                <g>
                  <rect x={bx} y={by} width={bw} height={bh} rx={5} fill={xh.halo} opacity={0.92} stroke={line} strokeWidth={1.5} />
                  {rows.map((r, i) => <text key={i} x={bx + 8} y={by + 18 + i * 16} fontSize={12} fontWeight={i === rows.length - 1 && snap?.kind ? 800 : 600} fill={i === rows.length - 1 && snap?.kind ? line : (view.textFill || "var(--text)")}>{r}</text>)}
                </g>
              );
            }

            return (
              <g pointerEvents="none" data-testid="crosshair">
                {showSnap && (
                  <g>
                    {[[tgt.x, 0, tgt.x, canvasHeight], [0, tgt.y, W, tgt.y]].map(([x1, y1, x2, y2], i) => (
                      <g key={i}>
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={xh.halo} strokeWidth={3} opacity={0.5} />
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={line} strokeWidth={1} strokeDasharray="5 4" opacity={0.75} />
                      </g>
                    ))}
                  </g>
                )}
                {arcInfo && prevS && tgt && (() => {
                  const dx = tgt.x - prevS.x, dy = tgt.y - prevS.y, n = Math.hypot(dx, dy) || 1;
                  const ray = [prevS.x, prevS.y, prevS.x + (dx / n) * 4000, prevS.y + (dy / n) * 4000];
                  return (
                    <g>
                      <line x1={prevS.x} y1={prevS.y} x2={prevS.x + 40} y2={prevS.y} stroke={line} strokeWidth={1} opacity={0.6} />
                      <line x1={ray[0]} y1={ray[1]} x2={ray[2]} y2={ray[3]} stroke={line} strokeWidth={1} strokeDasharray="7 5" opacity={0.5} />
                      <path d={arcInfo.d} fill="none" stroke={xh.halo} strokeWidth={4.5} opacity={0.7} />
                      <path d={arcInfo.d} fill="none" stroke={line} strokeWidth={2} />
                      {label(arcInfo.label.x, arcInfo.label.y, arcInfo.ortho ? `${arcInfo.text} ortho` : arcInfo.text)}
                    </g>
                  );
                })()}
                {dots.map((d, i) => (
                  <g key={i}>
                    <circle cx={sx(d.x)} cy={sy(d.y)} r={i === 0 ? 5 : 3.5} fill={line} stroke={xh.halo} strokeWidth={1.5} opacity={i === 0 ? 1 : 0.8} />
                  </g>
                ))}
                {[[xh.halo, xh.haloWidth, xh.haloOpacity], [line, xh.lineWidth, xh.lineOpacity]].map(([stroke, w, o], k) => (
                  <g key={k} stroke={stroke} strokeWidth={w} opacity={o} strokeLinecap="butt">
                    {segs.map(([x1, y1, x2, y2], i) => <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />)}
                  </g>
                ))}
                <circle cx={cx} cy={cy} r={ringR} fill="none" stroke={halo.stroke} strokeWidth={xh.lineWidth + 2.5} opacity={halo.opacity} />
                <circle cx={cx} cy={cy} r={ringR} fill="none" stroke={line} strokeWidth={xh.lineWidth} opacity={xh.lineOpacity} />
                {showSnap && (
                  <g>
                    <polygon points={`${tgt.x},${tgt.y - 7} ${tgt.x + 7},${tgt.y} ${tgt.x},${tgt.y + 7} ${tgt.x - 7},${tgt.y}`} fill={xh.halo} stroke={line} strokeWidth={2.25} />
                    {snap?.kind && label(tgt.x + 12, tgt.y - 10, snapKindLabel(snap.kind))}
                  </g>
                )}
                {readout}
              </g>
            );
          })()}
        </svg>
        <CanvasStatusBar
          compact={tierInfo.compact}
          mode={modeLabel({ sweep: sweepMode, calib: !!calib, imgMode, measure: measureMode, cond: condMode, area: areaMode, route: routeMode, backOut: backOutMode, symbol: symbolMode, draw: drawMode })}
          cursorRef={cursorElRef}
          scale={scaleChip({ isPdf, settings, knownScales: [...ARCH_SCALES, ...ENGINEERING_SCALES] })}
          needsScale={needsScale}
          units={units} onToggleUnits={toggleDisplayUnits}
          info={selectedCondIds.size > 1 ? `${selectedCondIds.size} conditions selected — Delete removes · Ctrl+C copies · Ctrl+D duplicates` : null}
          onScaleClick={() => { const el = scaleSelectRef.current; if (el) { el.focus(); try { el.showPicker?.(); } catch { /* not allowed without a gesture */ } } }}
          shapeText={`${effRooms.filter(r => r.CanvasX != null && r.CanvasX !== "").length} rooms · ${takeoffConditions.length} conditions`}
          saveState={saving || undoInfo?.busy ? "saving" : "saved"}
          readOnlyReason={isLocked ? "read-only" : null}
          crosshair={crosshairStyle}
          onCrosshairStyle={(st) => { setCrosshairStyle(st); writeCrosshairStyle(st); }}
          tuning={crosshairTuning}
          onTuningChange={(next) => { setCrosshairTuning(next); writeCrosshairTuning(next); }}
          layers={crosshairLayers}
          onLayersChange={(next) => { setCrosshairLayers(next); writeCrosshairLayers(next); }}
        />

        {drawMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: "#2563eb", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            {(() => {
              const placing = placingRoomId != null ? rooms.find(r => r.RoomID === placingRoomId) : null;
              const who = placing ? `“${placing.RoomName}”` : "room";
              return roomPts.length > 0
                ? `✏️ Tracing ${who} — click each corner, angle-locked to ${tunables.DRAW_ANGLE_STEP_DEG}° steps (Shift = free angle, Alt = no snap) · Enter/double-click finish · Backspace undo · Esc cancel`
                : `✏️ Draw ${who} — drag a box, or click to trace corners (Alt = no snap) · Esc to exit`;
            })()}
          </div>
        )}
        {measureMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            📏 Drag between two points — compare the readout against a dimension already on the drawing · Esc to exit
          </div>
        )}
        {routeMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: routePickSeg?.color || "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none", zIndex: 5 }} data-testid="route-draw-banner">
            🧭 Tracing route {routePickSeg?.systemLabel ? `${routePickSeg.systemLabel} · ` : ""}{routePickSeg?.label || `#${routePick}`} — click along the run (hold Shift for a free angle) · Enter finish · Backspace undo · Esc cancel · length becomes the segment's calculated LF, turns its elbow count
          </div>
        )}
        {condMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: condPickType?.Color || "#0d9488", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            📐 Drawing {condPickType?.Name || "condition"} — click along the walls (hold Shift for a free angle) · Enter finish · Backspace undo · Esc cancel
          </div>
        )}
        {symbolMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: symbolPickType?.Color || "#b45309", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            🚪 Placing {symbolPickType?.Name || "symbol"} — click a wall to place · Esc to stop{selectedCondId ? " · S to flip swing" : ""}
          </div>
        )}
        {editNotice && (
          <div style={{ position: "absolute", top: 44, left: 8, zIndex: 6, background: "var(--tint-red)", border: "1px solid #fca5a5", color: "#991b1b", fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 6, maxWidth: 420 }}>
            ⚠ {editNotice}
          </div>
        )}
        {calibPrompt && (() => {
          // The printed dimension is typed in the viewer's units (metres when metric) and checked in feet.
          const expectedFt = isMetric(units) ? (calibPrompt.input === "" ? "" : (parseLengthToFeet(calibPrompt.input, units) ?? "")) : calibPrompt.input;
          const check = verifyScale({ measuredFt: calibPrompt.worldDist, expectedFt });
          const proposal = proposedCalibration({ worldDist: calibPrompt.worldDist, expectedFt, isPdf, ftPerInch, ftPerPx: imgScale });
          const v = SCALE_VERDICTS[check.verdict];
          const tone = check.verdict === "good" ? "#166534" : check.verdict === "review" ? "#92400e" : check.verdict === "fail" ? "#991b1b" : "var(--text3)";
          const commit = (recalibrate) => {
            if (recalibrate && proposal && proposal.value > 0) onSaveCanvas({ [proposal.key]: proposal.value }, { prevScale: isPdf ? ftPerInch : imgScale });
            if (check.verdict !== "invalid") setScaleCheck({ verdict: check.verdict, errorPct: check.errorPct, expectedFt: check.expectedFt, measuredFt: check.measuredFt, recalibrated: !!recalibrate });
            setCalibPrompt(null);
          };
          return (
            <div onPointerDown={e => e.stopPropagation()} style={{ position: "absolute", top: 8, left: 8, zIndex: 5, width: 340, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", padding: "10px 12px", fontSize: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>📐 Verify scale</div>
              <div style={{ color: "var(--text2)" }}>At the current scale those two points are <strong>{lenText(calibPrompt.worldDist, units)}</strong> {isMetric(units) ? "" : `(${fmt(calibPrompt.worldDist, 2)} ft) `}apart.</div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                <span style={{ whiteSpace: "nowrap" }}>Printed dimension</span>
                <input autoFocus type={isMetric(units) ? "text" : "number"} inputMode="decimal" min={0} step="any" value={calibPrompt.input} placeholder={isMetric(units) ? "e.g. 3.8 or 380 cm" : "feet"}
                  onChange={e => setCalibPrompt(c => ({ ...c, input: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter" && check.verdict !== "invalid") commit(check.verdict !== "good"); if (e.key === "Escape") setCalibPrompt(null); }}
                  style={{ width: 90, fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4, background: "var(--surface)", color: "var(--text)" }} />
                <span>{isMetric(units) ? "m" : "ft"}</span>
              </label>
              <div style={{ marginTop: 6, fontWeight: 700, color: tone }}>
                {check.verdict === "invalid" ? v.label : `${v.label} — ${fmtPct(check.errorPct)} error${check.deltaFt != null ? ` (${check.deltaFt > 0 ? "+" : ""}${isMetric(units) ? fmtLength(check.deltaFt, units) : `${fmt(check.deltaFt, 2)} ft`})` : ""}`}
              </div>
              {proposal && check.verdict !== "invalid" && (
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  Re-calibrating would set {isPdf ? `${fmt(proposal.value, 3)} ft per paper inch` : `${proposal.value.toFixed(4)} ft per pixel`} ({proposal.changePct > 0 ? "+" : ""}{fmtPct(proposal.changePct)} vs now{isPdf && nearestArchScale(proposal.value) ? `, nearest ${nearestArchScale(proposal.value).scale.label}` : ""}).
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setCalibPrompt(null)} style={btn(false)}>Cancel</button>
                <button onClick={() => commit(false)} disabled={check.verdict === "invalid"} style={btn(false)} title="Record the verdict and keep the current calibration">Keep current scale</button>
                <button onClick={() => commit(true)} disabled={check.verdict === "invalid" || !proposal || isLocked} style={{ ...btn(true), opacity: check.verdict === "invalid" || !proposal || isLocked ? 0.5 : 1 }} title="Overwrite the sheet's calibration so these two points measure the printed dimension">Re-calibrate to this</button>
              </div>
            </div>
          );
        })()}
        {backOutMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: "#dc2626", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            ⛔ Drawing a back-out for {backOutTarget?.label} — click each corner (hold Shift for a free angle) · Enter/double-click finish (3+ corners) · Backspace undo · Esc cancel
          </div>
        )}
        {areaMode && (
          <div style={{ position: "absolute", top: 8, left: 8, background: areaPickType?.Color || "#0d9488", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            🟩 Tracing {areaPickType?.Name || "area"} — click each corner (hold Shift for a free angle) · Enter/double-click finish (3+ corners) · Backspace undo · Esc cancel
          </div>
        )}
        {settings?.NeedsReview && (
          <div role="alert" style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 24, maxWidth: 640, display: "flex", alignItems: "center", gap: 10, background: "var(--tint-amber)", border: "1px solid #f59e0b", color: "#92400e", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8, boxShadow: "0 4px 14px rgba(0,0,0,0.15)" }}>
            <span>⚠ This drawing's file was replaced (version {settings.VersionNumber || 1}). Its takeoffs were measured on the previous file — check they still line up (Verify scale, Measure), then mark it reviewed.</span>
            {!isLocked && <button onClick={() => onSaveCanvas({ needs_review: 0 })} style={{ ...btn(false), whiteSpace: "nowrap" }}>Mark reviewed</button>}
            {onLoadSheetVersions && <button onClick={() => setVersionsOpen(true)} style={{ ...btn(false), whiteSpace: "nowrap" }}>Versions…</button>}
          </div>
        )}
        {versionsOpen && settings && onLoadSheetVersions && (
          <SheetVersionsPanel sheet={settings} loadVersions={onLoadSheetVersions} onRestore={onRestoreSheetVersion} isLocked={isLocked} onClose={() => setVersionsOpen(false)} />
        )}
        {guideOpen && <ShortcutGuide onClose={() => setGuideOpen(false)} />}
        {addWindowOpen && (
          <AddConditionWindow types={drawableConditionTypes} rawTypes={rawConditionTypes} costCodes={costCodes} onCreateOneOff={onCreateOneOff} anchorRect={svgRef.current?.getBoundingClientRect?.()} activeKey={activeConditionPickId} drawing={conditionDrawActive}
            onSaveOverride={onSaveTypeOverride} onStart={beginConditionDraw} onSwitch={switchConditionType}
            onStop={stopConditionDraw} onClose={() => setAddWindowOpen(false)} locked={isLocked} />
        )}
        {counterOpen && <LiveCounter here={counterHere} all={counterAll} onClose={toggleCounter} />}
        {paletteOpen && (
          <CommandPalette
            onClose={() => setPaletteOpen(false)}
            items={buildPaletteItems({
              locked: isLocked, needsScale,
              flags: { drawMode, measureMode, calib: !!calib, imgMode, sweepMode, hasSelection: selectedIds.size > 0, hasDrawing: !!settings?.BackgroundImage, isPdf },
              undo: undoInfo, conditionTypes, pins, sheets, activeSheetId, scales: [...ARCH_SCALES, ...ENGINEERING_SCALES],
              dark: darkDrawing, uiDark: uiTheme === "dark", legendOn: legend.on, focusOn: focusMode, units, crosshair: crosshairStyle, layers: crosshairLayers, layerDefs: CROSSHAIR_LAYERS,
              run: {
                pointer: pointerTool, drawRoom: toggleDrawMode, measure: toggleMeasure, calibrate: toggleCalibrate, moveDrawing: toggleMoveDrawing, rotateDrawingCw: () => rotateDrawing(90), rotateDrawingCcw: () => rotateDrawing(-90),
                sweep: onCountByExample ? toggleSweep : null, search: () => setSearchOpen(true), backOut: () => setBackOutPickerOpen(true),
                armType: beginConditionDraw, selectSheet: (id) => onSelectSheet?.(id), setScale: pickScale,
                guide: () => setGuideOpen(true), togglePin: toggleTypePin,
                undo: () => onUndo?.(), redo: () => onRedo?.(), fit: fitView, zoomSelection: fitSelection, toggleDark: toggleDarkDrawing, toggleUiDark: toggleUiTheme, toggleLegend: () => setLegend({ on: !legend.on }), toggleFocus: () => setFocusMode(f => !f), toggleUnits: toggleDisplayUnits,
                setCrosshair: (st) => { setCrosshairStyle(st); writeCrosshairStyle(st); },
                toggleLayer: (key) => setCrosshairLayers(l => { const n = { ...l, [key]: !l[key] }; writeCrosshairLayers(n); return n; }),
              },
            })}
          />
        )}
        {sweepSeed && (
          <CountByExamplePanel
            revisionId={revisionId} sheets={sheets} activeSheetId={activeSheetId} seed={sweepSeed}
            conditionTypes={conditionTypes} conditions={sweepConditions} tunables={tunables} isLocked={isLocked}
            onMarkers={setSweepMarkers}
            onGo={(sheetId, x, y) => {
              const sheet = sheets.find(s => s.SheetID === sheetId);
              if (!sheet) return;
              searchPageSentRef.current = false;
              setPendingFocus({ sheetId, page: sheet.ImagePage || 1, x, y });
              if (sheetId !== activeSheetId) onSelectSheet?.(sheetId);
            }}
            onApply={({ conditionTypeId, placements }) => {
              const t = conditionTypes.find(c => c.TypeKey === conditionTypeId);
              return onCountByExample({ conditionTypeId, placements, widthFt: t?.DefaultWidthFt || defaultSymbolWidthFt(resolveShape(t)) });
            }}
            onPickAgain={() => { setSweepSeed(null); setSweepMarkers([]); setSweepMode(true); }}
            onClose={() => { setSweepSeed(null); setSweepMarkers([]); }}
          />
        )}
        {searchOpen && (
          <PlanSearchPanel sheets={sheets} activeSheetId={activeSheetId} activePage={pageNum} onGo={goToSearchHit} onClose={() => setSearchOpen(false)} />
        )}
        {isPdf && pdfError && (
          <div style={{ position: "absolute", top: 8, right: 8, background: "var(--surface)", border: "1px solid #dc2626", color: "#dc2626", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            {pdfError}
          </div>
        )}
        {isPdf && !pdfError && !renderUrl && (
          <div style={{ position: "absolute", top: 8, right: 8, background: "var(--surface)", border: "1px solid var(--cs-border)", color: "var(--text3)", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            Rendering PDF…
          </div>
        )}
        {focusMode && (
          <div role="toolbar" aria-label="Focus mode tools" style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 15, display: "flex", gap: 4, alignItems: "center", padding: 4, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.25)" }}>
            <button onClick={pointerTool} style={qbtn(!anyToolActive)} title="Pointer (Esc leaves any tool)" aria-label="Pointer">↖</button>
            <button onClick={toggleDrawMode} disabled={isLocked} style={{ ...qbtn(drawMode), opacity: isLocked ? 0.45 : 1 }} title="Draw a room (D)" aria-label="Draw room">▭</button>
            <button onClick={() => { setFocusMode(false); openDockPanel("conditions"); }} style={qbtn(condMode || symbolMode || areaMode)} title="Pick a condition — leaves focus mode and opens the Conditions panel" aria-label="Condition">📐</button>
            <button onClick={onUndo} disabled={!undoInfo?.canUndo || isLocked} style={{ ...qbtn(false), opacity: undoInfo?.canUndo && !isLocked ? 1 : 0.45 }} title="Undo (Ctrl+Z)" aria-label="Undo">↶</button>
            <button onClick={onRedo} disabled={!undoInfo?.canRedo || isLocked} style={{ ...qbtn(false), opacity: undoInfo?.canRedo && !isLocked ? 1 : 0.45 }} title="Redo (Ctrl+Y)" aria-label="Redo">↷</button>
            <button onClick={fitView} style={qbtn(false)} title="Fit all rooms (F)">Fit</button>
            {sheets.length > 1 && <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, -1))} style={iconBtn} title="Previous drawing  ( [ )" aria-label="Previous drawing">‹</button>}
            {sheets.length > 1 && <button onClick={() => onSelectSheet?.(adjacentSheetId(sheets, activeSheetId, 1))} style={iconBtn} title="Next drawing  ( ] )" aria-label="Next drawing">›</button>}
            <button onClick={() => setFocusMode(false)} style={qbtn(true)} title="Exit focus mode (M or Esc)">⤢ Exit</button>
          </div>
        )}

        {layoutReviewRoomId != null && layoutReviewByRoom.get(layoutReviewRoomId) && (() => {
          const r = layoutReviewByRoom.get(layoutReviewRoomId);
          const name = rooms.find(x => x.RoomID === layoutReviewRoomId)?.RoomName || "room";
          return (
            <div style={{ position: "absolute", bottom: 8 + STATUS_BAR_H, left: 8, background: "var(--surface)", border: "1px solid #16a34a", color: "var(--text2)", fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, display: "flex", gap: 8, alignItems: "center" }}>
              <span>🔍 Layout review · {name}</span>
              <span style={{ color: "var(--text3)" }}>HEPA {r.systems.hepa.placed}/{r.systems.hepa.effective} · lights {r.systems.light.placed}/{r.systems.light.effective}</span>
              <span style={{ color: r.counts.warn ? "#dc2626" : r.counts.info ? "#b45309" : "#15803d" }}>{r.counts.total ? `${r.counts.warn} conflict${r.counts.warn === 1 ? "" : "s"} · ${r.counts.info} note${r.counts.info === 1 ? "" : "s"}` : "no conflicts"}</span>
              <button onClick={() => setLayoutReviewRoomId(null)} style={iconBtn} title="Close layout review">✕</button>
            </div>
          );
        })()}
        {!drawMode && !condMode && !routeMode && !symbolMode && !areaMode && !backOutMode && selectedIds.size > 0 && !isLocked && (
          <div style={{ position: "absolute", top: 8, left: 8, background: "var(--text)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 6, pointerEvents: "none" }}>
            {selectedIds.size === 1 ? "1 room selected" : `${selectedIds.size} rooms selected`} — Delete to remove
            {selectedIds.size === 1 ? " · drag corner/edge to resize · R to rotate" : " · drag to move together"} · Shift-click to add/remove · Hold Alt to ignore snap
          </div>
        )}
      </div>

      {/* A3 — side-by-side: another sheet, read-only, beside the working canvas */}
      {compareSheet && (
        <SheetComparePane sheet={compareSheet} height={canvasHeight} view={view}
          onSwap={() => { const cur = activeSheetId; onSelectSheet?.(compareSheet.SheetID); setCompareSheetId(cur); }}
          onClose={() => setCompareSheetId(null)} />
      )}

      {/* Right dock — Keyplan/Rooms/Conditions/Layers, stacked as four
          always-visible, independently collapsible/scrollable sections
          (per direct feedback — replaces the old one-tab-at-a-time
          switcher) next to the canvas rather than as separate full-page
          views (see the dockOpen state comment above) */}
      {!focusMode && (
      <div style={drawerDock ? { position: "absolute", top: 6, right: dockOpen ? DRAWER_W + 14 : 6, zIndex: 26 } : { display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <button
          onClick={() => setDockOpen(o => !o)}
          title={dockOpen ? "Hide dock" : "Show dock"}
          style={{
            width: 32, height: 32, borderRadius: 6, cursor: "pointer", fontSize: 15,
            border: `1px solid ${dockOpen ? "var(--cs-blue)" : "var(--cs-border)"}`,
            background: dockOpen ? "var(--cs-blue)" : "var(--surface)",
            color: dockOpen ? "#fff" : "var(--text2)",
          }}
        >{dockOpen ? "◂" : "▸"}</button>
        {!drawerDock && (
          <button
            onClick={() => { setDockFloat({ floating: !dockFloating }); setDockOpen(true); }}
            title={dockFloating ? "Dock the panel back beside the drawing" : "Pop the panel out into a window you can drag around"}
            aria-label={dockFloating ? "Dock the panel" : "Pop out the panel"}
            style={{
              width: 32, height: 32, marginTop: 6, borderRadius: 6, cursor: "pointer", fontSize: 15,
              border: `1px solid ${dockFloating ? "var(--cs-blue)" : "var(--cs-border)"}`,
              background: dockFloating ? "var(--cs-blue)" : "var(--surface)",
              color: dockFloating ? "#fff" : "var(--text2)",
            }}
          >{dockFloating ? "⇲" : "⇱"}</button>
        )}
      </div>
      )}
      {dockOpen && !drawerDock && !focusMode && !dockFloating && (
        <div onPointerDown={startDockResize} onDoubleClick={() => { setDockWidth(effDockLayout.twoCol ? DOCK_TWO_COL_W : DOCK_ONE_COL_W); try { localStorage.removeItem("cs.takeoff.dockWidth"); } catch { /* ignore */ } }}
          title="Drag to resize the panel · double-click to reset"
          style={{ width: 6, flexShrink: 0, cursor: "col-resize", alignSelf: "stretch", margin: "0 -2px", borderRadius: 3, background: "transparent", touchAction: "none" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--cs-border)"; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }} />
      )}
      {dockOpen && !focusMode && dockFloating && (
        <div role="dialog" aria-label="Takeoff panel"
          style={{
            position: "fixed", left: dockFloat.x, top: dockFloat.y, width: dockFloat.w, height: dockFloat.h, zIndex: 55,
            display: "flex", flexDirection: "column", background: "var(--surface)", border: "1px solid var(--cs-border)",
            borderRadius: 8, boxShadow: "0 14px 40px rgba(0,0,0,0.32)", overflow: "hidden",
          }}>
          <div onPointerDown={e => startDockFloatGesture(e, "move")} title="Drag to move the panel"
            style={{ height: DOCK_FLOAT_TITLE_H, flexShrink: 0, padding: "0 6px 0 10px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#0f2744", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "move", userSelect: "none", touchAction: "none" }}>
            <span>Takeoff panel</span>
            <span style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setDockFloat({ floating: false })} title="Dock the panel back beside the drawing" aria-label="Dock the panel"
                style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", fontSize: 14, padding: "0 4px" }}>⇲</button>
              <button onClick={() => setDockOpen(false)} title="Hide the panel (▸ shows it again)" aria-label="Hide the panel"
                style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", fontSize: 13, padding: "0 4px" }}>✕</button>
            </span>
          </div>
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: effDockLayout.twoCol ? "row" : "column" }}>
            {dockColumns(effDockLayout).map((paneKeys, ci) => (
              <div key={ci} style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", borderLeft: ci > 0 ? "1px solid var(--cs-border)" : "none" }}>
                {paneKeys.map(k => <Fragment key={k}>{dockPanelNodes[k]}</Fragment>)}
                {paneKeys.length === 0 && <div style={{ padding: 12, fontSize: 11.5, color: "var(--text3)" }}>No panels here — use ▦ Panels to show some.</div>}
              </div>
            ))}
          </div>
          <div onPointerDown={e => startDockFloatGesture(e, "resize")} title="Drag to resize the panel" aria-hidden="true"
            style={{ position: "absolute", right: 0, bottom: 0, width: 14, height: 14, cursor: "nwse-resize", touchAction: "none",
              background: "linear-gradient(135deg, transparent 50%, var(--cs-border) 50%)" }} />
        </div>
      )}
      {dockOpen && !focusMode && !dockFloating && (
        <div style={{
          width: drawerDock ? Math.min(DRAWER_W, Math.max(240, tierInfo.width - 70)) : (effDockLayout.twoCol ? dockWidth : (dockWidth >= 600 ? DOCK_ONE_COL_W : dockWidth)),
          flexShrink: 0, height: canvasHeight, display: "flex", flexDirection: effDockLayout.twoCol ? "row" : "column",
          border: "1px solid var(--cs-border)", borderRadius: 8, background: "var(--surface)", overflow: "hidden",
          ...(drawerDock ? { position: "absolute", top: 0, right: 0, zIndex: 25, boxShadow: "-8px 0 24px rgba(0,0,0,0.28)" } : null),
        }}>
          {dockColumns(effDockLayout).map((paneKeys, ci) => (
            <div key={ci} style={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", borderLeft: ci > 0 ? "1px solid var(--cs-border)" : "none" }}>
              {paneKeys.map(k => <Fragment key={k}>{dockPanelNodes[k]}</Fragment>)}
              {paneKeys.length === 0 && <div style={{ padding: 12, fontSize: 11.5, color: "var(--text3)" }}>No panels here — use ▦ Panels to show some.</div>}
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Wall takeoff summary — one slim line; the how-to text that used to sit beside it is the (i) tooltip. Hidden in a small window / focus mode to give its height to the drawing. */}
      {summaryShown && (
      <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: "var(--text2)" }}>
        {[
          ["Placed", `${takeoff.placedCount}/${rooms.length}`],
          ["Net wall LF", fmt(takeoff.netWallLF)],
          ["Net wall SF", fmt(takeoff.netWallSF)],
          ["Shared (deduped)", qtyText({ q: takeoff.sharedLF, u: "LF" }, units)],
          ["Return cavities", takeoff.cavityPairs ? `${takeoff.cavityPairs} · ${qtyText({ q: takeoff.cavityPlanLF, u: "LF" }, units)} plan · ${qtyText({ q: takeoff.cavityWallSF, u: "SF" }, units)} walls` : "0"],
        ].map(([label, value]) => (
          <span key={label}><strong style={{ fontVariantNumeric: "tabular-nums", color: "var(--text)" }}>{value}</strong> <span style={{ color: "var(--text3)" }}>{label}</span></span>
        ))}
        <span style={{ cursor: "help", color: "var(--text3)", fontWeight: 700 }}
          title={"Click a joint to cycle shared → cavity → ignored → auto. Cavity joints keep both wall faces in the takeoff (low-wall return boxouts); shared joints count one wall.\n\nThe ring hugging each room shows its assigned wall assembly: solid = a Condition Type is picked, dashed grey = not yet. A flush shared wall with a different finish on each side is set from that wall's Condition Detail (a second \"other room's face\" picker appears there). Toggle the ring off under Layers if it clutters a dense sheet."}>ⓘ How walls work</span>
      </div>
      )}

      {detailRoomId && onSaveRoom && (
        <RoomDetailFlyout
          room={rooms.find(r => r.RoomID === detailRoomId)}
          layers={layers}
          zones={zones}
          isLocked={isLocked}
          criteria={criteria}
          scopeByDiscipline={scopeByDiscipline}
          scopeExclusions={scopeExclusions}
          onSaveRoom={onSaveRoom}
          onSaveCriterion={onSaveCriterion}
          onToggleRoomScope={onToggleRoomScope}
          roomScopeItems={roomScopeItems}
          onSaveRoomScopeItem={onSaveRoomScopeItem}
          onDeleteRoomScopeItem={onDeleteRoomScopeItem}
          costCodes={costCodes}
          roomTemplates={roomTemplates}
          neighborWalls={neighborWalls}
          onChangeRoomTemplate={onChangeRoomTemplate}
          systemsCtx={systemsCtx}
          takeoffConditions={allTakeoffConditions}
          rooms={rooms}
          onResetAutoLayout={onResetAutoLayout}
          onAddBackOut={!isLocked && onCreateBackOut ? beginBackOutDraw : undefined}
          onDeleteBackOut={!isLocked && onDeleteTakeoffCondition ? onDeleteTakeoffCondition : undefined}
          drawingBackOutFor={backOutMode ? (backOutTarget?.roomElementId ?? null) : null}
          onHighlight={highlightGeometry}
          layoutReview={layoutReviewByRoom.get(detailRoomId) || null}
          layoutReviewOn={layoutReviewRoomId === detailRoomId}
          onReviewLayout={(r) => setLayoutReviewRoomId(cur => cur === r.RoomID ? null : r.RoomID)}
          onRejectProposal={!isLocked && onDeleteRooms ? (r => onDeleteRooms([r])) : undefined}
          onDrawRoom={!isLocked && onPlaceDrawnRoom ? (r => startDrawingRoom(r.RoomID)) : undefined}
          isDrawingThis={drawMode && placingRoomId === detailRoomId}
          roomElements={roomElements}
          catalog={catalog}
          onSaveRoomElement={onSaveRoomElement}
          onDeleteRoomElement={onDeleteRoomElement}
          onPlaceElement={!isLocked ? placeElement : undefined}
          onClose={closeRoomDetail}
        />
      )}

      {pickGeom && (
        <RoomPickDialog
          geom={pickGeom}
          candidates={rooms.filter(r => !rectOf(r))}
          onPick={(r) => { onPlaceDrawnRoom?.(r, pickGeom); setPickGeom(null); }}
          onNew={(name) => { onAddRoom({ name, ...pickGeom }); setPickGeom(null); }}
          onCancel={() => setPickGeom(null)}
        />
      )}
      {editingCondId && onSaveTakeoffCondition && (
        <ConditionDetailFlyout
          condition={takeoffConditions.find(c => c.TakeoffConditionDrawnID === editingCondId)}
          conditions={takeoffConditions}
          conditionTypes={conditionTypes}
          layers={layers}
          rooms={rooms}
          isLocked={isLocked}
          onSave={onSaveTakeoffCondition}
          onClose={() => setEditingCondId(null)}
        />
      )}

      {addingCondition && onCreateManualCondition && (
        <ConditionDetailFlyout
          condition={null}
          conditionTypes={conditionTypes}
          rooms={rooms}
          isLocked={isLocked}
          onCreate={onCreateManualCondition}
          onClose={() => setAddingCondition(false)}
        />
      )}

      <ContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} />
      {chaseEdgeKey && onSaveChaseAttributes && (() => {
        const chaseEdge = edges.find(e => edgeOverrideKey(e.a.RoomID, e.b.RoomID, e.runKey) === chaseEdgeKey);
        if (!chaseEdge) return null; // edge no longer exists (room moved/deleted since right-click)
        return (
          <ChaseDetailFlyout
            edge={chaseEdge}
            attrs={edgeLookup(chaseAttrs, chaseEdge)}
            isLocked={isLocked}
            onSave={attrs => onSaveChaseAttributes(chaseEdge, attrs)}
            onClose={() => setChaseEdgeKey(null)}
          />
        );
      })()}
    </div>
    </UnitsContext.Provider>
  );
}
